import React, { useState, useEffect, useCallback } from 'react';
import PublicCalendar from './src/crm-public-calendar.jsx';
import PawsGallery from './src/paws-gallery.jsx';
import EventGallery from './src/event-gallery.jsx';
import EventGalleriesPanel, { PeoplePanel } from './src/event-galleries-admin.jsx';
import { adminHostActive, adminUrl, onAdminHost, siteUrl } from './src/hosts.js';
import { CONTACT_EMAIL } from './src/contact.js';

// The CRM and Team sections are admin-only and pull in the heaviest modules in
// the app (calendar, invoices, contracts, document upload). Splitting them out
// keeps them off the critical path for every public visitor, who will never
// open either one.
const CRMSection = React.lazy(() => import('./src/crm-section.jsx'));
const TeamSection = React.lazy(() => import('./src/team-section.jsx'));

function AdminChunk({ children }) {
  return (
    <React.Suspense fallback={<div style={{ padding: 40, fontFamily: fonts, fontSize: 13, color: palette.textMuted }}>Loading…</div>}>
      {children}
    </React.Suspense>
  );
}
import ContractSignPage from './src/contract-sign.jsx';
import ContractorOnboardPage from './src/contractor-onboard.jsx';
import SiteShell from './src/site/SiteShell.jsx';
import { matchSiteRoute, isRedirect, navigate, NAV_LINKS } from './src/site/routes.js';
import { trackBooking } from './src/meta-pixel.js';

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

const API_BASE = import.meta.env.VITE_API_URL || '';

// /paws-gallery -> the index; /paws-gallery/<slug> -> one pet's page.
// Returns null when the path is not the gallery at all.
function parsePawsGalleryPath(pathname) {
  const p = (pathname || '').replace(/\/+$/, '');
  if (p !== '/paws-gallery' && !p.startsWith('/paws-gallery/')) return null;
  const slug = p === '/paws-gallery' ? null : decodeURIComponent(p.slice('/paws-gallery/'.length));
  return { slug: slug || null };
}

// /events/<event> -> that event's gallery; /events/<event>/<slug> -> one
// person's portrait. Returns null for anything else, including a bare /events.
function parseEventGalleryPath(pathname) {
  const p = (pathname || '').replace(/\/+$/, '');
  if (p === '/events') return { event: null, slug: null };
  if (!p.startsWith('/events/')) return null;
  const [event, slug] = p.slice('/events/'.length).split('/').map((x) => { try { return decodeURIComponent(x); } catch { return x; } });
  if (!event) return null;
  return { event, slug: slug || null };
}

// Whether signing in as an admin should drop straight into the dashboard.
//
// Without the admin subdomain this is the long-standing behaviour: anywhere but
// a marketing page. With it, the dashboard has an address of its own, so an
// admin browsing the customer site stays on the customer site and the dashboard
// opens only where it lives.
function shouldOpenDashboard() {
  if (typeof window === 'undefined') return false;
  if (adminHostActive()) return onAdminHost();
  return !matchSiteRoute(window.location.pathname);
}

const ROOMS = [
  { id: 'upstairs-1', label: 'Full Studio' },
  { id: 'upstairs-2', label: 'Podcast Recording' },
];

const SERVICES = [
  { id: 'photography', label: 'Photography', room: 'upstairs-1' },
  { id: 'video-production', label: 'Video Production', room: 'upstairs-1' },
  { id: 'podcast-creator', label: 'Podcast — Creator Package', room: 'upstairs-2' },
  { id: 'podcast-pro', label: 'Podcast — Pro Package', room: 'upstairs-2' },
  { id: 'social-content', label: 'Social Content', room: 'upstairs-1' },
  { id: 'studio-rental', label: 'Studio Rental', room: 'upstairs-1' },
];

function getServicesForRoom(roomId, pkgConfig, { includeDisabled = false } = {}) {
  const podcastPackages = pkgConfig?.podcastPackages || PODCAST_PACKAGES;
  const base = SERVICES.filter((s) => s.room === roomId);

  if (roomId !== 'upstairs-2') return base;

  // For the podcast room, derive services from podcastPackages config so
  // admin-added packages show up and disabled ones are hidden.
  const services = base.filter((s) => {
    const pkg = podcastPackages[s.id];
    if (!pkg) return false;
    return includeDisabled ? true : pkg.enabled !== false;
  });

  // Include any admin-added packages (keys not in the static SERVICES list)
  for (const [pkgId, pkg] of Object.entries(podcastPackages)) {
    if (services.find((s) => s.id === pkgId)) continue;
    if (!includeDisabled && pkg.enabled === false) continue;
    services.push({
      id: pkgId,
      label: pkg.serviceLabel || (pkg.name ? `Podcast — ${pkg.name}` : pkgId),
      room: 'upstairs-2',
    });
  }
  return services;
}

// ---------------------------------------------------------------------------
// Podcast Package Pricing
// ---------------------------------------------------------------------------

const PODCAST_PACKAGES = {
  'podcast-creator': {
    name: 'Creator Package',
    tagline: 'Audio + Video + Content',
    description: 'Perfect for: Podcasters, content creators, small brands',
    durations: [
      { hours: 1, price: 150 },
      { hours: 2, price: 280 },
      { hours: 4, price: 520 },
    ],
    includes: [
      'Professional audio + single camera video',
      '4x 60-second social clips (IG, TikTok, Shorts ready)',
      '1x YouTube episode edit (up to 10 min)',
      'Basic editing (cleanup, levels, color correction)',
      'Studio engineer present',
      'MP3 audio + MP4 video exports',
    ],
  },
  'podcast-pro': {
    name: 'Pro Package',
    tagline: 'Full Production + Multi-Angle',
    description: 'Perfect for: Brands, serious podcasters, production companies',
    durations: [
      { hours: 2, price: 420 },
      { hours: 4, price: 800 },
    ],
    includes: [
      'Multi-angle video with live switching (final edited video)',
      'Professional audio recording',
      'Studio engineer + producer present',
      '6x 60-second social clips (4 standard + 2 premium w/ graphics)',
      'Full episode YouTube edit (up to 10 min)',
      'Episode editing (up to 7 edits included)',
      'BTS photography (30 minutes)',
      'MP3 audio + MP4 video exports',
    ],
  },
};

const PODCAST_ADD_ONS = [
  { id: 'same-day-booking', label: 'Same-Day Booking', price: 30 },
  { id: 'late-night', label: 'Late Night (after 10pm)', price: 25 },
  { id: 'extra-social-clip', label: 'Additional Social Clip', price: 40 },
  { id: 'extended-editing', label: 'Extended Editing (per edit)', price: 10 },
  { id: 'rushed-delivery', label: 'Rushed Deliverables (24hr)', price: 35 },
  { id: 'lapel-mic', label: 'Lapel Mic (per person)', price: 50 },
];

function isPodcastService(serviceId) {
  return serviceId === 'podcast-creator' || serviceId === 'podcast-pro';
}

function getPodcastPrice(serviceId, hours) {
  const pkg = PODCAST_PACKAGES[serviceId];
  if (!pkg) return null;
  const dur = pkg.durations.find((d) => d.hours === hours);
  return dur ? dur.price : null;
}

function getPodcastAddOnsTotal(selectedAddOns) {
  return selectedAddOns.reduce((sum, id) => {
    const addon = PODCAST_ADD_ONS.find((a) => a.id === id);
    return sum + (addon ? addon.price : 0);
  }, 0);
}

const OPEN_HOUR = 8;
const CLOSE_HOUR = 20;
const MIN_HOURS = 3;

// Generate start time options (8:00 AM through 7:00 PM to allow min 3hrs)
const START_TIMES = [];
for (let h = OPEN_HOUR; h <= CLOSE_HOUR - MIN_HOURS; h++) {
  const label = h === 0 ? '12:00 AM' : h < 12 ? `${h}:00 AM` : h === 12 ? '12:00 PM' : `${h - 12}:00 PM`;
  START_TIMES.push({ value: `${String(h).padStart(2, '0')}:00`, label });
}

function formatTime(timeStr) {
  const h = parseInt(timeStr.split(':')[0]);
  if (h === 0) return '12:00 AM';
  if (h < 12) return `${h}:00 AM`;
  if (h === 12) return '12:00 PM';
  return `${h - 12}:00 PM`;
}

function formatEndTime(startTime, hours) {
  const h = parseInt(startTime.split(':')[0]) + hours;
  if (h === 0) return '12:00 AM';
  if (h < 12) return `${h}:00 AM`;
  if (h === 12) return '12:00 PM';
  if (h === 24) return '12:00 AM';
  return `${h - 12}:00 PM`;
}

const ADD_ONS = [
  { id: 'aputure-300d', label: 'Aputure 300D Light', price: 50 },
  { id: 'camera-cart-kit', label: 'Digital Camera Cart Kit', price: 75, note: 'Atomos Sumo 19" monitor · Inovativ Voyager 36 · HDMI included' },
  { id: 'backdrop-7x9', label: '7x9 Hand-Painted Textured Backdrop', price: 50, note: '24hr notice · no stepping · damage = full charge', options: ['Gray + Light Beige', 'Warm Gray', 'Rosy + Beige', 'Green + Beige'] },
  { id: 'backdrop-8x12', label: '8x12 Ash Gray Hand-Painted Textured Backdrop', price: 75, note: '24hr notice · no stepping · damage = full charge' },
  { id: 'backdrop-colored', label: 'In-Stock Colored Backdrop', price: 25, options: ['Orange', 'Green', 'Red'] },
  { id: 'profoto-flash', label: '2x Profoto 500W Flash Set', price: 150, note: 'Bowen mount adapters · compatible trigger required' },
];

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// ---------------------------------------------------------------------------
// Toast notification component
// ---------------------------------------------------------------------------

function Toast({ message, type, onDismiss }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 4000);
    return () => clearTimeout(t);
  }, [message]);

  return (
    <div style={{
      position: 'fixed', top: 24, right: 24, zIndex: 99999,
      background: type === 'error' ? '#a84032' : '#6b7c5e',
      color: '#ffffff', padding: '14px 22px', fontSize: 13,
      fontFamily: `'Space Mono', 'Courier Prime', 'Courier New', monospace`,
      fontWeight: 700, letterSpacing: 0.5,
      boxShadow: '0 4px 20px rgba(0,0,0,0.18)',
      display: 'flex', alignItems: 'center', gap: 14,
      maxWidth: 420,
      animation: 'toastSlideIn 0.3s ease-out',
    }}>
      <span>{message}</span>
      <button onClick={onDismiss} style={{
        background: 'none', border: 'none', color: '#ffffff',
        fontSize: 18, cursor: 'pointer', padding: 0, lineHeight: 1,
        opacity: 0.8, fontFamily: 'inherit',
      }}>&times;</button>
      <style>{`@keyframes toastSlideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const palette = {
  cream: '#faf8f3',
  beige: '#f5f1e8',
  orange: '#b8560f',
  orangeLight: '#d4893f',
  sage: '#7a8a5e',
  sageLight: '#a3b085',
  brown: '#3d2b1f',
  brownLight: '#5c4033',
  text: '#2c2016',
  textMuted: '#7a6e60',
  border: '#d9d0c3',
  white: '#ffffff',
  red: '#a84032',
  blocked: '#c9b99a',
  partial: '#e8f0dc',
};

// Returns a brand color as rgba() with the given alpha — used for the blog's
// low-opacity text/border treatments without introducing new color tokens.
const withAlpha = (hex, a) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
};

const fonts = `'Space Mono', 'Courier Prime', 'Courier New', monospace`;

// The marketing site's tokens, mirrored from src/site/theme.js (light theme —
// the booking flow has no dark mode). The booking pages and the pages that lead
// into them should read as one site, so the chrome around both comes from the
// same numbers rather than two sets that drift.
const sans = `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif`;
const site = {
  paper: '#fdf9f3',
  ink: '#2b1810',
  muted: '#6b5648',
  accent: '#d97540',
  accentInk: '#b8560f',
  line: '#e0d3c2',
  ground: '#2b1810',
  onGround: '#fdf9f3',
  groundMuted: '#b8a495',
  groundLine: '#4a2e1f',
};

// iOS-style sliding toggle. Drop-in replacement for `<input type="checkbox">`
// for boolean settings — pass `checked` + `onChange(boolean)`.
function Switch({ checked, onChange, disabled, ariaLabel }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!!checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      style={{
        position: 'relative',
        width: 44,
        height: 26,
        flexShrink: 0,
        borderRadius: 999,
        background: checked ? palette.sage : palette.border,
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        padding: 0,
        transition: 'background 0.15s',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 2,
          left: checked ? 20 : 2,
          width: 22,
          height: 22,
          borderRadius: '50%',
          background: palette.white,
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          transition: 'left 0.15s',
        }}
      />
    </button>
  );
}

const baseStyles = {
  app: {
    fontFamily: fonts,
    background: palette.cream,
    color: palette.text,
    minHeight: '100vh',
    lineHeight: 1.5,
  },
  container: {
    maxWidth: 960,
    margin: '0 auto',
    padding: '0 20px',
  },
};

// ---------------------------------------------------------------------------
// Utility helpers
// ---------------------------------------------------------------------------

function api(path, options = {}) {
  const token = localStorage.getItem('hddn_token');
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(`${API_BASE}${path}`, { ...options, headers }).then(async (r) => {
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw { status: r.status, ...data };
    return data;
  });
}

// Cents in, dollars out — and never rounded. A $57.50 charge that renders as
// "$58" is wrong on an invoice and wrong in a revenue total, and tips make
// fractional amounts the norm rather than the exception. Whole dollars still
// print clean, so $50 stays "$50" rather than "$50.00".
function money(cents) {
  const v = (cents || 0) / 100;
  return `$${Number.isInteger(v) ? v : v.toFixed(2)}`;
}

function daysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function firstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
}

function formatDate(y, m, d) {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function todayString() {
  const t = new Date();
  return formatDate(t.getFullYear(), t.getMonth(), t.getDate());
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

// The site header, shared by the booking flow and every legacy page.
//
// It carries the marketing site's own NAV_LINKS and its exact chrome — the dark
// ground, the accent rule, the mono uppercase links — so crossing from /studio
// into /book no longer looks like arriving at a different product. The links
// that only exist over here (Manage Booking, the account menu) sit alongside
// them; Paws, the journal and the newsletter moved to the footer, which is
// where the marketing site keeps its secondary links.
function Header({ user, onLogout, onProfile, onAdmin, onAdminLogin, onCustomerView, onLogin, onManageBooking, onBlog, onSubscribe, onPhotographer, onAbout, onGallery }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const closeNav = () => setNavOpen(false);

  const here = typeof window !== 'undefined' ? window.location.pathname : '';

  const linkStyle = (active) => ({
    background: 'none',
    border: 'none',
    fontFamily: fonts,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: active ? site.accent : site.groundMuted,
    padding: '10px 12px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  });

  const navBtn = (label, onClick, active) => (
    <button
      key={label}
      onClick={onClick}
      className="hddn-touch-target"
      style={linkStyle(active)}
      onMouseEnter={(e) => { e.currentTarget.style.color = site.onGround; }}
      onMouseLeave={(e) => { e.currentTarget.style.color = active ? site.accent : site.groundMuted; }}
    >
      {label}
    </button>
  );

  const siteLinks = NAV_LINKS.map((l) => navBtn(l.label, () => navigate(l.href), here === l.href));

  return (
    <header style={{ position: 'sticky', top: 0, zIndex: 40, background: site.ground, color: site.onGround, borderBottom: `2px solid ${site.accent}` }}>
      <div
        className="hddn-container"
        style={{ ...baseStyles.container, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, minHeight: 62 }}
      >
        <a
          href="/"
          onClick={(e) => { if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return; e.preventDefault(); navigate('/'); }}
          aria-label="HDDN LAB — home"
          style={{ display: 'flex', alignItems: 'baseline', gap: 8, textDecoration: 'none', color: site.onGround, minWidth: 0 }}
        >
          <b style={{ fontFamily: sans, fontWeight: 800, fontSize: 17, letterSpacing: '0.16em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>HDDN LAB</b>
          <i style={{ fontStyle: 'normal', fontFamily: fonts, fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', color: site.accent, whiteSpace: 'nowrap' }}>New Rochelle</i>
        </a>

        <div className="hddn-nav-desktop" style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {siteLinks}
          {navBtn('Manage Booking', onManageBooking, false)}
          {user ? (
            <div style={{ position: 'relative', marginLeft: 6 }}>
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                style={{ ...linkStyle(false), border: `1px solid ${site.groundLine}`, color: site.onGround, maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {user.name}
              </button>
              {menuOpen && (
                <div style={{ position: 'absolute', right: 0, top: '110%', background: palette.white, border: `1px solid ${site.line}`, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', minWidth: 190, zIndex: 100 }}>
                  {user.role === 'admin' && (
                    <button onClick={() => { setMenuOpen(false); onAdmin(); }} style={{ ...menuItemStyle, color: site.accentInk, fontWeight: 700 }}>Admin Dashboard</button>
                  )}
                  {user.role === 'admin' && (
                    <button onClick={() => { setMenuOpen(false); if (onCustomerView) onCustomerView(); }} style={menuItemStyle}>Customer View</button>
                  )}
                  {user.role !== 'admin' && (
                    <button onClick={() => { setMenuOpen(false); onProfile(); }} style={menuItemStyle}>Booking History</button>
                  )}
                  <button onClick={() => { setMenuOpen(false); onLogout(); }} style={{ ...menuItemStyle, color: palette.red }}>Logout</button>
                </div>
              )}
            </div>
          ) : null}
        </div>

        <button
          onClick={() => setNavOpen(!navOpen)}
          aria-label={navOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={navOpen}
          className="hddn-nav-mobile"
          style={{ display: 'none', alignItems: 'center', gap: 9, background: 'none', border: 'none', color: site.onGround, fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', padding: 12, cursor: 'pointer' }}
        >
          {navOpen ? 'Close' : 'Menu'}
          <svg width="16" height="12" viewBox="0 0 16 12" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2">
            {navOpen ? <><path d="M2 1l12 10" /><path d="M14 1L2 11" /></> : <><path d="M0 1.5h16" /><path d="M0 6h16" /><path d="M0 10.5h16" /></>}
          </svg>
        </button>
      </div>

      {navOpen && (
        <div className="hddn-nav-drawer" style={{ display: 'none', borderTop: `1px solid ${site.groundLine}`, padding: '8px 20px 18px' }}>
          {NAV_LINKS.map((l) => (
            <button key={l.href} onClick={() => { closeNav(); navigate(l.href); }} style={mobileNavItemStyle}>{l.label}</button>
          ))}
          <button onClick={() => { closeNav(); onManageBooking(); }} style={mobileNavItemStyle}>Manage Booking</button>
          <button onClick={() => { closeNav(); navigate('/paws-gallery'); }} style={mobileNavItemStyle}>Paws &amp; Portraits</button>
          <button onClick={() => { closeNav(); onBlog(); }} style={mobileNavItemStyle}>Journal</button>
          <button onClick={() => { closeNav(); onSubscribe(); }} style={mobileNavItemStyle}>Subscribe</button>
          {user ? (
            <>
              {user.role === 'admin' && <button onClick={() => { closeNav(); onAdmin(); }} style={{ ...mobileNavItemStyle, color: site.accent }}>Admin Dashboard</button>}
              {user.role !== 'admin' && <button onClick={() => { closeNav(); onProfile(); }} style={mobileNavItemStyle}>Booking History</button>}
              <button onClick={() => { closeNav(); onLogout(); }} style={{ ...mobileNavItemStyle, color: site.accent }}>Logout</button>
            </>
          ) : null}
        </div>
      )}
    </header>
  );
}

// Brand bar for the standalone pages — the newsletter, the privacy policy —
// which need the wordmark but none of the booking nav. Same lockup, ground and
// accent rule as Header, so those pages start like every other page. The old
// one used a font nobody loads (Trade Gothic) and fell back to Arial Narrow.
function SimpleMasthead({ label = 'New Rochelle' }) {
  return (
    <header style={{ background: site.ground, color: site.onGround, borderBottom: `2px solid ${site.accent}` }}>
      <div className="hddn-container" style={{ ...baseStyles.container, display: 'flex', alignItems: 'center', minHeight: 62 }}>
        <a
          href="/"
          onClick={(e) => { if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return; e.preventDefault(); navigate('/'); }}
          aria-label="HDDN LAB — home"
          style={{ display: 'flex', alignItems: 'baseline', gap: 8, textDecoration: 'none', color: site.onGround }}
        >
          <b style={{ fontFamily: sans, fontWeight: 800, fontSize: 17, letterSpacing: '0.16em', textTransform: 'uppercase' }}>HDDN LAB</b>
          <i style={{ fontStyle: 'normal', fontFamily: fonts, fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', color: site.accent }}>{label}</i>
        </a>
      </div>
    </header>
  );
}


const menuItemStyle = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  background: 'none',
  border: 'none',
  fontFamily: fonts,
  fontSize: 13,
  padding: '10px 16px',
  cursor: 'pointer',
  color: palette.text,
};

const mobileNavItemStyle = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  background: 'none',
  border: 'none',
  fontFamily: fonts,
  fontSize: 14,
  padding: '12px 4px',
  cursor: 'pointer',
  color: palette.cream,
  letterSpacing: 1,
  textTransform: 'uppercase',
  fontWeight: 700,
};

// ---------------------------------------------------------------------------
// Auth forms
// ---------------------------------------------------------------------------

function AuthScreen({ onAuth, initialResetToken }) {
  const [mode, setMode] = useState(initialResetToken ? 'reset' : 'login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetToken] = useState(initialResetToken || '');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (mode === 'signup' && password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    if (mode === 'reset' && password !== confirm) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      if (mode === 'forgot') {
        await api('/api/auth/forgot-password', {
          method: 'POST',
          body: JSON.stringify({ email }),
        });
        setSuccess('If that email is registered, a reset link has been sent. Check your inbox.');
        setLoading(false);
        return;
      }
      if (mode === 'reset') {
        const data = await api('/api/auth/reset-password', {
          method: 'POST',
          body: JSON.stringify({ token: resetToken, password }),
        });
        setSuccess(data.message || 'Password reset successfully. You can now sign in.');
        setTimeout(() => { setMode('login'); setSuccess(''); }, 2000);
        setLoading(false);
        return;
      }
      const body = mode === 'signup' ? { name, email, password } : { email, password };
      const data = await api(`/api/auth/${mode}`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      localStorage.setItem('hddn_token', data.token);
      onAuth(data.user);
    } catch (err) {
      setError(err.error || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    fontFamily: sans,
    fontSize: 14,
    border: `1px solid ${palette.border}`,
    background: palette.white,
    color: palette.text,
    boxSizing: 'border-box',
    outline: 'none',
  };

  const labelStyle = {
    display: 'block',
    fontSize: 12,
    letterSpacing: 1,
    textTransform: 'uppercase',
    color: palette.textMuted,
    marginBottom: 6,
  };

  return (
    <div style={baseStyles.app}>
      <header
        style={{
          background: palette.brown,
          padding: '16px 0',
          borderBottom: `3px solid ${palette.orange}`,
          textAlign: 'center',
        }}
      >
        <span
          style={{
            fontFamily: `'Trade Gothic Next LT Pro Bold Compressed', 'Trade Gothic Next LT Pro', 'Trade Gothic Next', 'Arial Narrow', sans-serif`,
            fontWeight: 700,
            fontSize: 22,
            color: palette.cream,
            letterSpacing: 4,
            textTransform: 'uppercase',
          }}
        >
          HDDN LAB
        </span>
        <span style={{ color: palette.orange, fontSize: 13, letterSpacing: 2, marginLeft: 10 }}>
          STUDIO
        </span>
      </header>

      <div
        style={{
          maxWidth: 420,
          margin: '60px auto',
          padding: '40px 32px',
          background: palette.white,
          border: `1px solid ${palette.border}`,
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        }}
      >
        <h2
          style={{
            fontFamily: fonts,
            fontSize: 18,
            fontWeight: 700,
            letterSpacing: 2,
            textTransform: 'uppercase',
            marginTop: 0,
            marginBottom: 28,
            textAlign: 'center',
            color: palette.brown,
          }}
        >
          {mode === 'login' ? 'Sign In' : mode === 'signup' ? 'Create Account' : mode === 'forgot' ? 'Reset Password' : 'Set New Password'}
        </h2>

        {error && (
          <div
            style={{
              background: '#fdf2f0',
              border: `1px solid ${palette.red}`,
              color: palette.red,
              padding: '8px 12px',
              fontSize: 13,
              marginBottom: 20,
            }}
          >
            {error}
          </div>
        )}

        {success && (
          <div
            style={{
              background: '#f0fdf2',
              border: `1px solid ${palette.sage}`,
              color: palette.sage,
              padding: '8px 12px',
              fontSize: 13,
              marginBottom: 20,
            }}
          >
            {success}
          </div>
        )}

        <form onSubmit={submit}>
          {mode === 'signup' && (
            <div style={{ marginBottom: 18 }}>
              <label style={labelStyle}>Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                style={inputStyle}
              />
            </div>
          )}
          {(mode === 'login' || mode === 'signup' || mode === 'forgot') && (
            <div style={{ marginBottom: 18 }}>
              <label style={labelStyle}>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={inputStyle}
              />
            </div>
          )}
          {(mode === 'login' || mode === 'signup' || mode === 'reset') && (
            <div style={{ marginBottom: 18 }}>
              <label style={labelStyle}>{mode === 'reset' ? 'New Password' : 'Password'}</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                style={inputStyle}
              />
            </div>
          )}
          {(mode === 'signup' || mode === 'reset') && (
            <div style={{ marginBottom: 18 }}>
              <label style={labelStyle}>Confirm Password</label>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                style={inputStyle}
              />
            </div>
          )}

          {mode === 'forgot' && (
            <p style={{ fontSize: 13, color: palette.textMuted, marginBottom: 18, lineHeight: 1.6 }}>
              Enter your email and we'll send you a link to reset your password.
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px',
              fontFamily: fonts,
              fontSize: 14,
              fontWeight: 700,
              letterSpacing: 2,
              textTransform: 'uppercase',
              background: palette.orange,
              color: palette.cream,
              border: 'none',
              cursor: loading ? 'wait' : 'pointer',
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? '...' : mode === 'login' ? 'Sign In' : mode === 'signup' ? 'Sign Up' : mode === 'forgot' ? 'Send Reset Link' : 'Reset Password'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: palette.textMuted }}>
          {mode === 'login' ? (
            <>
              No account?{' '}
              <button
                onClick={() => { setMode('signup'); setError(''); setSuccess(''); }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: palette.orange,
                  fontFamily: fonts,
                  fontSize: 13,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  padding: 0,
                }}
              >
                Sign up
              </button>
            </>
          ) : mode === 'forgot' || mode === 'reset' ? (
            <>
              Back to{' '}
              <button
                onClick={() => { setMode('login'); setError(''); setSuccess(''); }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: palette.orange,
                  fontFamily: fonts,
                  fontSize: 13,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  padding: 0,
                }}
              >
                Sign in
              </button>
            </>
          ) : (
            <>
              Already have an account?{' '}
              <button
                onClick={() => { setMode('login'); setError(''); setSuccess(''); }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: palette.orange,
                  fontFamily: fonts,
                  fontSize: 13,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  padding: 0,
                }}
              >
                Sign in
              </button>
            </>
          )}
        </div>

        {mode === 'login' && (
          <div style={{ textAlign: 'center', marginTop: 10 }}>
            <button
              onClick={() => { setMode('forgot'); setError(''); setSuccess(''); }}
              style={{
                background: 'none',
                border: 'none',
                color: palette.textMuted,
                fontFamily: fonts,
                fontSize: 12,
                cursor: 'pointer',
                textDecoration: 'underline',
                padding: 0,
              }}
            >
              Forgot password?
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Admin Login Modal (overlay)
// ---------------------------------------------------------------------------

function AdminLoginModal({ onAuth, onClose }) {
  const [mode, setMode] = useState('login'); // login | forgot
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    // The customer modal and the full auth screen both offer this; this one
    // did not, which was the worst place for the gap. An admin who forgets
    // their password is the one person nobody can reset from the inside.
    if (mode === 'forgot') {
      try {
        await api('/api/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email }) });
        setSuccess('If that email has an account, a reset link is on its way. It expires in an hour.');
      } catch (err) {
        setError(err.error || 'Something went wrong');
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      const data = await api('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      if (data.user.role !== 'admin') {
        setError('Admin access only');
        setLoading(false);
        return;
      }
      localStorage.setItem('hddn_token', data.token);
      onAuth(data.user);
    } catch (err) {
      setError(err.error || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    fontFamily: sans,
    fontSize: 14,
    border: `1px solid ${palette.border}`,
    background: palette.white,
    color: palette.text,
    boxSizing: 'border-box',
    outline: 'none',
  };

  const labelStyle = {
    display: 'block',
    fontSize: 12,
    letterSpacing: 1,
    textTransform: 'uppercase',
    color: palette.textMuted,
    marginBottom: 6,
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(60,43,31,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: palette.white,
          border: `1px solid ${palette.border}`,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          maxWidth: 380,
          width: '100%',
          padding: '32px 28px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h3 style={{ margin: 0, fontFamily: fonts, fontSize: 15, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown }}>
            {mode === 'forgot' ? 'Reset Password' : 'Admin Login'}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: palette.textMuted, lineHeight: 1 }}>
            &times;
          </button>
        </div>

        {error && (
          <div style={{ background: '#fdf2f0', border: `1px solid ${palette.red}`, color: palette.red, padding: '8px 12px', fontSize: 13, marginBottom: 16 }}>
            {error}
          </div>
        )}

        {success && (
          <div role="status" style={{ background: palette.partial, border: `1px solid ${palette.sage}`, color: palette.text, padding: '8px 12px', fontSize: 13, marginBottom: 16 }}>
            {success}
          </div>
        )}

        <form onSubmit={submit}>
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required style={inputStyle} />
          </div>
          {mode === 'login' && (
            <div style={{ marginBottom: 18 }}>
              <label style={labelStyle}>Password</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required style={inputStyle} />
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px',
              fontFamily: fonts,
              fontSize: 14,
              fontWeight: 700,
              letterSpacing: 2,
              textTransform: 'uppercase',
              background: palette.orange,
              color: palette.cream,
              border: 'none',
              cursor: loading ? 'wait' : 'pointer',
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? '...' : mode === 'forgot' ? 'Send reset link' : 'Sign In'}
          </button>

          <button
            type="button"
            onClick={() => { setMode(mode === 'forgot' ? 'login' : 'forgot'); setError(''); setSuccess(''); }}
            style={{ display: 'block', margin: '14px auto 0', background: 'none', border: 'none', fontFamily: fonts, fontSize: 12, color: palette.orange, cursor: 'pointer', textDecoration: 'underline' }}
          >
            {mode === 'forgot' ? '\u2190 Back to sign in' : 'Forgot password?'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Customer Login Modal (with forgot password)
// ---------------------------------------------------------------------------

function CustomerLoginModal({ onAuth, onClose }) {
  const [mode, setMode] = useState('login'); // login | signup | forgot
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');

    if (mode === 'forgot') {
      setLoading(true);
      try {
        await api('/api/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email }) });
        setSuccess('If that email is registered, a reset link has been sent. Check your inbox.');
      } catch (err) { setError(err.error || 'Something went wrong'); }
      finally { setLoading(false); }
      return;
    }

    if (mode === 'signup' && password !== confirm) { setError('Passwords do not match'); return; }

    setLoading(true);
    try {
      const body = mode === 'signup' ? { name, email, password } : { email, password };
      const data = await api(`/api/auth/${mode}`, { method: 'POST', body: JSON.stringify(body) });
      localStorage.setItem('hddn_token', data.token);
      onAuth(data.user);
    } catch (err) { setError(err.error || 'Something went wrong'); }
    finally { setLoading(false); }
  };

  const inputStyle = {
    width: '100%', padding: '10px 12px', fontFamily: sans, fontSize: 14,
    border: `1px solid ${palette.border}`, background: palette.white,
    color: palette.text, boxSizing: 'border-box', outline: 'none',
  };
  const labelStyle = {
    display: 'block', fontSize: 12, letterSpacing: 1,
    textTransform: 'uppercase', color: palette.textMuted, marginBottom: 6,
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(60,43,31,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: palette.white, border: `1px solid ${palette.border}`, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', maxWidth: 380, width: '100%', padding: '32px 28px' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h3 style={{ margin: 0, fontFamily: fonts, fontSize: 15, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown }}>
            {mode === 'login' ? 'Sign In' : mode === 'signup' ? 'Create Account' : 'Reset Password'}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: palette.textMuted, lineHeight: 1 }}>
            &times;
          </button>
        </div>

        {error && <div style={{ background: '#fdf2f0', border: `1px solid ${palette.red}`, color: palette.red, padding: '8px 12px', fontSize: 13, marginBottom: 16 }}>{error}</div>}
        {success && <div style={{ background: '#f0fdf2', border: `1px solid ${palette.sage}`, color: palette.sage, padding: '8px 12px', fontSize: 13, marginBottom: 16 }}>{success}</div>}

        <form onSubmit={submit}>
          {mode === 'signup' && (
            <div style={{ marginBottom: 18 }}>
              <label style={labelStyle}>Name</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} required style={inputStyle} />
            </div>
          )}
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required style={inputStyle} />
          </div>
          {mode !== 'forgot' && (
            <div style={{ marginBottom: 18 }}>
              <label style={labelStyle}>Password</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} style={inputStyle} />
            </div>
          )}
          {mode === 'signup' && (
            <div style={{ marginBottom: 18 }}>
              <label style={labelStyle}>Confirm Password</label>
              <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required style={inputStyle} />
            </div>
          )}
          {mode === 'forgot' && (
            <p style={{ fontSize: 13, color: palette.textMuted, marginBottom: 18, lineHeight: 1.6 }}>
              Enter your email and we'll send you a link to reset your password.
            </p>
          )}
          <button
            type="submit" disabled={loading}
            style={{ width: '100%', padding: '12px', fontFamily: fonts, fontSize: 14, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', background: palette.orange, color: palette.cream, border: 'none', cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.7 : 1 }}
          >
            {loading ? '...' : mode === 'login' ? 'Sign In' : mode === 'signup' ? 'Sign Up' : 'Send Reset Link'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: 16, fontSize: 13, color: palette.textMuted }}>
          {mode === 'login' ? (
            <>
              No account?{' '}
              <button onClick={() => { setMode('signup'); setError(''); setSuccess(''); }} style={{ background: 'none', border: 'none', color: palette.orange, fontFamily: fonts, fontSize: 13, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>
                Sign up
              </button>
            </>
          ) : (
            <>
              Back to{' '}
              <button onClick={() => { setMode('login'); setError(''); setSuccess(''); }} style={{ background: 'none', border: 'none', color: palette.orange, fontFamily: fonts, fontSize: 13, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>
                Sign in
              </button>
            </>
          )}
        </div>

        {mode === 'login' && (
          <div style={{ textAlign: 'center', marginTop: 8 }}>
            <button
              onClick={() => { setMode('forgot'); setError(''); setSuccess(''); }}
              style={{ background: 'none', border: 'none', color: palette.textMuted, fontFamily: fonts, fontSize: 12, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
            >
              Forgot password?
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Calendar
// ---------------------------------------------------------------------------

function Calendar({ year, month, booked, blocked, slots, selected, onSelect }) {
  const days = daysInMonth(year, month);
  const offset = firstDayOfMonth(year, month);
  const today = todayString();
  const cells = [];

  // Compute free hours for a day given its booked slots
  function getFreeHours(daySlots) {
    const occupied = new Array(24).fill(false);
    daySlots.forEach((s) => {
      for (let h = s.start; h < s.start + s.hours; h++) occupied[h] = true;
    });
    let free = 0;
    for (let h = OPEN_HOUR; h < CLOSE_HOUR; h++) {
      if (!occupied[h]) free++;
    }
    return free;
  }

  for (let i = 0; i < offset; i++) {
    cells.push(<div key={`e-${i}`} style={{ padding: 8 }} />);
  }

  for (let d = 1; d <= days; d++) {
    const ds = formatDate(year, month, d);
    const isPast = ds < today;
    const isFullyBooked = booked.includes(ds);
    const isBlocked = blocked.includes(ds);
    const isPartial = !isFullyBooked && !isBlocked && slots && slots[ds] && slots[ds].length > 0;
    const isSelected = ds === selected;
    const isAvailable = !isPast && !isFullyBooked && !isBlocked;

    const freeHours = isPartial ? getFreeHours(slots[ds]) : (!isPast && !isFullyBooked && !isBlocked ? (CLOSE_HOUR - OPEN_HOUR) : 0);

    let bg = palette.white;
    let fg = palette.text;
    let border = `1px solid ${palette.border}`;
    let cursor = 'default';

    if (isSelected) {
      bg = palette.orange;
      fg = palette.cream;
      border = `1px solid ${palette.orange}`;
    } else if (isBlocked) {
      bg = palette.blocked;
      fg = palette.textMuted;
    } else if (isFullyBooked) {
      bg = palette.beige;
      fg = palette.textMuted;
    } else if (isPartial) {
      bg = palette.partial;
      fg = palette.text;
      border = `2px solid ${palette.sage}`;
      cursor = 'pointer';
    } else if (isPast) {
      fg = palette.border;
    } else {
      cursor = 'pointer';
    }

    cells.push(
      <div
        key={d}
        onClick={() => isAvailable && onSelect(ds)}
        onMouseEnter={(e) => {
          if (isAvailable && !isSelected) e.currentTarget.style.background = isPartial ? palette.sageLight : palette.beige;
        }}
        onMouseLeave={(e) => {
          if (isAvailable && !isSelected) e.currentTarget.style.background = isPartial ? palette.partial : palette.white;
        }}
        style={{
          padding: '6px 4px',
          textAlign: 'center',
          fontSize: 14,
          fontFamily: fonts,
          background: bg,
          color: fg,
          border,
          cursor,
          userSelect: 'none',
          transition: 'background 0.15s',
          minHeight: 44,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
        }}
      >
        <span>{d}</span>
        {isAvailable && !isPast && freeHours > 0 && freeHours < (CLOSE_HOUR - OPEN_HOUR) && (
          <span style={{ fontSize: 9, color: isSelected ? palette.cream : palette.sage, fontWeight: 700, lineHeight: 1, letterSpacing: 0.5 }}>
            {freeHours}hr free
          </span>
        )}
      </div>,
    );
  }

  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 0,
          marginBottom: 2,
        }}
      >
        {DAY_NAMES.map((n) => (
          <div
            key={n}
            style={{
              textAlign: 'center',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 1,
              textTransform: 'uppercase',
              color: palette.textMuted,
              padding: '8px 0',
            }}
          >
            {n}
          </div>
        ))}
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 2,
        }}
      >
        {cells}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Legend
// ---------------------------------------------------------------------------

function Legend() {
  const items = [
    { color: palette.white, border: palette.border, label: 'Available' },
    { color: palette.partial, border: palette.sage, label: 'Partial' },
    { color: palette.beige, border: palette.border, label: 'Fully Booked' },
    { color: palette.blocked, border: palette.blocked, label: 'Blocked' },
    { color: palette.orange, border: palette.orange, label: 'Selected' },
  ];
  return (
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 14 }}>
      {items.map((it) => (
        <div key={it.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
          <div
            style={{
              width: 14,
              height: 14,
              background: it.color,
              border: `1px solid ${it.border}`,
            }}
          />
          <span style={{ color: palette.textMuted, letterSpacing: 0.5 }}>{it.label}</span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FAQ Section
// ---------------------------------------------------------------------------

const FAQ_ITEMS = [
  {
    q: 'How much does it cost to book?',
    a: 'Studio rental is $100/hr with a 3-hour minimum ($300). For podcast recording, our Creator Package starts at $150/hr (1 hr minimum) and includes audio + video + 4 social clips + YouTube edit.',
  },
  {
    q: 'What is the minimum booking duration?',
    a: 'Studio bookings require a minimum of 3 hours ($300). Podcast Creator Package is available starting at 1 hour ($150).',
  },
  {
    q: 'What are the studio operating hours?',
    a: 'HDDN LAB is open for bookings from 8:00 AM to 8:00 PM, seven days a week. Setup and breakdown must occur within your booked time slot.',
  },
  {
    q: 'What payment methods do you accept?',
    a: 'We accept Credit/Debit Card, Venmo, PayPal, and Zelle. Full payment is required at the time of booking to confirm your reservation.',
  },
  {
    q: 'What is your cancellation policy?',
    a: 'Full refund for cancellations 7+ days in advance or within 24 hours of booking. 50% refund for cancellations between 7 days and 24 hours before your session. No refund for cancellations less than 24 hours before start time.',
  },
  {
    q: 'How many people can attend my session?',
    a: 'Each booking can accommodate up to 15 people. Please include the total headcount when booking so we can ensure the best experience for your session.',
  },
  {
    q: 'Can I bring my own equipment?',
    a: 'Yes, you are welcome to bring your own equipment. If you need to bring materials like glitter, paint, smoke machines, or other hard-to-clean items, you must get prior written approval from HDDN LAB.',
  },
  {
    q: 'Do I need insurance to book?',
    a: 'Insurance is not required for individual content creators but is recommended. A Certificate of Insurance (COI) is required for large-scale productions, commercial/corporate shoots, and outside equipment rentals.',
  },
  {
    q: 'Are pets allowed in the studio?',
    a: 'No pets are allowed without prior written consent from HDDN LAB management. Certified service animals are permitted — please notify us at the time of booking.',
  },
  {
    q: 'What happens if I go over my booked time?',
    a: 'Overtime charges are applied at the applicable hourly rate for your booking type. Pre-approval from HDDN LAB is required for any overtime. Unpaid overtime may result in session termination.',
  },
  {
    q: 'What rooms are available?',
    a: 'HDDN LAB is a converted industrial warehouse turned creative studio, offering an upstairs space with two rooms: the Full Studio for photo/video shoots and creative sessions, and the Podcast Recording room for professional podcast recording.',
  },
  {
    q: 'Can I book the space for something other than photo, video, or podcast?',
    a: `Yes — we're open to other kinds of bookings: events, workshops, meetings, listening sessions, pop-ups, classes, and more. Email ${CONTACT_EMAIL} with what you have in mind and we'll work out pricing and logistics.`,
  },
  {
    q: 'Can I bring food and drinks?',
    a: 'Outside food and beverages are allowed for craft services. You are responsible for cleanup and proper disposal. Alcohol is not permitted without prior written consent.',
  },
];

const STUDIO_RULES = [
  { title: 'Smoking & Vaping', text: 'NO SMOKING OF ANY KIND in the building or on HDDN LAB property. This includes cigarettes, cigars, vapes, and cannabis. Violation will result in immediate session termination and $250 fine.' },
  { title: 'Pets', text: 'No pets allowed without prior consent of HDDN LAB management. Certified service animals are permitted — notify at booking.' },
  { title: 'Food & Beverages', text: 'Outside food and beverages are allowed for craft services. You are responsible for appropriate disposal of food waste. No alcohol permitted without prior written consent.' },
  { title: 'Materials & Props', text: 'Obtain written approval before bringing glitter, powder, balloons, oils, paint, or other hard-to-clean materials. Failure to obtain approval results in session termination and additional cleaning charges.' },
  { title: 'Space Maintenance', text: 'Keep the space clean during and after your rental. Return all equipment and furniture to its original location. Wipe down surfaces you use. A $100 cleaning fee will be applied if the studio is left in disarray.' },
  { title: 'Minors', text: 'No unsupervised children on premises. Anyone under 18 must be accompanied by a guardian (21+) at all times.' },
  { title: 'Noise & Common Areas', text: 'No loitering in hallways or common areas. Do not enter other studio spaces or private areas. Keep noise at reasonable levels in shared building spaces.' },
];

const POLICIES = [
  { title: 'Pricing', text: 'Studio rental is $100/hr with a 3-hour minimum ($300). Podcast Creator Package starts at $150/hr (1hr minimum). Full payment is required at time of booking.' },
  { title: 'Cancellation — Full Refund', text: 'Cancellations within 24 hours of booking confirmation (excluding bookings starting within 48 hours), or cancellations 7+ days in advance.' },
  { title: 'Cancellation — 50% Refund', text: 'Cancellations between 7 days and 24 hours before your session (excluding all fees).' },
  { title: 'Cancellation — No Refund', text: 'Cancellations less than 24 hours before event start time, or event rentals cancelled within 72 hours of start time.' },
  { title: 'Overtime', text: 'Charges are automatically applied for studio hours beyond your original booking at $100/hr. Pre-approval from HDDN LAB is required; unpaid overtime will result in session termination.' },
  { title: 'Cleaning Fee', text: 'You will be charged an additional $100 cleaning fee if the studio is left in disarray or shows signs of damage.' },
  { title: 'Insurance', text: 'Insurance is not required for individual content creators but is recommended. A Certificate of Insurance (COI) is required for large-scale productions, commercial/corporate shoots, and outside equipment rentals.' },
  { title: 'Damage Liability', text: 'The Renter is liable for the full cost of repairs or replacement of any equipment, furniture, or studio damage caused during the rental. Payment is due within 3 business days of written notice.' },
];

function AccordionList({ items }) {
  const [openIndex, setOpenIndex] = useState(null);
  return (
    <div>
      {items.map((item, i) => {
        const isOpen = openIndex === i;
        return (
          <div key={i} style={{ borderBottom: `1px solid ${palette.border}` }}>
            <button
              onClick={() => setOpenIndex(isOpen ? null : i)}
              style={{
                width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '14px 4px', fontFamily: sans, fontSize: 14, fontWeight: 700,
                color: isOpen ? palette.orange : palette.brown, background: 'none', border: 'none',
                cursor: 'pointer', textAlign: 'left', gap: 12, transition: 'color 0.15s',
              }}
            >
              <span>{item.title || item.q}</span>
              <span style={{ fontSize: 18, lineHeight: 1, flexShrink: 0, transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>+</span>
            </button>
            {isOpen && (
              <div style={{ padding: '0 4px 14px', fontSize: 13, lineHeight: 1.8, color: palette.text }}>
                {item.text || item.a}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function InfoSection({ initialTab, packagesConfig }) {
  const [activeTab, setActiveTab] = useState(initialTab || 'faqs');
  const [siteContent, setSiteContent] = useState(null);

  // Fetch editable content from API
  useEffect(() => {
    api('/api/site-content')
      .then((data) => { if (data && Object.keys(data).length > 0) setSiteContent(data); })
      .catch(() => {}); // silently fall back to hardcoded defaults
  }, []);

  // Reset to requested tab when initialTab changes
  useEffect(() => {
    if (initialTab) setActiveTab(initialTab);
  }, [initialTab]);


  const faqItems = siteContent?.faqs
    ? siteContent.faqs.map((f) => ({ title: f.q, text: f.a }))
    : FAQ_ITEMS.map((f) => ({ title: f.q, text: f.a }));

  const policyItems = siteContent?.policies || POLICIES;
  const ruleItems = siteContent?.studio_rules || STUDIO_RULES;
  const contactData = siteContent?.contact || null;

  // Derive "What's Included" from packages_config (single source of truth)
  const pkgCfg = packagesConfig || {};
  const dynHourlyRate = pkgCfg.hourlyRate || 100;
  const dynStudioAddOns = pkgCfg.studioAddOns || ADD_ONS;
  const dynPodcastPackages = pkgCfg.podcastPackages || PODCAST_PACKAGES;
  const dynPodcastAddOns = pkgCfg.podcastAddOns || PODCAST_ADD_ONS;

  const showUpstairsEquipment = pkgCfg.showUpstairsEquipment === true;
  const podcastGear = Array.isArray(pkgCfg.podcastGear) ? pkgCfg.podcastGear.filter((s) => s && s.trim()) : [];
  const podcastTurnaround = typeof pkgCfg.podcastTurnaround === 'string' ? pkgCfg.podcastTurnaround.trim() : '';

  const upstairsSections = [
    { heading: 'Studio Space', items: ['Open floor plan with hardwood floors', 'Natural lighting + blackout options', 'Furniture & props for styling', 'WiFi & Bluetooth speaker', 'Climate controlled space', 'Spring clamps', 'V-flat', 'Clothing rack'] },
    ...(showUpstairsEquipment ? [
      { heading: 'Lighting', items: ['Amaran 200x (bi-color, warm/cool)', 'Aputure 120D (daylight)'] },
      { heading: 'Modifiers', items: ['36" Lantern dome', '30x120 Lightbox', '36" Aputure dome', 'Snoot'] },
      { heading: 'Grip & Support', items: ['4 x 40" C-stands', '6 x 25lb sandbags', 'Extension cords'] },
    ] : []),
    { heading: 'Backdrops', items: ['White backdrop (no stepping — $25 stepping fee)', 'Black backdrop (no stepping — $25 stepping fee)'] },
    { heading: 'Amenities', items: ['MUA station', 'Props on site'] },
    { heading: 'Studio Add-ons', items: dynStudioAddOns.map((a) => a.price ? `${a.label} (+$${a.price})` : a.label) },
  ];

  const podcastSections = Object.entries(dynPodcastPackages)
    .filter(([, pkg]) => pkg.enabled !== false)
    .map(([, pkg]) => {
      const priceLine = (pkg.durations || []).map((d) => `${d.hours}hr $${d.price}`).join(' | ');
      return {
        heading: pkg.name || 'Package',
        items: [...(pkg.includes || []), ...(priceLine ? [priceLine] : [])],
      };
    });
  if (podcastGear.length > 0 || podcastTurnaround) {
    podcastSections.push({
      heading: 'Podcast Recording Gear',
      items: [...podcastGear, ...(podcastTurnaround ? [podcastTurnaround] : [])],
    });
  }
  podcastSections.push({
    heading: 'Podcast Add-ons',
    items: dynPodcastAddOns.map((a) => a.price ? `${a.label} (+$${a.price})` : a.label),
  });

  const tabs = [
    { id: 'faqs', label: 'FAQs' },
    { id: 'about', label: 'About' },
    { id: 'included', label: "What's Included" },
    { id: 'policies', label: 'Policies' },
    { id: 'rules', label: 'Studio Rules' },
    { id: 'contact', label: 'Contact' },
  ];

  const tabBtnStyle = (active) => ({
    fontFamily: fonts, fontSize: 12, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase',
    padding: '10px 18px', background: active ? palette.brown : 'transparent',
    color: active ? palette.cream : palette.textMuted, border: 'none', cursor: 'pointer',
    transition: 'all 0.15s',
  });

  const renderIncludedSection = (sections) => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
      {sections.map((section) => (
        <div key={section.heading} style={{ background: palette.beige, padding: '18px 20px', border: `1px solid ${palette.border}` }}>
          <h4 style={{ fontFamily: sans, fontSize: 13, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: palette.brown, marginTop: 0, marginBottom: 12 }}>
            {section.heading}
          </h4>
          <ul style={{ margin: 0, paddingLeft: 18, listStyle: 'none' }}>
            {section.items.filter((item) => item.trim()).map((item, idx) => (
              <li key={idx} style={{ fontSize: 13, lineHeight: 2, color: palette.text, position: 'relative', paddingLeft: 4 }}>
                <span style={{ position: 'absolute', left: -14, color: palette.sage }}>&#8226;</span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );

  return (
    <div id="info-section" style={{ marginTop: 48, paddingTop: 32, borderTop: `2px solid ${palette.border}` }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 0, marginBottom: 24, borderBottom: `2px solid ${palette.border}`, justifyContent: 'center' }}>
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} style={tabBtnStyle(activeTab === t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ maxWidth: 640, margin: '0 auto' }}>
        {activeTab === 'faqs' && <AccordionList items={faqItems} />}
        {activeTab === 'about' && (
          <div style={{ padding: '4px 0' }}>
            <p style={{ fontFamily: sans, fontSize: 14, lineHeight: 1.8, color: palette.text, marginTop: 0 }}>
              <strong>HDDN LAB</strong> is a creative studio tucked into downtown New Rochelle — hidden for a reason.
              Behind an unassuming tunnel entrance on Centre Avenue, you&apos;ll find a fully-equipped space built for
              photographers, content creators, podcasters, and brands.
            </p>
            <p style={{ fontFamily: sans, fontSize: 14, lineHeight: 1.8, color: palette.text }}>
              We&apos;re more than a room for rent. We&apos;re a community of local creatives — and a full production
              team when you need one, from studio sessions to social content to podcast production.
            </p>
            <div style={{ margin: '24px 0' }}>
              {renderIncludedSection([
                { heading: 'The Space', items: ['Two photo/portrait studios', 'Dedicated podcast studio', 'Natural light + blackout options', 'Hardwood floors, styling furniture & props'] },
                { heading: 'The Gear', items: ['Professional lighting & modifiers', 'White & black backdrops', 'MUA station', 'WiFi & Bluetooth speaker'] },
                { heading: 'Find Us', items: ['72 Centre Avenue, New Rochelle, NY', 'Look for the tunnel entrance', 'Follow @hddn.lab on Instagram & TikTok'] },
              ])}
            </div>
            <PhotoGallery event="about" title="Inside the Studio" />
          </div>
        )}
        {activeTab === 'included' && (
          <div style={{ padding: '16px 0' }}>
            <h3 style={{ fontFamily: sans, fontSize: 14, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, marginTop: 0, marginBottom: 14, paddingBottom: 8, borderBottom: `2px solid ${palette.border}` }}>
              Full Studio
            </h3>
            <div style={{ marginBottom: 32 }}>
              {renderIncludedSection(upstairsSections)}
            </div>
            <h3 style={{ fontFamily: sans, fontSize: 14, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, marginTop: 0, marginBottom: 14, paddingBottom: 8, borderBottom: `2px solid ${palette.border}` }}>
              Podcast Recording
            </h3>
            {renderIncludedSection(podcastSections)}
          </div>
        )}
        {activeTab === 'policies' && <AccordionList items={policyItems} />}
        {activeTab === 'rules' && <AccordionList items={ruleItems} />}
        {activeTab === 'contact' && (
          <ContactTab contactData={contactData} palette={palette} fonts={fonts} />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mock studio photos (shown until real photos are uploaded via admin)
// ---------------------------------------------------------------------------

// Parse the admin_notes field into individual reply-log entries. The reply
// endpoint stores each reply as "[<iso> — <author>] <text>" and joins multiple
// replies with a blank line. A reply body can itself contain blank lines, so we
// must NOT split on every blank line — we split only at the start-of-entry
// marker: a bracketed ISO timestamp followed by " — <author>". The separator is
// an em/en dash (never a hyphen, which appears inside the ISO date itself).
function parseReplyHistory(notes) {
  if (!notes || !notes.trim()) return [];
  const marker = /\[(\d{4}-\d{2}-\d{2}T[\d:.]+Z)\s*[—–]\s*([^\]\n]+?)\]\s*/g;
  const found = [];
  let m;
  while ((m = marker.exec(notes)) !== null) {
    found.push({ index: m.index, end: marker.lastIndex, iso: m[1], who: m[2].trim() });
  }
  // No recognizable markers — show the whole field as a single note.
  if (found.length === 0) return [{ who: 'Note', when: '', text: notes.trim(), matched: false }];

  const entries = [];
  // Any text before the first marker is a stray manual note.
  const pre = notes.slice(0, found[0].index).trim();
  if (pre) entries.push({ who: 'Note', when: '', text: pre, matched: false });

  found.forEach((f, i) => {
    const text = notes.slice(f.end, i + 1 < found.length ? found[i + 1].index : notes.length).trim();
    const d = new Date(f.iso);
    // The author segment may carry a trailing delivery status:
    // "HDDN Admin — sent" or "HDDN Admin — failed: <reason>". Older entries have
    // no status segment and render without a badge.
    let who = f.who, status = null, error = null;
    const sm = f.who.match(/^(.*?)\s*[—–]\s*(sent|failed)(?::\s*(.*))?$/i);
    if (sm) {
      who = sm[1].trim();
      status = sm[2].toLowerCase();
      error = sm[3] ? sm[3].trim() : null;
    }
    entries.push({
      who,
      when: isNaN(d.getTime()) ? f.iso : d.toLocaleString(),
      text,
      status,
      error,
      matched: true,
    });
  });
  return entries.filter((e) => e.text);
}

function MessagesTab({ palette, fonts, labelStyle, inputStyle }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);
  const [replyError, setReplyError] = useState('');
  const [replySuccess, setReplySuccess] = useState('');

  const authHeader = () => {
    const token = localStorage.getItem('hddn_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchMessages = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/contact-messages`, { headers: authHeader() });
      if (res.ok) setMessages(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchMessages(); }, []);

  // Reset reply state when selecting a different message
  useEffect(() => {
    setReplyText('');
    setReplyError('');
    setReplySuccess('');
  }, [selected?.id]);

  const updateStatus = async (id, status) => {
    await fetch(`${API_BASE}/api/contact-messages/${id}`, {
      method: 'PUT', headers: { ...authHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    fetchMessages();
  };

  const deleteMsg = async (id) => {
    if (!confirm('Delete this message?')) return;
    await fetch(`${API_BASE}/api/contact-messages/${id}`, { method: 'DELETE', headers: authHeader() });
    if (selected?.id === id) setSelected(null);
    fetchMessages();
  };

  const sendReply = async () => {
    if (!selected) return;
    const body = replyText.trim();
    if (!body) { setReplyError('Reply cannot be empty.'); return; }
    setReplying(true); setReplyError(''); setReplySuccess('');
    try {
      const res = await fetch(`${API_BASE}/api/contact-messages/${selected.id}/reply`, {
        method: 'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ body }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Failed to send (${res.status})`);
      // Use the row returned by the API so the new reply (stored in
      // admin_notes) shows up in the history immediately, not just the status.
      setSelected(data.message || selected);
      fetchMessages();
      if (data.delivered === false) {
        // The reply was saved and is flagged as failed in the history, but it
        // did NOT reach the customer — surface why and keep the text for a retry.
        setReplyError(`Not delivered: ${data.error || 'unknown error'}. The reply is saved and marked failed — fix the issue and resend.`);
      } else {
        setReplySuccess('Reply sent.');
        setReplyText('');
      }
    } catch (err) {
      setReplyError(err.message);
    } finally { setReplying(false); }
  };

  const statusColors = { unread: '#C8621A', read: '#6b7c5e', replied: '#1A4C6E', archived: '#999' };
  const unreadCount = messages.filter(m => m.status === 'unread').length;

  if (loading) return <div style={{ fontFamily: fonts, padding: 20, color: palette.textMuted }}>Loading messages...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontFamily: fonts, fontSize: 18, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, margin: 0 }}>
          Messages {unreadCount > 0 && <span style={{ background: '#C8621A', color: '#fff', fontSize: 11, padding: '2px 8px', borderRadius: 10, marginLeft: 8 }}>{unreadCount}</span>}
        </h2>
      </div>

      {messages.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: palette.textMuted, fontFamily: fonts }}>No messages yet.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 1fr' : '1fr', gap: 16 }}>
          <div style={{ maxHeight: 600, overflow: 'auto' }}>
            {messages.map(m => (
              <div key={m.id} onClick={() => { setSelected(m); if (m.status === 'unread') updateStatus(m.id, 'read'); }}
                style={{
                  padding: '12px 16px', marginBottom: 8, cursor: 'pointer',
                  background: selected?.id === m.id ? palette.beige : palette.white,
                  border: `1px solid ${selected?.id === m.id ? palette.brown : palette.border}`,
                  borderLeft: `4px solid ${statusColors[m.status] || palette.border}`,
                }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontFamily: fonts, fontSize: 13, fontWeight: m.status === 'unread' ? 700 : 400, color: palette.brown }}>{m.name}</span>
                  <span style={{ fontFamily: fonts, fontSize: 10, color: palette.textMuted }}>{new Date(m.created_at).toLocaleDateString()}</span>
                </div>
                <div style={{ fontFamily: fonts, fontSize: 12, color: palette.textMuted, marginTop: 2 }}>{m.subject || 'No subject'}</div>
                <div style={{ fontFamily: fonts, fontSize: 11, color: palette.textMuted, marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.message}</div>
              </div>
            ))}
          </div>

          {selected && (
            <div style={{ padding: 20, background: palette.white, border: `1px solid ${palette.border}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <div>
                  <h3 style={{ fontFamily: fonts, fontSize: 16, fontWeight: 700, color: palette.brown, margin: '0 0 4px' }}>{selected.name}</h3>
                  <div style={{ fontFamily: fonts, fontSize: 12, color: palette.textMuted }}>
                    <a href={`mailto:${selected.email}`} style={{ color: palette.orange }}>{selected.email}</a>
                    {selected.phone && <span> · {selected.phone}</span>}
                  </div>
                  <div style={{ fontFamily: fonts, fontSize: 11, color: palette.textMuted, marginTop: 2 }}>{new Date(selected.created_at).toLocaleString()}</div>
                </div>
                <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: palette.textMuted }}>&times;</button>
              </div>
              {selected.subject && <div style={{ fontFamily: fonts, fontSize: 13, fontWeight: 700, color: palette.brown, marginBottom: 8 }}>{selected.subject}</div>}
              <div style={{ fontFamily: fonts, fontSize: 13, color: palette.text, lineHeight: 1.6, whiteSpace: 'pre-wrap', marginBottom: 16, padding: 12, background: palette.beige, border: `1px solid ${palette.border}` }}>
                {selected.message}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {['unread', 'read', 'replied', 'archived'].map(s => (
                  <button key={s} onClick={() => { updateStatus(selected.id, s); setSelected({...selected, status: s}); }}
                    style={{
                      fontFamily: fonts, fontSize: 11, padding: '4px 10px', letterSpacing: 0.5, textTransform: 'uppercase',
                      background: selected.status === s ? statusColors[s] : 'transparent',
                      color: selected.status === s ? '#fff' : palette.text,
                      border: `1px solid ${statusColors[s]}`, cursor: 'pointer',
                    }}>{s}</button>
                ))}
                <button onClick={() => deleteMsg(selected.id)}
                  style={{ fontFamily: fonts, fontSize: 11, padding: '4px 10px', letterSpacing: 0.5, textTransform: 'uppercase', background: 'transparent', color: '#a84032', border: '1px solid #a84032', cursor: 'pointer', marginLeft: 'auto' }}>
                  Delete
                </button>
              </div>

              {/* Reply history — your past replies to this customer */}
              {(() => {
                const history = parseReplyHistory(selected.admin_notes);
                if (history.length === 0) return null;
                return (
                  <div style={{ marginTop: 18, paddingTop: 16, borderTop: `1px solid ${palette.border}` }}>
                    <div style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: palette.textMuted, marginBottom: 10 }}>
                      Reply history
                    </div>
                    {history.map((e, i) => {
                      const failed = e.status === 'failed';
                      return (
                        <div key={i} style={{ marginBottom: 10, padding: 12, background: failed ? '#fbeae7' : palette.beige, border: `1px solid ${failed ? '#a84032' : palette.border}`, borderLeft: `3px solid ${failed ? '#a84032' : palette.brown}` }}>
                          <div style={{ fontFamily: fonts, fontSize: 11, color: palette.textMuted, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span><strong style={{ color: palette.brown }}>{e.who}</strong>{e.when ? ` · ${e.when}` : ''}</span>
                            {e.status === 'sent' && (
                              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: '#fff', background: '#6b7c5e', padding: '1px 6px', borderRadius: 3 }}>✓ Sent</span>
                            )}
                            {failed && (
                              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: '#fff', background: '#a84032', padding: '1px 6px', borderRadius: 3 }}>✕ Not delivered</span>
                            )}
                          </div>
                          <div style={{ fontFamily: fonts, fontSize: 13, color: palette.text, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{e.text}</div>
                          {failed && e.error && (
                            <div style={{ fontFamily: fonts, fontSize: 11, color: '#a84032', marginTop: 6, fontStyle: 'italic' }}>Reason: {e.error}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Reply form */}
              <div style={{ marginTop: 18, paddingTop: 16, borderTop: `1px solid ${palette.border}` }}>
                <label style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: palette.textMuted, display: 'block', marginBottom: 6 }}>
                  Reply to {selected.name}
                </label>
                <textarea
                  value={replyText}
                  onChange={(e) => { setReplyText(e.target.value); setReplyError(''); setReplySuccess(''); }}
                  rows={5}
                  placeholder={`Hi ${(selected.name || '').split(' ')[0] || 'there'},\n\nThanks for reaching out…`}
                  style={{ width: '100%', padding: '10px 12px', fontFamily: fonts, fontSize: 13, border: `1px solid ${palette.border}`, background: palette.white, color: palette.text, boxSizing: 'border-box', resize: 'vertical', lineHeight: 1.5 }}
                />
                {replyError && (
                  <div style={{ color: '#a84032', fontSize: 12, marginTop: 6 }}>{replyError}</div>
                )}
                {replySuccess && (
                  <div style={{ color: '#6b7c5e', fontSize: 12, marginTop: 6 }}>{replySuccess}</div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                  <span style={{ fontSize: 11, color: palette.textMuted }}>
                    Sends to <strong>{selected.email}</strong>
                  </span>
                  <button
                    onClick={sendReply}
                    disabled={replying || !replyText.trim()}
                    style={{
                      fontFamily: fonts, fontSize: 12, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase',
                      padding: '8px 18px',
                      background: palette.brown, color: palette.cream, border: 'none',
                      cursor: replying || !replyText.trim() ? 'not-allowed' : 'pointer',
                      opacity: replying || !replyText.trim() ? 0.5 : 1,
                    }}
                  >
                    {replying ? 'Sending…' : 'Send Reply'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PhotoQuotesTab({ palette, fonts }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);
  const [replyError, setReplyError] = useState('');
  const [replySuccess, setReplySuccess] = useState('');

  const authHeader = () => {
    const token = localStorage.getItem('hddn_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchRequests = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/photographer-quotes`, { headers: authHeader() });
      if (res.ok) setRequests(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchRequests(); }, []);

  useEffect(() => {
    setReplyText('');
    setReplyError('');
    setReplySuccess('');
  }, [selected?.id]);

  const updateStatus = async (id, status) => {
    await fetch(`${API_BASE}/api/photographer-quotes/${id}`, {
      method: 'PUT',
      headers: { ...authHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    fetchRequests();
  };

  const deleteReq = async (id) => {
    if (!confirm('Delete this quote request?')) return;
    await fetch(`${API_BASE}/api/photographer-quotes/${id}`, { method: 'DELETE', headers: authHeader() });
    if (selected?.id === id) setSelected(null);
    fetchRequests();
  };

  const sendReply = async () => {
    if (!selected) return;
    const body = replyText.trim();
    if (!body) { setReplyError('Reply cannot be empty.'); return; }
    setReplying(true); setReplyError(''); setReplySuccess('');
    try {
      const res = await fetch(`${API_BASE}/api/photographer-quotes/${selected.id}/reply`, {
        method: 'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ body }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Failed to send (${res.status})`);
      setReplySuccess('Quote sent.');
      setReplyText('');
      setSelected({ ...selected, status: 'quoted' });
      fetchRequests();
    } catch (err) {
      setReplyError(err.message);
    } finally { setReplying(false); }
  };

  const statusColors = { unread: '#C8621A', read: '#6b7c5e', quoted: '#1A4C6E', archived: '#999' };
  const unreadCount = requests.filter(r => r.status === 'unread').length;

  if (loading) return <div style={{ fontFamily: fonts, padding: 20, color: palette.textMuted }}>Loading quote requests...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontFamily: fonts, fontSize: 18, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, margin: 0 }}>
          Photo Quote Requests {unreadCount > 0 && <span style={{ background: '#C8621A', color: '#fff', fontSize: 11, padding: '2px 8px', borderRadius: 10, marginLeft: 8 }}>{unreadCount}</span>}
        </h2>
      </div>

      {requests.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: palette.textMuted, fontFamily: fonts }}>No requests yet.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 1fr' : '1fr', gap: 16 }}>
          <div style={{ maxHeight: 600, overflow: 'auto' }}>
            {requests.map(r => (
              <div key={r.id} onClick={() => { setSelected(r); if (r.status === 'unread') updateStatus(r.id, 'read'); }}
                style={{
                  padding: '12px 16px', marginBottom: 8, cursor: 'pointer',
                  background: selected?.id === r.id ? palette.beige : palette.white,
                  border: `1px solid ${selected?.id === r.id ? palette.brown : palette.border}`,
                  borderLeft: `4px solid ${statusColors[r.status] || palette.border}`,
                }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontFamily: fonts, fontSize: 13, fontWeight: r.status === 'unread' ? 700 : 400, color: palette.brown }}>
                    {r.name || r.email}
                  </span>
                  <span style={{ fontFamily: fonts, fontSize: 10, color: palette.textMuted }}>{new Date(r.created_at).toLocaleDateString()}</span>
                </div>
                <div style={{ fontFamily: fonts, fontSize: 12, color: palette.textMuted, marginTop: 2 }}>
                  {r.project_type || 'Photography'} · {r.timeline || 'no timeline'}
                </div>
                <div style={{ fontFamily: fonts, fontSize: 11, color: palette.textMuted, marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.description}
                </div>
                {Array.isArray(r.attachments) && r.attachments.length > 0 && (
                  <div style={{ fontFamily: fonts, fontSize: 10, color: palette.orange, marginTop: 4 }}>
                    📎 {r.attachments.length} attachment{r.attachments.length === 1 ? '' : 's'}
                  </div>
                )}
              </div>
            ))}
          </div>

          {selected && (
            <div style={{ padding: 20, background: palette.white, border: `1px solid ${palette.border}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <div>
                  <h3 style={{ fontFamily: fonts, fontSize: 16, fontWeight: 700, color: palette.brown, margin: '0 0 4px' }}>
                    {selected.name || '(no name)'}
                  </h3>
                  <div style={{ fontFamily: fonts, fontSize: 12, color: palette.textMuted }}>
                    <a href={`mailto:${selected.email}`} style={{ color: palette.orange }}>{selected.email}</a>
                    {selected.phone && <span> · <a href={`tel:${selected.phone}`} style={{ color: palette.orange }}>{selected.phone}</a></span>}
                  </div>
                  <div style={{ fontFamily: fonts, fontSize: 11, color: palette.textMuted, marginTop: 2 }}>{new Date(selected.created_at).toLocaleString()}</div>
                </div>
                <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: palette.textMuted }}>&times;</button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, marginBottom: 12 }}>
                {[
                  ['Project', selected.project_type],
                  ['Timeline', selected.timeline],
                  ['Budget', selected.budget],
                ].filter(([, v]) => v).map(([k, v]) => (
                  <div key={k} style={{ background: palette.beige, padding: '8px 10px', border: `1px solid ${palette.border}` }}>
                    <div style={{ fontFamily: fonts, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: palette.textMuted }}>{k}</div>
                    <div style={{ fontFamily: fonts, fontSize: 13, color: palette.brown }}>{v}</div>
                  </div>
                ))}
              </div>

              <div style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: palette.textMuted, marginBottom: 4 }}>
                Looking for
              </div>
              <div style={{ fontFamily: fonts, fontSize: 13, color: palette.text, lineHeight: 1.6, whiteSpace: 'pre-wrap', marginBottom: 12, padding: 12, background: palette.beige, border: `1px solid ${palette.border}` }}>
                {selected.description}
              </div>

              {selected.reference_links && (
                <>
                  <div style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: palette.textMuted, marginBottom: 4 }}>
                    Reference links
                  </div>
                  <div style={{ fontFamily: fonts, fontSize: 12, color: palette.text, lineHeight: 1.6, whiteSpace: 'pre-wrap', marginBottom: 12, padding: 10, background: palette.beige, border: `1px solid ${palette.border}` }}>
                    {selected.reference_links.split(/\s+/).filter(Boolean).map((url, i) => {
                      const isUrl = /^https?:\/\//i.test(url);
                      return isUrl
                        ? <a key={i} href={url} target="_blank" rel="noopener noreferrer" style={{ color: palette.orange, display: 'block', wordBreak: 'break-all' }}>{url}</a>
                        : <span key={i} style={{ display: 'block', wordBreak: 'break-all' }}>{url}</span>;
                    })}
                  </div>
                </>
              )}

              {Array.isArray(selected.attachments) && selected.attachments.length > 0 && (
                <>
                  <div style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: palette.textMuted, marginBottom: 6 }}>
                    Attachments ({selected.attachments.length})
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8, marginBottom: 16 }}>
                    {selected.attachments.map((a, i) => {
                      const isImage = (a.mimeType || '').startsWith('image/');
                      return (
                        <a key={i} href={a.url} target="_blank" rel="noopener noreferrer"
                          style={{ display: 'block', border: `1px solid ${palette.border}`, background: palette.beige, textDecoration: 'none', color: palette.text }}>
                          {isImage ? (
                            <img src={a.url} alt={a.filename} style={{ width: '100%', height: 100, objectFit: 'cover', display: 'block' }} />
                          ) : (
                            <div style={{ height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, color: palette.textMuted }}>
                              📄
                            </div>
                          )}
                          <div style={{ fontFamily: fonts, fontSize: 10, padding: '6px 8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {a.filename}
                          </div>
                        </a>
                      );
                    })}
                  </div>
                </>
              )}

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {['unread', 'read', 'quoted', 'archived'].map(s => (
                  <button key={s} onClick={() => { updateStatus(selected.id, s); setSelected({...selected, status: s}); }}
                    style={{
                      fontFamily: fonts, fontSize: 11, padding: '4px 10px', letterSpacing: 0.5, textTransform: 'uppercase',
                      background: selected.status === s ? statusColors[s] : 'transparent',
                      color: selected.status === s ? '#fff' : palette.text,
                      border: `1px solid ${statusColors[s]}`, cursor: 'pointer',
                    }}>{s}</button>
                ))}
                <button onClick={() => deleteReq(selected.id)}
                  style={{ fontFamily: fonts, fontSize: 11, padding: '4px 10px', letterSpacing: 0.5, textTransform: 'uppercase', background: 'transparent', color: '#a84032', border: '1px solid #a84032', cursor: 'pointer', marginLeft: 'auto' }}>
                  Delete
                </button>
              </div>

              {/* Quote / reply form */}
              <div style={{ marginTop: 18, paddingTop: 16, borderTop: `1px solid ${palette.border}` }}>
                <label style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: palette.textMuted, display: 'block', marginBottom: 6 }}>
                  Send a quote to {selected.name || selected.email}
                </label>
                <textarea
                  value={replyText}
                  onChange={(e) => { setReplyText(e.target.value); setReplyError(''); setReplySuccess(''); }}
                  rows={6}
                  placeholder={`Hi ${(selected.name || '').split(' ')[0] || 'there'},\n\nThanks for reaching out — based on what you described we'd quote it at $___ for ___. A few more questions:\n\n1.\n2.`}
                  style={{ width: '100%', padding: '10px 12px', fontFamily: fonts, fontSize: 13, border: `1px solid ${palette.border}`, background: palette.white, color: palette.text, boxSizing: 'border-box', resize: 'vertical', lineHeight: 1.5 }}
                />
                {replyError && <div style={{ color: '#a84032', fontSize: 12, marginTop: 6 }}>{replyError}</div>}
                {replySuccess && <div style={{ color: '#6b7c5e', fontSize: 12, marginTop: 6 }}>{replySuccess}</div>}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                  <span style={{ fontSize: 11, color: palette.textMuted }}>
                    Sends to <strong>{selected.email}</strong>
                  </span>
                  <button
                    onClick={sendReply}
                    disabled={replying || !replyText.trim()}
                    style={{
                      fontFamily: fonts, fontSize: 12, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase',
                      padding: '8px 18px',
                      background: palette.brown, color: palette.cream, border: 'none',
                      cursor: replying || !replyText.trim() ? 'not-allowed' : 'pointer',
                      opacity: replying || !replyText.trim() ? 0.5 : 1,
                    }}
                  >
                    {replying ? 'Sending…' : 'Send Quote'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ContactTab({ contactData, palette, fonts }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const inputStyle = { fontFamily: fonts, fontSize: 13, padding: '10px 12px', border: `1px solid ${palette.border}`, background: palette.white, color: palette.text, width: '100%', boxSizing: 'border-box', letterSpacing: 0.5 };
  const labelStyle = { fontFamily: sans, fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: palette.brown, display: 'block', marginBottom: 4 };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { setError('Name, email, and message are required'); return; }
    setSending(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/api/contact`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to send');
      setSent(true);
    } catch (err) { setError(err.message); } finally { setSending(false); }
  };

  if (sent) return (
    <div style={{ textAlign: 'center', padding: '40px 0' }}>
      <div style={{ fontSize: 36, marginBottom: 12 }}>&#10003;</div>
      <h3 style={{ fontFamily: fonts, fontSize: 16, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, marginBottom: 8 }}>Message Sent</h3>
      <p style={{ fontFamily: fonts, fontSize: 13, color: palette.textMuted }}>We'll get back to you as soon as possible.</p>
    </div>
  );

  return (
    <div style={{ padding: '24px 0' }}>
      <div className="hddn-stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32, maxWidth: 700, margin: '0 auto' }}>
        <div>
          <h3 style={{ fontFamily: fonts, fontSize: 16, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, marginBottom: 16 }}>Get In Touch</h3>
          <div style={{ fontSize: 14, lineHeight: 2.2, color: palette.text }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>{contactData?.name || 'HDDN LAB'}</div>
            <div>{contactData?.location || 'Downtown New Rochelle'}</div>
            <div><a href={`mailto:${contactData?.email || CONTACT_EMAIL}`} style={{ color: palette.orange, textDecoration: 'none' }}>{contactData?.email || CONTACT_EMAIL}</a></div>
            <div style={{ display: 'flex', gap: 16, marginTop: 12 }}>
              <a href={contactData?.instagram || 'https://instagram.com/hddn.lab'} target="_blank" rel="noopener noreferrer" style={{ color: palette.brown, textDecoration: 'none', fontSize: 13, fontWeight: 600 }}>Instagram</a>
              <a href={contactData?.tiktok || 'https://tiktok.com/@hddn.lab'} target="_blank" rel="noopener noreferrer" style={{ color: palette.brown, textDecoration: 'none', fontSize: 13, fontWeight: 600 }}>TikTok</a>
            </div>
          </div>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gap: 10 }}>
            <div><label style={labelStyle}>Name *</label><input value={form.name} onChange={e => setForm({...form, name: e.target.value})} style={inputStyle} /></div>
            <div><label style={labelStyle}>Email *</label><input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} style={inputStyle} /></div>
            <div><label style={labelStyle}>Phone</label><input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} style={inputStyle} /></div>
            <div><label style={labelStyle}>Subject</label><input value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} style={inputStyle} /></div>
            <div><label style={labelStyle}>Message *</label><textarea value={form.message} onChange={e => setForm({...form, message: e.target.value})} style={{...inputStyle, minHeight: 80, resize: 'vertical'}} /></div>
            {error && <div style={{ fontSize: 12, color: '#a84032' }}>{error}</div>}
            <button type="submit" disabled={sending} style={{ fontFamily: fonts, fontSize: 13, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '12px 24px', background: palette.brown, color: palette.cream, border: 'none', cursor: sending ? 'wait' : 'pointer', opacity: sending ? 0.6 : 1 }}>
              {sending ? 'Sending...' : 'Send Message'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const MOCK_PHOTOS = [
  { id: 'm1', url: 'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=600&q=80', caption: 'Industrial Warehouse Studio' },
  { id: 'm2', url: 'https://images.unsplash.com/photo-1565608087341-404b25492fee?w=600&q=80', caption: 'Exposed Brick & Natural Light' },
  { id: 'm3', url: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=600&q=80', caption: 'Open Floor Hardwood Space' },
  { id: 'm4', url: 'https://images.unsplash.com/photo-1572025442646-866d16c84a54?w=600&q=80', caption: 'Raw Industrial Loft' },
  { id: 'm5', url: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=600&q=80', caption: 'Warehouse Conversion' },
  { id: 'm6', url: 'https://images.unsplash.com/photo-1519999482648-25049ddd37b1?w=600&q=80', caption: 'Creative Studio Floor' },
];

// ---------------------------------------------------------------------------
// Photo Gallery (customer-facing carousel + lightbox)
// ---------------------------------------------------------------------------

function isVideoMime(mime) {
  return typeof mime === 'string' && mime.startsWith('video/');
}

function PhotoGallery({ room, event, title }) {
  const [allPhotos, setAllPhotos] = useState(event ? [] : MOCK_PHOTOS);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const scrollRef = React.useRef(null);

  useEffect(() => {
    const url = event
      ? `${API_BASE}/api/photos?event=${encodeURIComponent(event)}`
      : `${API_BASE}/api/photos`;
    fetch(url)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        if (Array.isArray(data)) setAllPhotos(data);
      })
      .catch(() => {});
  }, [event]);

  // When filtering by event we trust the server response verbatim. Otherwise
  // this is the space-showcase carousel: exclude any event-tagged photos (the
  // "our work" gallery, About, Open House sets) so only space photos appear,
  // then filter by room with a fallback to shared ('all') photos.
  const photos = React.useMemo(() => {
    if (event) return allPhotos;
    const spacePhotos = allPhotos.filter((p) => !p.event);
    if (!room) return spacePhotos;
    const roomSpecific = spacePhotos.filter((p) => p.room === room);
    const shared = spacePhotos.filter((p) => !p.room || p.room === 'all');
    return roomSpecific.length > 0 ? [...roomSpecific, ...shared] : shared.length > 0 ? shared : spacePhotos;
  }, [allPhotos, room, event]);

  // Reset lightbox index when room changes to avoid out-of-range
  useEffect(() => { setLightboxIndex(null); }, [room]);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const handler = (e) => {
      if (e.key === 'Escape') setLightboxIndex(null);
      if (e.key === 'ArrowLeft') setLightboxIndex((i) => (i > 0 ? i - 1 : photos.length - 1));
      if (e.key === 'ArrowRight') setLightboxIndex((i) => (i < photos.length - 1 ? i + 1 : 0));
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxIndex, photos.length]);

  if (photos.length === 0) return null;

  const scroll = (dir) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir * 260, behavior: 'smooth' });
    }
  };

  const openLightbox = (i) => setLightboxIndex(i);
  const closeLightbox = () => setLightboxIndex(null);
  const prevPhoto = () => setLightboxIndex((i) => (i > 0 ? i - 1 : photos.length - 1));
  const nextPhoto = () => setLightboxIndex((i) => (i < photos.length - 1 ? i + 1 : 0));

  return (
    <div style={{ marginTop: 24 }}>
      <h2
        style={{
          fontFamily: fonts,
          fontSize: 14,
          fontWeight: 700,
          letterSpacing: 3,
          textTransform: 'uppercase',
          color: palette.brown,
          marginBottom: 16,
          textAlign: 'center',
        }}
      >
        {title || 'Our Space'}
      </h2>

      {/* Carousel */}
      <div style={{ position: 'relative' }}>
        {photos.length > 3 && (
          <>
            <button
              className="hddn-carousel-arrow"
              onClick={() => scroll(-1)}
              style={{
                position: 'absolute', left: -16, top: '50%', transform: 'translateY(-50%)',
                zIndex: 2, background: palette.white, border: `1px solid ${palette.border}`,
                width: 36, height: 36, fontSize: 18, cursor: 'pointer', color: palette.brown,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}
            >
              &#8592;
            </button>
            <button
              className="hddn-carousel-arrow"
              onClick={() => scroll(1)}
              style={{
                position: 'absolute', right: -16, top: '50%', transform: 'translateY(-50%)',
                zIndex: 2, background: palette.white, border: `1px solid ${palette.border}`,
                width: 36, height: 36, fontSize: 18, cursor: 'pointer', color: palette.brown,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}
            >
              &#8594;
            </button>
          </>
        )}
        <div
          ref={scrollRef}
          className="gallery-scroll"
          style={{
            display: 'flex',
            gap: 12,
            overflowX: 'auto',
            scrollSnapType: 'x mandatory',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            padding: '4px 0',
          }}
        >
          {photos.map((photo, i) => {
            const isVideo = isVideoMime(photo.mime_type);
            return (
              <div
                key={photo.id || i}
                onClick={() => openLightbox(i)}
                style={{
                  flexShrink: 0,
                  width: 240,
                  height: 180,
                  scrollSnapAlign: 'start',
                  cursor: 'pointer',
                  border: `1px solid ${palette.border}`,
                  overflow: 'hidden',
                  background: palette.beige,
                  position: 'relative',
                }}
              >
                {isVideo ? (
                  <>
                    <video
                      src={photo.url}
                      muted
                      playsInline
                      preload="metadata"
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                    <div style={{
                      position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      pointerEvents: 'none',
                    }}>
                      <div style={{
                        width: 44, height: 44, borderRadius: '50%', background: 'rgba(0,0,0,0.55)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 20,
                      }}>&#9654;</div>
                    </div>
                  </>
                ) : (
                  <img
                    src={photo.url}
                    alt={photo.caption || 'Studio photo'}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
          }}
          onClick={closeLightbox}
        >
          <button
            onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
            style={{
              position: 'absolute', top: 20, right: 20, background: 'none', border: 'none',
              color: palette.white, fontSize: 32, cursor: 'pointer', zIndex: 2001,
            }}
          >
            &times;
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); prevPhoto(); }}
            style={{
              position: 'absolute', left: 20, top: '50%', transform: 'translateY(-50%)',
              background: 'rgba(255,255,255,0.15)', border: 'none', color: palette.white,
              fontSize: 28, width: 48, height: 48, cursor: 'pointer', zIndex: 2001,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            &#8592;
          </button>

          <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: '85vw', maxHeight: '85vh', position: 'relative' }}>
            {isVideoMime(photos[lightboxIndex].mime_type) ? (
              <video
                src={photos[lightboxIndex].url}
                controls
                autoPlay
                playsInline
                style={{ maxWidth: '85vw', maxHeight: '80vh', display: 'block' }}
              />
            ) : (
              <img
                src={photos[lightboxIndex].url}
                alt={photos[lightboxIndex].caption || 'Studio photo'}
                style={{ maxWidth: '85vw', maxHeight: '80vh', objectFit: 'contain', display: 'block' }}
              />
            )}
            {photos[lightboxIndex].caption && (
              <div style={{
                textAlign: 'center', color: palette.white, fontFamily: fonts,
                fontSize: 13, marginTop: 12, letterSpacing: 1,
              }}>
                {photos[lightboxIndex].caption}
              </div>
            )}
            <div style={{
              textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontFamily: fonts,
              fontSize: 11, marginTop: 6,
            }}>
              {lightboxIndex + 1} / {photos.length}
            </div>
          </div>

          <button
            onClick={(e) => { e.stopPropagation(); nextPhoto(); }}
            style={{
              position: 'absolute', right: 20, top: '50%', transform: 'translateY(-50%)',
              background: 'rgba(255,255,255,0.15)', border: 'none', color: palette.white,
              fontSize: 28, width: 48, height: 48, cursor: 'pointer', zIndex: 2001,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            &#8594;
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Admin Photo Manager
// ---------------------------------------------------------------------------

function AdminPhotoManager() {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState('');
  const [uploadRoom, setUploadRoom] = useState('all');
  const [uploadEvent, setUploadEvent] = useState('');
  const [roomFilter, setRoomFilter] = useState('all-photos');
  const [eventFilter, setEventFilter] = useState('all');
  const [saved, setSaved] = useState(null); // { text, isError }
  const fileInputRef = React.useRef(null);
  const replaceInputRefs = React.useRef({});

  // Distinct event tags present, and the currently-visible list after applying
  // both the room and event/tag filters. `visible` drives the grid, the count,
  // and reordering so moving photos works correctly within any filtered view.
  const distinctEvents = Array.from(new Set(photos.map((p) => (p.event || '').trim()).filter(Boolean))).sort();
  const visible = photos.filter((p) => {
    const roomOk = roomFilter === 'all-photos' || (p.room || 'all') === roomFilter;
    const ev = (p.event || '').trim();
    const eventOk = eventFilter === 'all' || (eventFilter === '__none__' ? !ev : ev === eventFilter);
    return roomOk && eventOk;
  });

  const fetchPhotos = () => {
    fetch(`${API_BASE}/api/photos`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('hddn_token') || ''}` },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      // Only replace the grid on a *successful* load. A transient failure
      // (network blip, token refresh) must NOT wipe the photos on screen.
      .then((data) => { if (Array.isArray(data)) setPhotos(data); })
      .catch(() => { /* keep whatever's already on screen */ });
  };

  // Brief "Saved ✓" / error confirmation shown after a change persists.
  const flashSaved = (text, isError) => {
    setSaved({ text, isError });
    setTimeout(() => setSaved(null), 2600);
  };

  useEffect(() => { fetchPhotos(); }, []);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const token = localStorage.getItem('hddn_token');
      for (const file of files) {
        const formData = new FormData();
        formData.append('photo', file);
        if (caption.trim()) formData.append('caption', caption.trim());
        formData.append('room', uploadRoom);
        if (uploadEvent.trim()) formData.append('event', uploadEvent.trim());
        await fetch(`${API_BASE}/api/photos`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: formData,
        });
      }
      setCaption('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchPhotos();
    } catch {}
    setUploading(false);
  };

  const updateRoom = async (id, room) => {
    const token = localStorage.getItem('hddn_token');
    const prev = photos;
    setPhotos(prev.map((p) => (p.id === id ? { ...p, room } : p)));
    try {
      await fetch(`${API_BASE}/api/photos/${id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ room }),
      });
    } catch { setPhotos(prev); }
  };

  const updateEvent = async (id, event) => {
    const token = localStorage.getItem('hddn_token');
    try {
      await fetch(`${API_BASE}/api/photos/${id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ event: (event || '').trim() }),
      });
    } catch { /* ignore — local state already updated */ }
  };

  const replacePhoto = async (id, file) => {
    if (!file) return;
    setUploading(true);
    try {
      const token = localStorage.getItem('hddn_token');
      const formData = new FormData();
      formData.append('photo', file);
      await fetch(`${API_BASE}/api/photos/${id}`, {
        method: 'PUT',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      fetchPhotos();
    } catch {}
    setUploading(false);
  };

  const movePhoto = async (id, direction) => {
    // Move relative to the neighbor in the *visible* (filtered) list, then swap
    // those two photos' positions in the full list so the saved order is correct
    // even when a room/tag filter is active. Photos in between are untouched.
    const vIdx = visible.findIndex((p) => p.id === id);
    if (vIdx < 0) return;
    const neighbor = visible[vIdx + direction];
    if (!neighbor) return;
    const full = [...photos];
    const a = full.findIndex((p) => p.id === id);
    const b = full.findIndex((p) => p.id === neighbor.id);
    if (a < 0 || b < 0) return;
    [full[a], full[b]] = [full[b], full[a]];
    setPhotos(full); // optimistic
    try {
      const token = localStorage.getItem('hddn_token');
      const res = await fetch(`${API_BASE}/api/photos/reorder`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ order: full.map((p) => p.id) }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      flashSaved('Order saved ✓');
    } catch {
      flashSaved('Couldn’t save order — try again', true);
      fetchPhotos();
    }
  };

  // Explicit gallery membership toggle. Sets the photo's event tag to 'work'
  // (shows on the Gallery page) or clears it. Optimistic with a saved flash.
  const toggleGallery = async (photo) => {
    const inGallery = (photo.event || '').trim() === 'work';
    const newEvent = inGallery ? '' : 'work';
    const prev = photos;
    setPhotos(photos.map((p) => (p.id === photo.id ? { ...p, event: newEvent } : p)));
    try {
      const token = localStorage.getItem('hddn_token');
      const res = await fetch(`${API_BASE}/api/photos/${photo.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ event: newEvent }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      flashSaved(newEvent ? 'Added to gallery ✓' : 'Removed from gallery ✓');
    } catch {
      setPhotos(prev);
      flashSaved('Couldn’t save — try again', true);
    }
  };

  const deletePhoto = async (id) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await api(`/api/photos/${id}`, { method: 'DELETE' });
      fetchPhotos();
    } catch {}
  };

  const inputStyle = {
    padding: '8px 10px', fontFamily: fonts, fontSize: 13,
    border: `1px solid ${palette.border}`, background: palette.white,
    color: palette.text, boxSizing: 'border-box', outline: 'none',
  };
  const labelStyle = {
    display: 'block', fontSize: 11, letterSpacing: 1,
    textTransform: 'uppercase', color: palette.textMuted, marginBottom: 4,
  };

  return (
    <div>
      <div style={{ background: palette.white, border: `1px solid ${palette.border}`, padding: '20px', marginBottom: 24 }}>
        <h4 style={{ fontFamily: fonts, fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, margin: '0 0 16px' }}>
          Upload Photos
        </h4>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <label style={labelStyle}>Caption (optional)</label>
            <input type="text" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="e.g. Main shooting area" style={{ ...inputStyle, width: 220 }} />
          </div>
          <div>
            <label style={labelStyle}>Room</label>
            <select value={uploadRoom} onChange={(e) => setUploadRoom(e.target.value)} style={{ ...inputStyle, width: 180 }}>
              <option value="all">All Rooms (shared)</option>
              <option value="upstairs-1">Full Studio</option>
              <option value="upstairs-2">Podcast Recording</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Event tag (optional)</label>
            <input type="text" value={uploadEvent} onChange={(e) => setUploadEvent(e.target.value)} placeholder="e.g. open-house-2026-04" style={{ ...inputStyle, width: 200 }} />
          </div>
          <div>
            <label style={labelStyle}>Select Photos / Videos</label>
            <input ref={fileInputRef} type="file" accept="image/*,video/mp4,video/quicktime,video/webm" multiple onChange={handleUpload} disabled={uploading} style={{ fontFamily: fonts, fontSize: 12, color: palette.text }} />
          </div>
          {uploading && <span style={{ fontFamily: fonts, fontSize: 12, color: palette.textMuted }}>Uploading...</span>}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
        <h4 style={{ fontFamily: fonts, fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
          Photos ({visible.length})
          {saved && (
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'none', color: saved.isError ? palette.red : palette.sage }}>
              {saved.text}
            </span>
          )}
        </h4>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <select value={eventFilter} onChange={(e) => setEventFilter(e.target.value)} style={{ ...inputStyle, width: 200 }}>
            <option value="all">All tags</option>
            <option value="__none__">Space photos (no tag)</option>
            {distinctEvents.map((ev) => (
              <option key={ev} value={ev}>{ev === 'work' ? 'Gallery — Our Work' : ev}</option>
            ))}
          </select>
          <select value={roomFilter} onChange={(e) => setRoomFilter(e.target.value)} style={{ ...inputStyle, width: 200 }}>
            <option value="all-photos">All Rooms</option>
            <option value="all">Shared (All Rooms)</option>
            <option value="upstairs-1">Full Studio only</option>
            <option value="upstairs-2">Podcast Recording only</option>
          </select>
        </div>
      </div>
      <p style={{ fontFamily: fonts, fontSize: 11, color: palette.textMuted, margin: '0 0 12px', lineHeight: 1.6 }}>
        Tap <strong>★ Gallery</strong> on a photo to show it on the public Gallery page. Use <strong>▲▼</strong> to reorder{eventFilter === 'work' ? ' the gallery' : ''} — changes save automatically (you’ll see “Saved ✓”).
        {eventFilter !== 'work' && <> To arrange just your gallery photos, pick <strong>“Gallery — Our Work”</strong> in the tag filter.</>}
      </p>

      {photos.length === 0 ? (
        <p style={{ color: palette.textMuted, fontSize: 14 }}>No photos uploaded yet. Upload photos above to showcase your studio space.</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
          {visible.map((photo, idx) => {
            const roomLabel = photo.room === 'upstairs-1' ? 'Full' : photo.room === 'upstairs-2' ? 'Podcast' : 'All';
            const roomBg = photo.room === 'upstairs-1' ? palette.orange : photo.room === 'upstairs-2' ? palette.sage : 'rgba(0,0,0,0.6)';
            return (
            <div key={photo.id} style={{ border: `1px solid ${palette.border}`, background: palette.white, overflow: 'hidden', position: 'relative' }}>
              <img src={photo.url} alt={photo.caption || ''} style={{ width: '100%', height: 140, objectFit: 'cover', display: 'block' }} />
              {/* Order badge */}
              <div style={{ position: 'absolute', top: 4, left: 4, background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 10, fontWeight: 700, fontFamily: fonts, padding: '2px 6px', borderRadius: 2 }}>{idx + 1}</div>
              {/* Room badge */}
              <div style={{ position: 'absolute', bottom: 92, left: 4, background: roomBg, color: '#fff', fontSize: 10, fontWeight: 700, fontFamily: fonts, padding: '2px 6px', borderRadius: 2, letterSpacing: 0.5, textTransform: 'uppercase' }}>{roomLabel}</div>
              {/* Up/Down arrows */}
              <div style={{ position: 'absolute', top: 4, right: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {idx > 0 && <button onClick={() => movePhoto(photo.id, -1)} style={{ background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12, padding: '1px 6px', fontFamily: fonts }}>&#9650;</button>}
                {idx < visible.length - 1 && <button onClick={() => movePhoto(photo.id, 1)} style={{ background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12, padding: '1px 6px', fontFamily: fonts }}>&#9660;</button>}
              </div>
              {/* In-gallery toggle */}
              {(() => {
                const inGallery = (photo.event || '').trim() === 'work';
                return (
                  <button onClick={() => toggleGallery(photo)}
                    title={inGallery ? 'Showing on the Gallery page — tap to remove' : 'Add to the Gallery page'}
                    style={{
                      position: 'absolute', bottom: 92, right: 4, cursor: 'pointer', border: 'none',
                      fontFamily: fonts, fontSize: 10, fontWeight: 700, letterSpacing: 0.5, padding: '3px 8px', borderRadius: 2,
                      background: inGallery ? palette.orange : 'rgba(0,0,0,0.6)', color: '#fff',
                    }}>
                    {inGallery ? '★ In Gallery' : '☆ Gallery'}
                  </button>
                );
              })()}
              <div style={{ padding: '8px 10px' }}>
                <span style={{ fontSize: 11, color: palette.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', marginBottom: 6 }}>
                  {photo.caption || 'No caption'}
                </span>
                <select
                  value={photo.room || 'all'}
                  onChange={(e) => updateRoom(photo.id, e.target.value)}
                  style={{ fontFamily: fonts, fontSize: 10, padding: '3px 6px', border: `1px solid ${palette.border}`, width: '100%', marginBottom: 6, background: palette.white, color: palette.text }}
                >
                  <option value="all">All Rooms</option>
                  <option value="upstairs-1">Full Studio</option>
                  <option value="upstairs-2">Podcast Recording</option>
                </select>
                <input
                  type="text"
                  value={photo.event || ''}
                  onChange={(e) => setPhotos(photos.map((p) => (p.id === photo.id ? { ...p, event: e.target.value } : p)))}
                  onBlur={(e) => updateEvent(photo.id, e.target.value)}
                  placeholder="Event tag (optional)"
                  style={{ fontFamily: fonts, fontSize: 10, padding: '3px 6px', border: `1px solid ${palette.border}`, width: '100%', marginBottom: 6, background: palette.white, color: palette.text, boxSizing: 'border-box' }}
                />
                <div style={{ display: 'flex', gap: 6 }}>
                  <input type="file" accept="image/*,video/mp4,video/quicktime,video/webm" ref={el => replaceInputRefs.current[photo.id] = el} style={{ display: 'none' }}
                    onChange={(e) => { if (e.target.files[0]) replacePhoto(photo.id, e.target.files[0]); }} />
                  <button onClick={() => replaceInputRefs.current[photo.id]?.click()}
                    style={{ fontFamily: fonts, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '3px 8px', border: `1px solid ${palette.border}`, cursor: 'pointer', background: palette.white, color: palette.brown }}>
                    Replace
                  </button>
                  <button onClick={() => deletePhoto(photo.id)}
                    style={{ fontFamily: fonts, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '3px 8px', border: 'none', cursor: 'pointer', background: palette.red, color: palette.white }}>
                    Delete
                  </button>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Booking modal
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Studio Policies & Rules — structured component
// ---------------------------------------------------------------------------
const termsHeadingStyle = {
  fontFamily: fonts,
  fontSize: 18,
  fontWeight: 700,
  letterSpacing: 2,
  textTransform: 'uppercase',
  margin: '28px 0 12px',
  paddingBottom: 8,
  borderBottom: '2px solid #c9a96e',
};
const termsSubheadingStyle = {
  fontFamily: fonts,
  fontSize: 15,
  fontWeight: 700,
  margin: '18px 0 8px',
};
const termsParagraphStyle = {
  fontSize: 14,
  lineHeight: 1.8,
  margin: '0 0 10px',
};
const termsListStyle = {
  margin: '6px 0 12px 20px',
  padding: 0,
  fontSize: 14,
  lineHeight: 1.8,
};
const termsListItemStyle = {
  marginBottom: 4,
};

function TermsContent({ palette: p }) {
  const h2 = { ...termsHeadingStyle, color: p.brown, borderBottomColor: p.orange };
  const h3 = { ...termsSubheadingStyle, color: p.brown };
  const par = { ...termsParagraphStyle, color: p.text };

  return (
    <div>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <h1 style={{ fontFamily: fonts, fontSize: 22, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: p.brown, margin: '0 0 8px' }}>
          HDDN LAB
        </h1>
        <h2 style={{ fontFamily: fonts, fontSize: 16, fontWeight: 600, letterSpacing: 2, textTransform: 'uppercase', color: p.textMuted, margin: 0, border: 'none', padding: 0 }}>
          Studio Policies &amp; Rules
        </h2>
      </div>

      <div style={{ background: p.beige, padding: '14px 18px', marginBottom: 24, fontSize: 14, lineHeight: 1.8 }}>
        <p style={{ margin: '0 0 4px' }}><strong>Company:</strong> HDDN LAB</p>
        <p style={{ margin: '0 0 4px' }}><strong>Premises:</strong> Upstairs studio space located in New Rochelle, NY, including the Full Studio and Podcast Recording room, plus any adjacent property owned by HDDN LAB</p>
        <p style={{ margin: 0 }}><strong>Renter:</strong> The person or entity booking and renting studio space or equipment from HDDN LAB</p>
      </div>

      <h2 style={h2}>1. Payments &amp; Deposits</h2>
      <p style={par}>To confirm and hold a reservation, the full rental fee plus any applicable refundable damage deposits must be paid in full at the time of booking. Payment methods include: Credit/Debit Card, Venmo, PayPal, or Zelle.</p>
      <p style={par}>If additional equipment rentals or time duration are added on the shoot day, the Renter must modify the booking online or pay HDDN LAB the appropriate fee through one of the accepted payment methods listed above.</p>
      <p style={par}>Rental fees are non-refundable except as specified in the Cancellation Policy section below.</p>

      <h2 style={h2}>2. Insurance &amp; Certificates of Insurance (COI)</h2>
      <p style={par}>Certificate of Insurance is required for:</p>
      <ul style={termsListStyle}>
        <li style={termsListItemStyle}>Large-scale productions</li>
        <li style={termsListItemStyle}>Outside equipment rental</li>
        <li style={termsListItemStyle}>Commercial/corporate shoots</li>
        <li style={termsListItemStyle}>Any shoot requiring both rooms simultaneously</li>
      </ul>
      <p style={par}>If required, HDDN LAB will notify you at booking confirmation. Failure to provide a valid COI may result in cancellation of your reservation without refund.</p>
      <h3 style={h3}>For Businesses, Corporations, Production Companies, or Legal Entities:</h3>
      <p style={par}>Prior to rental, Renter may be required to present a certificate of general liability insurance naming HDDN LAB as additionally insured on the dates of the rental. If required:</p>
      <ul style={termsListStyle}>
        <li style={termsListItemStyle}>Renter{'\u2019'}s liability insurance shall be deemed primary and non-contributory insurance</li>
        <li style={termsListItemStyle}>Liability insurance shall be Commercial General Liability with a minimum of $1,000,000 per occurrence &amp; annual aggregate</li>
        <li style={termsListItemStyle}>HDDN LAB reserves the right to cancel the booking if proof of insurance is not provided</li>
      </ul>
      <h3 style={h3}>For Individuals / Content Creators:</h3>
      <p style={par}>Insurance is not required but recommended.</p>

      <h2 style={h2}>3. Security &amp; Surveillance</h2>
      <p style={par}>HDDN LAB Premises are under 24/7 video surveillance for security, insurance, and dispute resolution purposes. Video recordings are used for dispute resolution ONLY and will not be shared publicly without legal cause.</p>

      <h2 style={h2}>4. Automatic Charges</h2>
      <p style={par}><strong>Overtime:</strong> Charges will be automatically applied for any studio hours beyond your original booking at the same hourly rate as your booked session. Pre-approval from HDDN LAB is required; unpaid overtime will result in session termination.</p>
      <p style={par}><strong>Additional Add-ons:</strong> If you use add-on equipment during your session that is not included in your original booking (lighting package, backdrop kit, microphone package, podcast editing service, etc.), charges will be automatically applied at the stated rate.</p>
      <p style={par}><strong>Cleaning Fee:</strong> You will be charged an additional $100 cleaning fee if the studio is left in disarray or shows signs of damage.</p>
      <p style={par}><strong>Key Card Replacement:</strong> Lost or unreturned access cards/keys will incur a $50 replacement fee per card.</p>

      <h2 style={h2}>5. Materials &amp; Props Approval</h2>
      <p style={par}>You <strong>MUST RECEIVE PRIOR WRITTEN APPROVAL</strong> before bringing any hard-to-clean materials and props such as:</p>
      <ul style={termsListStyle}>
        <li style={termsListItemStyle}>Glitter, confetti, powder, talc</li>
        <li style={termsListItemStyle}>Balloons, streamers</li>
        <li style={termsListItemStyle}>Oils, lotions, makeup residue</li>
        <li style={termsListItemStyle}>Paint, wax, adhesives</li>
        <li style={termsListItemStyle}>Smoke machines, fog, haze</li>
        <li style={termsListItemStyle}>Any other materials that may stain, damage, or leave residue</li>
      </ul>
      <p style={par}>Failure to obtain approval will:</p>
      <ul style={termsListStyle}>
        <li style={termsListItemStyle}>Result in immediate session termination without refund</li>
        <li style={termsListItemStyle}>Result in additional cleaning/damage charges</li>
        <li style={termsListItemStyle}>Disqualify you from future HDDN LAB bookings</li>
      </ul>

      <h2 style={h2}>6. No Pets Policy</h2>
      <p style={par}>No pets are allowed on HDDN LAB Premises without prior written consent from HDDN LAB management.</p>
      <p style={par}>Exceptions may be made for certified service animals. If you have a service animal, please notify HDDN LAB at the time of booking.</p>
      <p style={par}>Violating this policy will result in immediate termination of your session without refund.</p>

      <h2 style={h2}>7. Cancellation Policy</h2>
      <h3 style={h3}>Full Refund:</h3>
      <ul style={termsListStyle}>
        <li style={termsListItemStyle}>Cancellations within 24 hours of booking confirmation (excluding bookings starting within 48 hours)</li>
        <li style={termsListItemStyle}>Cancellations 7+ days in advance</li>
      </ul>
      <h3 style={h3}>50% Refund (excluding all fees):</h3>
      <ul style={termsListStyle}>
        <li style={termsListItemStyle}>Cancellations 7 days to 24 hours in advance</li>
      </ul>
      <h3 style={h3}>No Refund:</h3>
      <ul style={termsListStyle}>
        <li style={termsListItemStyle}>Cancellations less than 24 hours before event start time</li>
        <li style={termsListItemStyle}>Event rentals cancelled within 72 hours of start time</li>
      </ul>
      <p style={par}>Rescheduling requests must be submitted in writing and are subject to availability. HDDN LAB will make reasonable efforts to accommodate rescheduling requests but does not guarantee availability on alternate dates.</p>

      <h2 style={h2}>8. Studio Rules</h2>
      <h3 style={h3}>Smoking &amp; Vaping</h3>
      <ul style={termsListStyle}>
        <li style={termsListItemStyle}>NO SMOKING OF ANY KIND in the building or on HDDN LAB property</li>
        <li style={termsListItemStyle}>This includes cigarettes, cigars, vapes, and cannabis</li>
        <li style={termsListItemStyle}>Violation will result in immediate session termination and $250 fine</li>
      </ul>
      <h3 style={h3}>Pets</h3>
      <ul style={termsListStyle}>
        <li style={termsListItemStyle}>No pets allowed without prior consent of HDDN LAB management</li>
        <li style={termsListItemStyle}>Certified service animals are permitted (notify at booking)</li>
      </ul>
      <h3 style={h3}>Food &amp; Beverages</h3>
      <ul style={termsListStyle}>
        <li style={termsListItemStyle}>Outside food and beverages are allowed for craft services</li>
        <li style={termsListItemStyle}>You are responsible for appropriate disposal of food waste</li>
        <li style={termsListItemStyle}>No alcohol permitted on Premises without prior written consent</li>
        <li style={termsListItemStyle}>Clean up all food and beverage spills immediately</li>
      </ul>
      <h3 style={h3}>Materials &amp; Props</h3>
      <ul style={termsListStyle}>
        <li style={termsListItemStyle}>Obtain written approval before bringing glitter, powder, balloons, oils, paint, or other hard-to-clean materials</li>
        <li style={termsListItemStyle}>Failure to obtain approval results in session termination and additional cleaning charges</li>
      </ul>
      <h3 style={h3}>Trash &amp; Recycling</h3>
      <ul style={termsListStyle}>
        <li style={termsListItemStyle}>Dispose of trash in the trash bin</li>
        <li style={termsListItemStyle}>Place recyclables in the recycling bin</li>
        <li style={termsListItemStyle}>Leave the studio clean and organized</li>
      </ul>
      <h3 style={h3}>Space Maintenance</h3>
      <ul style={termsListStyle}>
        <li style={termsListItemStyle}>Keep the space clean during and after your rental</li>
        <li style={termsListItemStyle}>Return all equipment and furniture to its original location</li>
        <li style={termsListItemStyle}>Wipe down surfaces you use (tables, counters, equipment)</li>
        <li style={termsListItemStyle}>A $100 cleaning fee will be applied if the studio is left in disarray</li>
      </ul>
      <h3 style={h3}>Minors &amp; Supervision</h3>
      <ul style={termsListStyle}>
        <li style={termsListItemStyle}>No unsupervised children on Premises</li>
        <li style={termsListItemStyle}>Anyone under 18 years old must be accompanied by an on-age guardian (21+) at all times</li>
        <li style={termsListItemStyle}>HDDN LAB reserves the right to verify guardian relationship via ID</li>
      </ul>
      <h3 style={h3}>Hallway &amp; Common Areas</h3>
      <ul style={termsListStyle}>
        <li style={termsListItemStyle}>No loitering in hallways or common areas</li>
        <li style={termsListItemStyle}>Do not enter other studio spaces or private areas</li>
        <li style={termsListItemStyle}>Keep noise at reasonable levels in shared building spaces</li>
      </ul>
      <p style={par}>An HDDN LAB representative may, at Company{'\u2019'}s sole discretion, be present at all times. If the representative observes or becomes aware of dangerous, illegal, pornographic, negligent, or disruptive practices or activities, the representative reserves the right to stop the session immediately and require the Renter and party to leave without refund for unused time. HDDN LAB assumes no responsibility to monitor for such activities.</p>

      <h2 style={h2}>9. Time Management</h2>
      <p style={par}>Setup and breakdown must occur during your booked time slot. HDDN LAB cannot guarantee extensions to your booking.</p>
      <ul style={termsListStyle}>
        <li style={termsListItemStyle}>Arrive 10-15 minutes early to plan your setup</li>
        <li style={termsListItemStyle}>Budget setup and breakdown time into your total booked hours</li>
        <li style={termsListItemStyle}>If you finish early, you do not get a refund for unused time</li>
        <li style={termsListItemStyle}>If you need additional time, request overtime pre-booking (subject to availability and approval)</li>
      </ul>

      <h2 style={h2}>10. Cleanliness &amp; Damages</h2>
      <p style={par}>The Renter agrees to leave the studio clean and organized upon departure.</p>
      <p style={par}><strong>Cleaning Fee:</strong> If the space is left unclean, disorganized, or requires additional cleaning beyond normal maintenance, HDDN LAB will charge a $100 cleaning fee.</p>
      <p style={par}><strong>Damage Liability:</strong> The Renter agrees to return all studio space, equipment, furniture, and add-ons in the same condition they were provided. The Renter is immediately responsible for notifying HDDN LAB of any damage, malfunction, or change in equipment condition during or immediately after the rental.</p>
      <p style={par}><strong>Damage Costs:</strong> The Renter is liable for the full cost of repairs or replacement of any equipment, furniture, or studio damage caused by the Renter, the Renter{'\u2019'}s employees, contractors, clients, or visitors. Payment is due within 3 business days of written notice from HDDN LAB.</p>
      <p style={par}><strong>No Warranty:</strong> There is no warranty that rented items are free of defects.</p>

      <h2 style={h2}>11. Studio &amp; Equipment Provided</h2>
      <p style={par}>HDDN LAB agrees to provide the studio, equipment, and furniture in good working order. However, HDDN LAB makes no special guarantees as to said equipment{'\u2019'}s functionality or suitability to the Renter{'\u2019'}s purposes.</p>
      <p style={par}>The Renter is entitled to use:</p>
      <ul style={termsListStyle}>
        <li style={termsListItemStyle}>The booked studio room (Full Studio or Podcast Recording)</li>
        <li style={termsListItemStyle}>In-studio equipment and furniture as specified in the booking</li>
        <li style={termsListItemStyle}>Any add-on equipment selected and paid for (lighting package, backdrop kit, microphone package, etc.)</li>
      </ul>
      <p style={par}><strong>Equipment Issues:</strong> The Renter shall notify HDDN LAB immediately of any malfunction, damage, or other issues with the equipment.</p>
      <p style={par}><strong>Liability Waiver:</strong> HDDN LAB is not liable for:</p>
      <ul style={termsListStyle}>
        <li style={termsListItemStyle}>Acts beyond Company control (power outages, weather, emergencies, Acts of God)</li>
        <li style={termsListItemStyle}>Any damage or injury caused by use of Company equipment to Renter or any third party</li>
        <li style={termsListItemStyle}>Loss of income or other consequential damages</li>
      </ul>
      <p style={par}>The Renter assumes all risk of personal property damage or personal injury. If any accident involving HDDN LAB{'\u2019'}s equipment occurs while in the Renter{'\u2019'}s possession, the Renter shall immediately notify HDDN LAB in writing with:</p>
      <ul style={termsListStyle}>
        <li style={termsListItemStyle}>Detailed description of the incident</li>
        <li style={termsListItemStyle}>Police report (if applicable)</li>
        <li style={termsListItemStyle}>Names and contact information of witnesses (if applicable)</li>
        <li style={termsListItemStyle}>Photos of damage (if applicable)</li>
      </ul>

      <h2 style={h2}>12. Age of Models &amp; Consent</h2>
      <p style={par}>The Renter is solely responsible for verifying that all photographic or video subjects (models, talent, participants) are of legal age (18+).</p>
      <p style={par}>HDDN LAB has no responsibility to verify participant age but reserves the right to:</p>
      <ul style={termsListStyle}>
        <li style={termsListItemStyle}>Demand proof of parental/guardian consent for any participants under 18</li>
        <li style={termsListItemStyle}>End the session immediately if HDDN LAB becomes aware of age violations</li>
        <li style={termsListItemStyle}>Refuse to provide further rental services to the Renter</li>
      </ul>
      <p style={par}>HDDN LAB is not liable in the case of invalid ID or any other form of age verification failure. The Renter assumes all liability related to age verification.</p>

      <h2 style={h2}>13. Podcast Studio Specifics</h2>
      <p style={par}>For Podcast Studio bookings:</p>
      <ul style={termsListStyle}>
        <li style={termsListItemStyle}>The Renter may not record or broadcast any session without explicit written consent from HDDN LAB</li>
        <li style={termsListItemStyle}>Podcast editing services (if selected as add-on) are provided on a best-effort basis and do not include commercial-grade mastering</li>
        <li style={termsListItemStyle}>Recording equipment must not be removed from the Premises</li>
        <li style={termsListItemStyle}>The Renter retains all rights to content recorded; HDDN LAB retains the right to use studio imagery/footage for marketing purposes only with Renter{'\u2019'}s prior approval</li>
      </ul>

      <h2 style={h2}>14. Intellectual Property &amp; Usage Rights</h2>
      <ul style={termsListStyle}>
        <li style={termsListItemStyle}>The Renter retains full ownership and rights to all content created during the rental.</li>
        <li style={termsListItemStyle}>HDDN LAB reserves the right to photograph or video the studio/session for portfolio and marketing purposes only.</li>
        <li style={termsListItemStyle}>HDDN LAB will request prior written approval from the Renter before using any Renter-created content or images of the Renter for marketing.</li>
        <li style={termsListItemStyle}>The Renter agrees not to use HDDN LAB{'\u2019'}s name, likeness, or branding in advertising without prior written consent.</li>
      </ul>

      <h2 style={h2}>15. Dispute Resolution &amp; Arbitration</h2>
      <p style={par}>If the parties are unable to resolve any controversy or claim arising under this Agreement, they agree to submit the dispute to binding arbitration subject to the Commercial Arbitration Rules of the American Arbitration Association.</p>
      <p style={par}><strong>Arbitration Terms:</strong></p>
      <ul style={termsListStyle}>
        <li style={termsListItemStyle}>One arbitrator selected from the AAA panel</li>
        <li style={termsListItemStyle}>Each party responsible for its share of arbitration fees per AAA Rules</li>
        <li style={termsListItemStyle}>If a party fails to proceed with arbitration, unsuccessfully challenges the arbitrator{'\u2019'}s award, or fails to comply with the award, the other party is entitled to costs of suit including reasonable attorney{'\u2019'}s fees</li>
        <li style={termsListItemStyle}>Either party may refuse to arbitrate if the dispute is for less than $200</li>
      </ul>

      <h2 style={h2}>16. Additional Terms</h2>
      <ul style={termsListStyle}>
        <li style={termsListItemStyle}>HDDN LAB is not responsible for lost, stolen, or damaged personal property left on the Premises</li>
        <li style={termsListItemStyle}>The Renter assumes all liability for any claims from third parties arising from the Renter{'\u2019'}s use of the Premises</li>
        <li style={termsListItemStyle}>HDDN LAB reserves the right to modify these Terms and Conditions with 30 days{'\u2019'} notice</li>
      </ul>

      <h2 style={{ ...h2, borderBottomColor: p.red }}>Acknowledgment</h2>
      <p style={par}>By booking with HDDN LAB, you acknowledge that you have read, understood, and agree to comply with all Studio Policies &amp; Rules outlined herein. Violation of these policies may result in:</p>
      <ul style={termsListStyle}>
        <li style={termsListItemStyle}>Immediate session termination</li>
        <li style={termsListItemStyle}>Forfeiture of payment / no refund</li>
        <li style={termsListItemStyle}>Disqualification from future HDDN LAB bookings</li>
        <li style={termsListItemStyle}>Additional fees or legal action for damages</li>
      </ul>

      <p style={{ ...par, textAlign: 'center', color: p.textMuted, marginTop: 28, fontSize: 12 }}>Last Updated: March 2026</p>
    </div>
  );
}

// Booking form "How did you hear about us?" choices. Ids must match
// HEARD_FROM_OPTIONS in api-server.js.
const HEARD_FROM_CHOICES = [
  { id: 'instagram', label: 'Instagram' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'google', label: 'Google search' },
  { id: 'peerspace', label: 'Peerspace' },
  { id: 'giggster', label: 'Giggster' },
  { id: 'friend', label: 'Friend or referral' },
  { id: 'event', label: 'An HDDN event' },
  { id: 'walked-by', label: 'Walked by' },
  { id: 'other', label: 'Other' },
];

function BookingModal({ date, room, user, slots, onClose, onSubmit, stripeEnabled, packagesConfig }) {
  const [guestName, setGuestName] = useState(user ? user.name : '');
  const [guestEmail, setGuestEmail] = useState(user ? user.email : '');
  const [service, setService] = useState('');
  const [startTime, setStartTime] = useState(`${String(OPEN_HOUR).padStart(2, '0')}:00`);
  const [hours, setHours] = useState(MIN_HOURS);
  const [addOns, setAddOns] = useState([]);
  const [addOnOptions, setAddOnOptions] = useState({}); // { 'backdrop-set': 'White' }
  const [notes, setNotes] = useState('');
  const [description, setDescription] = useState('');
  const [attendees, setAttendees] = useState(1);
  const [heardFrom, setHeardFrom] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showIncludedModal, setShowIncludedModal] = useState(false);
  const [showEquipmentModal, setShowEquipmentModal] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [signature, setSignature] = useState('');

  // Use dynamic config if available, fallback to hardcoded
  const dynPodcastPackages = packagesConfig?.podcastPackages || PODCAST_PACKAGES;
  const dynPodcastAddOns = packagesConfig?.podcastAddOns || PODCAST_ADD_ONS;
  const dynStudioAddOns = packagesConfig?.studioAddOns || ADD_ONS;
  const dynHourlyRate = packagesConfig?.hourlyRate || 100;

  const isPodcast = isPodcastService(service);
  const podcastPkg = isPodcast ? dynPodcastPackages[service] : null;

  // When switching to/from podcast, reset hours and add-ons
  useEffect(() => {
    if (isPodcast && podcastPkg) {
      setHours(podcastPkg.durations[0].hours);
      setAddOns([]);
    } else if (service) {
      setHours(MIN_HOURS);
      setAddOns([]);
    }
  }, [service]);

  // Compute which hours are already booked for this date
  const daySlots = slots || [];
  const occupied = new Array(24).fill(false);
  daySlots.forEach((s) => {
    for (let h = s.start; h < s.start + s.hours; h++) occupied[h] = true;
  });

  // For podcast: minimum needed is 1 hour (Creator 1hr option)
  const minNeeded = isPodcast ? 1 : MIN_HOURS;

  // Filter available start times (must have at least minNeeded contiguous)
  const availableStartTimes = START_TIMES.filter((t) => {
    const h = parseInt(t.value.split(':')[0]);
    for (let i = h; i < h + minNeeded && i < CLOSE_HOUR; i++) {
      if (occupied[i]) return false;
    }
    return h + minNeeded <= CLOSE_HOUR;
  });

  // Max hours from selected start time
  const startH = parseInt(startTime.split(':')[0]);
  let maxHours = 0;
  for (let h = startH; h < CLOSE_HOUR; h++) {
    if (occupied[h]) break;
    maxHours++;
  }
  maxHours = Math.max(maxHours, minNeeded);

  // Clamp hours when start time changes (non-podcast only)
  useEffect(() => {
    if (!isPodcast) {
      if (hours > maxHours) setHours(maxHours);
      if (hours < MIN_HOURS) setHours(MIN_HOURS);
    }
  }, [startTime]);

  // Detect same-day booking
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const isSameDay = date === todayStr;

  // Auto-apply same-day booking add-on when booking for today
  useEffect(() => {
    if (isSameDay && service) {
      setAddOns((prev) => prev.includes('same-day-booking') ? prev : [...prev, 'same-day-booking']);
    }
  }, [isSameDay, service]);

  // Same-day fee (applies to all services)
  const sameDayFee = isSameDay ? (dynPodcastAddOns.find((a) => a.id === 'same-day-booking')?.price || 30) : 0;

  // Pricing (using dynamic config)
  const podcastPrice = isPodcast && podcastPkg
    ? (podcastPkg.durations.find((d) => d.hours === hours)?.price || null)
    : null;
  const addOnsTotal = addOns.reduce((sum, id) => {
    if (id === 'same-day-booking') return sum; // counted separately via sameDayFee
    const addonList = isPodcast ? dynPodcastAddOns : dynStudioAddOns;
    const addon = addonList.find((a) => a.id === id);
    return sum + (addon && addon.price ? addon.price : 0);
  }, 0);
  const totalPrice = isPodcast
    ? (podcastPrice || 0) + addOnsTotal + sameDayFee
    : hours * dynHourlyRate + addOnsTotal + sameDayFee;

  const toggleAddon = (id) => {
    // Prevent toggling off the same-day booking fee — it's mandatory for same-day
    if (id === 'same-day-booking' && isSameDay) return;
    setAddOns((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!guestName.trim()) {
      setError('Name is required');
      return;
    }
    if (!guestEmail.trim()) {
      setError('Email is required');
      return;
    }
    if (!service) {
      setError('Select a service type');
      return;
    }
    if (!description.trim()) {
      setError('Please provide a brief description of your session');
      return;
    }
    if (attendees < 1 || attendees > 15) {
      setError('Number of attendees must be between 1 and 15');
      return;
    }
    if (!termsAgreed) {
      setError('You must agree to the HDDN LAB Studio Policies & Rules');
      return;
    }
    if (!signature.trim()) {
      setError('Please type your full name as a digital signature');
      return;
    }
    setLoading(true);
    setError('');
    try {
      // Append add-on options to notes
      const optionNotes = Object.entries(addOnOptions).filter(([id]) => addOns.includes(id)).map(([id, val]) => {
        const addon = (isPodcast ? dynPodcastAddOns : dynStudioAddOns).find(a => a.id === id);
        return `${addon?.label || id}: ${val}`;
      }).join('; ');
      const fullNotes = [notes, optionNotes].filter(Boolean).join(' | ');
      await onSubmit({ date, room, service, startTime, hours, addOns, notes: fullNotes, signature: signature.trim(), name: guestName.trim(), email: guestEmail.trim(), description: description.trim(), attendees, heardFrom: heardFrom || undefined, podcastPackagePrice: isPodcast ? totalPrice : undefined, isSameDay, totalPrice });
    } catch (err) {
      setError(err.error || 'Booking failed');
      setLoading(false);
    }
  };

  const roomLabel = ROOMS.find((r) => r.id === room)?.label || room;

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    fontFamily: sans,
    fontSize: 14,
    border: `1px solid ${palette.border}`,
    background: palette.white,
    color: palette.text,
    boxSizing: 'border-box',
    outline: 'none',
  };

  const labelStyle = {
    display: 'block',
    fontSize: 11,
    letterSpacing: 1,
    textTransform: 'uppercase',
    color: palette.textMuted,
    marginBottom: 6,
  };

  const stepBtnStyle = {
    fontFamily: fonts,
    fontSize: 18,
    fontWeight: 700,
    width: 40,
    height: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `1px solid ${palette.border}`,
    background: palette.white,
    color: palette.brown,
    cursor: 'pointer',
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(60,43,31,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 20,
        animation: 'fadeIn 0.2s',
      }}
      onClick={onClose}
    >
      <div
        className="hddn-modal-pad"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: palette.cream,
          border: `1px solid ${palette.border}`,
          boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
          width: '100%',
          maxWidth: 520,
          maxHeight: '90vh',
          overflowY: 'auto',
          padding: '32px 28px',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 24,
          }}
        >
          <h3
            style={{
              margin: 0,
              fontFamily: fonts,
              fontSize: 16,
              fontWeight: 700,
              letterSpacing: 2,
              textTransform: 'uppercase',
              color: palette.brown,
            }}
          >
            Book Session
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 22,
              cursor: 'pointer',
              color: palette.textMuted,
              lineHeight: 1,
            }}
          >
            &times;
          </button>
        </div>

        {error && (
          <div
            style={{
              background: '#fdf2f0',
              border: `1px solid ${palette.red}`,
              color: palette.red,
              padding: '8px 12px',
              fontSize: 13,
              marginBottom: 16,
            }}
          >
            {error}
          </div>
        )}

        <form onSubmit={submit}>
          {/* Booking info */}
          <div
            style={{
              background: palette.beige,
              padding: '12px 14px',
              marginBottom: 20,
              fontSize: 13,
              lineHeight: 1.8,
            }}
          >
            <div><strong>Date:</strong> {date}</div>
            <div><strong>Room:</strong> {roomLabel}</div>
            {!isPodcast && room !== 'upstairs-2' && <div><strong>Rate:</strong> ${dynHourlyRate}/hr ({MIN_HOURS} hr minimum)</div>}
            {isPodcast && podcastPkg && <div><strong>Package:</strong> {podcastPkg.name}{podcastPkg.tagline ? ` — ${podcastPkg.tagline}` : ''}</div>}
          </div>

          {/* Name */}
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Your Name *</label>
            <input
              type="text"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              required
              style={inputStyle}
              placeholder="Full name"
            />
          </div>

          {/* Email */}
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Email *</label>
            <input
              type="email"
              value={guestEmail}
              onChange={(e) => setGuestEmail(e.target.value)}
              required
              style={inputStyle}
              placeholder="your@email.com"
            />
          </div>

          {/* Service */}
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Service Type *</label>
            <select
              value={service}
              onChange={(e) => setService(e.target.value)}
              required
              style={inputStyle}
            >
              <option value="">Select service...</option>
              {getServicesForRoom(room, packagesConfig).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
            {service && (
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); setShowIncludedModal(true); }}
                style={{ display: 'inline-block', marginTop: 6, fontSize: 12, color: palette.orange, textDecoration: 'none', fontWeight: 600 }}
              >
                View What's Included &rarr;
              </a>
            )}
          </div>

          {/* Session description */}
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Please provide a brief description of your session *</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              rows={2}
              style={{ ...inputStyle, resize: 'vertical' }}
              placeholder="e.g. Product photography for clothing brand, podcast interview, music video shoot..."
            />
          </div>

          {/* Attendees */}
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>How many people will be attending? * (15 max)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button
                type="button"
                onClick={() => setAttendees(Math.max(1, attendees - 1))}
                disabled={attendees <= 1}
                style={{
                  ...stepBtnStyle,
                  opacity: attendees <= 1 ? 0.4 : 1,
                  cursor: attendees <= 1 ? 'not-allowed' : 'pointer',
                }}
              >
                &minus;
              </button>
              <span
                style={{
                  fontFamily: fonts,
                  fontSize: 20,
                  fontWeight: 700,
                  color: palette.brown,
                  minWidth: 40,
                  textAlign: 'center',
                }}
              >
                {attendees}
              </span>
              <button
                type="button"
                onClick={() => setAttendees(Math.min(15, attendees + 1))}
                disabled={attendees >= 15}
                style={{
                  ...stepBtnStyle,
                  opacity: attendees >= 15 ? 0.4 : 1,
                  cursor: attendees >= 15 ? 'not-allowed' : 'pointer',
                }}
              >
                +
              </button>
              <span style={{ fontSize: 13, color: palette.textMuted }}>
                {attendees === 1 ? 'person' : 'people'}
              </span>
            </div>
          </div>

          {/* Start Time */}
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Start Time *</label>
            <select
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              style={inputStyle}
            >
              {availableStartTimes.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          {/* Hours / Duration */}
          {isPodcast && podcastPkg ? (
            <div style={{ marginBottom: 18 }}>
              <label style={labelStyle}>Session Duration *</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                {podcastPkg.durations.map((d) => {
                  const active = hours === d.hours;
                  const available = d.hours <= maxHours;
                  return (
                    <button
                      key={d.hours}
                      type="button"
                      onClick={() => available && setHours(d.hours)}
                      disabled={!available}
                      style={{
                        fontFamily: fonts,
                        fontSize: 13,
                        padding: '10px 18px',
                        border: `1px solid ${active ? palette.sage : palette.border}`,
                        background: active ? palette.sage : !available ? palette.beige : palette.white,
                        color: active ? palette.white : !available ? palette.textMuted : palette.text,
                        cursor: available ? 'pointer' : 'not-allowed',
                        letterSpacing: 0.5,
                        transition: 'all 0.15s',
                        opacity: available ? 1 : 0.5,
                        fontWeight: active ? 700 : 400,
                      }}
                    >
                      {d.hours} hr{d.hours > 1 ? 's' : ''} — ${d.price}
                    </button>
                  );
                })}
              </div>
              <div style={{ fontSize: 12, color: palette.sage, marginTop: 6 }}>
                {formatTime(startTime)} &ndash; {formatEndTime(startTime, hours)}{(addOnsTotal + sameDayFee) > 0 ? ` | Package: $${podcastPrice} + Add-ons: $${addOnsTotal + sameDayFee} = $${totalPrice}` : ` | Total: $${podcastPrice}`}
              </div>
              {/* Package includes */}
              <div style={{ marginTop: 10, padding: '10px 12px', background: palette.beige, border: `1px solid ${palette.border}`, fontSize: 12, lineHeight: 1.8 }}>
                <div style={{ fontWeight: 700, fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: palette.brown, marginBottom: 4 }}>Includes:</div>
                {podcastPkg.includes.map((item, i) => (
                  <div key={i} style={{ color: palette.text, paddingLeft: 10, position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, color: palette.sage }}>&#8226;</span>
                    {item}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ marginBottom: 18 }}>
              <label style={labelStyle}>Duration (hours) — {MIN_HOURS} hr minimum</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button
                  type="button"
                  onClick={() => setHours(Math.max(MIN_HOURS, hours - 1))}
                  disabled={hours <= MIN_HOURS}
                  style={{
                    ...stepBtnStyle,
                    opacity: hours <= MIN_HOURS ? 0.4 : 1,
                    cursor: hours <= MIN_HOURS ? 'not-allowed' : 'pointer',
                  }}
                >
                  &minus;
                </button>
                <span
                  style={{
                    fontFamily: fonts,
                    fontSize: 20,
                    fontWeight: 700,
                    color: palette.brown,
                    minWidth: 40,
                    textAlign: 'center',
                  }}
                >
                  {hours}
                </span>
                <button
                  type="button"
                  onClick={() => setHours(Math.min(maxHours, hours + 1))}
                  disabled={hours >= maxHours}
                  style={{
                    ...stepBtnStyle,
                    opacity: hours >= maxHours ? 0.4 : 1,
                    cursor: hours >= maxHours ? 'not-allowed' : 'pointer',
                  }}
                >
                  +
                </button>
                <span style={{ fontSize: 13, color: palette.textMuted, marginLeft: 8 }}>
                  {formatTime(startTime)} &ndash; {formatEndTime(startTime, hours)}
                </span>
              </div>
              <div style={{ fontSize: 12, color: palette.sage, marginTop: 6 }}>
                {hours} hr{hours > 1 ? 's' : ''} x ${dynHourlyRate}/hr = ${hours * dynHourlyRate}
                {(addOnsTotal > 0 || sameDayFee > 0) && ` + Add-ons: $${addOnsTotal + sameDayFee}`}
                {(addOnsTotal > 0 || sameDayFee > 0) && ` = Total: $${totalPrice}`}
              </div>
            </div>
          )}

          {/* Same-day booking notice */}
          {isSameDay && service && (
            <div style={{ marginBottom: 12, padding: '10px 12px', background: '#fdf6e8', border: `1px solid ${palette.orange}`, fontSize: 12, color: palette.brown }}>
              <span style={{ fontWeight: 700 }}>Same-Day Booking (+${sameDayFee})</span> — automatically applied. Same-day bookings require admin approval to confirm availability.
            </div>
          )}

          {/* Add-ons */}
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Add-Ons</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
              {(isPodcast ? dynPodcastAddOns : dynStudioAddOns).map((a) => {
                const active = addOns.includes(a.id);
                const locked = a.id === 'same-day-booking' && isSameDay;
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => toggleAddon(a.id)}
                    style={{
                      fontFamily: sans,
                      fontSize: 13,
                      padding: '10px 14px',
                      width: '100%',
                      textAlign: 'left',
                      border: `1px solid ${active ? palette.sage : palette.border}`,
                      background: active ? palette.sage : palette.white,
                      color: active ? palette.white : palette.text,
                      cursor: locked ? 'not-allowed' : 'pointer',
                      letterSpacing: 0.3,
                      transition: 'all 0.15s',
                      opacity: locked ? 0.85 : 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 3,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                      <span style={{ fontWeight: 700 }}>
                        {a.label}{locked ? ' (auto)' : ''}
                      </span>
                      {a.price ? (
                        <span style={{ fontFamily: fonts, fontWeight: 700, whiteSpace: 'nowrap' }}>+${a.price}</span>
                      ) : null}
                    </div>
                    {a.note ? (
                      <span style={{ fontSize: 11, opacity: active ? 0.85 : 0.65, letterSpacing: 0.2 }}>
                        {a.note}
                      </span>
                    ) : null}
                  </button>
                );
              })}
              {/* Show same-day booking add-on for studio services too */}
              {!isPodcast && isSameDay && (
                <div
                  style={{
                    fontFamily: fonts, fontSize: 13, padding: '10px 14px',
                    border: `1px solid ${palette.sage}`, background: palette.sage,
                    color: palette.white, letterSpacing: 0.3, opacity: 0.9,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                  }}
                >
                  <span style={{ fontWeight: 700 }}>Same-Day Booking (auto)</span>
                  <span style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>+${sameDayFee}</span>
                </div>
              )}
            </div>

            {/* Sub-options for add-ons that have them */}
            {(isPodcast ? dynPodcastAddOns : dynStudioAddOns).filter(a => a.options && addOns.includes(a.id)).map(a => (
              <div key={`opt-${a.id}`} style={{ marginTop: 8, padding: '8px 12px', background: palette.beige, border: `1px solid ${palette.border}` }}>
                <label style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: palette.brown }}>
                  {a.label} — Select Option
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                  {a.options.map(opt => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setAddOnOptions(prev => ({ ...prev, [a.id]: opt }))}
                      style={{
                        fontFamily: fonts, fontSize: 11, padding: '4px 10px',
                        border: `1px solid ${addOnOptions[a.id] === opt ? palette.brown : palette.border}`,
                        background: addOnOptions[a.id] === opt ? palette.brown : palette.white,
                        color: addOnOptions[a.id] === opt ? palette.white : palette.text,
                        cursor: 'pointer', letterSpacing: 0.5,
                      }}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Equipment List Link */}
          <div style={{ marginBottom: 18, textAlign: 'center' }}>
            <button
              type="button"
              onClick={() => setShowEquipmentModal(true)}
              style={{
                fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1.5,
                textTransform: 'uppercase', background: 'none', border: 'none',
                color: palette.orange, cursor: 'pointer', textDecoration: 'underline',
              }}
            >
              View Full Equipment List &rarr;
            </button>
          </div>

          {/* Notes */}
          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              style={{ ...inputStyle, resize: 'vertical' }}
              placeholder="Special requests, setup needs, etc."
            />
          </div>

          {/* How did you hear about us? (optional) */}
          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>How did you hear about us?</label>
            <select value={heardFrom} onChange={(e) => setHeardFrom(e.target.value)} style={inputStyle}>
              <option value="">Select one (optional)</option>
              {HEARD_FROM_CHOICES.map((c) => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
          </div>

          {/* Terms agreement checkbox + link */}
          <div style={{ marginBottom: 18 }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                fontSize: 13,
                color: palette.text,
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={termsAgreed}
                onChange={(e) => setTermsAgreed(e.target.checked)}
                style={{ marginTop: 2, accentColor: palette.sage }}
              />
              <span>
                I have read and agree to the{' '}
                <a
                  href="#"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowTermsModal(true); }}
                  style={{ color: palette.orange, textDecoration: 'underline', fontWeight: 600 }}
                >
                  HDDN LAB Studio Policies & Rules
                </a>
              </span>
            </label>
          </div>

          {/* Digital signature */}
          {termsAgreed && (
            <div style={{ marginBottom: 24 }}>
              <label style={labelStyle}>Digital Signature — Type Your Full Name *</label>
              <input
                type="text"
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                placeholder="Your full legal name"
                style={{
                  ...inputStyle,
                  fontStyle: 'italic',
                  fontSize: 16,
                  letterSpacing: 0.5,
                }}
              />
              <div style={{ fontSize: 11, color: palette.textMuted, marginTop: 6 }}>
                By typing your name above, you acknowledge this as your electronic signature agreeing to all terms.
              </div>
            </div>
          )}

          {stripeEnabled && (
            <div style={{ fontSize: 11, color: palette.textMuted, marginBottom: 12, textAlign: 'center' }}>
              You will be redirected to a secure payment page. Your card will be saved on file for any incidentals (overtime, cleaning fees, etc.).
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !termsAgreed || !signature.trim()}
            style={{
              width: '100%',
              padding: '12px',
              fontFamily: fonts,
              fontSize: 14,
              fontWeight: 700,
              letterSpacing: 2,
              textTransform: 'uppercase',
              background: palette.orange,
              color: palette.cream,
              border: 'none',
              cursor: loading || !termsAgreed || !signature.trim() ? 'not-allowed' : 'pointer',
              opacity: loading || !termsAgreed || !signature.trim() ? 0.5 : 1,
            }}
          >
            {loading ? 'Processing...' : stripeEnabled ? `Pay $${totalPrice} & Book` : 'Book Session'}
          </button>
        </form>

        {/* Equipment List modal */}
        {showEquipmentModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
            onClick={() => setShowEquipmentModal(false)}>
            <div style={{ background: palette.cream, maxWidth: 600, width: '100%', maxHeight: '85vh', overflow: 'auto', position: 'relative' }}
              onClick={e => e.stopPropagation()}>
              <div style={{ padding: '20px 24px', borderBottom: `1px solid ${palette.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ fontFamily: sans, fontSize: 15, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, margin: 0 }}>
                  {isPodcast ? 'Podcast Recording Equipment' : 'Studio Equipment'}
                </h3>
                <button onClick={() => setShowEquipmentModal(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: palette.text, fontFamily: fonts }}>&times;</button>
              </div>
              <div style={{ padding: '20px 24px' }}>
                {isPodcast ? (
                  <div style={{ display: 'grid', gap: 16 }}>
                    <div>
                      <h4 style={{ fontFamily: sans, fontSize: 12, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: palette.brown, marginTop: 0, marginBottom: 8 }}>Podcast Equipment</h4>
                      <p style={{ fontFamily: sans, fontSize: 12, color: palette.textMuted, margin: 0 }}>Details coming soon — check back for our full podcast equipment list with specific brands and models.</p>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: 16 }}>
                    <div>
                      <h4 style={{ fontFamily: sans, fontSize: 12, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: palette.brown, marginTop: 0, marginBottom: 8 }}>Studio Equipment</h4>
                      <p style={{ fontFamily: sans, fontSize: 12, color: palette.textMuted, margin: 0 }}>Details coming soon — check back for our full studio equipment list with specific brands and models.</p>
                    </div>
                  </div>
                )}
              </div>
              <div style={{ padding: '14px 24px', borderTop: `1px solid ${palette.border}`, textAlign: 'center' }}>
                <button onClick={() => setShowEquipmentModal(false)} style={{ padding: '10px 40px', fontFamily: fonts, fontSize: 13, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', background: palette.brown, color: palette.cream, border: 'none', cursor: 'pointer' }}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Terms modal popup */}
        {showTermsModal && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(60,43,31,0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1100,
              padding: 20,
            }}
            onClick={() => setShowTermsModal(false)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: palette.cream,
                border: `1px solid ${palette.border}`,
                boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
                width: '100%',
                maxWidth: 680,
                maxHeight: '90vh',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '18px 24px',
                  borderBottom: `1px solid ${palette.border}`,
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    fontFamily: fonts,
                    fontSize: 14,
                    fontWeight: 700,
                    letterSpacing: 2,
                    textTransform: 'uppercase',
                    color: palette.brown,
                  }}
                >
                  Studio Policies &amp; Rules
                </h3>
                <button
                  onClick={() => setShowTermsModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: 22,
                    cursor: 'pointer',
                    color: palette.textMuted,
                    lineHeight: 1,
                  }}
                >
                  &times;
                </button>
              </div>
              <div
                id="hddn-terms-content"
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  padding: '24px 32px',
                  color: palette.text,
                }}
              >
                <TermsContent palette={palette} />
              </div>
              <div style={{ padding: '14px 24px', borderTop: `1px solid ${palette.border}`, display: 'flex', gap: 10 }}>
                <button
                  onClick={() => {
                    const content = document.getElementById('hddn-terms-content');
                    const win = window.open('', '_blank');
                    win.document.write(`<!DOCTYPE html><html><head><title>HDDN LAB - Studio Policies & Rules</title><style>
                      body { font-family: 'Helvetica Neue', Arial, sans-serif; max-width: 700px; margin: 40px auto; padding: 0 24px; color: #3C2B1F; }
                      h1 { font-size: 22px; text-align: center; letter-spacing: 3px; text-transform: uppercase; margin-bottom: 4px; }
                      h2 { font-size: 16px; text-align: center; letter-spacing: 2px; text-transform: uppercase; color: #888; margin-top: 0; border: none; }
                      h2:not([style]) { font-size: 18px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; margin: 28px 0 12px; padding-bottom: 8px; border-bottom: 2px solid #c9a96e; text-align: left; color: #3C2B1F; }
                      h3 { font-size: 15px; font-weight: 700; margin: 18px 0 8px; }
                      p { font-size: 14px; line-height: 1.8; margin: 0 0 10px; }
                      ul { margin: 6px 0 12px 20px; padding: 0; font-size: 14px; line-height: 1.8; }
                      li { margin-bottom: 4px; }
                      @media print { body { margin: 20px; } }
                    </style></head><body>${content.innerHTML}</body></html>`);
                    win.document.close();
                    setTimeout(() => win.print(), 300);
                  }}
                  style={{
                    flex: 1,
                    padding: '10px',
                    fontFamily: fonts,
                    fontSize: 13,
                    fontWeight: 700,
                    letterSpacing: 1,
                    textTransform: 'uppercase',
                    background: palette.sage,
                    color: palette.cream,
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  Save as PDF
                </button>
                <button
                  onClick={() => setShowTermsModal(false)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    fontFamily: fonts,
                    fontSize: 13,
                    fontWeight: 700,
                    letterSpacing: 1,
                    textTransform: 'uppercase',
                    background: palette.brown,
                    color: palette.cream,
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* What's Included popup */}
        {showIncludedModal && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(60,43,31,0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1100,
              padding: 20,
            }}
            onClick={() => setShowIncludedModal(false)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: palette.cream,
                border: `1px solid ${palette.border}`,
                boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
                width: '100%',
                maxWidth: 680,
                maxHeight: '90vh',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '18px 24px',
                  borderBottom: `1px solid ${palette.border}`,
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    fontFamily: fonts,
                    fontSize: 14,
                    fontWeight: 700,
                    letterSpacing: 2,
                    textTransform: 'uppercase',
                    color: palette.brown,
                  }}
                >
                  {room === 'upstairs-2' ? "Podcast Recording \u2014 What's Included" : "Full Studio \u2014 What's Included"}
                </h3>
                <button
                  onClick={() => setShowIncludedModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: 22,
                    cursor: 'pointer',
                    color: palette.textMuted,
                    lineHeight: 1,
                  }}
                >
                  &times;
                </button>
              </div>
              <div
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  padding: '24px 32px',
                  color: palette.text,
                }}
              >
                {room === 'upstairs-2' ? (
                  <div>
                    {Object.entries(dynPodcastPackages).filter(([, pkg]) => pkg.enabled !== false).map(([, pkg]) => {
                      const priceLine = (pkg.durations || []).map((d) => `${d.hours}hr \u2014 $${d.price}`).join(' | ');
                      return (
                        <div key={pkg.name} style={{ background: palette.beige, padding: '18px 20px', border: `1px solid ${palette.border}`, marginBottom: 12 }}>
                          <h4 style={{ fontFamily: sans, fontSize: 13, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: palette.brown, marginTop: 0, marginBottom: 12 }}>
                            {pkg.name}{pkg.tagline ? ` \u2014 ${pkg.tagline}` : ''}
                          </h4>
                          <ul style={{ margin: 0, paddingLeft: 18, listStyle: 'none' }}>
                            {(pkg.includes || []).filter((item) => item.trim()).map((item, idx) => (
                              <li key={idx} style={{ fontSize: 13, lineHeight: 2, color: palette.text, position: 'relative', paddingLeft: 4 }}>
                                <span style={{ position: 'absolute', left: -14, color: palette.sage }}>&#8226;</span>
                                {item}
                              </li>
                            ))}
                            {priceLine && (
                              <li style={{ fontSize: 13, lineHeight: 2, color: palette.text, position: 'relative', paddingLeft: 4 }}>
                                <span style={{ position: 'absolute', left: -14, color: palette.sage }}>&#8226;</span>
                                {priceLine}
                              </li>
                            )}
                          </ul>
                        </div>
                      );
                    })}
                    <div style={{ background: palette.beige, padding: '18px 20px', border: `1px solid ${palette.border}` }}>
                      <h4 style={{ fontFamily: sans, fontSize: 13, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: palette.brown, marginTop: 0, marginBottom: 12 }}>
                        Podcast Add-ons
                      </h4>
                      <ul style={{ margin: 0, paddingLeft: 18, listStyle: 'none' }}>
                        {dynPodcastAddOns.map((a, idx) => (
                          <li key={idx} style={{ fontSize: 13, lineHeight: 2, color: palette.text, position: 'relative', paddingLeft: 4 }}>
                            <span style={{ position: 'absolute', left: -14, color: palette.sage }}>&#8226;</span>
                            {a.price ? `${a.label} (+$${a.price})` : a.label}
                          </li>
                        ))}
                      </ul>
                    </div>
                    {(() => {
                      const gear = Array.isArray(packagesConfig?.podcastGear) ? packagesConfig.podcastGear.filter((s) => s && s.trim()) : [];
                      const turnaround = typeof packagesConfig?.podcastTurnaround === 'string' ? packagesConfig.podcastTurnaround.trim() : '';
                      if (gear.length === 0 && !turnaround) return null;
                      return (
                        <div style={{ background: palette.beige, padding: '18px 20px', border: `1px solid ${palette.border}`, marginTop: 12 }}>
                          <h4 style={{ fontFamily: sans, fontSize: 13, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: palette.brown, marginTop: 0, marginBottom: 12 }}>
                            Podcast Recording Gear
                          </h4>
                          <ul style={{ margin: 0, paddingLeft: 18, listStyle: 'none' }}>
                            {[...gear, ...(turnaround ? [turnaround] : [])].map((item, idx) => (
                              <li key={idx} style={{ fontSize: 13, lineHeight: 2, color: palette.text, position: 'relative', paddingLeft: 4 }}>
                                <span style={{ position: 'absolute', left: -14, color: palette.sage }}>&#8226;</span>
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
                    {[
                      { heading: 'Studio Space', items: ['Open floor plan with hardwood floors', 'Natural lighting + blackout options', 'Furniture & props for styling', 'WiFi & Bluetooth speaker', 'Climate controlled space', 'Spring clamps', 'V-flat', 'Clothing rack'] },
                      ...(packagesConfig?.showUpstairsEquipment === true ? [
                        { heading: 'Lighting', items: ['Amaran 200x (bi-color, warm/cool)', 'Aputure 120D (daylight)'] },
                        { heading: 'Modifiers', items: ['36" Lantern dome', '30x120 Lightbox', '36" Aputure dome', 'Snoot'] },
                        { heading: 'Grip & Support', items: ['4 x 40" C-stands', '6 x 25lb sandbags', 'Extension cords'] },
                      ] : []),
                      { heading: 'Backdrops', items: ['White backdrop (no stepping — $25 stepping fee)', 'Black backdrop (no stepping — $25 stepping fee)'] },
                      { heading: 'Amenities', items: ['MUA station', 'Props on site'] },
                      { heading: 'Studio Add-ons', items: dynStudioAddOns.map((a) => a.price ? `${a.label} (+$${a.price})` : a.label) },
                    ].map((section) => (
                      <div key={section.heading} style={{ background: palette.beige, padding: '18px 20px', border: `1px solid ${palette.border}` }}>
                        <h4 style={{ fontFamily: sans, fontSize: 13, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: palette.brown, marginTop: 0, marginBottom: 12 }}>
                          {section.heading}
                        </h4>
                        <ul style={{ margin: 0, paddingLeft: 18, listStyle: 'none' }}>
                          {section.items.filter((item) => item.trim()).map((item, idx) => (
                            <li key={idx} style={{ fontSize: 13, lineHeight: 2, color: palette.text, position: 'relative', paddingLeft: 4 }}>
                              <span style={{ position: 'absolute', left: -14, color: palette.sage }}>&#8226;</span>
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ padding: '14px 24px', borderTop: `1px solid ${palette.border}`, textAlign: 'center' }}>
                <button
                  onClick={() => setShowIncludedModal(false)}
                  style={{
                    padding: '10px 40px',
                    fontFamily: fonts,
                    fontSize: 13,
                    fontWeight: 700,
                    letterSpacing: 1,
                    textTransform: 'uppercase',
                    background: palette.brown,
                    color: palette.cream,
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Success confirmation
// ---------------------------------------------------------------------------

function SuccessModal({ booking, onClose }) {
  const roomLabel = ROOMS.find((r) => r.id === booking.room)?.label || booking.room;
  const serviceLabel = SERVICES.find((s) => s.id === booking.service)?.label || booking.service;
  const timeLabel = booking.start_time
    ? `${formatTime(booking.start_time)} – ${formatEndTime(booking.start_time, booking.hours)} (${booking.hours} hrs)`
    : booking.startTime
    ? `${formatTime(booking.startTime)} – ${formatEndTime(booking.startTime, booking.hours)} (${booking.hours} hrs)`
    : `${booking.hours || MIN_HOURS} hrs`;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(60,43,31,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: palette.cream,
          border: `1px solid ${palette.border}`,
          boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
          width: '100%',
          maxWidth: 440,
          padding: '36px 28px',
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 40, marginBottom: 12, color: palette.orange }}>&#9993;</div>
        <h3
          style={{
            fontFamily: fonts,
            fontSize: 16,
            fontWeight: 700,
            letterSpacing: 2,
            textTransform: 'uppercase',
            color: palette.orange,
            margin: '0 0 8px',
          }}
        >
          Request Submitted
        </h3>

        <p style={{ fontSize: 13, color: palette.textMuted, marginBottom: 20, lineHeight: 1.6 }}>
          Your booking request has been submitted and is pending approval. You'll receive a confirmation email once it's been reviewed.
        </p>

        <div
          style={{
            background: palette.beige,
            padding: '16px',
            textAlign: 'left',
            fontSize: 13,
            lineHeight: 2,
            marginBottom: 24,
          }}
        >
          <div><strong>Date:</strong> {booking.date}</div>
          <div><strong>Room:</strong> {roomLabel}</div>
          <div><strong>Service:</strong> {serviceLabel}</div>
          <div><strong>Time:</strong> {timeLabel}</div>
          <div><strong>Total:</strong> ${booking.podcastPackagePrice || (booking.hours || MIN_HOURS) * 100}</div>
          {booking.addOns && booking.addOns.length > 0 && (
            <div>
              <strong>Add-Ons:</strong>{' '}
              {booking.addOns
                .map((id) => {
                  const std = ADD_ONS.find((a) => a.id === id);
                  const pod = PODCAST_ADD_ONS.find((a) => a.id === id);
                  return std?.label || pod?.label || id;
                })
                .join(', ')}
            </div>
          )}
          {booking.notes && <div><strong>Notes:</strong> {booking.notes}</div>}
          <div style={{ marginTop: 8, fontSize: 11, color: palette.textMuted }}>
            Reference: {booking.booking_ref || booking.id?.slice(0, 8).toUpperCase()}
          </div>
        </div>

        <button
          onClick={onClose}
          style={{
            padding: '10px 32px',
            fontFamily: fonts,
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: 2,
            textTransform: 'uppercase',
            background: palette.brown,
            color: palette.cream,
            border: 'none',
            cursor: 'pointer',
          }}
        >
          Done
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Public Blog Listing Page
// ---------------------------------------------------------------------------

function BlogListingPage({ onViewPost, onBack }) {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch(`${API_BASE}/api/blog/posts`).then(r => r.json()),
      fetch(`${API_BASE}/api/blog/categories`).then(r => r.json()),
    ]).then(([p, c]) => {
      setPosts(p || []);
      setCategories(c || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = activeCategory === 'all' ? posts : posts.filter(p => p.category_slug === activeCategory);

  return (
    <div style={baseStyles.app}>
      <style>{`
        * { box-sizing: border-box; }
        body { margin: 0; }
        .blog-masonry { column-count: 1; column-gap: 20px; }
        @media (min-width: 640px) { .blog-masonry { column-count: 2; } }
        @media (min-width: 1024px) { .blog-masonry { column-count: 3; } }
        .blog-card { break-inside: avoid; margin: 0 0 20px; display: block; cursor: pointer; position: relative; border-radius: 12px; overflow: hidden; }
        /* Natural aspect ratio so landscape AND portrait covers show in full;
           min-height only floors ultra-wide covers so the title stays readable. */
        .blog-card-img { width: 100%; height: auto; min-height: 200px; max-height: 620px; object-fit: cover; display: block; transition: transform 0.4s ease; }
        .blog-card:hover .blog-card-img { transform: scale(1.05); }
        .blog-card-scrim { position: absolute; inset: 0; background: linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.15) 42%, rgba(0,0,0,0) 65%); pointer-events: none; }
        .blog-filter { transition: background 0.15s, color 0.15s; }
        .blog-filter:hover { color: ${palette.text}; }
      `}</style>

      <header style={{ background: palette.brown, padding: '16px 0', borderBottom: `3px solid ${palette.orange}` }}>
        <div className="hddn-container" style={{ ...baseStyles.container, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button onClick={onBack} style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '6px 14px', background: 'none', border: `1px solid ${palette.cream}40`, color: palette.cream, cursor: 'pointer' }}>
            Back to Studio
          </button>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <span style={{ fontFamily: fonts, fontWeight: 700, fontSize: 18, color: palette.cream, letterSpacing: 4, textTransform: 'uppercase' }}>HDDN LAB</span>
            <span style={{ color: palette.orange, fontSize: 12, letterSpacing: 2 }}>JOURNAL</span>
          </div>
          <div style={{ width: 100 }} />
        </div>
      </header>

      <main className="hddn-container" style={{ ...baseStyles.container, maxWidth: 1280, paddingTop: 48, paddingBottom: 72 }}>
        <div style={{ textAlign: 'center', marginBottom: 44 }}>
          <span style={{
            display: 'inline-block', fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 3,
            textTransform: 'uppercase', color: palette.orange, border: `1px solid ${withAlpha(palette.orange, 0.4)}`,
            background: withAlpha(palette.orange, 0.08), borderRadius: 999, padding: '4px 14px',
          }}>JOURNAL</span>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500, fontSize: 'clamp(2rem, 1.4rem + 2.4vw, 2.75rem)', color: palette.brown, margin: '14px 0 10px', lineHeight: 1.15 }}>
            Stories & Studio Life
          </h1>
          <p style={{ fontFamily: fonts, fontSize: 14, color: palette.textMuted, maxWidth: 520, margin: '0 auto' }}>
            Behind the scenes, creative tips, and stories from HDDN LAB sessions.
          </p>
        </div>

        {/* Category filter */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', marginBottom: 40 }}>
          <button
            onClick={() => setActiveCategory('all')}
            className="blog-filter"
            style={{
              fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
              padding: '7px 18px', borderRadius: 999, border: 'none', cursor: 'pointer',
              background: activeCategory === 'all' ? palette.orange : palette.beige,
              color: activeCategory === 'all' ? palette.cream : palette.textMuted,
            }}
          >
            All Posts
          </button>
          {categories.map(c => (
            <button
              key={c.id}
              onClick={() => setActiveCategory(c.slug)}
              className="blog-filter"
              style={{
                fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
                padding: '7px 18px', borderRadius: 999, border: 'none', cursor: 'pointer',
                background: activeCategory === c.slug ? palette.orange : palette.beige,
                color: activeCategory === c.slug ? palette.cream : palette.textMuted,
              }}
            >
              {c.name}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <span style={{ fontFamily: fonts, color: palette.textMuted, letterSpacing: 2 }}>LOADING...</span>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60 }}>
            <p style={{ fontFamily: fonts, fontSize: 14, color: palette.textMuted }}>No posts yet. Check back soon!</p>
          </div>
        ) : (
          <div className="blog-masonry">
            {filtered.map(post => {
              const cover = post.cover_url || post.featured_image_url;
              return (
                <div key={post.id} className="blog-card" onClick={() => onViewPost(post.slug)}>
                  {cover ? (
                    <img className="blog-card-img" src={cover} alt={post.title} />
                  ) : (
                    <div className="blog-card-img" style={{ height: 320, background: `linear-gradient(135deg, ${palette.brownLight}, ${palette.brown})` }} />
                  )}
                  <div className="blog-card-scrim" />
                  {post.category_name && (
                    <span style={{
                      position: 'absolute', top: 14, left: 14,
                      fontFamily: fonts, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
                      background: withAlpha('#000000', 0.35), color: palette.cream, padding: '4px 11px', borderRadius: 999,
                      backdropFilter: 'blur(2px)',
                    }}>
                      {post.category_name}
                    </span>
                  )}
                  <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '20px 20px 18px' }}>
                    <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(20px, 2.4vw, 26px)', fontWeight: 600, color: '#ffffff', margin: 0, lineHeight: 1.25, textShadow: '0 1px 8px rgba(0,0,0,0.4)' }}>
                      {post.title}
                    </h3>
                    {post.published_at && (
                      <div style={{ fontFamily: fonts, fontSize: 11, color: 'rgba(255,255,255,0.82)', letterSpacing: 0.5, marginTop: 8 }}>
                        {new Date(post.published_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Public Blog Post Detail Page
// ---------------------------------------------------------------------------

function BlogPostPage({ slug, onBack, onBackToList }) {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/api/blog/posts/slug/${slug}`)
      .then(r => { if (!r.ok) throw new Error('Not found'); return r.json(); })
      .then(p => setPost(p))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  // Set meta tags for SEO
  useEffect(() => {
    if (!post) return;
    document.title = post.seo_title || post.title;
    // Add JSON-LD structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.seo_description || post.excerpt,
      image: post.featured_image_url || undefined,
      datePublished: post.published_at,
      dateModified: post.updated_at,
      author: { '@type': 'Organization', name: 'HDDN LAB' },
    });
    document.head.appendChild(script);
    // OG meta tags
    const setMeta = (prop, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[property="${prop}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute('property', prop); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('og:title', post.seo_title || post.title);
    setMeta('og:description', post.seo_description || post.excerpt);
    setMeta('og:image', post.featured_image_url);
    setMeta('og:type', 'article');
    return () => {
      document.head.removeChild(script);
      document.title = 'HDDN LAB';
    };
  }, [post]);

  if (loading) {
    return (
      <div style={baseStyles.app}>
        <div className="hddn-container" style={{ ...baseStyles.container, paddingTop: 60, textAlign: 'center' }}>
          <span style={{ fontFamily: fonts, color: palette.textMuted, letterSpacing: 2 }}>LOADING...</span>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div style={baseStyles.app}>
        <div className="hddn-container" style={{ ...baseStyles.container, paddingTop: 60, textAlign: 'center' }}>
          <h2 style={{ fontFamily: fonts, color: palette.brown }}>Post not found</h2>
          <button onClick={onBackToList} style={{ fontFamily: fonts, fontSize: 13, color: palette.orange, background: 'none', border: 'none', cursor: 'pointer', letterSpacing: 1 }}>
            &larr; Back to Blog
          </button>
        </div>
      </div>
    );
  }

  const tags = post.tags || [];

  return (
    <div style={baseStyles.app}>
      <style>{`
        * { box-sizing: border-box; }
        body { margin: 0; }
        /* Breathing centered column — applied per-section with an inline max-width */
        .bp-col { width: 100%; margin: 0 auto; padding: 0 16px; }
        @media (min-width: 768px) { .bp-col { padding: 0 24px; } }
        .bp-header { padding-top: 48px; }
        @media (min-width: 768px) { .bp-header { padding-top: 64px; } }
        .bp-title { font-family: 'Playfair Display', serif; font-weight: 500; font-size: clamp(2rem, 1.2rem + 3.4vw, 3rem); line-height: 1.15; text-align: center; text-wrap: balance; color: ${palette.brown}; margin: 0; }
        .bp-meta { display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; gap: 12px; margin-top: 24px; }
        .bp-cat { justify-self: start; font-family: ${fonts}; font-size: 12px; letter-spacing: 1px; text-transform: uppercase; border-radius: 999px; border: 1px solid ${withAlpha(palette.text, 0.25)}; color: ${withAlpha(palette.text, 0.6)}; background: transparent; padding: 4px 13px; transition: color 0.15s, border-color 0.15s; }
        .bp-cat:hover { color: ${palette.text}; border-color: ${withAlpha(palette.text, 0.5)}; }
        .bp-date { text-align: center; font-family: ${fonts}; font-size: 14px; color: ${palette.textMuted}; letter-spacing: 0.5px; }
        .bp-share { justify-self: end; display: inline-flex; align-items: center; gap: 6px; font-family: ${fonts}; font-size: 14px; color: ${palette.textMuted}; background: none; border: none; cursor: pointer; padding: 4px 8px; border-radius: 999px; transition: color 0.15s; }
        .bp-share:hover { color: ${palette.text}; }
        /* Article body — narrow reading measure, generous rhythm */
        .blog-content p { font-family: ${fonts}; font-size: 1.06rem; line-height: 1.75; font-weight: 400; color: ${withAlpha(palette.text, 0.75)}; margin: 0 0 24px; }
        .blog-content h2 { font-family: 'Playfair Display', serif; font-weight: 500; font-size: clamp(1.75rem, 1.5rem + 1vw, 2rem); line-height: 1.25; color: ${palette.brown}; margin: 56px 0 20px; }
        .blog-content h3 { font-family: 'Playfair Display', serif; font-weight: 500; font-size: clamp(1.35rem, 1.2rem + 0.6vw, 1.5rem); line-height: 1.3; color: ${palette.brown}; margin: 40px 0 16px; }
        .blog-content a { color: ${palette.text}; text-decoration: underline; text-decoration-color: ${withAlpha(palette.text, 0.3)}; text-underline-offset: 3px; transition: text-decoration-color 0.15s; }
        .blog-content a:hover { text-decoration-color: ${withAlpha(palette.text, 0.7)}; }
        .blog-content strong { font-weight: 700; color: ${palette.text}; }
        .blog-content img { display: block; max-width: 100%; margin: 40px auto; border-radius: 12px; background: ${palette.beige}; }
        .blog-content blockquote { border-left: 3px solid ${withAlpha(palette.text, 0.2)}; margin: 32px 0; padding: 4px 0 4px 20px; color: ${palette.textMuted}; font-family: 'Playfair Display', serif; font-style: normal; font-size: 1.15rem; line-height: 1.6; }
        .blog-content ul, .blog-content ol { padding-left: 24px; margin: 0 0 24px; }
        .blog-content li { font-family: ${fonts}; font-size: 1.06rem; line-height: 1.75; color: ${withAlpha(palette.text, 0.75)}; margin-bottom: 8px; }
        .blog-content figure { margin: 40px 0; }
        .blog-content figcaption { text-align: center; font-family: ${fonts}; font-size: 0.85rem; color: ${palette.textMuted}; margin-top: 10px; }
        .blog-content iframe { width: 100%; aspect-ratio: 16 / 9; height: auto; border: 0; border-radius: 12px; }
        .blog-content hr { border: none; border-top: 1px solid ${palette.border}; margin: 40px 0; }
      `}</style>

      <header style={{ background: palette.brown, padding: '16px 0', borderBottom: `3px solid ${palette.orange}` }}>
        <div className="hddn-container" style={{ ...baseStyles.container, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button onClick={onBack} style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '6px 14px', background: 'none', border: `1px solid ${palette.cream}40`, color: palette.cream, cursor: 'pointer' }}>
            Back to Studio
          </button>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <span style={{ fontFamily: fonts, fontWeight: 700, fontSize: 18, color: palette.cream, letterSpacing: 4, textTransform: 'uppercase' }}>HDDN LAB</span>
            <span style={{ color: palette.orange, fontSize: 12, letterSpacing: 2 }}>JOURNAL</span>
          </div>
          <div style={{ width: 100 }} />
        </div>
      </header>

      <BlogPostBody post={post} onBackToList={onBackToList} />
    </div>
  );
}

function BlogPostBody({ post, onBackToList }) {
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [copied, setCopied] = useState(false);
  const media = Array.isArray(post.media) ? post.media : [];
  const cover = post.featured_image_url || (media[0] && !((media[0].mime_type || '').startsWith('video/')) ? media[0].url : null);
  const tags = post.tags || [];

  const handleCopyLink = () => {
    try {
      navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* clipboard unavailable */ }
  };

  useEffect(() => {
    if (lightboxIndex === null) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape') setLightboxIndex(null);
      if (e.key === 'ArrowLeft') setLightboxIndex((i) => (i > 0 ? i - 1 : media.length - 1));
      if (e.key === 'ArrowRight') setLightboxIndex((i) => (i < media.length - 1 ? i + 1 : 0));
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxIndex, media.length]);

  const renderMediaTile = (m, idx) => {
    const isVideo = (m.mime_type || '').startsWith('video/');
    return (
      <button
        key={m.id}
        onClick={() => setLightboxIndex(idx)}
        style={{
          position: 'relative',
          padding: 0,
          margin: 0,
          border: 'none',
          background: palette.beige,
          cursor: 'pointer',
          overflow: 'hidden',
          width: '100%',
          aspectRatio: '4 / 5',
          borderRadius: 8,
        }}
      >
        {isVideo ? (
          <>
            <video src={m.url} muted playsInline preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
              <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: palette.white, fontSize: 22 }}>&#9654;</div>
            </div>
          </>
        ) : (
          <img src={m.url} alt={m.caption || ''} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} loading="lazy" />
        )}
        {m.caption && (
          <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '10px 12px', background: 'linear-gradient(to top, rgba(0,0,0,0.65), transparent)', color: palette.white, fontFamily: fonts, fontSize: 11, letterSpacing: 0.5, textAlign: 'left' }}>
            {m.caption}
          </div>
        )}
      </button>
    );
  };

  return (
    <main style={{ paddingBottom: 0 }}>
      {/* Header block — ~768px measure */}
      <div className="bp-col bp-header" style={{ maxWidth: 768 }}>
        <h1 className="bp-title">{post.title}</h1>
        <div className="bp-meta">
          {post.category_name
            ? <span className="bp-cat">{post.category_name}</span>
            : <span />}
          <span className="bp-date">
            {post.published_at ? new Date(post.published_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}
          </span>
          <button className="bp-share" onClick={handleCopyLink} title="Copy link to this post">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
            </svg>
            {copied ? 'Copied!' : 'Copy link'}
          </button>
        </div>
      </div>

      {/* Featured image — ~896px, intentionally wider than the text */}
      {cover && (
        <div className="bp-col" style={{ maxWidth: 896, marginTop: 32 }}>
          <img src={cover} alt={post.title} style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 8, background: palette.beige }} />
        </div>
      )}

      {/* Lead / standfirst — ~672px */}
      {post.excerpt && post.excerpt.trim() && (
        <div className="bp-col" style={{ maxWidth: 672, marginTop: 40 }}>
          <p style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(1.125rem, 1rem + 0.6vw, 1.25rem)', lineHeight: 1.7, color: palette.brownLight, textAlign: 'center', margin: 0 }}>
            {post.excerpt}
          </p>
        </div>
      )}

      {/* Body — ~680px narrow reading measure */}
      {post.content && post.content.trim() && (
        <div className="bp-col" style={{ maxWidth: 680, marginTop: 40, paddingBottom: 64 }}>
          <div className="blog-content" dangerouslySetInnerHTML={{ __html: post.content }} />
        </div>
      )}

      {/* Gallery — the main event for photo posts */}
      {media.length > (cover && cover === media[0]?.url ? 1 : 0) && (
        <div className="bp-col" style={{ maxWidth: 896, marginTop: 8, marginBottom: 8 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 6 }}>
            {media
              // If the cover came from media[0], skip it in the gallery to avoid duplication
              .filter((m, i) => !(i === 0 && cover && cover === m.url))
              .map((m) => renderMediaTile(m, media.findIndex((x) => x.id === m.id)))}
          </div>
        </div>
      )}

      {/* Tags */}
      {tags.length > 0 && (
        <div className="bp-col" style={{ maxWidth: 680, marginTop: 48, paddingTop: 32, borderTop: `1px solid ${palette.border}` }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {tags.map((t, i) => (
              <span key={i} style={{
                fontFamily: fonts, fontSize: 11, letterSpacing: 0.5, padding: '5px 13px',
                borderRadius: 999, border: `1px solid ${palette.border}`, color: palette.textMuted,
              }}>{t}</span>
            ))}
          </div>
        </div>
      )}

      {/* Back link */}
      <div className="bp-col" style={{ maxWidth: 680, marginTop: tags.length > 0 ? 24 : 48, paddingTop: 24, paddingBottom: 56, borderTop: `1px solid ${palette.border}` }}>
        <button
          onClick={onBackToList}
          style={{ fontFamily: fonts, fontSize: 13, letterSpacing: 0.5, color: palette.textMuted, background: 'none', border: 'none', cursor: 'pointer', padding: 0, transition: 'color 0.15s' }}
          onMouseEnter={(e) => (e.currentTarget.style.color = palette.text)}
          onMouseLeave={(e) => (e.currentTarget.style.color = palette.textMuted)}
        >
          &larr; Back to all posts
        </button>
      </div>

      {lightboxIndex !== null && media[lightboxIndex] && (
        <div
          onClick={() => setLightboxIndex(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 20 }}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightboxIndex(null); }}
            style={{ position: 'absolute', top: 18, right: 22, background: 'none', border: 'none', color: palette.white, fontSize: 30, cursor: 'pointer' }}
          >&times;</button>
          <button
            onClick={(e) => { e.stopPropagation(); setLightboxIndex((i) => (i > 0 ? i - 1 : media.length - 1)); }}
            style={{ position: 'absolute', left: 18, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', color: palette.white, fontSize: 26, width: 46, height: 46, cursor: 'pointer' }}
          >&#8592;</button>
          <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: '92vw', maxHeight: '88vh', textAlign: 'center' }}>
            {(media[lightboxIndex].mime_type || '').startsWith('video/') ? (
              <video src={media[lightboxIndex].url} controls autoPlay playsInline style={{ maxWidth: '92vw', maxHeight: '80vh', display: 'block' }} />
            ) : (
              <img src={media[lightboxIndex].url} alt={media[lightboxIndex].caption || ''} style={{ maxWidth: '92vw', maxHeight: '80vh', objectFit: 'contain', display: 'block' }} />
            )}
            {media[lightboxIndex].caption && (
              <div style={{ color: palette.white, fontFamily: fonts, fontSize: 13, marginTop: 10 }}>{media[lightboxIndex].caption}</div>
            )}
            <div style={{ color: 'rgba(255,255,255,0.5)', fontFamily: fonts, fontSize: 11, marginTop: 6 }}>
              {lightboxIndex + 1} / {media.length}
            </div>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); setLightboxIndex((i) => (i < media.length - 1 ? i + 1 : 0)); }}
            style={{ position: 'absolute', right: 18, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', color: palette.white, fontSize: 26, width: 46, height: 46, cursor: 'pointer' }}
          >&#8594;</button>
        </div>
      )}
    </main>
  );
}

// ---------------------------------------------------------------------------
// Public booking management (cancel via booking ref + email)
// ---------------------------------------------------------------------------

function ManageBookingModal({ onClose }) {
  const [bookingRef, setBookingRef] = useState('');
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleCancel = async () => {
    if (!bookingRef.trim() || !email.trim()) {
      setError('Please enter your booking reference and email');
      return;
    }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/api/bookings/cancel-public`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bookingRef: bookingRef.trim(), email: email.trim(), reason }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to process cancellation');
      setSuccess(data.autoApproved
        ? 'Your booking has been cancelled. Check your email for confirmation.'
        : 'Your cancellation request has been submitted for review. We\'ll email you once it\'s processed.');
    } catch (err) {
      setError(err.message);
    } finally { setLoading(false); }
  };

  const inputStyle = {
    width: '100%', padding: '10px 12px', fontFamily: sans, fontSize: 14,
    border: `1px solid ${palette.border}`, background: palette.white,
    color: palette.text, boxSizing: 'border-box', outline: 'none',
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: palette.white, maxWidth: 440, width: '100%', maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${palette.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontFamily: sans, fontSize: 16, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, margin: 0 }}>Manage Booking</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: palette.textMuted }}>&times;</button>
        </div>
        <div style={{ padding: 24 }}>
          {success ? (
            <div style={{ background: palette.sage, color: palette.white, padding: 16, marginBottom: 16, fontSize: 14, fontFamily: fonts }}>
              {success}
            </div>
          ) : (
            <>
              <p style={{ fontFamily: sans, fontSize: 13, color: palette.textMuted, margin: '0 0 20px' }}>
                Enter your booking reference (e.g., HDDN-A3X7) and email to cancel your booking. Cancellations made 7+ days in advance are automatically approved.
              </p>
              {error && <div style={{ background: palette.orangeLight, color: palette.white, padding: '10px 14px', marginBottom: 16, fontSize: 13, fontFamily: fonts }}>{error}</div>}
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: palette.textMuted, marginBottom: 4, fontFamily: fonts }}>Booking Reference</label>
                <input value={bookingRef} onChange={(e) => setBookingRef(e.target.value.toUpperCase())} placeholder="HDDN-XXXX" style={inputStyle} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: palette.textMuted, marginBottom: 4, fontFamily: fonts }}>Email</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" style={inputStyle} />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: palette.textMuted, marginBottom: 4, fontFamily: fonts }}>Reason (optional)</label>
                <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Why are you cancelling?" />
              </div>
              <button
                onClick={handleCancel}
                disabled={loading}
                style={{
                  width: '100%', padding: '12px', fontFamily: fonts, fontSize: 13, fontWeight: 700,
                  letterSpacing: 1, textTransform: 'uppercase', border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                  background: palette.brown, color: palette.cream, opacity: loading ? 0.6 : 1,
                }}
              >
                {loading ? 'Processing...' : 'Cancel Booking'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Booking history panel
// ---------------------------------------------------------------------------

function BookingHistory({ bookings: list, onClose, onRefresh }) {
  const [modifyingBooking, setModifyingBooking] = useState(null);
  const [cancellingBooking, setCancellingBooking] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [modifyDate, setModifyDate] = useState('');
  const [modifyStartTime, setModifyStartTime] = useState('');
  const [modifyHours, setModifyHours] = useState('');
  const [modifyReason, setModifyReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');

  const submitCancelRequest = async () => {
    setActionLoading(true); setActionError('');
    try {
      const result = await api(`/api/bookings/${cancellingBooking.id}/change-request`, {
        method: 'POST',
        body: JSON.stringify({ changeType: 'cancel', reason: cancelReason }),
      });
      const msg = result.autoApproved
        ? 'Booking cancelled successfully! You\'ll receive a confirmation email shortly.'
        : 'Cancellation request submitted. You will be notified once reviewed.';
      setActionSuccess(msg);
      setCancellingBooking(null); setCancelReason('');
      if (onRefresh) onRefresh();
      setTimeout(() => setActionSuccess(''), 5000);
    } catch (err) { setActionError(err.error || 'Failed to submit request'); }
    finally { setActionLoading(false); }
  };

  const submitModifyRequest = async () => {
    setActionLoading(true); setActionError('');
    const changes = {};
    if (modifyDate) changes.date = modifyDate;
    if (modifyStartTime) changes.startTime = modifyStartTime;
    if (modifyHours) changes.hours = parseInt(modifyHours);
    if (Object.keys(changes).length === 0) { setActionError('Please specify at least one change'); setActionLoading(false); return; }
    try {
      await api(`/api/bookings/${modifyingBooking.id}/change-request`, {
        method: 'POST',
        body: JSON.stringify({ changeType: 'modify', requestedChanges: changes, reason: modifyReason }),
      });
      setActionSuccess('Modification request submitted. You will be notified once reviewed.');
      setModifyingBooking(null); setModifyDate(''); setModifyStartTime(''); setModifyHours(''); setModifyReason('');
      if (onRefresh) onRefresh();
      setTimeout(() => setActionSuccess(''), 4000);
    } catch (err) { setActionError(err.error || 'Failed to submit request'); }
    finally { setActionLoading(false); }
  };

  const inputStyle = {
    width: '100%', padding: '8px 10px', fontFamily: sans, fontSize: 13,
    border: `1px solid ${palette.border}`, background: palette.white,
    color: palette.text, boxSizing: 'border-box', outline: 'none',
  };
  const labelStyle = {
    display: 'block', fontSize: 11, letterSpacing: 1,
    textTransform: 'uppercase', color: palette.textMuted, marginBottom: 4,
  };
  const smallBtnStyle = {
    fontFamily: fonts, fontSize: 10, fontWeight: 700, letterSpacing: 1,
    textTransform: 'uppercase', padding: '4px 10px', border: 'none',
    cursor: 'pointer',
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(60,43,31,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 20,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: palette.cream, border: `1px solid ${palette.border}`,
          boxShadow: '0 8px 30px rgba(0,0,0,0.15)', width: '100%',
          maxWidth: 560, maxHeight: '80vh', overflowY: 'auto', padding: '32px 28px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3
            style={{ margin: 0, fontFamily: fonts, fontSize: 16, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown }}
          >
            Your Bookings
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: palette.textMuted, lineHeight: 1 }}>
            &times;
          </button>
        </div>

        {actionSuccess && (
          <div style={{ background: '#f0fdf2', border: `1px solid ${palette.sage}`, color: palette.sage, padding: '8px 12px', fontSize: 13, marginBottom: 16 }}>
            {actionSuccess}
          </div>
        )}
        {actionError && (
          <div style={{ background: '#fdf2f0', border: `1px solid ${palette.red}`, color: palette.red, padding: '8px 12px', fontSize: 13, marginBottom: 16 }}>
            {actionError}
          </div>
        )}

        {list.length === 0 ? (
          <p style={{ color: palette.textMuted, fontSize: 14 }}>No bookings yet.</p>
        ) : (
          list.map((b) => {
            const roomLabel = ROOMS.find((r) => r.id === b.room)?.label || b.room;
            const serviceLabel = SERVICES.find((s) => s.id === b.service)?.label || b.service;
            const isActive = b.status === 'confirmed' || b.status === 'pending_approval' || b.status === 'pending';
            const isFuture = b.date >= todayString();
            const canModify = isActive && isFuture;
            const statusColor = b.status === 'confirmed' ? palette.sage : b.status === 'cancelled' || b.status === 'denied' ? palette.red : palette.orangeLight;

            return (
              <div
                key={b.id}
                style={{
                  background: palette.white, border: `1px solid ${palette.border}`,
                  padding: '14px 16px', marginBottom: 10, fontSize: 13, lineHeight: 1.8,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <strong>{b.date}</strong>
                  <span style={{ fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: statusColor, fontWeight: 700 }}>
                    {b.status === 'pending_approval' ? 'AWAITING APPROVAL' : b.status}
                  </span>
                </div>
                <div>{roomLabel} &mdash; {serviceLabel}</div>
                <div style={{ fontSize: 12, color: palette.textMuted }}>
                  {b.start_time
                    ? `${formatTime(b.start_time)} – ${formatEndTime(b.start_time, b.hours)} (${b.hours} hrs)`
                    : `${b.hours || MIN_HOURS} hrs`}
                </div>
                {b.addOns && b.addOns.length > 0 && (
                  <div style={{ color: palette.textMuted, fontSize: 12 }}>
                    Add-ons: {b.addOns.map((id) => ADD_ONS.find((a) => a.id === id)?.label || id).join(', ')}
                  </div>
                )}
                <div style={{ fontSize: 11, color: palette.textMuted, marginTop: 4 }}>
                  Ref: {b.booking_ref || b.id?.slice(0, 8).toUpperCase()}
                </div>

                {canModify && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <button
                      onClick={() => { setModifyingBooking(b); setModifyDate(b.date); setModifyStartTime(b.start_time || ''); setModifyHours(String(b.hours || MIN_HOURS)); setModifyReason(''); setActionError(''); }}
                      style={{ ...smallBtnStyle, background: palette.brown, color: palette.cream }}
                    >
                      Request Change
                    </button>
                    <button
                      onClick={() => { setCancellingBooking(b); setCancelReason(''); setActionError(''); }}
                      style={{ ...smallBtnStyle, background: palette.red, color: palette.white }}
                    >
                      Request Cancel
                    </button>
                  </div>
                )}

                {/* Cancel form */}
                {cancellingBooking && cancellingBooking.id === b.id && (
                  <div style={{ marginTop: 12, padding: '12px', background: palette.beige, border: `1px solid ${palette.border}` }}>
                    <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: palette.brown, marginBottom: 8 }}>
                      Cancel Request
                    </div>
                    <div style={{ marginBottom: 10 }}>
                      <label style={labelStyle}>Reason (optional)</label>
                      <textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Why do you need to cancel?" />
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={submitCancelRequest} disabled={actionLoading} style={{ ...smallBtnStyle, background: palette.red, color: palette.white, opacity: actionLoading ? 0.6 : 1 }}>
                        {actionLoading ? '...' : 'Submit Request'}
                      </button>
                      <button onClick={() => setCancellingBooking(null)} style={{ ...smallBtnStyle, background: palette.border, color: palette.text }}>
                        Nevermind
                      </button>
                    </div>
                  </div>
                )}

                {/* Modify form */}
                {modifyingBooking && modifyingBooking.id === b.id && (
                  <div style={{ marginTop: 12, padding: '12px', background: palette.beige, border: `1px solid ${palette.border}` }}>
                    <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: palette.brown, marginBottom: 8 }}>
                      Modification Request
                    </div>
                    <p style={{ fontSize: 12, color: palette.textMuted, margin: '0 0 10px', lineHeight: 1.5 }}>
                      Update any fields you'd like to change. An admin will review your request.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                      <div>
                        <label style={labelStyle}>New Date</label>
                        <input type="date" value={modifyDate} onChange={(e) => setModifyDate(e.target.value)} style={inputStyle} />
                      </div>
                      <div>
                        <label style={labelStyle}>New Start Time</label>
                        <select value={modifyStartTime} onChange={(e) => setModifyStartTime(e.target.value)} style={inputStyle}>
                          {START_TIMES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </div>
                    </div>
                    <div style={{ marginBottom: 10 }}>
                      <label style={labelStyle}>New Duration (hours)</label>
                      <input type="number" value={modifyHours} onChange={(e) => setModifyHours(e.target.value)} min={MIN_HOURS} max={12} style={{ ...inputStyle, width: 100 }} />
                    </div>
                    <div style={{ marginBottom: 10 }}>
                      <label style={labelStyle}>Reason for change</label>
                      <textarea value={modifyReason} onChange={(e) => setModifyReason(e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Why do you need this change?" />
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={submitModifyRequest} disabled={actionLoading} style={{ ...smallBtnStyle, background: palette.orange, color: palette.cream, opacity: actionLoading ? 0.6 : 1 }}>
                        {actionLoading ? '...' : 'Submit Request'}
                      </button>
                      <button onClick={() => setModifyingBooking(null)} style={{ ...smallBtnStyle, background: palette.border, color: palette.text }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Admin Dashboard
// ---------------------------------------------------------------------------

// The calendar feeds carry secret tokens, so their URLs come from an
// admin-only endpoint. team = full detail for our own calendars; busy =
// times only, for Peerspace / Giggster.
function useCalendarFeeds() {
  const [feeds, setFeeds] = useState({ team: '', busy: '' });
  useEffect(() => {
    api('/api/admin/calendar-feeds').then(setFeeds).catch(() => {});
  }, []);
  return feeds;
}

// Busy-only feed for booking platforms: times only, no guest names.
function PlatformFeedPanel({ palette, fonts }) {
  const { busy } = useCalendarFeeds();
  return (
    <div style={{ background: palette.white, border: `1px solid ${palette.border}`, padding: '20px', marginBottom: 24 }}>
      <h4 style={{ fontFamily: fonts, fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, margin: '0 0 12px' }}>
        Export HDDN LAB Calendar
      </h4>
      <p style={{ fontSize: 12, color: palette.textMuted, margin: '0 0 12px' }}>
        Paste this iCal feed URL into Giggster or Peerspace so they see your HDDN LAB bookings as unavailable.
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input
          readOnly
          value={busy}
          style={{ flex: 1, padding: '8px 10px', fontFamily: fonts, fontSize: 12, border: `1px solid ${palette.border}`, background: palette.beige, minWidth: 200 }}
          onFocus={(e) => e.target.select()}
        />
        <button
          onClick={() => { navigator.clipboard.writeText(busy); }}
          style={{ padding: '8px 16px', fontFamily: fonts, fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', background: palette.brown, color: palette.cream, border: 'none', cursor: 'pointer' }}
        >
          Copy
        </button>
      </div>
      <div style={{ marginTop: 10 }}>
        <details style={{ fontSize: 12, color: palette.textMuted }}>
          <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Per-room feeds</summary>
          <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {ROOMS.map((r) => (
              <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ minWidth: 100 }}>{r.label}:</span>
                <code style={{ fontSize: 11, background: palette.beige, padding: '2px 6px', wordBreak: 'break-all' }}>
                  {`${busy}${busy.includes('?') ? '&' : '?'}room=${r.id}`}
                </code>
              </div>
            ))}
          </div>
        </details>
      </div>
    </div>
  );
}

function CalendarSubscribePanel({ palette, fonts }) {
  const [copied, setCopied] = useState(false);
  const icsUrl = useCalendarFeeds().team;
  const webcalUrl = icsUrl.replace(/^https?:/, 'webcal:');

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(icsUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: open a prompt so the user can copy manually
      window.prompt('Copy the calendar URL:', icsUrl);
    }
  };

  return (
    <div style={{ background: palette.beige, border: `1px solid ${palette.border}`, padding: '16px 20px', marginBottom: 24 }}>
      <h4 style={{ fontFamily: fonts, fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, margin: '0 0 10px' }}>
        Subscribe to HDDN LAB Calendar
      </h4>
      <p style={{ fontSize: 12, color: palette.textMuted, margin: '0 0 12px', lineHeight: 1.6 }}>
        Add this feed to Google, Apple, or Outlook calendar to see every booking and blocked date on your personal calendar. Updates automatically — no re-import needed.
      </p>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="text"
          readOnly
          value={icsUrl}
          onFocus={(e) => e.target.select()}
          style={{
            flex: '1 1 300px', minWidth: 260, padding: '8px 10px',
            fontFamily: fonts, fontSize: 12, background: palette.white,
            border: `1px solid ${palette.border}`, color: palette.text,
          }}
        />
        <button
          type="button"
          onClick={copy}
          style={{
            fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
            padding: '8px 14px', background: palette.brown, color: palette.cream,
            border: 'none', cursor: 'pointer',
          }}
        >
          {copied ? 'Copied!' : 'Copy URL'}
        </button>
        <a
          href={webcalUrl}
          style={{
            fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
            padding: '8px 14px', background: palette.sage, color: palette.white,
            border: 'none', cursor: 'pointer', textDecoration: 'none',
          }}
        >
          Subscribe (Apple)
        </a>
      </div>
      <div style={{ fontSize: 11, color: palette.textMuted, marginTop: 10, lineHeight: 1.7 }}>
        <strong>Google Calendar:</strong> Settings → Add calendar → From URL → paste the URL.<br/>
        <strong>Apple Calendar:</strong> click "Subscribe (Apple)" above, or File → New Calendar Subscription.<br/>
        <strong>Outlook:</strong> Add calendar → Subscribe from web → paste the URL.
      </div>
    </div>
  );
}

function BlogPostGalleryEditor({ postId }) {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = React.useRef(null);
  // Local copy — this is a standalone component, so it can't see AdminPanel's smallBtnStyle.
  const smallBtnStyle = {
    fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1,
    textTransform: 'uppercase', padding: '5px 12px', border: 'none',
    cursor: 'pointer', transition: 'all 0.15s',
  };

  const fetchPost = async () => {
    if (!postId) return;
    try {
      const data = await api(`/api/admin/blog/posts/${postId}`);
      setItems(Array.isArray(data.media) ? data.media : []);
    } catch { setItems([]); }
  };

  useEffect(() => { fetchPost(); }, [postId]);

  const onFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    setError('');
    const token = localStorage.getItem('hddn_token');
    try {
      for (const file of Array.from(fileList)) {
        const fd = new FormData();
        fd.append('file', file);
        const res = await fetch(`${API_BASE}/api/admin/blog/posts/${postId}/media`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: fd,
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || `Upload failed (${res.status})`);
        }
      }
      await fetchPost();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const updateCaption = async (id, caption) => {
    setItems((prev) => prev.map((m) => (m.id === id ? { ...m, caption } : m)));
    try {
      await api(`/api/admin/blog/media/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ caption }),
      });
    } catch {}
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[idx], next[j]] = [next[j], next[idx]];
    setItems(next);
    try {
      await api(`/api/admin/blog/posts/${postId}/media/reorder`, {
        method: 'PUT',
        body: JSON.stringify({ order: next.map((m) => m.id) }),
      });
    } catch {}
  };

  const removeItem = async (id) => {
    if (!window.confirm('Delete this item from the gallery?')) return;
    try {
      await api(`/api/admin/blog/media/${id}`, { method: 'DELETE' });
      setItems((prev) => prev.filter((m) => m.id !== id));
    } catch {}
  };

  if (!postId) {
    return (
      <div style={{ padding: '14px 16px', background: palette.beige, border: `1px dashed ${palette.border}`, fontSize: 13, color: palette.textMuted }}>
        Save the post first, then you can attach photos and videos here.
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <span style={{ fontFamily: fonts, fontSize: 12, color: palette.textMuted }}>
          {items.length} {items.length === 1 ? 'item' : 'items'} — first one is used as the cover
        </span>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/mp4,video/quicktime,video/webm"
          multiple
          onChange={(e) => onFiles(e.target.files)}
          disabled={uploading}
          style={{ display: 'none' }}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          style={{ ...smallBtnStyle, background: palette.brown, color: palette.cream, padding: '8px 14px' }}
        >
          {uploading ? 'Uploading…' : '+ Add photos / videos'}
        </button>
      </div>
      {error && <div style={{ color: palette.red, fontSize: 12, marginBottom: 8 }}>{error}</div>}
      {items.length === 0 ? (
        <div style={{ padding: '24px 16px', background: palette.beige, border: `1px dashed ${palette.border}`, textAlign: 'center', fontSize: 13, color: palette.textMuted }}>
          No media yet. Add photos or videos — drag to reorder.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
          {items.map((m, idx) => {
            const isVideo = (m.mime_type || '').startsWith('video/');
            return (
              <div key={m.id} style={{ background: palette.white, border: `1px solid ${palette.border}`, padding: 8 }}>
                <div style={{ position: 'relative', height: 130, background: palette.beige, marginBottom: 8 }}>
                  {isVideo ? (
                    <video src={m.url} muted playsInline preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <img src={m.url} alt={m.caption || ''} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  )}
                  {idx === 0 && (
                    <div style={{ position: 'absolute', top: 6, left: 6, background: palette.orange, color: palette.white, fontSize: 9, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', padding: '2px 6px' }}>Cover</div>
                  )}
                  {isVideo && (
                    <div style={{ position: 'absolute', bottom: 6, right: 6, background: 'rgba(0,0,0,0.55)', color: palette.white, fontSize: 9, fontWeight: 700, letterSpacing: 1.5, padding: '2px 6px' }}>VIDEO</div>
                  )}
                </div>
                <input
                  type="text"
                  value={m.caption || ''}
                  onChange={(e) => setItems((prev) => prev.map((x) => (x.id === m.id ? { ...x, caption: e.target.value } : x)))}
                  onBlur={(e) => updateCaption(m.id, e.target.value)}
                  placeholder="Caption (optional)"
                  style={{ width: '100%', padding: '6px 8px', fontFamily: fonts, fontSize: 11, border: `1px solid ${palette.border}`, marginBottom: 6, boxSizing: 'border-box' }}
                />
                <div style={{ display: 'flex', gap: 4 }}>
                  <button onClick={() => move(idx, -1)} disabled={idx === 0} style={{ flex: 1, fontFamily: fonts, fontSize: 11, padding: '4px 6px', background: palette.white, border: `1px solid ${palette.border}`, cursor: idx === 0 ? 'not-allowed' : 'pointer', opacity: idx === 0 ? 0.4 : 1 }}>↑</button>
                  <button onClick={() => move(idx, 1)} disabled={idx === items.length - 1} style={{ flex: 1, fontFamily: fonts, fontSize: 11, padding: '4px 6px', background: palette.white, border: `1px solid ${palette.border}`, cursor: idx === items.length - 1 ? 'not-allowed' : 'pointer', opacity: idx === items.length - 1 ? 0.4 : 1 }}>↓</button>
                  <button onClick={() => removeItem(m.id)} style={{ flex: 1, fontFamily: fonts, fontSize: 11, padding: '4px 6px', background: palette.red, color: palette.white, border: 'none', cursor: 'pointer' }}>Delete</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Tiny markdown→HTML preview renderer for the admin Access Instructions editor.
// Mirrors the subset the server's markdownToHtml supports — must stay in sync.
function renderMarkdownPreview(md) {
  if (!md) return '';
  const escape = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const inline = (s) => escape(s)
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, txt, href) => `<a href="${href}" style="color:${palette.orange}">${txt}</a>`)
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  const lines = md.replace(/\r\n/g, '\n').split('\n');
  const out = [];
  let i = 0;
  let para = [];
  const flushPara = () => {
    if (para.length === 0) return;
    out.push(`<p style="font-family:${fonts};font-size:13px;line-height:1.7;color:${palette.text};margin:0 0 12px">${para.map(inline).join('<br/>')}</p>`);
    para = [];
  };
  while (i < lines.length) {
    const line = lines[i];
    const t = line.trim();
    if (!t) { flushPara(); i++; continue; }
    if (/^-{3,}$/.test(t)) { flushPara(); out.push(`<hr style="border:none;border-top:1px solid ${palette.border};margin:18px 0"/>`); i++; continue; }
    const h = /^(#{1,3})\s+(.+)$/.exec(t);
    if (h) {
      flushPara();
      const lvl = h[1].length;
      const sz = { 1: 22, 2: 16, 3: 14 }[lvl];
      out.push(`<h${lvl} style="font-family:${fonts};font-weight:700;letter-spacing:1px;color:${palette.brown};font-size:${sz}px;margin:${lvl === 1 ? '6px 0 12px' : '16px 0 8px'};text-transform:${lvl === 1 ? 'uppercase' : 'none'}">${inline(h[2])}</h${lvl}>`);
      i++; continue;
    }
    if (/^\|.+\|$/.test(t) && i + 1 < lines.length && /^\|[\s:|-]+\|$/.test(lines[i + 1].trim())) {
      flushPara();
      const headerCells = t.replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
      i += 2;
      const body = [];
      while (i < lines.length && /^\|.+\|$/.test(lines[i].trim())) {
        body.push(lines[i].trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim()));
        i++;
      }
      const ths = headerCells.map((c) => `<th style="text-align:left;padding:6px 10px;background:${palette.beige};border-bottom:2px solid ${palette.border};font-size:11px;letter-spacing:1px;text-transform:uppercase;color:${palette.brown}">${inline(c)}</th>`).join('');
      const trs = body.map((row) => `<tr>${row.map((c) => `<td style="padding:6px 10px;border-bottom:1px solid ${palette.border};font-size:12px;color:${palette.text}">${inline(c)}</td>`).join('')}</tr>`).join('');
      out.push(`<table style="border-collapse:collapse;width:100%;margin:0 0 14px"><thead><tr>${ths}</tr></thead><tbody>${trs}</tbody></table>`);
      continue;
    }
    if (/^[-*]\s+/.test(t)) {
      flushPara();
      const items = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^[-*]\s+/, ''));
        i++;
      }
      out.push(`<ul style="font-family:${fonts};font-size:13px;line-height:1.8;color:${palette.text};margin:0 0 12px;padding-left:20px">${items.map((it) => `<li style="margin-bottom:3px">${inline(it)}</li>`).join('')}</ul>`);
      continue;
    }
    para.push(line);
    i++;
  }
  flushPara();
  return out.join('\n');
}

function OpenHouseArchiveList() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');

  const fetchItems = async () => {
    setError('');
    try {
      const data = await api('/api/admin/openhouse/archive');
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.error || err.message || 'Failed to load');
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const remove = async (slug) => {
    if (!confirm(`Delete archive for "${slug}"? /openhouse/${slug} will stop working.`)) return;
    try {
      await api(`/api/openhouse/archive/${encodeURIComponent(slug)}`, { method: 'DELETE' });
      setItems((prev) => (Array.isArray(prev) ? prev.filter((i) => i.slug !== slug) : prev));
    } catch (err) {
      alert(`Delete failed: ${err.error || err.message}`);
    }
  };

  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';

  return (
    <div style={{ marginTop: 18, paddingTop: 14, borderTop: `1px dashed ${palette.border}` }}>
      <div style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: palette.textMuted, marginBottom: 8 }}>
        Past events {items ? `(${items.length})` : ''}
      </div>
      {error && <p style={{ fontSize: 12, color: palette.red }}>{error}</p>}
      {!items && !error && <p style={{ fontSize: 12, color: palette.textMuted }}>Loading...</p>}
      {Array.isArray(items) && items.length === 0 && (
        <p style={{ fontSize: 12, color: palette.textMuted }}>No archived events yet. Use "Start a new open house" to archive the current one.</p>
      )}
      {Array.isArray(items) && items.length > 0 && (
        <div>
          {items.map((it) => (
            <div key={it.slug} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${palette.border}`, gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ fontFamily: fonts, fontSize: 13, color: palette.brown, fontWeight: 700 }}>{it.headline || it.eventTag}</div>
                <div style={{ fontFamily: fonts, fontSize: 11, color: palette.textMuted, marginTop: 2 }}>
                  <a href={`/openhouse/${it.slug}`} target="_blank" rel="noopener noreferrer" style={{ color: palette.orange }}>{baseUrl}/openhouse/{it.slug}</a>
                  {it.archived_at && <span style={{ marginLeft: 10 }}>archived {new Date(it.archived_at).toLocaleDateString()}</span>}
                </div>
              </div>
              <button onClick={() => remove(it.slug)} style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, padding: '4px 10px', background: 'transparent', color: palette.red, border: `1px solid ${palette.red}`, cursor: 'pointer' }}>
                Delete
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AccessInstructionsEditor({ data, onChange }) {
  const [showPreview, setShowPreview] = useState(false);
  const text = data.text || '';

  return (
    <div style={{ background: palette.white, border: `1px solid ${palette.border}`, padding: '16px' }}>
      <p style={{ fontFamily: fonts, fontSize: 13, color: palette.textMuted, margin: '0 0 14px', lineHeight: 1.5 }}>
        Sent in the booking confirmation email (and the 24-hr reminder) as a styled HDDN LAB document. Use markdown to format: <code>#</code>, <code>##</code>, <code>###</code> for headers, <code>**bold**</code>, <code>[text](url)</code>, <code>-</code> for bullet lists, <code>---</code> for a divider, and pipe-tables for the parking grid. Personalization: <code>[First Name]</code>, <code>[Date]</code>, <code>[Room]</code>, <code>[Time]</code>, <code>[Booking Ref]</code> are filled in per booking.
      </p>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 12 }}>
        <label style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: palette.textMuted, margin: 0 }}>
          Access & Arrival Instructions (markdown)
        </label>
        <button
          type="button"
          onClick={() => setShowPreview(!showPreview)}
          style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', padding: '6px 12px', background: showPreview ? palette.brown : palette.white, color: showPreview ? palette.cream : palette.brown, border: `1px solid ${palette.brown}`, cursor: 'pointer' }}
        >
          {showPreview ? 'Hide preview' : 'Preview'}
        </button>
      </div>
      <textarea
        value={text}
        onChange={(e) => onChange({ text: e.target.value })}
        rows={20}
        style={{ width: '100%', fontFamily: 'monospace', fontSize: 13, padding: '12px 14px', border: `1px solid ${palette.border}`, background: palette.white, color: palette.text, boxSizing: 'border-box', resize: 'vertical', lineHeight: 1.6 }}
      />
      {showPreview && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.textMuted, marginBottom: 8 }}>
            Email preview
          </div>
          <div style={{ background: palette.cream, border: `1px solid ${palette.border}`, overflow: 'hidden' }}>
            <div style={{ background: palette.brown, borderBottom: `3px solid ${palette.orange}`, padding: '14px 22px', textAlign: 'center' }}>
              <span style={{ fontFamily: fonts, fontWeight: 700, fontSize: 18, letterSpacing: 5, color: palette.cream, textTransform: 'uppercase' }}>HDDN LAB</span>
              <span style={{ color: palette.orange, fontSize: 11, letterSpacing: 2, marginLeft: 10 }}>STUDIO</span>
            </div>
            <div
              style={{ background: palette.white, padding: '22px 24px' }}
              dangerouslySetInnerHTML={{ __html: renderMarkdownPreview(text.replace(/\[First Name\]/g, 'Jane').replace(/\[Date\]/g, 'Apr 28, 2026').replace(/\[Room\]/g, 'Full Studio').replace(/\[Time\]/g, '10:00 AM – 1:00 PM (3 hrs)').replace(/\[Booking Ref\]/g, 'HDN-1234')) }}
            />
            <div style={{ background: palette.beige, padding: '12px 22px', textAlign: 'center', fontFamily: fonts, fontSize: 11, letterSpacing: 1, color: palette.textMuted }}>
              72 Centre Avenue, New Rochelle, NY 10801 · {CONTACT_EMAIL}
            </div>
          </div>
          <p style={{ fontSize: 11, color: palette.textMuted, marginTop: 8, fontStyle: 'italic' }}>
            Placeholders are shown filled with sample values. Booking details (date, room, time, ref, price) are added above this content as a separate card by the email template.
          </p>
        </div>
      )}
    </div>
  );
}

function OpenHouseResponsesPanel() {
  const [responses, setResponses] = useState(null);
  const [error, setError] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all'); // 'all' | 'open-house' | 'open-house-waitlist'
  const [showWaitlistEmail, setShowWaitlistEmail] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailResult, setEmailResult] = useState(null);
  const [emailError, setEmailError] = useState('');

  const fetchResponses = async () => {
    setError('');
    const token = localStorage.getItem('hddn_token');
    try {
      const r = await fetch(`${API_BASE}/api/openhouse/responses`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const text = await r.text();
      let data = null;
      try { data = text ? JSON.parse(text) : null; } catch (_e) { /* not JSON */ }
      if (!r.ok) {
        const detail = (data && (data.error || data.message)) || text || `HTTP ${r.status}`;
        throw new Error(`Load failed (${r.status}): ${detail}`);
      }
      setResponses(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => { fetchResponses(); }, []);

  const deleteOne = async (id, email) => {
    if (!confirm(`Delete the response from ${email}? This won't remove them from Beehiiv.`)) return;
    const token = localStorage.getItem('hddn_token');
    try {
      const r = await fetch(`${API_BASE}/api/openhouse/responses/${id}`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${r.status}`);
      }
      setResponses((prev) => (Array.isArray(prev) ? prev.filter((x) => x.id !== id) : prev));
    } catch (err) {
      alert(`Delete failed: ${err.message}`);
    }
  };

  const deleteAll = async () => {
    if (!Array.isArray(responses) || responses.length === 0) return;
    if (!confirm(`Delete ALL ${responses.length} responses? This won't remove anyone from Beehiiv.`)) return;
    if (!confirm('Are you sure? This cannot be undone.')) return;
    const token = localStorage.getItem('hddn_token');
    try {
      const r = await fetch(`${API_BASE}/api/openhouse/responses`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${r.status}`);
      }
      setResponses([]);
    } catch (err) {
      alert(`Clear all failed: ${err.message}`);
    }
  };

  // Count of unique waitlist subscribers (those between events)
  const waitlistCount = Array.isArray(responses)
    ? new Set(responses.filter((r) => r.source === 'open-house-waitlist' && r.newsletter_opt_in !== false).map((r) => (r.email || '').toLowerCase())).size
    : 0;

  const sendWaitlistEmail = async () => {
    setEmailSending(true);
    setEmailError('');
    setEmailResult(null);
    const token = localStorage.getItem('hddn_token');
    try {
      const r = await fetch(`${API_BASE}/api/openhouse/waitlist/email`, {
        method: 'POST',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject: emailSubject, body: emailBody }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
      setEmailResult(data);
    } catch (err) {
      setEmailError(err.message);
    } finally {
      setEmailSending(false);
    }
  };

  const filteredResponses = Array.isArray(responses)
    ? (sourceFilter === 'all' ? responses : responses.filter((r) => r.source === sourceFilter))
    : null;

  const downloadCsv = () => {
    const list = filteredResponses;
    if (!Array.isArray(list) || list.length === 0) return;
    // Collect all answer keys across responses for stable columns
    const answerKeys = Array.from(new Set(
      list.flatMap((r) => Object.keys(r.answers || {}))
    ));
    const headers = ['Submitted', 'Source', 'Name', 'Email', 'Newsletter opt-in', 'Beehiiv synced', ...answerKeys];
    const escape = (val) => {
      const s = val == null ? '' : Array.isArray(val) ? val.join('; ') : String(val);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = list.map((r) => [
      r.created_at,
      r.source || 'open-house',
      escape(r.name || ''),
      r.email,
      r.newsletter_opt_in === false ? 'no' : 'yes',
      r.beehiiv_synced ? 'yes' : 'no',
      ...answerKeys.map((k) => escape(r.answers?.[k])),
    ].join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `openhouse-responses-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ background: palette.white, border: `1px solid ${palette.border}`, padding: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
        <h4 style={{ fontFamily: fonts, fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, margin: 0 }}>
          Survey responses {filteredResponses ? `(${filteredResponses.length}${responses && filteredResponses.length !== responses.length ? ` / ${responses.length}` : ''})` : ''}
        </h4>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '6px 10px', background: palette.white, color: palette.brown, border: `1px solid ${palette.border}`, cursor: 'pointer' }}
          >
            <option value="all">All sources</option>
            <option value="open-house">Active event</option>
            <option value="open-house-waitlist">Waitlist (between events)</option>
            <option value="newsletter">Newsletter (site)</option>
          </select>
          <button onClick={fetchResponses} style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', padding: '6px 12px', background: palette.white, color: palette.brown, border: `1px solid ${palette.border}`, cursor: 'pointer' }}>Refresh</button>
          <button onClick={downloadCsv} disabled={!filteredResponses || filteredResponses.length === 0} style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', padding: '6px 12px', background: palette.brown, color: palette.cream, border: 'none', cursor: !filteredResponses || filteredResponses.length === 0 ? 'not-allowed' : 'pointer', opacity: !filteredResponses || filteredResponses.length === 0 ? 0.5 : 1 }}>Download CSV</button>
          <button onClick={() => { setShowWaitlistEmail(true); setEmailResult(null); setEmailError(''); }} disabled={waitlistCount === 0} title={waitlistCount === 0 ? 'No waitlist subscribers yet' : `${waitlistCount} waitlist subscriber${waitlistCount === 1 ? '' : 's'}`} style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', padding: '6px 12px', background: palette.sage, color: palette.white, border: 'none', cursor: waitlistCount === 0 ? 'not-allowed' : 'pointer', opacity: waitlistCount === 0 ? 0.5 : 1 }}>Email waitlist {waitlistCount > 0 ? `(${waitlistCount})` : ''}</button>
          <button onClick={deleteAll} disabled={!responses || responses.length === 0} style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', padding: '6px 12px', background: 'transparent', color: palette.red, border: `1px solid ${palette.red}`, cursor: !responses || responses.length === 0 ? 'not-allowed' : 'pointer', opacity: !responses || responses.length === 0 ? 0.5 : 1 }}>Clear all</button>
        </div>
      </div>
      {error && <p style={{ color: palette.red, fontSize: 13 }}>{error}</p>}
      {!responses && !error && <p style={{ color: palette.textMuted, fontSize: 13 }}>Loading...</p>}
      {Array.isArray(responses) && responses.length === 0 && (
        <p style={{ color: palette.textMuted, fontSize: 13 }}>No responses yet.</p>
      )}
      {Array.isArray(filteredResponses) && responses && responses.length > 0 && filteredResponses.length === 0 && (
        <p style={{ color: palette.textMuted, fontSize: 13 }}>No responses match this filter.</p>
      )}
      {Array.isArray(filteredResponses) && filteredResponses.length > 0 && (
        <div style={{ overflow: 'auto' }}>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: palette.beige, textAlign: 'left' }}>
                <th style={{ padding: '8px 10px', borderBottom: `1px solid ${palette.border}`, fontFamily: fonts, fontWeight: 700, letterSpacing: 1 }}>Submitted</th>
                <th style={{ padding: '8px 10px', borderBottom: `1px solid ${palette.border}`, fontFamily: fonts, fontWeight: 700, letterSpacing: 1 }}>Source</th>
                <th style={{ padding: '8px 10px', borderBottom: `1px solid ${palette.border}`, fontFamily: fonts, fontWeight: 700, letterSpacing: 1 }}>Name</th>
                <th style={{ padding: '8px 10px', borderBottom: `1px solid ${palette.border}`, fontFamily: fonts, fontWeight: 700, letterSpacing: 1 }}>Email</th>
                <th style={{ padding: '8px 10px', borderBottom: `1px solid ${palette.border}`, fontFamily: fonts, fontWeight: 700, letterSpacing: 1 }}>Answers</th>
                <th style={{ padding: '8px 10px', borderBottom: `1px solid ${palette.border}`, fontFamily: fonts, fontWeight: 700, letterSpacing: 1 }}>Newsletter</th>
                <th style={{ padding: '8px 10px', borderBottom: `1px solid ${palette.border}`, fontFamily: fonts, fontWeight: 700, letterSpacing: 1 }}>Beehiiv</th>
                <th style={{ padding: '8px 10px', borderBottom: `1px solid ${palette.border}`, fontFamily: fonts, fontWeight: 700, letterSpacing: 1, width: 1 }}></th>
              </tr>
            </thead>
            <tbody>
              {filteredResponses.map((r) => (
                <tr key={r.id}>
                  <td style={{ padding: '8px 10px', borderBottom: `1px solid ${palette.border}`, color: palette.textMuted, whiteSpace: 'nowrap' }}>{r.created_at ? new Date(r.created_at).toLocaleString() : ''}</td>
                  <td style={{ padding: '8px 10px', borderBottom: `1px solid ${palette.border}`, fontSize: 10, letterSpacing: 0.5, textTransform: 'uppercase', color: palette.textMuted, whiteSpace: 'nowrap' }}>
                    {r.source === 'open-house-waitlist' ? 'Waitlist' : r.source === 'newsletter' ? 'Newsletter' : 'Event'}
                  </td>
                  <td style={{ padding: '8px 10px', borderBottom: `1px solid ${palette.border}` }}>{r.name || '—'}</td>
                  <td style={{ padding: '8px 10px', borderBottom: `1px solid ${palette.border}` }}>{r.email}</td>
                  <td style={{ padding: '8px 10px', borderBottom: `1px solid ${palette.border}` }}>
                    {Object.entries(r.answers || {}).map(([k, v]) => (
                      <div key={k} style={{ marginBottom: 2 }}>
                        <strong style={{ color: palette.brown }}>{k}:</strong>{' '}
                        <span style={{ color: palette.text }}>{Array.isArray(v) ? v.join(', ') : String(v ?? '')}</span>
                      </div>
                    ))}
                  </td>
                  <td style={{ padding: '8px 10px', borderBottom: `1px solid ${palette.border}`, color: r.newsletter_opt_in === false ? palette.textMuted : palette.sage }}>
                    {r.newsletter_opt_in === false ? 'no' : 'yes'}
                  </td>
                  <td style={{ padding: '8px 10px', borderBottom: `1px solid ${palette.border}`, color: r.beehiiv_synced ? palette.sage : (r.beehiiv_error ? palette.red : palette.textMuted), maxWidth: 280 }}>
                    <div>{r.beehiiv_synced ? (r.beehiiv_error ? 'synced (warn)' : 'synced') : (r.beehiiv_error ? 'error' : '—')}</div>
                    {r.beehiiv_error && (
                      <div style={{ fontSize: 10, color: palette.textMuted, marginTop: 4, lineHeight: 1.4, wordBreak: 'break-word' }}>
                        {r.beehiiv_error}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '8px 10px', borderBottom: `1px solid ${palette.border}`, whiteSpace: 'nowrap' }}>
                    <button
                      onClick={() => deleteOne(r.id, r.email)}
                      title="Delete this response"
                      style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '4px 10px', background: 'transparent', color: palette.red, border: `1px solid ${palette.red}`, cursor: 'pointer' }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showWaitlistEmail && (
        <div
          onClick={() => !emailSending && setShowWaitlistEmail(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(60,43,31,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: palette.cream, border: `1px solid ${palette.border}`, boxShadow: '0 8px 30px rgba(0,0,0,0.15)', width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', padding: '28px 26px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ fontFamily: fonts, fontSize: 14, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, margin: 0 }}>
                Email waitlist ({waitlistCount} subscriber{waitlistCount === 1 ? '' : 's'})
              </h3>
              <button onClick={() => !emailSending && setShowWaitlistEmail(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: palette.text, fontFamily: fonts }}>&times;</button>
            </div>
            {emailResult ? (
              <div>
                <div style={{ fontSize: 38, color: palette.sage, textAlign: 'center', marginBottom: 12 }}>&#10003;</div>
                <p style={{ fontFamily: fonts, fontSize: 14, color: palette.text, textAlign: 'center', margin: '0 0 6px' }}>
                  Sent to {emailResult.sent} of {emailResult.total} subscribers.
                </p>
                {emailResult.failed > 0 && (
                  <p style={{ fontSize: 12, color: palette.red, textAlign: 'center' }}>{emailResult.failed} failed — check Railway logs.</p>
                )}
                <button onClick={() => { setShowWaitlistEmail(false); setEmailResult(null); setEmailSubject(''); setEmailBody(''); }} style={{ ...smallBtnStyle, background: palette.brown, color: palette.cream, padding: '10px 20px', display: 'block', margin: '16px auto 0' }}>Done</button>
              </div>
            ) : (
              <div>
                <p style={{ fontFamily: fonts, fontSize: 12, color: palette.textMuted, margin: '0 0 14px', lineHeight: 1.5 }}>
                  Send an announcement to everyone on the waitlist (people who signed up between events). One email per recipient, sent via your existing email pipeline. Markdown formatting + <code>[First Name]</code> placeholder are supported. Newsletter opt-outs are excluded automatically.
                </p>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: palette.textMuted, display: 'block', marginBottom: 4 }}>Subject</label>
                  <input
                    type="text"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="The next HDDN Lab Open House is coming up"
                    style={{ width: '100%', padding: '10px 12px', fontFamily: fonts, fontSize: 13, border: `1px solid ${palette.border}`, background: palette.white, color: palette.text, boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: palette.textMuted, display: 'block', marginBottom: 4 }}>Message (markdown)</label>
                  <textarea
                    value={emailBody}
                    onChange={(e) => setEmailBody(e.target.value)}
                    rows={10}
                    placeholder={`Hi [First Name],\n\nWe're hosting our next Open House on **Friday, June 14**. Doors open at 7pm — drinks, music, and a chance to meet the rest of the HDDN community.\n\nRSVP at [hddnlab.com/openhouse](https://hddnlab.com/openhouse).\n\nSee you there.`}
                    style={{ width: '100%', padding: '10px 12px', fontFamily: 'monospace', fontSize: 13, border: `1px solid ${palette.border}`, background: palette.white, color: palette.text, boxSizing: 'border-box', resize: 'vertical' }}
                  />
                </div>
                {emailError && <div style={{ color: palette.red, fontSize: 12, marginBottom: 10 }}>{emailError}</div>}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                  <button onClick={() => setShowWaitlistEmail(false)} disabled={emailSending} style={{ fontFamily: fonts, fontSize: 12, padding: '8px 16px', background: 'transparent', color: palette.textMuted, border: `1px solid ${palette.border}`, cursor: emailSending ? 'not-allowed' : 'pointer' }}>
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      if (!confirm(`Send to ${waitlistCount} waitlist subscriber${waitlistCount === 1 ? '' : 's'}? This cannot be undone.`)) return;
                      sendWaitlistEmail();
                    }}
                    disabled={emailSending || !emailSubject.trim() || !emailBody.trim()}
                    style={{ fontFamily: fonts, fontSize: 12, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', padding: '10px 20px', background: palette.sage, color: palette.white, border: 'none', cursor: emailSending || !emailSubject.trim() || !emailBody.trim() ? 'not-allowed' : 'pointer', opacity: emailSending || !emailSubject.trim() || !emailBody.trim() ? 0.5 : 1 }}
                  >
                    {emailSending ? 'Sending…' : `Send to ${waitlistCount}`}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Team Access — who can sign in, and at what level.
//
// New accounts are created without a password: the person sets their own from
// an emailed invite link, so a password is never typed by one person for
// another or read off a screen. The server refuses to strip the last admin or
// to let anyone change their own role, so this screen cannot lock you out of
// the dashboard you are standing in.
function TeamAccessSection({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', role: 'admin' });
  const [msg, setMsg] = useState(null);

  const load = async () => {
    try {
      setUsers(await api('/api/admin/users'));
    } catch (err) {
      setMsg({ bad: true, text: err.error || 'Could not load the user list.' });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy('create'); setMsg(null);
    try {
      const out = await api('/api/admin/users', { method: 'POST', body: JSON.stringify(form) });
      setForm({ name: '', email: '', role: 'admin' });
      await load();
      setMsg(out.inviteEmailed
        ? { text: `Account created. ${out.user.name} has an email with a link to set their password.` }
        : { bad: true, text: `Account created, but the invite email did not send. Use "Resend invite" to try again.` });
    } catch (err) {
      setMsg({ bad: true, text: err.error || 'Could not create that account.' });
    } finally { setBusy(null); }
  };

  const act = async (id, label, fn) => {
    setBusy(id); setMsg(null);
    try { setMsg({ text: await fn() }); await load(); }
    catch (err) { setMsg({ bad: true, text: err.error || `Could not ${label}.` }); }
    finally { setBusy(null); }
  };

  const resend = (u) => act(u.id, 'send that invite', async () => {
    await api(`/api/admin/users/${u.id}/invite`, { method: 'POST' });
    return `Invite re-sent to ${u.email}. Any earlier link for them stops working.`;
  });

  const setRole = (u, role) => act(u.id, 'change that role', async () => {
    await api(`/api/admin/users/${u.id}`, { method: 'PATCH', body: JSON.stringify({ role }) });
    return `${u.name} is now ${role === 'admin' ? 'an admin' : 'a standard user'}.`;
  });

  const remove = (u) => {
    if (!confirm(`Remove ${u.name} (${u.email})? They lose access immediately. Their bookings are kept.`)) return;
    act(u.id, 'remove that account', async () => {
      await api(`/api/admin/users/${u.id}`, { method: 'DELETE' });
      return `${u.name} no longer has access.`;
    });
  };

  const lbl = { fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: palette.textMuted, display: 'block', marginBottom: 6 };
  const inp = { fontFamily: fonts, fontSize: 14, padding: '10px 12px', border: `1px solid ${palette.border}`, background: palette.white, color: palette.text, width: '100%' };
  const btn = { fontFamily: fonts, fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '9px 16px', cursor: 'pointer', border: 'none' };
  const admins = users.filter((u) => u.role === 'admin').length;

  return (
    <div style={{ fontFamily: fonts, color: palette.text }}>
      <h2 style={{ fontSize: 18, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: palette.brown, margin: '0 0 6px' }}>Team Access</h2>
      <p style={{ fontSize: 13, color: palette.textMuted, margin: '0 0 22px', maxWidth: 620 }}>
        Who can sign in. Admins see this whole dashboard; standard users can only sign in and manage
        their own bookings. New people set their own password from a link we email them.
      </p>

      {msg && (
        <div role="status" style={{ fontSize: 13, padding: '11px 14px', marginBottom: 18, background: msg.bad ? '#fdf0ee' : palette.partial, borderLeft: `3px solid ${msg.bad ? palette.red : palette.sage}`, color: palette.text }}>
          {msg.text}
        </div>
      )}

      <form onSubmit={create} style={{ background: palette.beige, border: `1px solid ${palette.border}`, padding: 20, marginBottom: 26 }}>
        <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', alignItems: 'end' }}>
          <div>
            <label style={lbl} htmlFor="ta-name">Name</label>
            <input id="ta-name" style={inp} value={form.name} required
                   onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Donnell Culver" />
          </div>
          <div>
            <label style={lbl} htmlFor="ta-email">Email</label>
            <input id="ta-email" style={inp} type="email" value={form.email} required
                   onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@hddnlab.com" />
          </div>
          <div>
            <label style={lbl} htmlFor="ta-role">Access</label>
            <select id="ta-role" style={{ ...inp, appearance: 'auto' }} value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}>
              <option value="admin">Admin — full dashboard</option>
              <option value="user">Standard — own bookings only</option>
            </select>
          </div>
          <button type="submit" disabled={busy === 'create'}
                  style={{ ...btn, background: palette.orange, color: palette.white, opacity: busy === 'create' ? 0.6 : 1 }}>
            {busy === 'create' ? 'Sending…' : 'Create & invite'}
          </button>
        </div>
      </form>

      {loading ? (
        <p style={{ fontSize: 13, color: palette.textMuted }}>Loading…</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 620 }}>
            <thead>
              <tr>
                {['Name', 'Email', 'Access', 'Status', ''].map((h) => (
                  <th key={h} style={{ textAlign: 'left', padding: '10px 12px 10px 0', borderBottom: `2px solid ${palette.border}`, fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: palette.textMuted }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const me = u.id === (currentUser && currentUser.id);
                const lastAdmin = u.role === 'admin' && admins === 1;
                return (
                  <tr key={u.id}>
                    <td style={{ padding: '12px 12px 12px 0', borderBottom: `1px solid ${palette.border}`, fontWeight: 700 }}>
                      {u.name}{me && <span style={{ fontWeight: 400, color: palette.textMuted }}> — you</span>}
                    </td>
                    <td style={{ padding: '12px 12px 12px 0', borderBottom: `1px solid ${palette.border}`, color: palette.textMuted }}>{u.email}</td>
                    <td style={{ padding: '12px 12px 12px 0', borderBottom: `1px solid ${palette.border}` }}>
                      <select
                        value={u.role}
                        disabled={me || lastAdmin || busy === u.id}
                        onChange={(e) => setRole(u, e.target.value)}
                        title={me ? 'You cannot change your own role' : lastAdmin ? 'The only admin — promote someone else first' : ''}
                        style={{ ...inp, appearance: 'auto', width: 'auto', padding: '6px 8px', fontSize: 12, opacity: (me || lastAdmin) ? 0.55 : 1 }}
                      >
                        <option value="admin">Admin</option>
                        <option value="user">Standard</option>
                      </select>
                    </td>
                    <td style={{ padding: '12px 12px 12px 0', borderBottom: `1px solid ${palette.border}`, color: palette.textMuted }}>
                      {u.invite_pending ? 'Invite pending' : 'Active'}
                    </td>
                    <td style={{ padding: '12px 0', borderBottom: `1px solid ${palette.border}`, whiteSpace: 'nowrap' }}>
                      <button onClick={() => resend(u)} disabled={busy === u.id}
                              style={{ ...btn, background: 'none', color: palette.orange, padding: '6px 10px', fontSize: 11 }}>
                        Resend invite
                      </button>
                      {!me && (
                        <button onClick={() => remove(u)} disabled={busy === u.id || lastAdmin}
                                title={lastAdmin ? 'The only admin — promote someone else first' : ''}
                                style={{ ...btn, background: 'none', color: lastAdmin ? palette.textMuted : palette.red, padding: '6px 10px', fontSize: 11, opacity: lastAdmin ? 0.55 : 1 }}>
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AdminDashboard({ user, onBack, onPackagesConfigSaved }) {
  const [tab, setTab] = useState('dashboard');
  // Sub-tabs (rendered as pill nav inside the parent tab)
  const [dashSubTab, setDashSubTab] = useState('overview'); // 'overview' | 'revenue' | 'customers' | 'messages'
  const [calSubTab, setCalSubTab] = useState('calendar');   // 'calendar' | 'blocked' | 'requests'
  const [bookings, setBookings] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [roomFilter, setRoomFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Calendar tab state
  const calNow = new Date();
  const [calYear, setCalYear] = useState(calNow.getFullYear());
  const [calMonth, setCalMonth] = useState(calNow.getMonth());
  const [calRoom, setCalRoom] = useState('upstairs-1');
  const [calSelectedDate, setCalSelectedDate] = useState(null);

  // Edit booking state
  const [editingBooking, setEditingBooking] = useState(null);

  // Incidental charge state
  const [chargingBooking, setChargingBooking] = useState(null);
  const [denyingBooking, setDenyingBooking] = useState(null);
  const [denyReason, setDenyReason] = useState('');
  const [chargeAmount, setChargeAmount] = useState('');
  const [chargeDescription, setChargeDescription] = useState('');
  const [chargeLoading, setChargeLoading] = useState(false);
  const [chargeError, setChargeError] = useState('');
  const [chargeSuccess, setChargeSuccess] = useState('');

  const chargeIncidental = async () => {
    if (!chargingBooking || !chargeAmount || !chargeDescription) return;
    setChargeLoading(true);
    setChargeError('');
    setChargeSuccess('');
    try {
      const result = await api('/api/stripe/charge-incidental', {
        method: 'POST',
        body: JSON.stringify({
          bookingId: chargingBooking.id,
          amount: parseFloat(chargeAmount),
          description: chargeDescription,
        }),
      });
      setChargeSuccess(`Charged $${parseFloat(chargeAmount).toFixed(2)} successfully`);
      setChargeAmount('');
      setChargeDescription('');
      setTimeout(() => { setChargingBooking(null); setChargeSuccess(''); }, 2000);
    } catch (err) {
      setChargeError(err.error || 'Charge failed');
    } finally {
      setChargeLoading(false);
    }
  };

  // Block dates form
  const [blockStart, setBlockStart] = useState('');
  const [blockEnd, setBlockEnd] = useState('');
  const [blockRoom, setBlockRoom] = useState('all');
  const [blockReason, setBlockReason] = useState('');
  const [blockedBy, setBlockedBy] = useState('');
  const [blockError, setBlockError] = useState('');
  const [blockSuccess, setBlockSuccess] = useState('');

  // Change requests state
  const [changeRequests, setChangeRequests] = useState([]);
  const [crAdminNote, setCrAdminNote] = useState('');
  const [crLoading, setCrLoading] = useState(null);

  const fetchChangeRequests = async () => {
    try {
      const data = await api('/api/change-requests?status=pending');
      setChangeRequests(data);
    } catch { setChangeRequests([]); }
  };

  const resolveChangeRequest = async (id, status) => {
    setCrLoading(id);
    try {
      await api(`/api/change-requests/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status, adminNote: crAdminNote }),
      });
      setCrAdminNote('');
      await Promise.all([fetchChangeRequests(), fetchBookings()]);
    } catch {}
    setCrLoading(null);
  };

  // External calendar sync state
  const [extCalendars, setExtCalendars] = useState([]);
  const [newCalName, setNewCalName] = useState('');
  const [newCalPlatform, setNewCalPlatform] = useState('');
  const [newCalRoom, setNewCalRoom] = useState('all');
  const [newCalUrl, setNewCalUrl] = useState('');
  const [calSyncError, setCalSyncError] = useState('');
  const [calSyncSuccess, setCalSyncSuccess] = useState('');

  // Dashboard home state
  const [dashStats, setDashStats] = useState(null);
  const [openhouseConfig, setOpenhouseConfig] = useState(null);

  // Revenue state
  const [revenueData, setRevenueData] = useState(null);
  const [revPeriod, setRevPeriod] = useState('30');

  // Customers state
  const [customers, setCustomers] = useState([]);
  const [custSearch, setCustSearch] = useState('');
  const [custSort, setCustSort] = useState('recent');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [custDetail, setCustDetail] = useState(null);
  const [custNoteText, setCustNoteText] = useState('');

  // Blog admin state
  const [blogPosts, setBlogPosts] = useState([]);
  const [blogCategories, setBlogCategories] = useState([]);
  const [editingPost, setEditingPost] = useState(null);
  const [blogForm, setBlogForm] = useState({ title: '', slug: '', excerpt: '', content: '', category_id: '', featured_image: '', status: 'draft', meta_title: '', meta_description: '' });

  // Sidebar collapsed state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [moreSheetOpen, setMoreSheetOpen] = useState(false);

  const fetchExternalCalendars = async () => {
    try {
      const data = await api('/api/external-calendars');
      setExtCalendars(data);
    } catch { setExtCalendars([]); }
  };

  const addExternalCalendar = async () => {
    if (!newCalName || !newCalPlatform || !newCalUrl) return;
    setCalSyncError('');
    setCalSyncSuccess('');
    try {
      await api('/api/external-calendars', {
        method: 'POST',
        body: JSON.stringify({ name: newCalName, platform: newCalPlatform, room: newCalRoom, icalUrl: newCalUrl }),
      });
      setNewCalName(''); setNewCalPlatform(''); setNewCalUrl(''); setNewCalRoom('all');
      setCalSyncSuccess('Calendar added! Initial sync started.');
      fetchExternalCalendars();
    } catch (err) {
      setCalSyncError(err.error || 'Failed to add calendar');
    }
  };

  const deleteExternalCalendar = async (id) => {
    if (!confirm('Remove this calendar feed? External events will be deleted.')) return;
    try {
      await api(`/api/external-calendars/${id}`, { method: 'DELETE' });
      fetchExternalCalendars();
    } catch { }
  };

  const syncCalendar = async (id) => {
    try {
      await api(`/api/external-calendars/${id}/sync`, { method: 'POST' });
      fetchExternalCalendars();
    } catch { }
  };

  const fetchBookings = async () => {
    try {
      const data = await api('/api/bookings/all');
      setBookings(data);
    } catch { setBookings([]); }
  };

  const fetchBlocked = async () => {
    try {
      const data = await api('/api/blocked-dates');
      setBlockedDates(data);
    } catch { setBlockedDates([]); }
  };

  const fetchDashStats = async () => {
    try {
      const data = await api('/api/admin/dashboard-stats');
      setDashStats(data);
    } catch { setDashStats(null); }
  };

  const fetchOpenhouseConfig = async () => {
    try {
      const data = await api('/api/openhouse/config');
      setOpenhouseConfig(data);
    } catch { setOpenhouseConfig(null); }
  };

  const fetchRevenue = async (days) => {
    try {
      const data = await api(`/api/admin/revenue?days=${days || revPeriod}`);
      setRevenueData(data);
    } catch { setRevenueData(null); }
  };

  const fetchCustomers = async () => {
    try {
      const data = await api('/api/admin/customers');
      setCustomers(data);
    } catch { setCustomers([]); }
  };

  const fetchCustomerDetail = async (email) => {
    try {
      const data = await api(`/api/admin/customers/${encodeURIComponent(email)}`);
      setCustDetail(data);
    } catch { setCustDetail(null); }
  };

  const addCustomerNote = async (email) => {
    if (!custNoteText.trim()) return;
    try {
      await api('/api/admin/customers/notes', {
        method: 'POST',
        body: JSON.stringify({ customer_email: email, note: custNoteText }),
      });
      setCustNoteText('');
      await fetchCustomerDetail(email);
    } catch {}
  };

  const fetchBlogPosts = async () => {
    try {
      const data = await api('/api/admin/blog/posts');
      setBlogPosts(data);
    } catch { setBlogPosts([]); }
  };

  const fetchBlogCategories = async () => {
    try {
      const data = await api('/api/blog/categories');
      setBlogCategories(data);
    } catch { setBlogCategories([]); }
  };

  const saveBlogPost = async () => {
    if (!blogForm.title || !blogForm.title.trim()) {
      alert('Please add a title before saving.');
      return;
    }
    const payload = {
      title: blogForm.title,
      slug: blogForm.slug,
      excerpt: blogForm.excerpt,
      content: blogForm.content,
      category_id: blogForm.category_id || null,
      featured_image_url: blogForm.featured_image || null,
      status: blogForm.status,
      seo_title: blogForm.meta_title,
      seo_description: blogForm.meta_description,
    };
    try {
      if (editingPost) {
        const updated = await api(`/api/admin/blog/posts/${editingPost}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        await fetchBlogPosts();
        alert(updated && updated.status === 'published'
          ? 'Saved — this post is now published and live.'
          : 'Draft saved. Set Status to Published and hit Update Post to make it live.');
      } else {
        const created = await api('/api/admin/blog/posts', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        // Stay on the editor for the new post so admin can immediately attach media.
        if (created && created.id) {
          setEditingPost(created.id);
          await fetchBlogPosts();
          alert(created.status === 'published'
            ? 'Post created and published — it is now live.'
            : 'Draft created. You can attach photos/videos, then set Status to Published and hit Update Post to go live.');
          return;
        }
        setEditingPost(null);
        setBlogForm({ title: '', slug: '', excerpt: '', content: '', category_id: '', featured_image: '', status: 'draft', meta_title: '', meta_description: '' });
        await fetchBlogPosts();
      }
    } catch (err) {
      alert(`Could not save the post: ${(err && (err.error || err.message)) || 'unknown error'}${err && err.status ? ` (HTTP ${err.status})` : ''}`);
    }
  };

  const deleteBlogPost = async (id) => {
    if (!window.confirm('Delete this blog post?')) return;
    try {
      await api(`/api/admin/blog/posts/${id}`, { method: 'DELETE' });
      await fetchBlogPosts();
    } catch {}
  };

  useEffect(() => {
    Promise.all([fetchBookings(), fetchBlocked(), fetchExternalCalendars(), fetchChangeRequests(), fetchDashStats(), fetchRevenue(), fetchCustomers(), fetchBlogPosts(), fetchBlogCategories(), fetchOpenhouseConfig()]).finally(() => setLoading(false));
  }, []);

  const updateStatus = async (id, status, denyReasonText) => {
    setActionLoading(id);
    try {
      const body = { status };
      if (denyReasonText) body.deny_reason = denyReasonText;
      await api(`/api/bookings/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      await fetchBookings();
    } catch {}
    setActionLoading(null);
  };

  const removeBlocked = async (id) => {
    setActionLoading(id);
    try {
      await api(`/api/blocked-dates/${id}`, { method: 'DELETE' });
      await fetchBlocked();
    } catch {}
    setActionLoading(null);
  };

  const deleteBooking = async (id) => {
    if (!window.confirm('Permanently delete this booking? This cannot be undone.')) return;
    setActionLoading(id);
    try {
      await api(`/api/bookings/${id}/permanent`, { method: 'DELETE' });
      await fetchBookings();
    } catch {}
    setActionLoading(null);
  };

  const saveEditBooking = async (data) => {
    await api(`/api/bookings/${data.id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    setEditingBooking(null);
    await fetchBookings();
  };

  const handleBlockSubmit = async (e) => {
    e.preventDefault();
    setBlockError('');
    setBlockSuccess('');
    if (!blockStart || !blockEnd) { setBlockError('Start and end dates required'); return; }
    if (blockEnd < blockStart) { setBlockError('End date must be after start date'); return; }
    if (!blockedBy) { setBlockError('Select who is blocking these dates'); return; }
    try {
      await api('/api/block-dates', {
        method: 'POST',
        body: JSON.stringify({ startDate: blockStart, endDate: blockEnd, room: blockRoom, reason: blockReason, blockedBy }),
      });
      setBlockSuccess('Dates blocked');
      setBlockStart(''); setBlockEnd(''); setBlockReason(''); setBlockedBy('');
      await fetchBlocked();
      setTimeout(() => setBlockSuccess(''), 3000);
    } catch (err) {
      setBlockError(err.error || 'Failed to block dates');
    }
  };

  const filteredBookings = bookings.filter((b) => {
    if (statusFilter !== 'all' && b.status !== statusFilter) return false;
    if (roomFilter !== 'all' && b.room !== roomFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const nameMatch = (b.user_name || '').toLowerCase().includes(q);
      const emailMatch = (b.user_email || '').toLowerCase().includes(q);
      const refMatch = (b.booking_ref || '').toLowerCase().includes(q);
      if (!nameMatch && !emailMatch && !refMatch) return false;
    }
    if (dateFrom && b.date < dateFrom) return false;
    if (dateTo && b.date > dateTo) return false;
    return true;
  });

  const exportCSV = () => {
    const headers = ['Date', 'Room', 'Service', 'Start Time', 'Hours', 'Name', 'Email', 'Status', 'Reference', 'Source', 'Description', 'Attendees', 'Notes'];
    const roomLabels = Object.fromEntries(ROOMS.map((r) => [r.id, r.label]));
    const serviceLabels = Object.fromEntries(SERVICES.map((s) => [s.id, s.label]));
    const rows = filteredBookings.map((b) => [
      b.date,
      roomLabels[b.room] || b.room,
      serviceLabels[b.service] || b.service,
      b.start_time || '',
      b.hours || '',
      b.user_name || '',
      b.user_email || '',
      b.status,
      b.booking_ref || b.id?.slice(0, 8).toUpperCase(),
      b.source || 'website',
      (b.description || '').replace(/"/g, '""'),
      b.attendees || '',
      (b.notes || '').replace(/"/g, '""'),
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hddn-lab-bookings-${todayString()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const stats = {
    total: bookings.length,
    pendingApproval: bookings.filter((b) => b.status === 'pending_approval').length,
    confirmed: bookings.filter((b) => b.status === 'confirmed').length,
    pending: bookings.filter((b) => b.status === 'pending').length,
    cancelled: bookings.filter((b) => b.status === 'cancelled').length,
    upcoming: bookings.filter((b) => b.status !== 'cancelled' && b.status !== 'denied' && b.date >= todayString()).length,
  };

  const inputStyle = {
    padding: '8px 10px', fontFamily: fonts, fontSize: 13,
    border: `1px solid ${palette.border}`, background: palette.white,
    color: palette.text, boxSizing: 'border-box', outline: 'none',
  };
  const labelStyle = {
    display: 'block', fontSize: 11, letterSpacing: 1,
    textTransform: 'uppercase', color: palette.textMuted, marginBottom: 4,
  };
  const tabStyle = (active) => ({
    fontFamily: fonts, fontSize: 12, fontWeight: 700, letterSpacing: 1.5,
    textTransform: 'uppercase', padding: '8px 20px',
    background: active ? palette.brown : 'transparent',
    color: active ? palette.cream : palette.textMuted,
    border: 'none', cursor: 'pointer', transition: 'all 0.15s',
  });
  const smallBtnStyle = {
    fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1,
    textTransform: 'uppercase', padding: '5px 12px', border: 'none',
    cursor: 'pointer', transition: 'all 0.15s',
  };

  if (loading) {
    return (
      <div style={baseStyles.app}>
        <Header user={user} onLogout={() => {}} onProfile={() => {}} />
        <div className="hddn-container" style={{ ...baseStyles.container, paddingTop: 60, textAlign: 'center' }}>
          <span style={{ fontFamily: fonts, color: palette.textMuted, letterSpacing: 2 }}>LOADING...</span>
        </div>
      </div>
    );
  }

  // Grouped navigation \u2014 6 sidebar destinations instead of 11. Every old tab
  // id still exists and renders the same pane; groups just decide which tabs
  // share a sidebar slot, and a pill row inside the page switches between a
  // group's tabs.
  const navGroups = [
    { id: 'dashboard', label: 'Dashboard', icon: '\u25A0', tabs: [
      { id: 'dashboard', label: 'Dashboard' },
    ] },
    { id: 'studio', label: 'Studio', icon: '\u2630', tabs: [
      { id: 'bookings', label: 'Bookings', badge: stats.pendingApproval > 0 ? stats.pendingApproval : null },
      { id: 'calendar', label: 'Calendar', badge: changeRequests.length > 0 ? changeRequests.length : null },
      { id: 'jobs', label: 'Custom Jobs' },
      { id: 'calendars', label: 'Calendar Sync' },
    ] },
    { id: 'clientsGroup', label: 'Clients', icon: '\u2B50', tabs: [
      { id: 'clients', label: 'Clients' },
      { id: 'renewals', label: 'Renewals' },
    ] },
    { id: 'teamGroup', label: 'Team', icon: '\uD83C\uDFAC', tabs: [
      { id: 'team', label: 'Team' },
      { id: 'access', label: 'Team Access' },
    ] },
    { id: 'pawsGroup', label: 'Galleries', icon: '\ud83d\udc3e', tabs: [
      { id: 'paws', label: 'Paws & Portraits' },
      { id: 'events', label: 'Event Galleries' },
      { id: 'people', label: 'People' },
    ] },
    { id: 'website', label: 'Website', icon: '\u2699', tabs: [
      { id: 'openhouse', label: 'Open House' },
      { id: 'photos', label: 'Photos' },
      { id: 'blog', label: 'Journal' },
      { id: 'content', label: 'Site Content' },
    ] },
  ];
  const activeGroup = navGroups.find((g) => g.tabs.some((t) => t.id === tab)) || navGroups[0];
  const activeTabDef = activeGroup.tabs.find((t) => t.id === tab) || activeGroup.tabs[0];
  const groupBadge = (g) => {
    const sum = g.tabs.reduce((s, t) => s + (t.badge || 0), 0);
    return sum > 0 ? sum : null;
  };
  const pageTitle = activeGroup.tabs.length > 1 && activeTabDef.label !== activeGroup.label
    ? `${activeGroup.label} \u00B7 ${activeTabDef.label}`
    : activeGroup.label;

  const sidebarWidth = sidebarCollapsed ? 60 : 220;

  // Mobile bottom tab bar — primary groups visible, rest in a "More" sheet
  const mobilePrimaryGroups = ['dashboard', 'studio', 'clientsGroup', 'pawsGroup'];
  const tabIcon = (id) => {
    const stroke = 'currentColor';
    const props = { width: 22, height: 22, viewBox: '0 0 24 24', fill: 'none', stroke, strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round' };
    switch (id) {
      case 'dashboard': return (<svg {...props}><path d="M3 11l9-7 9 7" /><path d="M5 10v9h5v-6h4v6h5v-9" /></svg>);
      case 'studio': return (<svg {...props}><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /><path d="M9 15l2 2 4-4" /></svg>);
      case 'clientsGroup': return (<svg {...props}><circle cx="9" cy="8" r="3.2" /><path d="M3.5 20c.7-3.4 2.8-5 5.5-5s4.8 1.6 5.5 5" /><circle cx="17" cy="9" r="2.4" /><path d="M15.5 14.6c2.6.2 4.3 1.7 4.9 4.4" /></svg>);
      case 'pawsGroup': return (<svg {...props}><circle cx="7" cy="9" r="1.8" /><circle cx="12" cy="7" r="1.8" /><circle cx="17" cy="9" r="1.8" /><path d="M8 16.5c0-2.2 1.8-4 4-4s4 1.8 4 4-1.8 3.5-4 3.5-4-1.3-4-3.5z" /></svg>);
      case 'more': return (<svg {...props}><circle cx="6" cy="12" r="1.4" fill="currentColor" /><circle cx="12" cy="12" r="1.4" fill="currentColor" /><circle cx="18" cy="12" r="1.4" fill="currentColor" /></svg>);
      default: return null;
    }
  };

  // Sub-tab pill nav rendered inside a parent tab (e.g. Dashboard sub-tabs).
  const SubTabs = ({ value, onChange, options }) => (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 22, paddingBottom: 6, borderBottom: `1px solid ${palette.border}` }}>
      {options.map((opt) => {
        const active = value === opt.id;
        return (
          <button
            key={opt.id}
            onClick={() => onChange(opt.id)}
            style={{
              fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase',
              padding: '8px 16px',
              background: active ? palette.brown : 'transparent',
              color: active ? palette.cream : palette.textMuted,
              border: 'none', cursor: 'pointer',
              position: 'relative',
            }}
          >
            {opt.label}
            {opt.badge ? (
              <span style={{
                marginLeft: 6, background: palette.red, color: palette.white,
                fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 999,
              }}>{opt.badge}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );

  const sidebarNavStyle = (active) => ({
    display: 'flex', alignItems: 'center', gap: sidebarCollapsed ? 0 : 10,
    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
    width: '100%', padding: sidebarCollapsed ? '10px 0' : '9px 16px',
    fontFamily: fonts, fontSize: 12, fontWeight: active ? 700 : 400,
    letterSpacing: active ? 1.5 : 1, textTransform: 'uppercase',
    background: active ? palette.orange : 'transparent',
    color: active ? palette.cream : palette.cream + 'cc',
    border: 'none', cursor: 'pointer', transition: 'all 0.15s',
    textAlign: sidebarCollapsed ? 'center' : 'left',
    position: 'relative',
  });

  return (
    <div style={baseStyles.app}>
      <style>{`
        * { box-sizing: border-box; }
        body { margin: 0; }
        .admin-sidebar-btn:hover { background: ${palette.orange}40 !important; }
        .admin-sidebar-btn.active:hover { background: ${palette.orange} !important; }
        @media (max-width: 640px) {
          .admin-sidebar { display: none !important; }
          .admin-mobile-topbar { display: flex !important; }
          .admin-mobile-tabbar { display: block !important; }
          .admin-main { margin-left: 0 !important; padding: 16px 14px calc(env(safe-area-inset-bottom, 0px) + 84px) !important; }
          .admin-page-header { display: none !important; }
          .admin-booking-card { border-radius: 14px !important; padding: 14px 14px 0 !important; box-shadow: 0 1px 3px rgba(60,43,31,0.06) !important; margin-bottom: 12px !important; }
          .admin-booking-row { flex-direction: column !important; align-items: stretch !important; }
          .admin-booking-actions {
            flex-wrap: nowrap !important;
            overflow-x: auto !important;
            -webkit-overflow-scrolling: touch !important;
            margin: 12px -14px 0 !important;
            padding: 0 14px 14px !important;
            scrollbar-width: none;
          }
          .admin-booking-actions::-webkit-scrollbar { display: none; }
          .admin-booking-actions > button {
            flex-shrink: 0 !important;
            border-radius: 999px !important;
            padding: 8px 16px !important;
            font-size: 12px !important;
            min-height: 36px !important;
          }
          .admin-status-pill { background: ${palette.beige}; padding: 3px 10px !important; border-radius: 999px !important; }
          .hddn-sheet-backdrop { align-items: flex-end !important; padding: 0 !important; }
          .hddn-sheet {
            max-width: 100% !important;
            width: 100% !important;
            border-top-left-radius: 20px !important;
            border-top-right-radius: 20px !important;
            border-bottom-left-radius: 0 !important;
            border-bottom-right-radius: 0 !important;
            border: none !important;
            max-height: 88vh !important;
            padding: 22px 18px calc(env(safe-area-inset-bottom, 0px) + 22px) !important;
            animation: hddnSheetIn 0.22s ease-out;
          }
          .hddn-sheet::before {
            content: '';
            display: block;
            width: 40px; height: 4px;
            background: ${palette.border};
            border-radius: 2px;
            margin: -6px auto 14px;
          }
          @keyframes hddnSheetIn {
            from { transform: translateY(100%); }
            to { transform: translateY(0); }
          }
        }
      `}</style>

      {/* Mobile sticky header (lives outside the flex row so it spans full width) */}
      <header className="admin-mobile-topbar" style={{
        display: 'none', position: 'sticky', top: 0, zIndex: 90,
        background: palette.brown, color: palette.cream,
        padding: '14px 16px', borderBottom: `2px solid ${palette.orange}`,
        alignItems: 'center', justifyContent: 'space-between', gap: 12,
      }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: palette.cream, fontFamily: fonts, fontSize: 13, cursor: 'pointer', padding: '4px 6px', letterSpacing: 1 }}>
          ← Back
        </button>
        <div style={{ fontFamily: fonts, fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', textAlign: 'center' }}>
          {pageTitle}
        </div>
        <span style={{ width: 48 }} />
      </header>

      <div style={{ display: 'flex', minHeight: '100vh' }}>
        {/* Sidebar (desktop) */}
        <aside className="admin-sidebar" style={{
          width: sidebarWidth, minHeight: '100vh', background: palette.brown,
          display: 'flex', flexDirection: 'column', transition: 'width 0.2s',
          borderRight: `3px solid ${palette.orange}`, position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 100,
          overflowY: 'auto', overflowX: 'hidden',
        }}>
          {/* Logo */}
          <div style={{ padding: sidebarCollapsed ? '18px 8px' : '18px 16px', borderBottom: `1px solid ${palette.cream}20`, textAlign: sidebarCollapsed ? 'center' : 'left' }}>
            <div style={{ fontFamily: fonts, fontWeight: 700, fontSize: sidebarCollapsed ? 14 : 16, color: palette.cream, letterSpacing: 3, textTransform: 'uppercase' }}>
              {sidebarCollapsed ? 'H' : 'HDDN LAB'}
            </div>
            {!sidebarCollapsed && (
              <div style={{ color: palette.orange, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', marginTop: 2 }}>Admin Panel</div>
            )}
          </div>

          {/* Collapse toggle */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            style={{ background: 'transparent', border: 'none', color: palette.cream + '80', padding: '8px', cursor: 'pointer', fontSize: 14, textAlign: sidebarCollapsed ? 'center' : 'right', width: '100%' }}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? '\u00BB' : '\u00AB'}
          </button>

          {/* Nav items */}
          <nav style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, padding: '4px 0' }}>
            {navGroups.map((group) => {
              const active = activeGroup.id === group.id;
              const badge = groupBadge(group);
              return (
                <button
                  key={group.id}
                  onClick={() => setTab(group.tabs[0].id)}
                  className={`admin-sidebar-btn${active ? ' active' : ''}`}
                  style={sidebarNavStyle(active)}
                  title={sidebarCollapsed ? group.label : undefined}
                >
                  <span style={{ fontSize: 14, width: 20, textAlign: 'center', flexShrink: 0 }}>{group.icon}</span>
                  {!sidebarCollapsed && <span>{group.label}</span>}
                  {badge && (
                    <span style={{
                      background: palette.red, color: palette.white, fontSize: 10, fontWeight: 700,
                      borderRadius: '50%', minWidth: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      marginLeft: sidebarCollapsed ? 0 : 'auto',
                      position: sidebarCollapsed ? 'absolute' : 'static',
                      top: sidebarCollapsed ? 2 : 'auto', right: sidebarCollapsed ? 4 : 'auto',
                    }}>
                      {badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          {/* Bottom actions */}
          <div style={{ padding: sidebarCollapsed ? '12px 4px' : '12px 16px', borderTop: `1px solid ${palette.cream}20` }}>
            <button
              onClick={onBack}
              style={{
                ...smallBtnStyle, width: '100%', padding: '8px',
                background: 'transparent', color: palette.cream, border: `1px solid ${palette.cream}40`,
                fontSize: sidebarCollapsed ? 10 : 11, textAlign: 'center',
              }}
            >
              {sidebarCollapsed ? '\u2190' : '\u2190 Customer View'}
            </button>
          </div>
        </aside>

        {/* Main content */}
        <main className="admin-main" style={{ flex: 1, marginLeft: sidebarWidth, padding: '28px 32px 60px', transition: 'margin-left 0.2s', minHeight: '100vh', maxWidth: 1200 }}>
          {/* Page header (desktop only) */}
          <div className="admin-page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <h2 style={{ fontFamily: fonts, fontSize: 18, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: palette.brown, margin: 0 }}>
              {pageTitle}
            </h2>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <span style={{ fontFamily: fonts, fontSize: 11, color: palette.textMuted, letterSpacing: 1 }}>
                {new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              </span>
            </div>
          </div>

        {/* Group tab row — switches between the tabs sharing this sidebar slot */}
        {activeGroup.tabs.length > 1 && (
          <SubTabs value={tab} onChange={setTab} options={activeGroup.tabs} />
        )}

        {/* Dashboard Tab — sub-tabs: Overview / Revenue / Customers / Messages / Photo Quotes */}
        {tab === 'dashboard' && (
          <SubTabs
            value={dashSubTab}
            onChange={setDashSubTab}
            options={[
              { id: 'overview', label: 'Overview' },
              { id: 'revenue', label: 'Revenue' },
              { id: 'customers', label: 'Customers' },
              { id: 'messages', label: 'Messages' },
              { id: 'photo-quotes', label: 'Photo Quotes' },
            ]}
          />
        )}

        {tab === 'dashboard' && dashSubTab === 'overview' && (
          <div>
            {(() => {
              if (!openhouseConfig) return null;
              const expiresAt = openhouseConfig.expiresAt;
              if (!expiresAt) return null;
              const end = new Date(expiresAt);
              if (Number.isNaN(end.getTime())) return null;
              end.setHours(23, 59, 59, 999);
              const msPerDay = 86400000;
              const diffDays = Math.ceil((end.getTime() - Date.now()) / msPerDay);
              const eventTag = openhouseConfig.eventTag || 'open-house';
              let label, color;
              if (diffDays < 0) {
                label = `Open house "${eventTag}" expired ${Math.abs(diffDays)} day${Math.abs(diffDays) === 1 ? '' : 's'} ago — /openhouse is showing the waitlist signup view. Click to start the next round.`;
                color = palette.red;
              } else if (diffDays === 0) {
                label = `Open house "${eventTag}" expires today — /openhouse will switch to the waitlist signup view at midnight.`;
                color = palette.orange;
              } else if (diffDays <= 7) {
                label = `Open house "${eventTag}" expires in ${diffDays} day${diffDays === 1 ? '' : 's'} — plan the next round.`;
                color = palette.orange;
              } else {
                return null;
              }
              return (
                <div
                  onClick={() => { setTab('openhouse'); }}
                  style={{
                    background: palette.beige, borderLeft: `4px solid ${color}`, border: `1px solid ${palette.border}`,
                    padding: '12px 16px', marginBottom: 18, fontFamily: fonts, fontSize: 13, color: palette.text,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
                  }}
                >
                  <span><strong style={{ color, letterSpacing: 1, textTransform: 'uppercase', fontSize: 11, marginRight: 8 }}>Open House</strong>{label}</span>
                  <span style={{ fontSize: 11, color: palette.textMuted, letterSpacing: 1 }}>Open House &rarr;</span>
                </div>
              );
            })()}
            {/* Quick stats grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14, marginBottom: 28 }}>
              {[
                { label: 'Awaiting Approval', value: stats.pendingApproval, color: palette.orange },
                { label: 'Confirmed', value: stats.confirmed, color: palette.sage },
                { label: 'Upcoming', value: stats.upcoming, color: palette.brown },
                { label: 'Total Bookings', value: stats.total, color: palette.textMuted },
                { label: 'Cancelled', value: stats.cancelled, color: palette.red },
                { label: 'Change Requests', value: changeRequests.length, color: palette.orangeLight },
              ].map((s) => (
                <div key={s.label} style={{ background: palette.white, border: `1px solid ${palette.border}`, padding: '18px 16px', textAlign: 'center', cursor: 'pointer', transition: 'border-color 0.15s' }}
                  onClick={() => { if (s.label === 'Awaiting Approval') { setTab('bookings'); setStatusFilter('pending_approval'); } else if (s.label === 'Change Requests') { setTab('calendar'); setCalSubTab('requests'); } }}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = palette.orange}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = palette.border}
                >
                  <div style={{ fontFamily: fonts, fontSize: 32, fontWeight: 700, color: s.color }}>{s.value}</div>
                  <div style={{ fontFamily: fonts, fontSize: 10, letterSpacing: 1.2, textTransform: 'uppercase', color: palette.textMuted, marginTop: 4 }}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* Revenue snapshot */}
            {dashStats && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 28 }}>
                <div style={{ background: palette.white, border: `1px solid ${palette.border}`, padding: '20px' }}>
                  <h4 style={{ fontFamily: fonts, fontSize: 12, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, margin: '0 0 14px' }}>
                    Revenue (30 Days)
                  </h4>
                  <div style={{ fontFamily: fonts, fontSize: 28, fontWeight: 700, color: palette.sage }}>
                    ${(dashStats.revenue30d / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </div>
                  <div style={{ fontSize: 11, color: palette.textMuted, marginTop: 4 }}>
                    {dashStats.bookings30d || 0} bookings this period
                  </div>
                  <button onClick={() => { setTab('dashboard'); setDashSubTab('revenue'); }} style={{ ...smallBtnStyle, background: palette.beige, color: palette.brown, marginTop: 12, padding: '6px 14px' }}>
                    View Details &rarr;
                  </button>
                </div>
                <div style={{ background: palette.white, border: `1px solid ${palette.border}`, padding: '20px' }}>
                  <h4 style={{ fontFamily: fonts, fontSize: 12, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, margin: '0 0 14px' }}>
                    Today&apos;s Schedule
                  </h4>
                  {(() => {
                    const todayBookings = bookings.filter((b) => b.date === todayString() && b.status !== 'cancelled' && b.status !== 'denied');
                    return todayBookings.length === 0 ? (
                      <div style={{ fontSize: 13, color: palette.textMuted }}>No bookings today</div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {todayBookings.slice(0, 4).map((b) => (
                          <div key={b.id} style={{ fontSize: 12, display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: `1px solid ${palette.border}` }}>
                            <span><strong>{b.start_time ? formatTime(b.start_time) : ''}</strong> {b.user_name || 'Guest'}</span>
                            <span style={{ color: palette.textMuted }}>{ROOMS.find((r) => r.id === b.room)?.label}</span>
                          </div>
                        ))}
                        {todayBookings.length > 4 && <div style={{ fontSize: 11, color: palette.textMuted }}>+{todayBookings.length - 4} more</div>}
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Quick actions */}
            <div style={{ background: palette.white, border: `1px solid ${palette.border}`, padding: '20px' }}>
              <h4 style={{ fontFamily: fonts, fontSize: 12, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, margin: '0 0 14px' }}>
                Quick Actions
              </h4>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button onClick={() => setTab('bookings')} style={{ ...smallBtnStyle, background: palette.brown, color: palette.cream, padding: '8px 16px' }}>
                  View All Bookings
                </button>
                <button onClick={() => { setTab('calendar'); setCalSubTab('blocked'); }} style={{ ...smallBtnStyle, background: palette.orange, color: palette.cream, padding: '8px 16px' }}>
                  Block Dates
                </button>
                <button onClick={() => setTab('blog')} style={{ ...smallBtnStyle, background: palette.sage, color: palette.white, padding: '8px 16px' }}>
                  New Blog Post
                </button>
                <button onClick={() => setTab('content')} style={{ ...smallBtnStyle, background: palette.beige, color: palette.brown, padding: '8px 16px' }}>
                  Edit Site Content
                </button>
                <button onClick={exportCSV} style={{ ...smallBtnStyle, background: palette.beige, color: palette.brown, padding: '8px 16px' }}>
                  Export CSV
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Calendar Tab — sub-tabs: Calendar / Blocked / Change Requests */}
        {tab === 'calendar' && (
          <SubTabs
            value={calSubTab}
            onChange={setCalSubTab}
            options={[
              { id: 'calendar', label: 'Calendar' },
              { id: 'blocked', label: 'Blocked Dates' },
              { id: 'requests', label: 'Change Requests', badge: changeRequests.length > 0 ? changeRequests.length : null },
            ]}
          />
        )}

        {tab === 'calendar' && calSubTab === 'calendar' && (() => {
          // Build booked/blocked data for the calendar
          const confirmedBookings = bookings.filter((b) => b.status !== 'cancelled');
          const calSlots = {};
          const calFullyBooked = [];
          const calBlocked = [];

          // Collect blocked dates for this room
          blockedDates.forEach((bd) => {
            const start = new Date(bd.start_date + 'T00:00:00');
            const end = new Date(bd.end_date + 'T00:00:00');
            for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
              const ds = formatDate(d.getFullYear(), d.getMonth(), d.getDate());
              if (bd.room === 'all' || bd.room === calRoom) {
                if (!calBlocked.includes(ds)) calBlocked.push(ds);
              }
            }
          });

          // Collect booked slots
          confirmedBookings.forEach((b) => {
            if (b.room !== calRoom) return;
            const ds = typeof b.date === 'string' ? b.date.split('T')[0] : b.date;
            if (!calSlots[ds]) calSlots[ds] = [];
            const st = (b.start_time || '10:00').split(':').map(Number);
            calSlots[ds].push({ start: st[0], hours: b.hours || 3 });
          });

          // Determine fully booked days (no contiguous gap of MIN_HOURS available)
          const totalHours = CLOSE_HOUR - OPEN_HOUR;
          Object.entries(calSlots).forEach(([ds, daySlots]) => {
            const bookedHrs = daySlots.reduce((sum, s) => sum + s.hours, 0);
            if (bookedHrs >= totalHours) {
              calFullyBooked.push(ds);
            } else {
              const occupied = new Array(24).fill(false);
              daySlots.forEach((s) => {
                for (let h = s.start; h < s.start + s.hours; h++) occupied[h] = true;
              });
              let maxGap = 0, gap = 0;
              for (let h = OPEN_HOUR; h < CLOSE_HOUR; h++) {
                if (!occupied[h]) { gap++; maxGap = Math.max(maxGap, gap); }
                else gap = 0;
              }
              if (maxGap < MIN_HOURS) calFullyBooked.push(ds);
            }
          });

          // Bookings for selected date
          const selectedBookings = calSelectedDate
            ? confirmedBookings.filter((b) => {
                const ds = typeof b.date === 'string' ? b.date.split('T')[0] : b.date;
                return ds === calSelectedDate && b.room === calRoom;
              })
            : [];

          const calPrev = () => {
            if (calMonth === 0) { setCalMonth(11); setCalYear(calYear - 1); } else setCalMonth(calMonth - 1);
            setCalSelectedDate(null);
          };
          const calNext = () => {
            if (calMonth === 11) { setCalMonth(0); setCalYear(calYear + 1); } else setCalMonth(calMonth + 1);
            setCalSelectedDate(null);
          };

          return (
            <div>
              {/* Room selector */}
              <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: `1px solid ${palette.border}` }}>
                {ROOMS.map((r) => {
                  const active = r.id === calRoom;
                  return (
                    <button
                      key={r.id}
                      onClick={() => { setCalRoom(r.id); setCalSelectedDate(null); }}
                      style={{
                        fontFamily: fonts, fontSize: 12, fontWeight: 700, letterSpacing: 1,
                        textTransform: 'uppercase', padding: '8px 18px',
                        background: active ? palette.orange : 'transparent',
                        color: active ? palette.cream : palette.textMuted,
                        border: 'none', cursor: 'pointer', marginBottom: -1,
                        borderBottom: active ? `2px solid ${palette.orange}` : '1px solid transparent',
                      }}
                    >
                      {r.label}
                    </button>
                  );
                })}
              </div>

              {/* Month nav */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <button onClick={calPrev} style={navBtnStyle}>&#8592;</button>
                <span style={{ fontFamily: fonts, fontSize: 15, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown }}>
                  {MONTH_NAMES[calMonth]} {calYear}
                </span>
                <button onClick={calNext} style={navBtnStyle}>&#8594;</button>
              </div>

              {/* Calendar grid */}
              <Calendar
                year={calYear}
                month={calMonth}
                booked={calFullyBooked}
                blocked={calBlocked}
                slots={calSlots}
                selected={calSelectedDate}
                onSelect={(ds) => setCalSelectedDate(ds)}
              />
              <Legend />

              {/* Selected date bookings */}
              {calSelectedDate && (
                <div style={{ marginTop: 24 }}>
                  <h4 style={{ fontFamily: fonts, fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, margin: '0 0 12px' }}>
                    {calSelectedDate} &mdash; {ROOMS.find((r) => r.id === calRoom)?.label}
                  </h4>
                  {selectedBookings.length === 0 ? (
                    <p style={{ color: palette.textMuted, fontSize: 13 }}>No bookings on this date.</p>
                  ) : (
                    selectedBookings.map((b) => {
                      const serviceLabel = SERVICES.find((s) => s.id === b.service)?.label || b.service;
                      const isLoading = actionLoading === b.id;
                      return (
                        <div key={b.id} style={{ background: palette.white, border: `1px solid ${palette.border}`, padding: '12px 14px', marginBottom: 8, fontSize: 13, lineHeight: 1.7 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                            <div>
                              <strong>{b.start_time ? `${formatTime(b.start_time)} – ${formatEndTime(b.start_time, b.hours)}` : `${b.hours} hrs`}</strong>
                              <span style={{ marginLeft: 8, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', fontWeight: 700, color: b.status === 'confirmed' ? palette.sage : b.status === 'cancelled' ? palette.red : palette.orangeLight }}>{b.status}</span>
                              <span style={{ marginLeft: 8, color: palette.textMuted }}>{serviceLabel}</span>
                              <div style={{ fontSize: 12, color: palette.textMuted }}>{b.user_name || 'Guest'} {b.user_email ? `(${b.user_email})` : ''}</div>
                              {b.description && <div style={{ fontSize: 12, color: palette.textMuted }}>Session: {b.description}</div>}
                              {b.attendees > 0 && <div style={{ fontSize: 12, color: palette.textMuted }}>Attendees: {b.attendees}</div>}
                              <div style={{ fontSize: 11, color: palette.textMuted }}>Ref: {b.booking_ref || b.id?.slice(0, 8).toUpperCase()}</div>
                            </div>
                            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                              <button onClick={() => setEditingBooking(b)} style={{ ...smallBtnStyle, background: palette.brown, color: palette.cream }}>Edit</button>
                              <button onClick={() => deleteBooking(b.id)} disabled={isLoading} style={{ ...smallBtnStyle, background: palette.red, color: palette.white, opacity: isLoading ? 0.5 : 1 }}>Delete</button>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* Bookings Tab */}
        {tab === 'bookings' && (
          <div>
            {/* Filters */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div>
                <label style={labelStyle}>Search</label>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Name, email, or ref..."
                  style={{ ...inputStyle, width: 180 }}
                />
              </div>
              <div>
                <label style={labelStyle}>Status</label>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={inputStyle}>
                  <option value="all">All Statuses</option>
                  <option value="pending_approval">Pending Approval</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="denied">Denied</option>
                  <option value="pending">Pending Payment</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Room</label>
                <select value={roomFilter} onChange={(e) => setRoomFilter(e.target.value)} style={inputStyle}>
                  <option value="all">All Rooms</option>
                  {ROOMS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>From</label>
                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={{ ...inputStyle, width: 140 }} />
              </div>
              <div>
                <label style={labelStyle}>To</label>
                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={{ ...inputStyle, width: 140 }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontFamily: fonts, fontSize: 12, color: palette.textMuted }}>
                  {filteredBookings.length} booking{filteredBookings.length !== 1 ? 's' : ''}
                </span>
                <button
                  onClick={exportCSV}
                  style={{ ...smallBtnStyle, background: palette.brown, color: palette.cream, padding: '7px 14px' }}
                  title="Export filtered bookings as CSV"
                >
                  Export CSV
                </button>
              </div>
            </div>

            {/* Bookings list */}
            {filteredBookings.length === 0 ? (
              <p style={{ color: palette.textMuted, fontSize: 14 }}>No bookings match filters.</p>
            ) : (
              filteredBookings.map((b) => {
                const roomLabel = ROOMS.find((r) => r.id === b.room)?.label || b.room;
                const serviceLabel = SERVICES.find((s) => s.id === b.service)?.label || b.service;
                const isLoading = actionLoading === b.id;
                const statusColor = b.status === 'confirmed' ? palette.sage : b.status === 'cancelled' || b.status === 'denied' ? palette.red : b.status === 'pending_approval' ? palette.orange : palette.orangeLight;
                return (
                  <div
                    key={b.id}
                    className="admin-booking-card"
                    style={{
                      background: palette.white, border: `1px solid ${palette.border}`,
                      padding: '14px 16px', marginBottom: 8, fontSize: 13, lineHeight: 1.7,
                    }}
                  >
                    <div className="admin-booking-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div className="admin-booking-meta" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
                          <strong style={{ fontSize: 14 }}>{b.date}</strong>
                          <span className="admin-status-pill" style={{ fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: statusColor, fontWeight: 700 }}>
                            {b.status === 'pending_approval' ? 'AWAITING APPROVAL' : b.status}
                          </span>
                          <span style={{ fontSize: 10, letterSpacing: 0.5, color: palette.textMuted, background: palette.beige, padding: '1px 6px' }}>
                            {b.source || 'website'}
                          </span>
                        </div>
                        <div>{roomLabel} &mdash; {serviceLabel}</div>
                        <div style={{ fontSize: 12, color: palette.textMuted }}>
                          {b.start_time
                            ? `${formatTime(b.start_time)} – ${formatEndTime(b.start_time, b.hours)} (${b.hours} hrs)`
                            : `${b.hours || MIN_HOURS} hrs`}
                        </div>
                        <div style={{ fontSize: 12, color: palette.textMuted }}>
                          {b.user_name || 'Guest'} {b.user_email ? `(${b.user_email})` : ''}
                        </div>
                        {b.description && <div style={{ fontSize: 12, color: palette.textMuted }}>Session: {b.description}</div>}
                        {b.attendees > 0 && <div style={{ fontSize: 12, color: palette.textMuted }}>Attendees: {b.attendees}</div>}
                        {b.notes && <div style={{ fontSize: 12, color: palette.textMuted, fontStyle: 'italic' }}>Note: {b.notes}</div>}
                        <div style={{ fontSize: 11, color: palette.textMuted }}>Ref: {b.booking_ref || b.id?.slice(0, 8).toUpperCase()}</div>
                      </div>
                      <div className="admin-booking-actions" style={{ display: 'flex', gap: 6, flexShrink: 0, flexWrap: 'wrap' }}>
                        <button
                          onClick={() => setEditingBooking(b)}
                          style={{ ...smallBtnStyle, background: palette.brown, color: palette.cream }}
                        >
                          Edit
                        </button>
                        {b.status === 'pending_approval' && (
                          <button
                            onClick={() => updateStatus(b.id, 'confirmed')}
                            disabled={isLoading}
                            style={{ ...smallBtnStyle, background: palette.sage, color: palette.white, opacity: isLoading ? 0.5 : 1, fontWeight: 700 }}
                          >
                            Approve
                          </button>
                        )}
                        {b.status === 'pending_approval' && (
                          <button
                            onClick={() => setDenyingBooking(b)}
                            disabled={isLoading}
                            style={{ ...smallBtnStyle, background: palette.red, color: palette.white, opacity: isLoading ? 0.5 : 1, fontWeight: 700 }}
                          >
                            Deny
                          </button>
                        )}
                        {b.status !== 'confirmed' && b.status !== 'pending_approval' && (
                          <button
                            onClick={() => updateStatus(b.id, 'confirmed')}
                            disabled={isLoading}
                            style={{ ...smallBtnStyle, background: palette.sage, color: palette.white, opacity: isLoading ? 0.5 : 1 }}
                          >
                            Confirm
                          </button>
                        )}
                        {b.status !== 'cancelled' && b.status !== 'denied' && b.status !== 'pending_approval' && (
                          <button
                            onClick={() => updateStatus(b.id, 'cancelled')}
                            disabled={isLoading}
                            style={{ ...smallBtnStyle, background: palette.red, color: palette.white, opacity: isLoading ? 0.5 : 1 }}
                          >
                            Cancel
                          </button>
                        )}
                        {b.stripe_customer_id && (
                          <button
                            onClick={() => { setChargingBooking(b); setChargeError(''); setChargeSuccess(''); setChargeAmount(''); setChargeDescription(''); }}
                            style={{ ...smallBtnStyle, background: palette.orange, color: palette.cream }}
                          >
                            Charge Card
                          </button>
                        )}
                        <button
                          onClick={() => deleteBooking(b.id)}
                          disabled={isLoading}
                          style={{ ...smallBtnStyle, background: '#5c1a1a', color: palette.white, opacity: isLoading ? 0.5 : 1 }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    {b.payment_status && b.payment_status !== 'none' && (
                      <div style={{ fontSize: 11, color: b.payment_status === 'paid' ? palette.sage : palette.orangeLight, marginTop: 6, letterSpacing: 0.5 }}>
                        Payment: {b.payment_status}{b.amount_paid ? ` — $${(b.amount_paid / 100).toFixed(2)}` : ''}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Incidental Charge Modal */}
        {chargingBooking && (
          <div
            className="hddn-sheet-backdrop"
            style={{
              position: 'fixed', inset: 0, background: 'rgba(60,43,31,0.5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 1000, padding: 20,
            }}
            onClick={() => setChargingBooking(null)}
          >
            <div onClick={(e) => e.stopPropagation()} className="hddn-sheet" style={{
              background: palette.cream, border: `1px solid ${palette.border}`,
              boxShadow: '0 8px 30px rgba(0,0,0,0.15)', width: '100%', maxWidth: 420, padding: '28px 24px',
            }}>
              <h3 style={{ fontFamily: fonts, fontSize: 14, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, margin: '0 0 4px' }}>
                Charge Card on File
              </h3>
              <div style={{ fontSize: 12, color: palette.textMuted, marginBottom: 20 }}>
                {chargingBooking.user_name} &mdash; Ref: {chargingBooking.booking_ref}
              </div>

              {chargeError && (
                <div style={{ background: '#fdf2f0', border: `1px solid ${palette.red}`, color: palette.red, padding: '6px 10px', fontSize: 12, marginBottom: 12 }}>
                  {chargeError}
                </div>
              )}
              {chargeSuccess && (
                <div style={{ background: '#e8f0dc', border: `1px solid ${palette.sage}`, color: palette.sage, padding: '6px 10px', fontSize: 12, marginBottom: 12 }}>
                  {chargeSuccess}
                </div>
              )}

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: palette.textMuted, marginBottom: 4 }}>Reason</label>
                <select
                  value={chargeDescription}
                  onChange={(e) => setChargeDescription(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', fontFamily: fonts, fontSize: 13, border: `1px solid ${palette.border}`, background: palette.white }}
                >
                  <option value="">Select reason...</option>
                  <option value="Overtime charges">Overtime charges</option>
                  <option value="Cleaning fee">Cleaning fee ($100)</option>
                  <option value="Equipment damage">Equipment damage</option>
                  <option value="Studio damage">Studio damage</option>
                  <option value="Other incidental">Other incidental</option>
                </select>
              </div>

              <div style={{ marginBottom: 18 }}>
                <label style={{ display: 'block', fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: palette.textMuted, marginBottom: 4 }}>Amount ($)</label>
                <input
                  type="number"
                  min="1"
                  step="0.01"
                  value={chargeAmount}
                  onChange={(e) => setChargeAmount(e.target.value)}
                  placeholder="100.00"
                  style={{ width: '100%', padding: '8px 10px', fontFamily: fonts, fontSize: 13, border: `1px solid ${palette.border}`, background: palette.white }}
                />
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={chargeIncidental}
                  disabled={chargeLoading || !chargeAmount || !chargeDescription}
                  style={{
                    flex: 1, padding: '10px', fontFamily: fonts, fontSize: 13, fontWeight: 700,
                    letterSpacing: 1, textTransform: 'uppercase',
                    background: palette.orange, color: palette.cream, border: 'none',
                    cursor: chargeLoading || !chargeAmount || !chargeDescription ? 'not-allowed' : 'pointer',
                    opacity: chargeLoading || !chargeAmount || !chargeDescription ? 0.5 : 1,
                  }}
                >
                  {chargeLoading ? 'Charging...' : `Charge $${chargeAmount || '0'}`}
                </button>
                <button
                  onClick={() => setChargingBooking(null)}
                  style={{
                    padding: '10px 20px', fontFamily: fonts, fontSize: 13, fontWeight: 700,
                    letterSpacing: 1, textTransform: 'uppercase',
                    background: 'transparent', color: palette.textMuted,
                    border: `1px solid ${palette.border}`, cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Deny Booking Modal */}
        {denyingBooking && (
          <div
            className="hddn-sheet-backdrop"
            style={{
              position: 'fixed', inset: 0, background: 'rgba(60,43,31,0.5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 1000, padding: 20,
            }}
            onClick={() => { setDenyingBooking(null); setDenyReason(''); }}
          >
            <div onClick={(e) => e.stopPropagation()} className="hddn-sheet" style={{
              background: palette.cream, border: `1px solid ${palette.border}`,
              boxShadow: '0 8px 30px rgba(0,0,0,0.15)', width: '100%', maxWidth: 420, padding: '28px 24px',
            }}>
              <h3 style={{ fontFamily: fonts, fontSize: 14, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.red, margin: '0 0 4px' }}>
                Deny Booking Request
              </h3>
              <div style={{ fontSize: 12, color: palette.textMuted, marginBottom: 20 }}>
                {denyingBooking.user_name} &mdash; {denyingBooking.date} &mdash; Ref: {denyingBooking.booking_ref}
              </div>

              <div style={{ marginBottom: 18 }}>
                <label style={{ display: 'block', fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: palette.textMuted, marginBottom: 4 }}>Reason (optional — sent to client)</label>
                <textarea
                  value={denyReason}
                  onChange={(e) => setDenyReason(e.target.value)}
                  placeholder="e.g. Date unavailable, session type not supported..."
                  rows={3}
                  style={{ width: '100%', padding: '8px 10px', fontFamily: fonts, fontSize: 13, border: `1px solid ${palette.border}`, background: palette.white, boxSizing: 'border-box', resize: 'vertical' }}
                />
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={async () => {
                    await updateStatus(denyingBooking.id, 'denied', denyReason);
                    setDenyingBooking(null);
                    setDenyReason('');
                  }}
                  style={{
                    flex: 1, padding: '10px', fontFamily: fonts, fontSize: 13, fontWeight: 700,
                    letterSpacing: 1, textTransform: 'uppercase',
                    background: palette.red, color: palette.white, border: 'none', cursor: 'pointer',
                  }}
                >
                  Deny Booking
                </button>
                <button
                  onClick={() => { setDenyingBooking(null); setDenyReason(''); }}
                  style={{
                    padding: '10px 20px', fontFamily: fonts, fontSize: 13, fontWeight: 700,
                    letterSpacing: 1, textTransform: 'uppercase',
                    background: 'transparent', color: palette.textMuted,
                    border: `1px solid ${palette.border}`, cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Blocked Dates Tab */}
        {tab === 'calendar' && calSubTab === 'blocked' && (
          <div>
            <CalendarSubscribePanel palette={palette} fonts={fonts} />
            {/* Block dates form */}
            <div style={{ background: palette.white, border: `1px solid ${palette.border}`, padding: '20px', marginBottom: 24 }}>
              <h4 style={{ fontFamily: fonts, fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, margin: '0 0 16px' }}>
                Block Date Range
              </h4>
              {blockError && (
                <div style={{ background: '#fdf2f0', border: `1px solid ${palette.red}`, color: palette.red, padding: '6px 10px', fontSize: 12, marginBottom: 12 }}>
                  {blockError}
                </div>
              )}
              {blockSuccess && (
                <div style={{ background: '#f0fdf2', border: `1px solid ${palette.sage}`, color: palette.sage, padding: '6px 10px', fontSize: 12, marginBottom: 12 }}>
                  {blockSuccess}
                </div>
              )}
              <form onSubmit={handleBlockSubmit}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 12 }}>
                  <div>
                    <label style={labelStyle}>Start Date *</label>
                    <input type="date" value={blockStart} onChange={(e) => setBlockStart(e.target.value)} required style={{ ...inputStyle, width: '100%' }} />
                  </div>
                  <div>
                    <label style={labelStyle}>End Date *</label>
                    <input type="date" value={blockEnd} onChange={(e) => setBlockEnd(e.target.value)} required style={{ ...inputStyle, width: '100%' }} />
                  </div>
                  <div>
                    <label style={labelStyle}>Room</label>
                    <select value={blockRoom} onChange={(e) => setBlockRoom(e.target.value)} style={{ ...inputStyle, width: '100%' }}>
                      <option value="all">All Rooms</option>
                      {ROOMS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Blocked By *</label>
                    <select value={blockedBy} onChange={(e) => setBlockedBy(e.target.value)} required style={{ ...inputStyle, width: '100%' }}>
                      <option value="">Select...</option>
                      <option value="Tutes">Tutes</option>
                      <option value="Donnell">Donnell</option>
                      <option value="Kevin">Kevin</option>
                    </select>
                  </div>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={labelStyle}>Reason</label>
                  <input type="text" value={blockReason} onChange={(e) => setBlockReason(e.target.value)} placeholder="e.g. Private event, maintenance..." style={{ ...inputStyle, width: '100%' }} />
                </div>
                <button type="submit" style={{ ...smallBtnStyle, background: palette.orange, color: palette.cream, padding: '8px 20px' }}>
                  Block Dates
                </button>
              </form>
            </div>

            {/* Blocked dates list */}
            <h4 style={{ fontFamily: fonts, fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, margin: '0 0 12px' }}>
              Active Blocks
            </h4>
            {blockedDates.length === 0 ? (
              <p style={{ color: palette.textMuted, fontSize: 14 }}>No blocked dates.</p>
            ) : (
              blockedDates.map((bd) => (
                <div
                  key={bd.id}
                  style={{
                    background: palette.white, border: `1px solid ${palette.border}`,
                    padding: '12px 16px', marginBottom: 8, fontSize: 13,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}
                >
                  <div>
                    <strong>{bd.start_date}</strong> &mdash; <strong>{bd.end_date}</strong>
                    <span style={{ fontSize: 11, color: palette.textMuted, marginLeft: 10 }}>
                      [{bd.room === 'all' ? 'All Rooms' : ROOMS.find((r) => r.id === bd.room)?.label || bd.room}]
                    </span>
                    {bd.blocked_by && (
                      <span style={{ fontSize: 11, fontWeight: 700, color: palette.orange, marginLeft: 8, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                        by {bd.blocked_by}
                      </span>
                    )}
                    {bd.reason && <span style={{ fontSize: 12, color: palette.textMuted, marginLeft: 8, fontStyle: 'italic' }}>{bd.reason}</span>}
                  </div>
                  <button
                    onClick={() => removeBlocked(bd.id)}
                    disabled={actionLoading === bd.id}
                    style={{ ...smallBtnStyle, background: palette.red, color: palette.white, opacity: actionLoading === bd.id ? 0.5 : 1 }}
                  >
                    Remove
                  </button>
                </div>
              ))
            )}
          </div>
        )}

        {/* Calendar Sync Tab */}
        {tab === 'calendars' && (
          <div>
            {/* iCal Export */}
            <PlatformFeedPanel palette={palette} fonts={fonts} />

            {/* iCal Import */}
            <div style={{ background: palette.white, border: `1px solid ${palette.border}`, padding: '20px', marginBottom: 24 }}>
              <h4 style={{ fontFamily: fonts, fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, margin: '0 0 12px' }}>
                Import External Calendars
              </h4>
              <p style={{ fontSize: 12, color: palette.textMuted, margin: '0 0 16px' }}>
                Add iCal feed URLs from Giggster, Peerspace, or other platforms. HDDN LAB will sync every 15 minutes and block overlapping times.
              </p>

              {calSyncError && (
                <div style={{ background: '#fdf2f0', border: `1px solid ${palette.red}`, color: palette.red, padding: '6px 10px', fontSize: 12, marginBottom: 12 }}>
                  {calSyncError}
                </div>
              )}
              {calSyncSuccess && (
                <div style={{ background: '#e8f0dc', border: `1px solid ${palette.sage}`, color: palette.sage, padding: '6px 10px', fontSize: 12, marginBottom: 12 }}>
                  {calSyncSuccess}
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: palette.textMuted, marginBottom: 4 }}>Name</label>
                  <input
                    value={newCalName}
                    onChange={(e) => setNewCalName(e.target.value)}
                    placeholder="e.g. Giggster Main"
                    style={{ width: '100%', padding: '8px 10px', fontFamily: fonts, fontSize: 12, border: `1px solid ${palette.border}`, background: palette.white }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: palette.textMuted, marginBottom: 4 }}>Platform</label>
                  <select
                    value={newCalPlatform}
                    onChange={(e) => setNewCalPlatform(e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', fontFamily: fonts, fontSize: 12, border: `1px solid ${palette.border}`, background: palette.white }}
                  >
                    <option value="">Select...</option>
                    <option value="giggster">Giggster</option>
                    <option value="peerspace">Peerspace</option>
                    <option value="airbnb">Airbnb</option>
                    <option value="google">Google Calendar</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, marginBottom: 14 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: palette.textMuted, marginBottom: 4 }}>iCal Feed URL</label>
                  <input
                    value={newCalUrl}
                    onChange={(e) => setNewCalUrl(e.target.value)}
                    placeholder="https://..."
                    style={{ width: '100%', padding: '8px 10px', fontFamily: fonts, fontSize: 12, border: `1px solid ${palette.border}`, background: palette.white }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: palette.textMuted, marginBottom: 4 }}>Room</label>
                  <select
                    value={newCalRoom}
                    onChange={(e) => setNewCalRoom(e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', fontFamily: fonts, fontSize: 12, border: `1px solid ${palette.border}`, background: palette.white }}
                  >
                    <option value="all">All Rooms</option>
                    {ROOMS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                  </select>
                </div>
              </div>
              <button
                onClick={addExternalCalendar}
                disabled={!newCalName || !newCalPlatform || !newCalUrl}
                style={{
                  padding: '10px 24px', fontFamily: fonts, fontSize: 12, fontWeight: 700,
                  letterSpacing: 1, textTransform: 'uppercase',
                  background: palette.sage, color: palette.white, border: 'none',
                  cursor: !newCalName || !newCalPlatform || !newCalUrl ? 'not-allowed' : 'pointer',
                  opacity: !newCalName || !newCalPlatform || !newCalUrl ? 0.5 : 1,
                }}
              >
                Add Calendar Feed
              </button>
            </div>

            {/* Connected Calendars */}
            {extCalendars.length > 0 && (
              <div style={{ background: palette.white, border: `1px solid ${palette.border}`, padding: '20px' }}>
                <h4 style={{ fontFamily: fonts, fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, margin: '0 0 16px' }}>
                  Connected Feeds ({extCalendars.length})
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {extCalendars.map((cal) => {
                    const roomLabel = ROOMS.find((r) => r.id === cal.room)?.label || (cal.room === 'all' ? 'All Rooms' : cal.room);
                    return (
                      <div key={cal.id} style={{ padding: '12px 14px', background: palette.beige, border: `1px solid ${palette.border}`, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: 180 }}>
                          <div style={{ fontFamily: fonts, fontSize: 13, fontWeight: 700, color: palette.brown }}>
                            {cal.name}
                            <span style={{ fontWeight: 400, fontSize: 11, marginLeft: 8, color: palette.textMuted, textTransform: 'capitalize' }}>
                              {cal.platform}
                            </span>
                          </div>
                          <div style={{ fontSize: 11, color: palette.textMuted, marginTop: 2 }}>
                            {roomLabel} &bull; {cal.last_synced_at
                              ? `Last synced: ${new Date(cal.last_synced_at).toLocaleString()}`
                              : 'Not synced yet'}
                          </div>
                          {cal.last_sync_error && (
                            <div style={{ fontSize: 11, color: palette.red, marginTop: 2 }}>
                              Error: {cal.last_sync_error}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            onClick={() => syncCalendar(cal.id)}
                            style={{ ...smallBtnStyle, background: palette.brown, color: palette.cream }}
                          >
                            Sync Now
                          </button>
                          <button
                            onClick={() => deleteExternalCalendar(cal.id)}
                            style={{ ...smallBtnStyle, background: palette.red, color: palette.white }}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Setup instructions */}
            <div style={{ marginTop: 24, padding: '16px 20px', background: palette.beige, border: `1px solid ${palette.border}` }}>
              <h4 style={{ fontFamily: fonts, fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: palette.brown, margin: '0 0 10px' }}>
                Setup Guide
              </h4>
              <div style={{ fontSize: 12, color: palette.textMuted, lineHeight: 1.6 }}>
                <p style={{ margin: '0 0 8px' }}><strong>Giggster:</strong> Go to your listing &rarr; Calendar &rarr; Import Calendar &rarr; paste the HDDN LAB iCal URL above. Then copy Giggster's export URL and add it here.</p>
                <p style={{ margin: '0 0 8px' }}><strong>Peerspace:</strong> Go to your listing &rarr; Availability &rarr; Sync Calendar &rarr; paste the HDDN LAB iCal URL. Copy Peerspace's iCal export URL and add it here.</p>
                <p style={{ margin: 0 }}><strong>Two-way sync:</strong> Add HDDN LAB's URL to the platform AND add the platform's URL here. This ensures double bookings are prevented on both sides.</p>
              </div>
            </div>
          </div>
        )}

        {/* Clients CRM Tab */}
        {(tab === 'clients' || tab === 'renewals') && (
          <AdminChunk><CRMSection view={tab === 'renewals' ? 'renewals' : 'clients'} onViewChange={(v) => setTab(v)} /></AdminChunk>
        )}

        {/* Team — contractor roster, outreach, gigs, contracts & NDAs */}
        {tab === 'team' && <AdminChunk><TeamSection /></AdminChunk>}

        {tab === 'access' && <TeamAccessSection currentUser={user} />}

        {/* Custom Jobs Tab — hand-quoted one-off shoots */}
        {tab === 'jobs' && <CustomJobsTab palette={palette} fonts={fonts} />}

        {/* Paws & Portraits signups Tab */}
        {tab === 'paws' && <PawsPortraitsTab palette={palette} fonts={fonts} />}

        {/* Event galleries — free portraits from events we cover */}
        {tab === 'events' && <EventGalleriesPanel palette={palette} fonts={fonts} />}
        {tab === 'people' && <PeoplePanel palette={palette} fonts={fonts} />}

        {/* Photos Tab */}
        {tab === 'photos' && <AdminPhotoManager />}

        {/* Change Requests Tab */}
        {tab === 'calendar' && calSubTab === 'requests' && (
          <div>
            <h3 style={{ fontFamily: fonts, fontSize: 14, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, marginTop: 0, marginBottom: 16 }}>
              Pending Change Requests
            </h3>

            {changeRequests.length === 0 ? (
              <p style={{ color: palette.textMuted, fontSize: 14 }}>No pending change requests.</p>
            ) : (
              changeRequests.map((cr) => {
                const roomLabel = ROOMS.find((r) => r.id === cr.room)?.label || cr.room;
                const serviceLabel = SERVICES.find((s) => s.id === cr.service)?.label || cr.service;
                const isLoading = crLoading === cr.id;
                const changes = typeof cr.requested_changes === 'string' ? JSON.parse(cr.requested_changes) : (cr.requested_changes || {});

                return (
                  <div key={cr.id} style={{ background: palette.white, border: `1px solid ${palette.border}`, padding: '16px', marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                      <div>
                        <span style={{
                          fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', fontWeight: 700,
                          color: cr.change_type === 'cancel' ? palette.red : palette.orange,
                          background: cr.change_type === 'cancel' ? '#fdf2f0' : '#fef7ed',
                          padding: '2px 8px',
                        }}>
                          {cr.change_type === 'cancel' ? 'CANCELLATION' : 'MODIFICATION'}
                        </span>
                        <span style={{ fontSize: 11, color: palette.textMuted, marginLeft: 8 }}>
                          {new Date(cr.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 700, color: palette.brown }}>
                        {cr.booking_ref}
                      </span>
                    </div>

                    <div style={{ fontSize: 13, lineHeight: 1.8, marginBottom: 10 }}>
                      <div><strong>Client:</strong> {cr.user_name} ({cr.user_email})</div>
                      <div><strong>Current Booking:</strong> {cr.date} &mdash; {roomLabel} &mdash; {serviceLabel}</div>
                      <div><strong>Time:</strong> {cr.start_time ? `${formatTime(cr.start_time)} – ${formatEndTime(cr.start_time, cr.hours)} (${cr.hours} hrs)` : `${cr.hours} hrs`}</div>
                      {cr.reason && <div><strong>Reason:</strong> {cr.reason}</div>}
                    </div>

                    {cr.change_type === 'modify' && Object.keys(changes).length > 0 && (
                      <div style={{ background: palette.beige, padding: '8px 12px', marginBottom: 10, fontSize: 13 }}>
                        <strong>Requested Changes:</strong>
                        {changes.date && <div>New Date: {changes.date}</div>}
                        {changes.startTime && <div>New Start Time: {formatTime(changes.startTime)}</div>}
                        {changes.hours && <div>New Duration: {changes.hours} hours</div>}
                      </div>
                    )}

                    <div style={{ marginBottom: 10 }}>
                      <label style={{ display: 'block', fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: palette.textMuted, marginBottom: 4 }}>
                        Note to Customer (optional)
                      </label>
                      <input
                        type="text"
                        value={crLoading === cr.id ? '' : crAdminNote}
                        onChange={(e) => setCrAdminNote(e.target.value)}
                        placeholder="e.g. Approved - please note our cancellation policy"
                        style={{ width: '100%', padding: '8px 10px', fontFamily: fonts, fontSize: 13, border: `1px solid ${palette.border}`, background: palette.white, color: palette.text, boxSizing: 'border-box', outline: 'none' }}
                      />
                    </div>

                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={() => resolveChangeRequest(cr.id, 'approved')}
                        disabled={isLoading}
                        style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '6px 16px', border: 'none', cursor: isLoading ? 'wait' : 'pointer', background: palette.sage, color: palette.white, opacity: isLoading ? 0.6 : 1 }}
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => resolveChangeRequest(cr.id, 'denied')}
                        disabled={isLoading}
                        style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '6px 16px', border: 'none', cursor: isLoading ? 'wait' : 'pointer', background: palette.red, color: palette.white, opacity: isLoading ? 0.6 : 1 }}
                      >
                        Deny
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Content Editor Tab */}
        {tab === 'content' && <AdminContentEditor onPackagesConfigSaved={onPackagesConfigSaved} />}

        {tab === 'openhouse' && <AdminContentEditor onPackagesConfigSaved={onPackagesConfigSaved} defaultSection="openhouse_settings" hideSectionNav />}

        {/* Revenue Tab */}
        {tab === 'dashboard' && dashSubTab === 'revenue' && (
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
              {[{ v: '7', l: '7 Days' }, { v: '30', l: '30 Days' }, { v: '90', l: '90 Days' }, { v: '365', l: '1 Year' }].map((p) => (
                <button key={p.v} onClick={() => { setRevPeriod(p.v); fetchRevenue(p.v); }}
                  style={{ ...smallBtnStyle, background: revPeriod === p.v ? palette.brown : palette.beige, color: revPeriod === p.v ? palette.cream : palette.brown, padding: '7px 14px' }}>
                  {p.l}
                </button>
              ))}
            </div>

            {revenueData ? (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 24 }}>
                  <div style={{ background: palette.white, border: `1px solid ${palette.border}`, padding: '18px 16px', textAlign: 'center' }}>
                    <div style={{ fontFamily: fonts, fontSize: 28, fontWeight: 700, color: palette.sage }}>
                      ${(revenueData.totalRevenue / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </div>
                    <div style={{ fontFamily: fonts, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: palette.textMuted, marginTop: 4 }}>Total Revenue</div>
                  </div>
                  <div style={{ background: palette.white, border: `1px solid ${palette.border}`, padding: '18px 16px', textAlign: 'center' }}>
                    <div style={{ fontFamily: fonts, fontSize: 28, fontWeight: 700, color: palette.brown }}>
                      {revenueData.totalBookings || 0}
                    </div>
                    <div style={{ fontFamily: fonts, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: palette.textMuted, marginTop: 4 }}>Paid Bookings</div>
                  </div>
                  <div style={{ background: palette.white, border: `1px solid ${palette.border}`, padding: '18px 16px', textAlign: 'center' }}>
                    <div style={{ fontFamily: fonts, fontSize: 28, fontWeight: 700, color: palette.orange }}>
                      ${revenueData.totalBookings > 0 ? ((revenueData.totalRevenue / 100) / revenueData.totalBookings).toFixed(0) : '0'}
                    </div>
                    <div style={{ fontFamily: fonts, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: palette.textMuted, marginTop: 4 }}>Avg Per Booking</div>
                  </div>
                </div>

                {/* Platform breakdown */}
                {revenueData.byPlatform && revenueData.byPlatform.length > 0 && (
                  <div style={{ background: palette.white, border: `1px solid ${palette.border}`, padding: '20px', marginBottom: 20 }}>
                    <h4 style={{ fontFamily: fonts, fontSize: 12, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, margin: '0 0 14px' }}>
                      Revenue by Source
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {revenueData.byPlatform.map((p) => {
                        const pct = revenueData.totalRevenue > 0 ? (p.revenue / revenueData.totalRevenue * 100) : 0;
                        return (
                          <div key={p.source} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <span style={{ fontFamily: fonts, fontSize: 12, fontWeight: 700, textTransform: 'capitalize', width: 100, color: palette.brown }}>{p.source}</span>
                            <div style={{ flex: 1, height: 20, background: palette.beige, position: 'relative' }}>
                              <div style={{ height: '100%', width: `${pct}%`, background: palette.sage, transition: 'width 0.3s' }} />
                            </div>
                            <span style={{ fontFamily: fonts, fontSize: 12, fontWeight: 700, width: 80, textAlign: 'right', color: palette.brown }}>
                              ${(p.revenue / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </span>
                            <span style={{ fontSize: 11, color: palette.textMuted, width: 40, textAlign: 'right' }}>
                              {pct.toFixed(0)}%
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Monthly comparison */}
                {revenueData.monthly && revenueData.monthly.length > 0 && (
                  <div style={{ background: palette.white, border: `1px solid ${palette.border}`, padding: '20px' }}>
                    <h4 style={{ fontFamily: fonts, fontSize: 12, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, margin: '0 0 14px' }}>
                      Monthly Breakdown
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {revenueData.monthly.map((m) => (
                        <div key={m.month} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${palette.border}`, fontSize: 13 }}>
                          <span style={{ fontWeight: 700, color: palette.brown }}>{m.month}</span>
                          <span>{m.count} bookings</span>
                          <span style={{ fontWeight: 700, color: palette.sage }}>${(m.revenue / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ color: palette.textMuted, fontSize: 13, textAlign: 'center', padding: 40 }}>Loading revenue data...</div>
            )}
          </div>
        )}

        {/* Messages Tab */}
        {tab === 'dashboard' && dashSubTab === 'messages' && <MessagesTab palette={palette} fonts={fonts} labelStyle={labelStyle} inputStyle={inputStyle} />}

        {/* Photographer Quote Requests Tab */}
        {tab === 'dashboard' && dashSubTab === 'photo-quotes' && <PhotoQuotesTab palette={palette} fonts={fonts} />}

        {/* Customers Tab */}
        {tab === 'dashboard' && dashSubTab === 'customers' && (
          <div>
            {selectedCustomer ? (
              <div>
                <button onClick={() => { setSelectedCustomer(null); setCustDetail(null); }} style={{ ...smallBtnStyle, background: palette.beige, color: palette.brown, padding: '6px 14px', marginBottom: 16 }}>
                  &larr; Back to Customers
                </button>
                {custDetail ? (
                  <div>
                    <div style={{ background: palette.white, border: `1px solid ${palette.border}`, padding: '20px', marginBottom: 20 }}>
                      <h3 style={{ fontFamily: fonts, fontSize: 16, fontWeight: 700, color: palette.brown, margin: '0 0 4px' }}>
                        {custDetail.customer.user_name || 'Guest'}
                      </h3>
                      <div style={{ fontSize: 13, color: palette.textMuted, marginBottom: 16 }}>{custDetail.customer.user_email}</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12 }}>
                        <div style={{ textAlign: 'center', padding: '10px', background: palette.beige }}>
                          <div style={{ fontFamily: fonts, fontSize: 22, fontWeight: 700, color: palette.brown }}>{custDetail.customer.total_bookings}</div>
                          <div style={{ fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: palette.textMuted }}>Bookings</div>
                        </div>
                        <div style={{ textAlign: 'center', padding: '10px', background: palette.beige }}>
                          <div style={{ fontFamily: fonts, fontSize: 22, fontWeight: 700, color: palette.sage }}>
                            ${((custDetail.customer.total_spent || 0) / 100).toFixed(0)}
                          </div>
                          <div style={{ fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: palette.textMuted }}>Total Spent</div>
                        </div>
                        <div style={{ textAlign: 'center', padding: '10px', background: palette.beige }}>
                          <div style={{ fontFamily: fonts, fontSize: 12, fontWeight: 700, color: palette.brown }}>
                            {custDetail.customer.last_booking ? new Date(custDetail.customer.last_booking).toLocaleDateString() : 'N/A'}
                          </div>
                          <div style={{ fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: palette.textMuted }}>Last Booking</div>
                        </div>
                      </div>
                    </div>

                    {/* Booking history */}
                    <div style={{ background: palette.white, border: `1px solid ${palette.border}`, padding: '20px', marginBottom: 20 }}>
                      <h4 style={{ fontFamily: fonts, fontSize: 12, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, margin: '0 0 14px' }}>
                        Booking History
                      </h4>
                      {(custDetail.bookings || []).length === 0 ? (
                        <div style={{ fontSize: 13, color: palette.textMuted }}>No bookings found</div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {custDetail.bookings.map((b) => (
                            <div key={b.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px', background: palette.beige, fontSize: 12, alignItems: 'center' }}>
                              <span><strong>{b.date}</strong> &mdash; {ROOMS.find((r) => r.id === b.room)?.label || b.room}</span>
                              <span style={{ color: b.status === 'confirmed' ? palette.sage : b.status === 'cancelled' ? palette.red : palette.orange, fontWeight: 700, textTransform: 'uppercase', fontSize: 10, letterSpacing: 1 }}>
                                {b.status}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Notes */}
                    <div style={{ background: palette.white, border: `1px solid ${palette.border}`, padding: '20px' }}>
                      <h4 style={{ fontFamily: fonts, fontSize: 12, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, margin: '0 0 14px' }}>
                        Notes
                      </h4>
                      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                        <input
                          value={custNoteText}
                          onChange={(e) => setCustNoteText(e.target.value)}
                          placeholder="Add a note about this customer..."
                          style={{ flex: 1, ...inputStyle }}
                          onKeyDown={(e) => { if (e.key === 'Enter') addCustomerNote(custDetail.customer.user_email); }}
                        />
                        <button onClick={() => addCustomerNote(custDetail.customer.user_email)} style={{ ...smallBtnStyle, background: palette.brown, color: palette.cream, padding: '8px 16px' }}>
                          Add
                        </button>
                      </div>
                      {(custDetail.notes || []).length === 0 ? (
                        <div style={{ fontSize: 13, color: palette.textMuted }}>No notes yet</div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {custDetail.notes.map((n) => (
                            <div key={n.id} style={{ padding: '8px 10px', background: palette.beige, fontSize: 12 }}>
                              <div style={{ color: palette.textMuted, fontSize: 11, marginBottom: 2 }}>{new Date(n.created_at).toLocaleString()}</div>
                              <div>{n.note}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div style={{ color: palette.textMuted, fontSize: 13, textAlign: 'center', padding: 40 }}>Loading customer details...</div>
                )}
              </div>
            ) : (
              <div>
                <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div>
                    <label style={labelStyle}>Search</label>
                    <input
                      value={custSearch}
                      onChange={(e) => setCustSearch(e.target.value)}
                      placeholder="Name or email..."
                      style={{ ...inputStyle, width: 220 }}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Sort</label>
                    <select value={custSort} onChange={(e) => setCustSort(e.target.value)} style={inputStyle}>
                      <option value="recent">Most Recent</option>
                      <option value="bookings">Most Bookings</option>
                      <option value="spent">Most Spent</option>
                      <option value="name">Name A-Z</option>
                    </select>
                  </div>
                  <span style={{ fontFamily: fonts, fontSize: 12, color: palette.textMuted }}>
                    {customers.length} customer{customers.length !== 1 ? 's' : ''}
                  </span>
                </div>

                {(() => {
                  let filtered = customers.filter((c) => {
                    if (!custSearch) return true;
                    const q = custSearch.toLowerCase();
                    return (c.user_name || '').toLowerCase().includes(q) || (c.user_email || '').toLowerCase().includes(q);
                  });
                  filtered.sort((a, b) => {
                    if (custSort === 'bookings') return (b.total_bookings || 0) - (a.total_bookings || 0);
                    if (custSort === 'spent') return (b.total_spent || 0) - (a.total_spent || 0);
                    if (custSort === 'name') return (a.user_name || '').localeCompare(b.user_name || '');
                    return new Date(b.last_booking || 0) - new Date(a.last_booking || 0);
                  });
                  return filtered.length === 0 ? (
                    <div style={{ color: palette.textMuted, fontSize: 13, textAlign: 'center', padding: 40 }}>No customers found</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {filtered.map((c) => (
                        <div
                          key={c.user_email}
                          onClick={() => { setSelectedCustomer(c.user_email); fetchCustomerDetail(c.user_email); }}
                          style={{
                            background: palette.white, border: `1px solid ${palette.border}`, padding: '14px 16px',
                            cursor: 'pointer', transition: 'border-color 0.15s', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.borderColor = palette.orange}
                          onMouseLeave={(e) => e.currentTarget.style.borderColor = palette.border}
                        >
                          <div>
                            <div style={{ fontFamily: fonts, fontWeight: 700, fontSize: 13, color: palette.brown }}>{c.user_name || 'Guest'}</div>
                            <div style={{ fontSize: 12, color: palette.textMuted }}>{c.user_email}</div>
                          </div>
                          <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontFamily: fonts, fontSize: 16, fontWeight: 700, color: palette.brown }}>{c.total_bookings || 0}</div>
                              <div style={{ fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', color: palette.textMuted }}>Bookings</div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontFamily: fonts, fontSize: 16, fontWeight: 700, color: palette.sage }}>${((c.total_spent || 0) / 100).toFixed(0)}</div>
                              <div style={{ fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', color: palette.textMuted }}>Spent</div>
                            </div>
                            <span style={{ fontSize: 14, color: palette.textMuted }}>&rarr;</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {/* Blog Admin Tab */}
        {tab === 'blog' && (
          <div>
            {editingPost !== null ? (
              <div>
                <button onClick={() => { setEditingPost(null); setBlogForm({ title: '', slug: '', excerpt: '', content: '', category_id: '', featured_image: '', status: 'draft', meta_title: '', meta_description: '' }); }}
                  style={{ ...smallBtnStyle, background: palette.beige, color: palette.brown, padding: '6px 14px', marginBottom: 16 }}>
                  &larr; Back to Posts
                </button>
                <div style={{ background: palette.white, border: `1px solid ${palette.border}`, padding: '24px' }}>
                  <h3 style={{ fontFamily: fonts, fontSize: 14, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, margin: '0 0 20px' }}>
                    {editingPost ? 'Edit Post' : 'New Post'}
                  </h3>
                  <div style={{ display: 'grid', gap: 14 }}>
                    <div>
                      <label style={labelStyle}>Title *</label>
                      <input
                        value={blogForm.title}
                        onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value, slug: blogForm.slug || e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') })}
                        style={{ ...inputStyle, width: '100%' }}
                        placeholder="Post title"
                      />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                      <div>
                        <label style={labelStyle}>Slug</label>
                        <input
                          value={blogForm.slug}
                          onChange={(e) => setBlogForm({ ...blogForm, slug: e.target.value })}
                          style={{ ...inputStyle, width: '100%' }}
                          placeholder="url-friendly-slug"
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Category</label>
                        <select
                          value={blogForm.category_id}
                          onChange={(e) => setBlogForm({ ...blogForm, category_id: e.target.value })}
                          style={{ ...inputStyle, width: '100%' }}
                        >
                          <option value="">No Category</option>
                          {blogCategories.map((cat) => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label style={labelStyle}>Excerpt</label>
                      <textarea
                        value={blogForm.excerpt}
                        onChange={(e) => setBlogForm({ ...blogForm, excerpt: e.target.value })}
                        rows={2}
                        style={{ ...inputStyle, width: '100%', resize: 'vertical' }}
                        placeholder="Brief summary for listings..."
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Description (optional, supports HTML)</label>
                      <textarea
                        value={blogForm.content}
                        onChange={(e) => setBlogForm({ ...blogForm, content: e.target.value })}
                        rows={6}
                        style={{ ...inputStyle, width: '100%', resize: 'vertical', fontFamily: 'monospace', fontSize: 12 }}
                        placeholder="A short blurb to set the scene. Photos do most of the talking."
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Gallery (photos &amp; videos)</label>
                      <BlogPostGalleryEditor postId={typeof editingPost === 'string' ? editingPost : null} />
                    </div>
                    <div>
                      <label style={labelStyle}>Featured Image URL (optional override — leave blank to use the first gallery item as cover)</label>
                      <input
                        value={blogForm.featured_image}
                        onChange={(e) => setBlogForm({ ...blogForm, featured_image: e.target.value })}
                        style={{ ...inputStyle, width: '100%' }}
                        placeholder="https://..."
                      />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                      <div>
                        <label style={labelStyle}>Meta Title (SEO)</label>
                        <input
                          value={blogForm.meta_title}
                          onChange={(e) => setBlogForm({ ...blogForm, meta_title: e.target.value })}
                          style={{ ...inputStyle, width: '100%' }}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Status</label>
                        <select
                          value={blogForm.status}
                          onChange={(e) => setBlogForm({ ...blogForm, status: e.target.value })}
                          style={{ ...inputStyle, width: '100%' }}
                        >
                          <option value="draft">Draft</option>
                          <option value="published">Published</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label style={labelStyle}>Meta Description (SEO)</label>
                      <textarea
                        value={blogForm.meta_description}
                        onChange={(e) => setBlogForm({ ...blogForm, meta_description: e.target.value })}
                        rows={2}
                        style={{ ...inputStyle, width: '100%', resize: 'vertical' }}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                      <button onClick={saveBlogPost} style={{ ...smallBtnStyle, background: palette.sage, color: palette.white, padding: '10px 24px', fontSize: 12 }}>
                        {editingPost ? 'Update Post' : 'Create Post'}
                      </button>
                      <button onClick={() => { setEditingPost(null); setBlogForm({ title: '', slug: '', excerpt: '', content: '', category_id: '', featured_image: '', status: 'draft', meta_title: '', meta_description: '' }); }}
                        style={{ ...smallBtnStyle, background: 'transparent', color: palette.textMuted, border: `1px solid ${palette.border}`, padding: '10px 24px', fontSize: 12 }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                  <span style={{ fontFamily: fonts, fontSize: 12, color: palette.textMuted }}>
                    {blogPosts.length} post{blogPosts.length !== 1 ? 's' : ''}
                  </span>
                  <button onClick={() => { setEditingPost(false); setBlogForm({ title: '', slug: '', excerpt: '', content: '', category_id: '', featured_image: '', status: 'draft', meta_title: '', meta_description: '' }); }}
                    style={{ ...smallBtnStyle, background: palette.sage, color: palette.white, padding: '8px 18px' }}>
                    + New Post
                  </button>
                </div>

                {blogPosts.length === 0 ? (
                  <div style={{ color: palette.textMuted, fontSize: 13, textAlign: 'center', padding: 40 }}>
                    No blog posts yet. Create your first post to start your journal.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {blogPosts.map((post) => (
                      <div key={post.id} style={{ background: palette.white, border: `1px solid ${palette.border}`, padding: '16px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                            <strong style={{ fontFamily: fonts, fontSize: 14, color: palette.brown }}>{post.title}</strong>
                            <span style={{
                              fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', fontWeight: 700,
                              color: post.status === 'published' ? palette.sage : palette.orangeLight,
                              background: post.status === 'published' ? '#e8f0dc' : '#fef7ed',
                              padding: '2px 8px',
                            }}>
                              {post.status}
                            </span>
                          </div>
                          {post.excerpt && <div style={{ fontSize: 12, color: palette.textMuted, marginBottom: 4 }}>{post.excerpt.slice(0, 120)}{post.excerpt.length > 120 ? '...' : ''}</div>}
                          <div style={{ fontSize: 11, color: palette.textMuted }}>
                            {post.published_at ? new Date(post.published_at).toLocaleDateString() : 'Not published'}
                            {post.category_name && <span> &bull; {post.category_name}</span>}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexShrink: 0, marginLeft: 12 }}>
                          <button onClick={() => {
                            setEditingPost(post.id);
                            setBlogForm({
                              title: post.title || '', slug: post.slug || '', excerpt: post.excerpt || '',
                              content: post.content || '', category_id: post.category_id || '',
                              featured_image: post.featured_image_url || '', status: post.status || 'draft',
                              meta_title: post.seo_title || '', meta_description: post.seo_description || '',
                            });
                          }} style={{ ...smallBtnStyle, background: palette.brown, color: palette.cream }}>
                            Edit
                          </button>
                          <button onClick={() => deleteBlogPost(post.id)} style={{ ...smallBtnStyle, background: palette.red, color: palette.white }}>
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Edit Booking Modal */}
        {editingBooking && (
          <EditBookingModal
            booking={editingBooking}
            onClose={() => setEditingBooking(null)}
            onSave={saveEditBooking}
          />
        )}
      </main>
      </div>

      {/* Mobile bottom tab bar */}
      <nav className="admin-mobile-tabbar" style={{
        display: 'none', position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 95,
        background: palette.cream, borderTop: `1px solid ${palette.border}`,
        padding: '6px 4px calc(env(safe-area-inset-bottom, 0px) + 6px)',
        boxShadow: '0 -2px 14px rgba(0,0,0,0.06)',
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 0 }}>
          {mobilePrimaryGroups.map((id) => {
            const group = navGroups.find((g) => g.id === id);
            if (!group) return null;
            const active = activeGroup.id === id;
            const badge = groupBadge(group);
            return (
              <button
                key={id}
                onClick={() => { setTab(group.tabs[0].id); setMoreSheetOpen(false); }}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  gap: 3, padding: '8px 4px', background: 'transparent', border: 'none', cursor: 'pointer',
                  color: active ? palette.orange : palette.textMuted, position: 'relative',
                }}
              >
                {tabIcon(id)}
                <span style={{ fontFamily: fonts, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>{group.label.split(' ')[0]}</span>
                {badge && (
                  <span style={{
                    position: 'absolute', top: 4, right: 'calc(50% - 18px)',
                    background: palette.red, color: palette.white, fontSize: 9, fontWeight: 700,
                    minWidth: 16, height: 16, borderRadius: 8, padding: '0 4px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {badge}
                  </span>
                )}
              </button>
            );
          })}
          <button
            onClick={() => setMoreSheetOpen(true)}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 3, padding: '8px 4px', background: 'transparent', border: 'none', cursor: 'pointer',
              color: moreSheetOpen ? palette.orange : palette.textMuted,
            }}
          >
            {tabIcon('more')}
            <span style={{ fontFamily: fonts, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>More</span>
          </button>
        </div>
      </nav>

      {/* More sheet (mobile) */}
      {moreSheetOpen && (
        <div
          className="admin-mobile-tabbar"
          onClick={() => setMoreSheetOpen(false)}
          style={{ display: 'none', position: 'fixed', inset: 0, background: 'rgba(60,43,31,0.5)', zIndex: 110 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute', left: 0, right: 0, bottom: 0,
              background: palette.cream,
              borderRadius: '20px 20px 0 0',
              padding: '14px 16px calc(env(safe-area-inset-bottom, 0px) + 18px)',
              maxHeight: '85vh', overflowY: 'auto',
              boxShadow: '0 -8px 30px rgba(0,0,0,0.18)',
            }}
          >
            <div style={{ width: 40, height: 4, background: palette.border, borderRadius: 2, margin: '0 auto 14px' }} />
            <div style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.textMuted, marginBottom: 8 }}>
              More
            </div>
            <div>
              {navGroups.filter((g) => !mobilePrimaryGroups.includes(g.id)).map((group) => (
                <button
                  key={group.id}
                  onClick={() => { setTab(group.tabs[0].id); setMoreSheetOpen(false); }}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    width: '100%', padding: '14px 4px',
                    background: 'transparent', border: 'none', borderBottom: `1px solid ${palette.border}`,
                    cursor: 'pointer', fontFamily: fonts, fontSize: 14,
                    color: activeGroup.id === group.id ? palette.orange : palette.text, textAlign: 'left',
                  }}
                >
                  <span>{group.icon} {group.label}</span>
                  {groupBadge(group) ? (
                    <span style={{ background: palette.red, color: palette.white, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10 }}>
                      {groupBadge(group)}
                    </span>
                  ) : (
                    <span style={{ color: palette.textMuted }}>›</span>
                  )}
                </button>
              ))}
              <button
                onClick={() => { setMoreSheetOpen(false); onBack(); }}
                style={{
                  display: 'flex', alignItems: 'center', width: '100%', padding: '14px 4px', marginTop: 6,
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  fontFamily: fonts, fontSize: 13, color: palette.textMuted, textAlign: 'left', letterSpacing: 1,
                }}
              >
                ← Back to customer view
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Admin Content Editor
// ---------------------------------------------------------------------------

// Notifications health check.
//
// Slack alerts are fire-and-forget so a booking can never fail because Slack
// is down — which also means a dead webhook says nothing. This is how you find
// out, rather than noticing the channel has been quiet for a fortnight.
function NotificationsPanel() {
  const [slack, setSlack] = useState(null);   // { results, channels, delivered } | { error }
  const [email, setEmail] = useState(null);
  const [busy, setBusy] = useState('');

  const run = async (which) => {
    setBusy(which);
    if (which === 'slack') setSlack(null); else setEmail(null);
    try {
      const out = await api(`/api/admin/test-${which}`, { method: 'POST', body: JSON.stringify({}) });
      if (which === 'slack') setSlack(out); else setEmail(out);
    } catch (err) {
      const e = { error: err.error || 'The test could not run' };
      if (which === 'slack') setSlack(e); else setEmail(e);
    } finally { setBusy(''); }
  };

  const card = { background: palette.white, border: `1px solid ${palette.border}`, padding: 20, marginBottom: 18 };
  const h = { fontFamily: fonts, fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, margin: '0 0 6px' };
  const sub = { fontFamily: fonts, fontSize: 12, color: palette.textMuted, lineHeight: 1.65, margin: '0 0 14px' };
  const btn = (bg, fg) => ({ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '9px 16px', background: bg, color: fg, border: 'none', cursor: 'pointer' });

  const Row = ({ mark, color, title, note }) => (
    <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', padding: '7px 0', borderTop: `1px solid ${palette.border}` }}>
      <span style={{ fontFamily: fonts, fontWeight: 700, color, width: 14, flexShrink: 0 }}>{mark}</span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontFamily: fonts, fontSize: 13, color: palette.text }}>{title}</div>
        {note && <div style={{ fontFamily: fonts, fontSize: 11, color: palette.textMuted, lineHeight: 1.6 }}>{note}</div>}
      </div>
    </div>
  );

  return (
    <div>
      <div style={card}>
        <h4 style={h}>Slack</h4>
        <p style={sub}>
          Posts a test message to every channel you have configured, and tells you which alerts land where.
          Worth running straight after changing a webhook — a broken one fails silently, so this is the only
          way to know before the alerts you're waiting on don't arrive.
        </p>
        <button onClick={() => run('slack')} disabled={!!busy} style={btn(palette.brown, palette.cream)}>
          {busy === 'slack' ? 'Testing…' : 'Send test to Slack'}
        </button>

        {slack && (
          <div style={{ marginTop: 16 }}>
            {slack.error ? (
              <div style={{ fontFamily: fonts, fontSize: 12, color: palette.red }}>✗ {slack.error}</div>
            ) : (
              <>
                {slack.results.map((r) => (
                  <Row
                    key={r.key}
                    mark={r.configured ? (r.ok ? '✓' : '✗') : '○'}
                    color={r.configured ? (r.ok ? palette.sage : palette.red) : palette.textMuted}
                    title={r.label}
                    note={
                      !r.configured
                        ? `Not set up — ${r.env} is empty, so these alerts go nowhere.`
                        : r.ok
                          ? (r.usingFallback ? `Delivered — sharing the general channel, because ${r.env} isn't set.` : 'Delivered.')
                          : `Failed — Slack said "${r.error}". Check ${r.env} in Railway.`
                    }
                  />
                ))}
                <div style={{ fontFamily: fonts, fontSize: 11, color: palette.textMuted, marginTop: 10, lineHeight: 1.6 }}>
                  {slack.channels === 0
                    ? 'No Slack webhooks are configured at all — nothing will ever be posted.'
                    : `Posted to ${slack.channels} ${slack.channels === 1 ? 'channel' : 'channels'}, ${slack.delivered} delivered.`}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <div style={card}>
        <h4 style={h}>Email</h4>
        <p style={sub}>
          Sends a test to the studio address. Unlike Slack, email failures are already surfaced where they
          matter — a delivery that fails after someone pays raises its own alert.
        </p>
        <button onClick={() => run('email')} disabled={!!busy} style={btn(palette.beige, palette.brown)}>
          {busy === 'email' ? 'Sending…' : 'Send test email'}
        </button>
        {email && (
          <div style={{ marginTop: 14, fontFamily: fonts, fontSize: 12, lineHeight: 1.7 }}>
            {email.error ? (
              <span style={{ color: palette.red }}>✗ {email.error}</span>
            ) : email.ok ? (
              <span style={{ color: palette.sage }}>✓ Sent to {email.to} via {email.provider}.</span>
            ) : (
              <span style={{ color: palette.red }}>
                ✗ Not sent{email.provider ? ` via ${email.provider}` : ''} — {email.error || 'no email provider is configured'}.
              </span>
            )}
          </div>
        )}
      </div>

      <p style={{ fontFamily: fonts, fontSize: 11, color: palette.textMuted, lineHeight: 1.7, margin: 0 }}>
        Moving Slack workspaces? Webhook URLs can't be carried over — Slack issues them per workspace. The app
        manifest and the full swap order are in <code style={{ fontFamily: fonts }}>scripts/SLACK-SETUP.md</code> in the repo.
        Test a brand-new URL with curl <em>before</em> putting it into Railway, then use the button above to confirm it landed.
      </p>
    </div>
  );
}

function AdminContentEditor({ onPackagesConfigSaved, defaultSection, hideSectionNav }) {
  const [activeSection, setActiveSection] = useState(defaultSection || 'faqs');
  const [siteContent, setSiteContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [saveErr, setSaveErr] = useState('');

  // 'openhouse_settings' has its own dedicated top-level admin tab now and
  // is excluded from the regular Site Content section list. Pass
  // defaultSection="openhouse_settings" + hideSectionNav to render it standalone.
  const sections = [
    { id: 'faqs', label: 'FAQs' },
    { id: 'packages_config', label: 'Packages & Pricing' },
    { id: 'policies', label: 'Policies' },
    { id: 'studio_rules', label: 'Studio Rules' },
    { id: 'contact', label: 'Contact' },
    { id: 'auto_approve_settings', label: 'Auto-Approve' },
    { id: 'access_instructions', label: 'Access Instructions' },
    // Not site content — a health check. It lives here because this is where
    // the settings are, and because a dead Slack webhook is otherwise silent.
    { id: 'notifications', label: 'Notifications' },
    ...(defaultSection === 'openhouse_settings' ? [{ id: 'openhouse_settings', label: 'Open House' }] : []),
  ];

  // Default content that matches existing hardcoded data
  const defaults = {
    faqs: FAQ_ITEMS.map((f) => ({ q: f.q, a: f.a })),
    packages_config: {
      hourlyRate: 100,
      showUpstairsEquipment: true,
      podcastTurnaround: '72hr turnaround for all deliverables',
      podcastGear: ['Rodecaster Pro', '4x Shure MV7X Podcast XLR Microphone'],
      podcastPackages: {
        'podcast-creator': {
          name: 'Creator Package',
          tagline: 'Audio + Video + Content',
          description: 'Perfect for: Podcasters, content creators, small brands',
          enabled: true,
          durations: [{ hours: 1, price: 150 }, { hours: 2, price: 280 }, { hours: 4, price: 520 }],
          includes: [
            'Professional audio + single camera video',
            '4x 60-second social clips (IG, TikTok, Shorts ready)',
            '1x YouTube episode edit (up to 10 min)',
            'Basic editing (cleanup, levels, color correction)',
            'Studio engineer present',
            'MP3 audio + MP4 video exports',
          ],
        },
        'podcast-pro': {
          name: 'Pro Package',
          tagline: 'Full Production + Multi-Angle',
          description: 'Perfect for: Brands, serious podcasters, production companies',
          enabled: true,
          durations: [{ hours: 2, price: 420 }, { hours: 4, price: 800 }],
          includes: [
            'Multi-angle video with live switching (final edited video)',
            'Professional audio recording',
            'Studio engineer + producer present',
            '6x 60-second social clips (4 standard + 2 premium w/ graphics)',
            'Full episode YouTube edit (up to 10 min)',
            'Episode editing (up to 7 edits included)',
            'BTS photography (30 minutes)',
            'MP3 audio + MP4 video exports',
          ],
        },
      },
      podcastAddOns: [
        { id: 'same-day-booking', label: 'Same-Day Booking', price: 30 },
        { id: 'late-night', label: 'Late Night (after 10pm)', price: 25 },
        { id: 'extra-social-clip', label: 'Additional Social Clip', price: 40 },
        { id: 'extended-editing', label: 'Extended Editing (per edit)', price: 10 },
        { id: 'rushed-delivery', label: 'Rushed Deliverables (24hr)', price: 35 },
        { id: 'lapel-mic', label: 'Lapel Mic (per person)', price: 50 },
      ],
      studioAddOns: [
        { id: 'aputure-300d', label: 'Aputure 300D Light', price: 50 },
        { id: 'camera-cart-kit', label: 'Digital Camera Cart Kit', price: 75, note: 'Atomos Sumo 19" monitor · Inovativ Voyager 36 · HDMI included' },
        { id: 'backdrop-7x9', label: '7x9 Hand-Painted Textured Backdrop', price: 50, note: '24hr notice · no stepping · damage = full charge', options: ['Gray + Light Beige', 'Warm Gray', 'Rosy + Beige', 'Green + Beige'] },
        { id: 'backdrop-8x12', label: '8x12 Ash Gray Hand-Painted Textured Backdrop', price: 75, note: '24hr notice · no stepping · damage = full charge' },
        { id: 'backdrop-colored', label: 'In-Stock Colored Backdrop', price: 25, options: ['Orange', 'Green', 'Red'] },
        { id: 'profoto-flash', label: '2x Profoto 500W Flash Set', price: 150, note: 'Bowen mount adapters · compatible trigger required' },
      ],
    },
    policies: POLICIES.map((p) => ({ title: p.title, text: p.text })),
    studio_rules: STUDIO_RULES.map((r) => ({ title: r.title, text: r.text })),
    contact: { name: 'HDDN LAB', location: 'Downtown New Rochelle', email: CONTACT_EMAIL, instagram: 'https://instagram.com/hddn.lab', tiktok: 'https://tiktok.com/@hddn.lab' },
    auto_approve_settings: { enabled: true, minHoursInAdvance: 48, requirePayment: true },
    access_instructions: { text: `Hey [First Name],

Thanks for booking with HDDN LAB! We're looking forward to your session.

Here's everything you need to know to find us and arrive smoothly.

---

## Studio Address

**HDDN LAB**
72 Centre Avenue
New Rochelle, NY 10801

---

## How to Find Us

We're not your typical street-level studio — we're hidden for a reason.

**When you arrive at 72 Centre Ave:**

- **Call or text Donnell at 917-816-3056** to let us know you've arrived
- Look for the **tunnel entrance** next to the building
- Walk through the tunnel
- Our door is at the end, labeled **HDDN**
- Someone from our team will be there to greet you

If you can't find the tunnel, call or text Donnell at **917-816-3056** — we'll come out and meet you.

---

## Parking & Drop-Off

**Please note: we do not have on-site parking.** You're welcome to pull up briefly at the entrance to drop off gear and passengers, then park at one of the nearby public lots:

| Lot | Address | Walk |
|---|---|---|
| **Skyline Garage** (closest & easiest) | 26 Division St | 3 min |
| **360 Huguenot Garage** | 360 Huguenot St | 3 min |
| **Prospect Street Public Lot** | 35 Prospect St | 3 min |

**Don't trust the signs:** Street parking is **free after 7 PM every day** and **free all day Sunday** — meters only run until 7 PM.

---

## Public Transit

- **Metro-North:** New Rochelle Station (5–10 min walk)
- **Bee-Line Bus:** Routes 1, 8, 15, 23 (stop near Centre Ave)

---

## What to Bring

- Anything specific to your session (props, wardrobe, talent, etc.)
- Water/snacks if needed (outside food/drink permitted with proper disposal)

---

## Reminders

- **Arrive on time** — your booking includes setup and breakdown
- **Bring your booking confirmation** (email or screenshot)
- **Setup/breakdown** must occur within your booked time
- **Need overtime?** Let us know in advance — subject to availability

---

## Questions?

Reach out anytime: **[${CONTACT_EMAIL}](mailto:${CONTACT_EMAIL})**

We'll see you at the tunnel.

— **The HDDN LAB Team**` },
    openhouse_settings: {
      headline: 'Thanks for coming to the HDDN Lab Open House',
      intro: "We snapped photos and shot videos of everyone — fill out a quick survey and we'll send the link.",
      eventTag: 'open-house-2026-04',
      galleryUrl: '',
      instagramUrl: 'https://instagram.com/hddn.lab',
      tiktokUrl: 'https://tiktok.com/@hddn.lab',
      expiresAt: '',
      expiredHeadline: 'Open House — Stay in the Loop',
      expiredDescription: "Open House is a chance to come experience HDDN Lab in person — meet other creators, network, and get a feel for the space. We host one every couple of months. Drop your email and we'll send you the details for the next one.",
      questions: [
        { id: 'event_interest', type: 'long', label: 'What types of events or workshops would you like to see at HDDN Lab?', required: true },
        { id: 'time_preference', type: 'single', label: 'What time of week works best for you?', options: ['Weekday evenings', 'Weekend afternoons', 'Weekend evenings', "Doesn't matter"], required: true },
        { id: 'referral_source', type: 'single', label: 'How did you hear about HDDN Lab?', options: ['Instagram', 'TikTok', 'Friend', 'Walked by', 'The open house', 'Other'], required: true },
        { id: 'comments', type: 'long', label: 'Anything else you’d like to share?', required: false },
      ],
    },
  };

  useEffect(() => {
    api('/api/site-content')
      .then((data) => {
        // Merge with defaults — use saved content if exists, otherwise defaults
        const merged = {};
        sections.forEach((s) => {
          merged[s.id] = data[s.id] || defaults[s.id];
        });
        setSiteContent(merged);
      })
      .catch(() => setSiteContent({ ...defaults }))
      .finally(() => setLoading(false));
  }, []);

  const saveSection = async (sectionId) => {
    setSaving(true);
    setSaveMsg('');
    setSaveErr('');
    try {
      await api(`/api/site-content/${sectionId}`, {
        method: 'PUT',
        body: JSON.stringify({ content: siteContent[sectionId] }),
      });
      if (sectionId === 'packages_config' && onPackagesConfigSaved) {
        onPackagesConfigSaved();
        setSaveMsg('Packages & Pricing saved — booking form updated!');
      } else {
        setSaveMsg('Saved successfully!');
      }
    } catch (err) {
      setSaveErr(err.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const updateContent = (sectionId, newContent) => {
    setSiteContent((prev) => ({ ...prev, [sectionId]: newContent }));
  };

  const inputStyle = {
    width: '100%', padding: '8px 10px', fontFamily: fonts, fontSize: 13,
    border: `1px solid ${palette.border}`, background: palette.white,
    color: palette.text, boxSizing: 'border-box', outline: 'none',
  };

  const labelStyle = {
    display: 'block', fontSize: 11, letterSpacing: 1, textTransform: 'uppercase',
    color: palette.textMuted, marginBottom: 4,
  };

  if (loading) return <div style={{ padding: 20, color: palette.textMuted }}>Loading content...</div>;

  const content = siteContent[activeSection] || defaults[activeSection];

  return (
    <div>
      {!hideSectionNav && (
        <h3 style={{ fontFamily: fonts, fontSize: 14, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, marginTop: 0, marginBottom: 16 }}>
          Edit Site Content
        </h3>
      )}

      {/* Section selector — hidden when admin tab is dedicated to a single section */}
      {!hideSectionNav && (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 0, marginBottom: 20, borderBottom: `2px solid ${palette.border}` }}>
        {sections.map((s) => (
          <button
            key={s.id}
            onClick={() => { setActiveSection(s.id); setSaveMsg(''); setSaveErr(''); }}
            style={{
              fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
              padding: '8px 14px', background: activeSection === s.id ? palette.brown : 'transparent',
              color: activeSection === s.id ? palette.cream : palette.textMuted, border: 'none', cursor: 'pointer',
            }}
          >
            {s.label}
          </button>
        ))}
      </div>
      )}

      {saveMsg && <div style={{ background: '#e8f0dc', border: `1px solid ${palette.sage}`, color: palette.sage, padding: '6px 10px', fontSize: 12, marginBottom: 12 }}>{saveMsg}</div>}
      {saveErr && <div style={{ background: '#fdf2f0', border: `1px solid ${palette.red}`, color: palette.red, padding: '6px 10px', fontSize: 12, marginBottom: 12 }}>{saveErr}</div>}
      {saveMsg && <Toast message={saveMsg} type="success" onDismiss={() => setSaveMsg('')} />}
      {saveErr && <Toast message={saveErr} type="error" onDismiss={() => setSaveErr('')} />}

      {/* FAQs Editor */}
      {activeSection === 'faqs' && (() => {
        const items = Array.isArray(content) ? content : [];
        return (
          <div>
            {items.map((item, i) => (
              <div key={i} style={{ background: palette.white, border: `1px solid ${palette.border}`, padding: '14px 16px', marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Question</label>
                    <input
                      value={item.q}
                      onChange={(e) => {
                        const updated = [...items];
                        updated[i] = { ...updated[i], q: e.target.value };
                        updateContent('faqs', updated);
                      }}
                      style={inputStyle}
                    />
                  </div>
                  <button
                    onClick={() => updateContent('faqs', items.filter((_, idx) => idx !== i))}
                    style={{ background: palette.red, color: palette.white, border: 'none', padding: '4px 10px', fontSize: 11, cursor: 'pointer', marginTop: 16, fontFamily: fonts, fontWeight: 700 }}
                  >
                    Remove
                  </button>
                </div>
                <div style={{ marginTop: 8 }}>
                  <label style={labelStyle}>Answer</label>
                  <textarea
                    value={item.a}
                    onChange={(e) => {
                      const updated = [...items];
                      updated[i] = { ...updated[i], a: e.target.value };
                      updateContent('faqs', updated);
                    }}
                    rows={3}
                    style={{ ...inputStyle, resize: 'vertical' }}
                  />
                </div>
              </div>
            ))}
            <button
              onClick={() => updateContent('faqs', [...items, { q: '', a: '' }])}
              style={{ fontFamily: fonts, fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '8px 16px', background: palette.sage, color: palette.white, border: 'none', cursor: 'pointer', marginBottom: 16 }}
            >
              + Add FAQ
            </button>
          </div>
        );
      })()}

      {/* What's Included is now auto-derived from Packages & Pricing — no separate editor needed */}

      {/* Policies Editor */}
      {activeSection === 'policies' && (() => {
        const items = Array.isArray(content) ? content : [];
        return (
          <div>
            {items.map((item, i) => (
              <div key={i} style={{ background: palette.white, border: `1px solid ${palette.border}`, padding: '14px 16px', marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Title</label>
                    <input
                      value={item.title}
                      onChange={(e) => {
                        const updated = [...items];
                        updated[i] = { ...updated[i], title: e.target.value };
                        updateContent('policies', updated);
                      }}
                      style={inputStyle}
                    />
                  </div>
                  <button
                    onClick={() => updateContent('policies', items.filter((_, idx) => idx !== i))}
                    style={{ background: palette.red, color: palette.white, border: 'none', padding: '4px 10px', fontSize: 11, cursor: 'pointer', marginTop: 16, fontFamily: fonts, fontWeight: 700 }}
                  >
                    Remove
                  </button>
                </div>
                <div style={{ marginTop: 8 }}>
                  <label style={labelStyle}>Text</label>
                  <textarea
                    value={item.text}
                    onChange={(e) => {
                      const updated = [...items];
                      updated[i] = { ...updated[i], text: e.target.value };
                      updateContent('policies', updated);
                    }}
                    rows={2}
                    style={{ ...inputStyle, resize: 'vertical' }}
                  />
                </div>
              </div>
            ))}
            <button
              onClick={() => updateContent('policies', [...items, { title: '', text: '' }])}
              style={{ fontFamily: fonts, fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '8px 16px', background: palette.sage, color: palette.white, border: 'none', cursor: 'pointer', marginBottom: 16 }}
            >
              + Add Policy
            </button>
          </div>
        );
      })()}

      {/* Studio Rules Editor */}
      {activeSection === 'studio_rules' && (() => {
        const items = Array.isArray(content) ? content : [];
        return (
          <div>
            {items.map((item, i) => (
              <div key={i} style={{ background: palette.white, border: `1px solid ${palette.border}`, padding: '14px 16px', marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Title</label>
                    <input
                      value={item.title}
                      onChange={(e) => {
                        const updated = [...items];
                        updated[i] = { ...updated[i], title: e.target.value };
                        updateContent('studio_rules', updated);
                      }}
                      style={inputStyle}
                    />
                  </div>
                  <button
                    onClick={() => updateContent('studio_rules', items.filter((_, idx) => idx !== i))}
                    style={{ background: palette.red, color: palette.white, border: 'none', padding: '4px 10px', fontSize: 11, cursor: 'pointer', marginTop: 16, fontFamily: fonts, fontWeight: 700 }}
                  >
                    Remove
                  </button>
                </div>
                <div style={{ marginTop: 8 }}>
                  <label style={labelStyle}>Text</label>
                  <textarea
                    value={item.text}
                    onChange={(e) => {
                      const updated = [...items];
                      updated[i] = { ...updated[i], text: e.target.value };
                      updateContent('studio_rules', updated);
                    }}
                    rows={2}
                    style={{ ...inputStyle, resize: 'vertical' }}
                  />
                </div>
              </div>
            ))}
            <button
              onClick={() => updateContent('studio_rules', [...items, { title: '', text: '' }])}
              style={{ fontFamily: fonts, fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '8px 16px', background: palette.sage, color: palette.white, border: 'none', cursor: 'pointer', marginBottom: 16 }}
            >
              + Add Rule
            </button>
          </div>
        );
      })()}

      {/* Contact Editor */}
      {/* Packages & Pricing Editor */}
      {activeSection === 'packages_config' && (() => {
        const data = content && typeof content === 'object' && !Array.isArray(content) ? content : defaults.packages_config;
        const updatePkgConfig = (newData) => updateContent('packages_config', newData);
        return (
          <div>
            {/* Studio Hourly Rate */}
            <div style={{ background: palette.white, border: `1px solid ${palette.border}`, padding: '16px', marginBottom: 20 }}>
              <h4 style={{ fontFamily: fonts, fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, marginTop: 0, marginBottom: 12 }}>
                Studio Hourly Rate
              </h4>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: palette.brown }}>$</span>
                <input
                  type="number"
                  value={data.hourlyRate || 100}
                  onChange={(e) => updatePkgConfig({ ...data, hourlyRate: parseInt(e.target.value) || 0 })}
                  min={0}
                  style={{ ...inputStyle, width: 120 }}
                />
                <span style={{ fontSize: 12, color: palette.textMuted }}>per hour ({MIN_HOURS} hr minimum)</span>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14, fontSize: 12, color: palette.textMuted, cursor: 'pointer' }}>
                <Switch
                  checked={data.showUpstairsEquipment === true}
                  onChange={(v) => updatePkgConfig({ ...data, showUpstairsEquipment: v })}
                  ariaLabel="Show upstairs equipment list"
                />
                <span style={{ fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>
                  Show detailed equipment list for Full Studio
                </span>
              </label>
              <div style={{ fontSize: 11, color: palette.textMuted, marginLeft: 24, marginTop: 4, lineHeight: 1.5 }}>
                When off, the Lighting / Modifiers / Grip sections are hidden from the public "What's Included" panel.
              </div>
            </div>

            {/* Podcast Recording Gear */}
            <div style={{ background: palette.white, border: `1px solid ${palette.border}`, padding: '16px', marginBottom: 20 }}>
              <h4 style={{ fontFamily: fonts, fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, marginTop: 0, marginBottom: 12 }}>
                Podcast Recording — What's Included
              </h4>
              <label style={labelStyle}>Gear (one per line)</label>
              <textarea
                value={Array.isArray(data.podcastGear) ? data.podcastGear.join('\n') : ''}
                onChange={(e) => updatePkgConfig({ ...data, podcastGear: e.target.value.split('\n') })}
                rows={Math.max(3, (data.podcastGear || []).length + 1)}
                placeholder={'Rodecaster Pro\n4x Shure MV7X Podcast XLR Microphone'}
                style={{ ...inputStyle, resize: 'vertical' }}
              />
              <label style={{ ...labelStyle, marginTop: 12 }}>Deliverables Turnaround Note</label>
              <input
                type="text"
                value={data.podcastTurnaround || ''}
                onChange={(e) => updatePkgConfig({ ...data, podcastTurnaround: e.target.value })}
                placeholder="e.g. 72hr turnaround for all deliverables"
                style={inputStyle}
              />
            </div>

            {/* Podcast Packages */}
            {Object.entries(data.podcastPackages || {}).map(([pkgId, pkg]) => (
              <div key={pkgId} style={{ background: palette.white, border: `1px solid ${palette.border}`, padding: '16px', marginBottom: 20, opacity: pkg.enabled === false ? 0.6 : 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
                  <h4 style={{ fontFamily: fonts, fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, margin: 0 }}>
                    {pkg.name}{pkg.enabled === false ? ' (Disabled)' : ''}
                  </h4>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: palette.textMuted, cursor: 'pointer', userSelect: 'none' }}>
                      <Switch
                        checked={pkg.enabled !== false}
                        onChange={(v) => {
                          const updated = { ...data, podcastPackages: { ...data.podcastPackages, [pkgId]: { ...pkg, enabled: v } } };
                          updatePkgConfig(updated);
                        }}
                        ariaLabel={`${pkg.name} enabled`}
                      />
                      <span style={{ fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>Offer this package</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        if (!window.confirm(`Delete "${pkg.name || pkgId}"? Existing bookings for this package will still display, but it won't be bookable anymore.`)) return;
                        const newPackages = { ...data.podcastPackages };
                        delete newPackages[pkgId];
                        updatePkgConfig({ ...data, podcastPackages: newPackages });
                      }}
                      style={{ background: palette.red, color: palette.white, border: 'none', padding: '6px 10px', fontSize: 11, cursor: 'pointer', fontFamily: fonts, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                  <div>
                    <label style={labelStyle}>Package Name</label>
                    <input
                      value={pkg.name || ''}
                      onChange={(e) => {
                        const updated = { ...data, podcastPackages: { ...data.podcastPackages, [pkgId]: { ...pkg, name: e.target.value } } };
                        updatePkgConfig(updated);
                      }}
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Tagline</label>
                    <input
                      value={pkg.tagline || ''}
                      onChange={(e) => {
                        const updated = { ...data, podcastPackages: { ...data.podcastPackages, [pkgId]: { ...pkg, tagline: e.target.value } } };
                        updatePkgConfig(updated);
                      }}
                      style={inputStyle}
                    />
                  </div>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={labelStyle}>Description</label>
                  <input
                    value={pkg.description || ''}
                    onChange={(e) => {
                      const updated = { ...data, podcastPackages: { ...data.podcastPackages, [pkgId]: { ...pkg, description: e.target.value } } };
                      updatePkgConfig(updated);
                    }}
                    style={inputStyle}
                  />
                </div>

                {/* Duration/Pricing tiers */}
                <label style={labelStyle}>Pricing Tiers</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                  {(pkg.durations || []).map((dur, di) => (
                    <div key={di} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input
                        type="number"
                        value={dur.hours}
                        onChange={(e) => {
                          const newDurations = [...pkg.durations];
                          newDurations[di] = { ...newDurations[di], hours: parseInt(e.target.value) || 0 };
                          const updated = { ...data, podcastPackages: { ...data.podcastPackages, [pkgId]: { ...pkg, durations: newDurations } } };
                          updatePkgConfig(updated);
                        }}
                        min={1}
                        style={{ ...inputStyle, width: 70 }}
                      />
                      <span style={{ fontSize: 12, color: palette.textMuted }}>hr(s)</span>
                      <span style={{ fontSize: 16, fontWeight: 700, color: palette.brown }}>$</span>
                      <input
                        type="number"
                        value={dur.price}
                        onChange={(e) => {
                          const newDurations = [...pkg.durations];
                          newDurations[di] = { ...newDurations[di], price: parseInt(e.target.value) || 0 };
                          const updated = { ...data, podcastPackages: { ...data.podcastPackages, [pkgId]: { ...pkg, durations: newDurations } } };
                          updatePkgConfig(updated);
                        }}
                        min={0}
                        style={{ ...inputStyle, width: 100 }}
                      />
                      <button
                        onClick={() => {
                          const newDurations = pkg.durations.filter((_, idx) => idx !== di);
                          const updated = { ...data, podcastPackages: { ...data.podcastPackages, [pkgId]: { ...pkg, durations: newDurations } } };
                          updatePkgConfig(updated);
                        }}
                        style={{ background: palette.red, color: palette.white, border: 'none', padding: '4px 8px', fontSize: 11, cursor: 'pointer', fontFamily: fonts, fontWeight: 700 }}
                      >
                        X
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      const newDurations = [...(pkg.durations || []), { hours: 1, price: 0 }];
                      const updated = { ...data, podcastPackages: { ...data.podcastPackages, [pkgId]: { ...pkg, durations: newDurations } } };
                      updatePkgConfig(updated);
                    }}
                    style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '4px 12px', background: palette.sage, color: palette.white, border: 'none', cursor: 'pointer', alignSelf: 'flex-start' }}
                  >
                    + Add Tier
                  </button>
                </div>

                {/* Package Includes */}
                <label style={labelStyle}>What's Included (one per line)</label>
                <textarea
                  value={(pkg.includes || []).join('\n')}
                  onChange={(e) => {
                    const updated = { ...data, podcastPackages: { ...data.podcastPackages, [pkgId]: { ...pkg, includes: e.target.value.split('\n') } } };
                    updatePkgConfig(updated);
                  }}
                  rows={Math.max(4, (pkg.includes || []).length + 1)}
                  style={{ ...inputStyle, resize: 'vertical' }}
                />
              </div>
            ))}

            <div style={{ marginBottom: 20 }}>
              <button
                type="button"
                onClick={() => {
                  const newId = `podcast-custom-${Date.now()}`;
                  const newPkg = {
                    name: 'New Package',
                    tagline: '',
                    description: '',
                    enabled: true,
                    durations: [{ hours: 1, price: 0 }],
                    includes: [],
                  };
                  updatePkgConfig({ ...data, podcastPackages: { ...(data.podcastPackages || {}), [newId]: newPkg } });
                }}
                style={{ fontFamily: fonts, fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '8px 16px', background: palette.brown, color: palette.cream, border: 'none', cursor: 'pointer' }}
              >
                + Add Podcast Package
              </button>
            </div>

            {/* Podcast Add-ons */}
            <div style={{ background: palette.white, border: `1px solid ${palette.border}`, padding: '16px', marginBottom: 20 }}>
              <h4 style={{ fontFamily: fonts, fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, marginTop: 0, marginBottom: 12 }}>
                Podcast Add-ons
              </h4>
              {(data.podcastAddOns || []).map((addon, ai) => (
                <div key={ai} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <input
                    value={addon.label}
                    onChange={(e) => {
                      const newAddOns = [...data.podcastAddOns];
                      newAddOns[ai] = { ...newAddOns[ai], label: e.target.value };
                      updatePkgConfig({ ...data, podcastAddOns: newAddOns });
                    }}
                    placeholder="Label"
                    style={{ ...inputStyle, flex: 1 }}
                  />
                  <span style={{ fontSize: 14, fontWeight: 700, color: palette.brown }}>$</span>
                  <input
                    type="number"
                    value={addon.price}
                    onChange={(e) => {
                      const newAddOns = [...data.podcastAddOns];
                      newAddOns[ai] = { ...newAddOns[ai], price: parseInt(e.target.value) || 0 };
                      updatePkgConfig({ ...data, podcastAddOns: newAddOns });
                    }}
                    min={0}
                    style={{ ...inputStyle, width: 80 }}
                  />
                  <button
                    onClick={() => {
                      updatePkgConfig({ ...data, podcastAddOns: data.podcastAddOns.filter((_, idx) => idx !== ai) });
                    }}
                    style={{ background: palette.red, color: palette.white, border: 'none', padding: '4px 8px', fontSize: 11, cursor: 'pointer', fontFamily: fonts, fontWeight: 700 }}
                  >
                    X
                  </button>
                </div>
              ))}
              <button
                onClick={() => {
                  const newId = 'addon-' + Date.now();
                  updatePkgConfig({ ...data, podcastAddOns: [...(data.podcastAddOns || []), { id: newId, label: '', price: 0 }] });
                }}
                style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '6px 14px', background: palette.sage, color: palette.white, border: 'none', cursor: 'pointer' }}
              >
                + Add Podcast Add-on
              </button>
            </div>

            {/* Studio Add-ons */}
            <div style={{ background: palette.white, border: `1px solid ${palette.border}`, padding: '16px' }}>
              <h4 style={{ fontFamily: fonts, fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, marginTop: 0, marginBottom: 12 }}>
                Studio Add-ons
              </h4>
              {(data.studioAddOns || []).map((addon, ai) => (
                <div key={ai} style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12, padding: '10px 12px', background: palette.beige, border: `1px solid ${palette.border}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      value={addon.label}
                      onChange={(e) => {
                        const newAddOns = [...data.studioAddOns];
                        newAddOns[ai] = { ...newAddOns[ai], label: e.target.value };
                        updatePkgConfig({ ...data, studioAddOns: newAddOns });
                      }}
                      placeholder="Label"
                      style={{ ...inputStyle, flex: 1 }}
                    />
                    <span style={{ fontSize: 14, fontWeight: 700, color: palette.brown }}>$</span>
                    <input
                      type="number"
                      value={addon.price || 0}
                      onChange={(e) => {
                        const newAddOns = [...data.studioAddOns];
                        newAddOns[ai] = { ...newAddOns[ai], price: parseInt(e.target.value) || 0 };
                        updatePkgConfig({ ...data, studioAddOns: newAddOns });
                      }}
                      min={0}
                      style={{ ...inputStyle, width: 80 }}
                    />
                    <button
                      onClick={() => {
                        updatePkgConfig({ ...data, studioAddOns: data.studioAddOns.filter((_, idx) => idx !== ai) });
                      }}
                      style={{ background: palette.red, color: palette.white, border: 'none', padding: '4px 8px', fontSize: 11, cursor: 'pointer', fontFamily: fonts, fontWeight: 700 }}
                    >
                      X
                    </button>
                  </div>
                  <div>
                    <label style={{ fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: palette.textMuted, display: 'block', marginBottom: 4 }}>
                      Note / Fine Print (optional — shown under the label)
                    </label>
                    <input
                      value={addon.note || ''}
                      onChange={(e) => {
                        const newAddOns = [...data.studioAddOns];
                        const next = { ...newAddOns[ai] };
                        if (e.target.value) {
                          next.note = e.target.value;
                        } else {
                          delete next.note;
                        }
                        newAddOns[ai] = next;
                        updatePkgConfig({ ...data, studioAddOns: newAddOns });
                      }}
                      placeholder="e.g. 24hr notice · no stepping · damage = full charge"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: palette.textMuted, display: 'block', marginBottom: 4 }}>
                      Color / Style Options (comma-separated, leave blank for no picker)
                    </label>
                    <input
                      value={Array.isArray(addon.options) ? addon.options.join(', ') : ''}
                      onChange={(e) => {
                        const raw = e.target.value;
                        const parsed = raw.split(',').map((s) => s.trim()).filter(Boolean);
                        const newAddOns = [...data.studioAddOns];
                        const next = { ...newAddOns[ai] };
                        if (parsed.length === 0) {
                          delete next.options;
                        } else {
                          next.options = parsed;
                        }
                        newAddOns[ai] = next;
                        updatePkgConfig({ ...data, studioAddOns: newAddOns });
                      }}
                      placeholder="e.g. Gray + Light Beige, Warm Gray, Rosy + Beige, Green + Beige"
                      style={inputStyle}
                    />
                  </div>
                </div>
              ))}
              <button
                onClick={() => {
                  const newId = 'addon-' + Date.now();
                  updatePkgConfig({ ...data, studioAddOns: [...(data.studioAddOns || []), { id: newId, label: '', price: 20 }] });
                }}
                style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '6px 14px', background: palette.sage, color: palette.white, border: 'none', cursor: 'pointer' }}
              >
                + Add Studio Add-on
              </button>
            </div>
          </div>
        );
      })()}

      {activeSection === 'contact' && (() => {
        const data = content && typeof content === 'object' && !Array.isArray(content) ? content : defaults.contact;
        const updateField = (field, value) => updateContent('contact', { ...data, [field]: value });
        return (
          <div style={{ background: palette.white, border: `1px solid ${palette.border}`, padding: '16px' }}>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Business Name</label>
              <input value={data.name || ''} onChange={(e) => updateField('name', e.target.value)} style={inputStyle} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Location</label>
              <input value={data.location || ''} onChange={(e) => updateField('location', e.target.value)} style={inputStyle} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Email</label>
              <input type="email" value={data.email || ''} onChange={(e) => updateField('email', e.target.value)} style={inputStyle} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Instagram URL</label>
              <input value={data.instagram || ''} onChange={(e) => updateField('instagram', e.target.value)} style={inputStyle} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>TikTok URL</label>
              <input value={data.tiktok || ''} onChange={(e) => updateField('tiktok', e.target.value)} style={inputStyle} />
            </div>
          </div>
        );
      })()}

      {activeSection === 'auto_approve_settings' && (() => {
        const data = content && typeof content === 'object' && !Array.isArray(content) ? content : defaults.auto_approve_settings;
        const updateField = (field, value) => updateContent('auto_approve_settings', { ...data, [field]: value });
        return (
          <div style={{ background: palette.white, border: `1px solid ${palette.border}`, padding: '16px' }}>
            <p style={{ fontFamily: fonts, fontSize: 13, color: palette.textMuted, margin: '0 0 16px' }}>
              When enabled, bookings that meet the criteria below will be automatically confirmed without requiring manual approval.
            </p>
            <div style={{ marginBottom: 16 }}>
              <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <Switch checked={data.enabled !== false} onChange={(v) => updateField('enabled', v)} ariaLabel="Enable auto-approve" />
                Enable Auto-Approve
              </label>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Minimum Hours in Advance</label>
              <input type="number" min="0" value={data.minHoursInAdvance || 48} onChange={(e) => updateField('minHoursInAdvance', parseInt(e.target.value) || 0)} style={{ ...inputStyle, width: 120 }} />
              <p style={{ fontFamily: fonts, fontSize: 11, color: palette.textMuted, marginTop: 4 }}>Bookings must be at least this many hours in the future to auto-approve. (Default: 48)</p>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <Switch checked={data.requirePayment !== false} onChange={(v) => updateField('requirePayment', v)} ariaLabel="Require payment before auto-approve" />
                Require Payment Before Auto-Approve
              </label>
              <p style={{ fontFamily: fonts, fontSize: 11, color: palette.textMuted, marginTop: 4 }}>If on, only bookings with completed Stripe payment will auto-approve. Turn off to auto-approve without payment (use with caution).</p>
            </div>
          </div>
        );
      })()}

      {activeSection === 'access_instructions' && (() => {
        const data = content && typeof content === 'object' && !Array.isArray(content) ? content : defaults.access_instructions;
        return <AccessInstructionsEditor data={data} onChange={(next) => updateContent('access_instructions', next)} />;
      })()}

      {activeSection === 'notifications' && <NotificationsPanel />}

      {activeSection === 'openhouse_settings' && (() => {
        const data = content && typeof content === 'object' && !Array.isArray(content) ? content : defaults.openhouse_settings;
        const questions = Array.isArray(data.questions) ? data.questions : [];
        const setData = (next) => updateContent('openhouse_settings', next);
        const updateQuestion = (idx, patch) => {
          const next = questions.map((q, i) => (i === idx ? { ...q, ...patch } : q));
          setData({ ...data, questions: next });
        };
        const moveQuestion = (idx, dir) => {
          const j = idx + dir;
          if (j < 0 || j >= questions.length) return;
          const next = [...questions];
          [next[idx], next[j]] = [next[j], next[idx]];
          setData({ ...data, questions: next });
        };
        const removeQuestion = (idx) => setData({ ...data, questions: questions.filter((_, i) => i !== idx) });
        const addQuestion = () => setData({ ...data, questions: [...questions, { id: `q_${Date.now()}`, type: 'single', label: 'New question', options: ['Option 1', 'Option 2'], required: true }] });
        return (
          <div>
            {/* Settings */}
            <div style={{ background: palette.white, border: `1px solid ${palette.border}`, padding: '16px', marginBottom: 16 }}>
              <h4 style={{ fontFamily: fonts, fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, margin: '0 0 12px' }}>Page settings</h4>
              <p style={{ fontFamily: fonts, fontSize: 12, color: palette.textMuted, margin: '0 0 12px' }}>
                Lives at <strong>/openhouse</strong>. If <strong>Gallery URL</strong> is set, the unlock screen shows a button to that link (e.g. a Google Drive folder). Otherwise the unlock screen shows photos uploaded with the matching <strong>Event tag</strong> below.
              </p>
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Headline</label>
                <input type="text" value={data.headline || ''} onChange={(e) => setData({ ...data, headline: e.target.value })} style={inputStyle} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Intro paragraph</label>
                <textarea value={data.intro || ''} onChange={(e) => setData({ ...data, intro: e.target.value })} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Gallery URL (Google Drive folder, etc.) — leave blank to use uploaded photos</label>
                <input type="text" value={data.galleryUrl || ''} onChange={(e) => setData({ ...data, galleryUrl: e.target.value })} placeholder="https://drive.google.com/drive/folders/..." style={inputStyle} />
              </div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 220px' }}>
                  <label style={labelStyle}>Event tag (photo filter slug)</label>
                  <input type="text" value={data.eventTag || ''} onChange={(e) => setData({ ...data, eventTag: e.target.value })} placeholder="open-house-2026-04" style={inputStyle} />
                </div>
                <div style={{ flex: '1 1 220px' }}>
                  <label style={labelStyle}>Expires after (event ends)</label>
                  <input type="date" value={data.expiresAt || ''} onChange={(e) => setData({ ...data, expiresAt: e.target.value })} style={inputStyle} />
                </div>
                <div style={{ flex: '1 1 220px' }}>
                  <label style={labelStyle}>Instagram URL</label>
                  <input type="text" value={data.instagramUrl || ''} onChange={(e) => setData({ ...data, instagramUrl: e.target.value })} placeholder="https://instagram.com/hddn.lab" style={inputStyle} />
                </div>
                <div style={{ flex: '1 1 220px' }}>
                  <label style={labelStyle}>TikTok URL</label>
                  <input type="text" value={data.tiktokUrl || ''} onChange={(e) => setData({ ...data, tiktokUrl: e.target.value })} placeholder="https://tiktok.com/@hddn.lab" style={inputStyle} />
                </div>
              </div>

              {/* Between-events copy */}
              <div style={{ marginTop: 18, paddingTop: 14, borderTop: `1px dashed ${palette.border}` }}>
                <p style={{ fontFamily: fonts, fontSize: 11, color: palette.textMuted, margin: '0 0 10px', letterSpacing: 1, textTransform: 'uppercase' }}>
                  After expiration — newsletter signup view
                </p>
                <p style={{ fontFamily: fonts, fontSize: 12, color: palette.textMuted, margin: '0 0 10px' }}>
                  Once <strong>Expires after</strong> is in the past, /openhouse swaps to a simple newsletter signup form using the copy below. Visitors can drop their email to be notified about the next event.
                </p>
                <div style={{ marginBottom: 10 }}>
                  <label style={labelStyle}>Headline (after expiration)</label>
                  <input type="text" value={data.expiredHeadline || ''} onChange={(e) => setData({ ...data, expiredHeadline: e.target.value })} style={inputStyle} placeholder="Open House — Stay in the Loop" />
                </div>
                <div>
                  <label style={labelStyle}>Description (after expiration)</label>
                  <textarea value={data.expiredDescription || ''} onChange={(e) => setData({ ...data, expiredDescription: e.target.value })} rows={4} style={{ ...inputStyle, resize: 'vertical' }} placeholder="What is Open House? Why should they sign up?" />
                </div>
              </div>

              {/* Start a new open house */}
              <div style={{ marginTop: 18, paddingTop: 14, borderTop: `1px dashed ${palette.border}` }}>
                <button
                  type="button"
                  onClick={async () => {
                    const oldEventTag = (data.eventTag || '').trim();
                    const today = new Date();
                    const ym = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
                    const slug = window.prompt('Slug for the NEW open house (used to tag photos and group responses)', `open-house-${ym}`);
                    if (!slug) return;
                    const expires = window.prompt('Expires on (YYYY-MM-DD) — leave blank to keep open indefinitely', '');

                    // Snapshot current state so /openhouse/<oldEventTag> still works for sharing.
                    if (oldEventTag) {
                      try {
                        await api('/api/openhouse/archive', {
                          method: 'POST',
                          body: JSON.stringify({ slug: oldEventTag, content: data }),
                        });
                      } catch (err) {
                        const msg = (err && (err.error || err.message)) || 'unknown error';
                        if (!confirm(`Could not archive the current event (${msg}). Continue anyway? Old event won't be accessible at /openhouse/${oldEventTag}.`)) return;
                      }
                    }

                    setData({
                      ...data,
                      eventTag: slug.trim(),
                      galleryUrl: '',
                      expiresAt: expires ? expires.trim() : '',
                    });
                    window.alert(`New event prepped. The previous event was archived at /openhouse/${oldEventTag} so attendees can still revisit it. Update headline / intro / Gallery URL for this round, then click Save Changes.`);
                  }}
                  style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', padding: '8px 14px', background: palette.brown, color: palette.cream, border: 'none', cursor: 'pointer' }}
                >
                  Start a new open house
                </button>
                <span style={{ fontFamily: fonts, fontSize: 11, color: palette.textMuted, marginLeft: 10 }}>
                  Archives the current event under its slug, then resets event tag, gallery URL, and expiration.
                </span>
              </div>

              {/* Past events list */}
              <OpenHouseArchiveList />
            </div>

            {/* Survey questions */}
            <div style={{ background: palette.white, border: `1px solid ${palette.border}`, padding: '16px', marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h4 style={{ fontFamily: fonts, fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, margin: 0 }}>Survey questions</h4>
                <button onClick={addQuestion} style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', padding: '6px 12px', background: palette.brown, color: palette.cream, border: 'none', cursor: 'pointer' }}>+ Add question</button>
              </div>
              <p style={{ fontFamily: fonts, fontSize: 12, color: palette.textMuted, margin: '0 0 12px' }}>
                Email is collected automatically as the final step — do not add it here.
              </p>
              {questions.length === 0 && <p style={{ color: palette.textMuted, fontSize: 13 }}>No questions yet. Click "Add question" to start.</p>}
              {questions.map((q, idx) => (
                <div key={q.id || idx} style={{ border: `1px solid ${palette.border}`, padding: '12px', marginBottom: 10, background: palette.beige }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8 }}>
                    <span style={{ fontFamily: fonts, fontSize: 11, color: palette.textMuted, padding: '4px 6px' }}>#{idx + 1}</span>
                    <input type="text" value={q.label || ''} onChange={(e) => updateQuestion(idx, { label: e.target.value })} placeholder="Question text" style={{ ...inputStyle, flex: 1 }} />
                    <select value={q.type || 'single'} onChange={(e) => updateQuestion(idx, { type: e.target.value })} style={{ ...inputStyle, width: 130 }}>
                      <option value="single">Single choice</option>
                      <option value="multi">Multi choice</option>
                      <option value="short">Short text</option>
                      <option value="long">Long text</option>
                    </select>
                    <label style={{ fontFamily: fonts, fontSize: 11, color: palette.textMuted, display: 'flex', alignItems: 'center', gap: 4, padding: '6px' }}>
                      <input type="checkbox" checked={q.required !== false} onChange={(e) => updateQuestion(idx, { required: e.target.checked })} />
                      Required
                    </label>
                    <button onClick={() => moveQuestion(idx, -1)} disabled={idx === 0} style={{ fontFamily: fonts, fontSize: 12, padding: '4px 8px', background: palette.white, border: `1px solid ${palette.border}`, cursor: idx === 0 ? 'not-allowed' : 'pointer', opacity: idx === 0 ? 0.4 : 1 }}>↑</button>
                    <button onClick={() => moveQuestion(idx, 1)} disabled={idx === questions.length - 1} style={{ fontFamily: fonts, fontSize: 12, padding: '4px 8px', background: palette.white, border: `1px solid ${palette.border}`, cursor: idx === questions.length - 1 ? 'not-allowed' : 'pointer', opacity: idx === questions.length - 1 ? 0.4 : 1 }}>↓</button>
                    <button onClick={() => removeQuestion(idx)} style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, padding: '4px 10px', background: palette.red, color: palette.white, border: 'none', cursor: 'pointer' }}>Delete</button>
                  </div>
                  {(q.type === 'single' || q.type === 'multi') && (
                    <div style={{ paddingLeft: 30 }}>
                      <label style={labelStyle}>Options (one per line)</label>
                      <textarea
                        value={(q.options || []).join('\n')}
                        onChange={(e) => updateQuestion(idx, { options: e.target.value.split('\n') })}
                        rows={Math.max(2, (q.options || []).length)}
                        style={{ ...inputStyle, resize: 'vertical' }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Responses viewer */}
            <OpenHouseResponsesPanel />
          </div>
        );
      })()}

      {/* Save Button */}
      <div style={{ marginTop: 20, display: 'flex', gap: 12, alignItems: 'center' }}>
        <button
          onClick={() => saveSection(activeSection)}
          disabled={saving}
          style={{
            fontFamily: fonts, fontSize: 13, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase',
            padding: '10px 28px', background: palette.orange, color: palette.cream, border: 'none',
            cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1,
          }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
        <button
          onClick={() => {
            if (window.confirm('Reset this section to defaults? Unsaved changes will be lost.')) {
              updateContent(activeSection, defaults[activeSection]);
            }
          }}
          style={{
            fontFamily: fonts, fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
            padding: '10px 20px', background: 'transparent', color: palette.textMuted,
            border: `1px solid ${palette.border}`, cursor: 'pointer',
          }}
        >
          Reset to Defaults
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Edit Booking Modal (Admin)
// ---------------------------------------------------------------------------

function EditBookingModal({ booking, onClose, onSave }) {
  const [date, setDate] = useState(typeof booking.date === 'string' ? booking.date.split('T')[0] : booking.date);
  const [room, setRoom] = useState(booking.room);
  const [service, setService] = useState(booking.service);
  const [startTime, setStartTime] = useState(booking.start_time || `${String(OPEN_HOUR).padStart(2, '0')}:00`);
  const [hours, setHours] = useState(booking.hours || MIN_HOURS);
  const [addOns, setAddOns] = useState(() => {
    try { return typeof booking.add_ons === 'string' ? JSON.parse(booking.add_ons) : (booking.add_ons || []); } catch { return []; }
  });
  const [notes, setNotes] = useState(booking.notes || '');
  const [description, setDescription] = useState(booking.description || '');
  const [attendees, setAttendees] = useState(booking.attendees || 1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const toggleAddon = (id) => {
    setAddOns((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  // Reset service and add-ons when room changes (admin sees all, including disabled)
  useEffect(() => {
    const roomServices = getServicesForRoom(room, null, { includeDisabled: true });
    if (!roomServices.find((s) => s.id === service)) {
      setService(roomServices[0]?.id || '');
      setAddOns([]);
    }
  }, [room]);

  const startH = parseInt(startTime.split(':')[0]);
  const maxHours = CLOSE_HOUR - startH;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await onSave({ id: booking.id, date, room, service, startTime, hours, addOns, notes, description, attendees });
    } catch (err) {
      setError(err.error || 'Failed to update booking');
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '10px 12px', fontFamily: fonts, fontSize: 14,
    border: `1px solid ${palette.border}`, background: palette.white,
    color: palette.text, boxSizing: 'border-box', outline: 'none',
  };
  const labelStyle = {
    display: 'block', fontSize: 11, letterSpacing: 1,
    textTransform: 'uppercase', color: palette.textMuted, marginBottom: 6,
  };
  const stepBtnStyle = {
    fontFamily: fonts, fontSize: 18, fontWeight: 700, width: 40, height: 40,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    border: `1px solid ${palette.border}`, background: palette.white,
    color: palette.brown, cursor: 'pointer',
  };

  const roomLabel = ROOMS.find((r) => r.id === room)?.label || room;

  return (
    <div
      className="hddn-sheet-backdrop"
      style={{ position: 'fixed', inset: 0, background: 'rgba(60,43,31,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="hddn-sheet"
        style={{ background: palette.cream, border: `1px solid ${palette.border}`, boxShadow: '0 8px 30px rgba(0,0,0,0.15)', width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', padding: '32px 28px' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h3 style={{ margin: 0, fontFamily: fonts, fontSize: 16, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown }}>
            Edit Booking
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: palette.textMuted, lineHeight: 1 }}>
            &times;
          </button>
        </div>

        {/* Guest info (read-only) */}
        <div style={{ background: palette.beige, padding: '10px 14px', marginBottom: 20, fontSize: 13, lineHeight: 1.8 }}>
          <div><strong>Name:</strong> {booking.user_name || 'Guest'}</div>
          <div><strong>Email:</strong> {booking.user_email || '—'}</div>
          <div><strong>Ref:</strong> {booking.booking_ref || booking.id?.slice(0, 8).toUpperCase()}</div>
        </div>

        {error && (
          <div style={{ background: '#fdf2f0', border: `1px solid ${palette.red}`, color: palette.red, padding: '8px 12px', fontSize: 13, marginBottom: 16 }}>
            {error}
          </div>
        )}

        <form onSubmit={submit}>
          {/* Date */}
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Date</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required style={inputStyle} />
          </div>

          {/* Room */}
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Room</label>
            <select value={room} onChange={(e) => setRoom(e.target.value)} style={inputStyle}>
              {ROOMS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
          </div>

          {/* Service */}
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Service</label>
            <select value={service} onChange={(e) => setService(e.target.value)} style={inputStyle}>
              {getServicesForRoom(room, null, { includeDisabled: true }).map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>

          {/* Description */}
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Session Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
          </div>

          {/* Attendees */}
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Attendees (15 max)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button type="button" onClick={() => setAttendees(Math.max(1, attendees - 1))} disabled={attendees <= 1} style={{ ...stepBtnStyle, opacity: attendees <= 1 ? 0.4 : 1 }}>&minus;</button>
              <span style={{ fontFamily: fonts, fontSize: 20, fontWeight: 700, color: palette.brown, minWidth: 40, textAlign: 'center' }}>{attendees}</span>
              <button type="button" onClick={() => setAttendees(Math.min(15, attendees + 1))} disabled={attendees >= 15} style={{ ...stepBtnStyle, opacity: attendees >= 15 ? 0.4 : 1 }}>+</button>
            </div>
          </div>

          {/* Start Time */}
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Start Time</label>
            <select value={startTime} onChange={(e) => setStartTime(e.target.value)} style={inputStyle}>
              {START_TIMES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>

          {/* Hours */}
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Duration (hours)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button type="button" onClick={() => setHours(Math.max(MIN_HOURS, hours - 1))} disabled={hours <= MIN_HOURS} style={{ ...stepBtnStyle, opacity: hours <= MIN_HOURS ? 0.4 : 1 }}>&minus;</button>
              <span style={{ fontFamily: fonts, fontSize: 20, fontWeight: 700, color: palette.brown, minWidth: 40, textAlign: 'center' }}>{hours}</span>
              <button type="button" onClick={() => setHours(Math.min(maxHours, hours + 1))} disabled={hours >= maxHours} style={{ ...stepBtnStyle, opacity: hours >= maxHours ? 0.4 : 1 }}>+</button>
              <span style={{ fontSize: 13, color: palette.textMuted }}>{formatTime(startTime)} &ndash; {formatEndTime(startTime, hours)}</span>
            </div>
            <div style={{ fontSize: 12, color: palette.sage, marginTop: 6 }}>{hours} hr{hours > 1 ? 's' : ''} x $100/hr = ${hours * 100}</div>
          </div>

          {/* Add-ons */}
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Add-Ons</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {(isPodcastService(service) ? PODCAST_ADD_ONS : ADD_ONS).map((a) => {
                const active = addOns.includes(a.id);
                return (
                  <button key={a.id} type="button" onClick={() => toggleAddon(a.id)} style={{
                    fontFamily: fonts, fontSize: 12, padding: '6px 12px',
                    border: `1px solid ${active ? palette.sage : palette.border}`,
                    background: active ? palette.sage : palette.white,
                    color: active ? palette.white : palette.text,
                    cursor: 'pointer',
                  }}>
                    {a.label}{a.price ? ` (+$${a.price})` : ''}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>Notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%', padding: '12px', fontFamily: fonts, fontSize: 14, fontWeight: 700,
              letterSpacing: 2, textTransform: 'uppercase', background: palette.orange,
              color: palette.cream, border: 'none', cursor: loading ? 'wait' : 'pointer',
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reset Password Modal
// ---------------------------------------------------------------------------

function ResetPasswordModal({ token, onClose }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) { setError('Passwords do not match'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      const data = await api('/api/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, password }),
      });
      setSuccess(data.message || 'Password reset successfully!');
      setTimeout(onClose, 2500);
    } catch (err) {
      setError(err.error || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '10px 12px', fontFamily: sans, fontSize: 14,
    border: `1px solid ${palette.border}`, background: palette.white,
    color: palette.text, boxSizing: 'border-box', outline: 'none',
  };
  const labelStyle = {
    display: 'block', fontSize: 12, letterSpacing: 1,
    textTransform: 'uppercase', color: palette.textMuted, marginBottom: 6,
  };

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        background: palette.white, border: `1px solid ${palette.border}`,
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)', maxWidth: 420, width: '100%',
        padding: '32px 28px',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h3 style={{ margin: 0, fontFamily: fonts, fontSize: 16, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown }}>
          Set New Password
        </h3>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: palette.textMuted, lineHeight: 1 }}>
          &times;
        </button>
      </div>

      {error && (
        <div style={{ background: '#fdf2f0', border: `1px solid ${palette.red}`, color: palette.red, padding: '8px 12px', fontSize: 13, marginBottom: 16 }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{ background: '#f0fdf2', border: `1px solid ${palette.sage}`, color: palette.sage, padding: '8px 12px', fontSize: 13, marginBottom: 16 }}>
          {success}
        </div>
      )}

      {!success && (
        <form onSubmit={submit}>
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>New Password</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} style={inputStyle} />
          </div>
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>Confirm Password</label>
            <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required style={inputStyle} />
          </div>
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%', padding: '12px', fontFamily: fonts, fontSize: 14,
              fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase',
              background: palette.orange, color: palette.cream, border: 'none',
              cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? '...' : 'Reset Password'}
          </button>
        </form>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Open House Landing Page (gated photo/video gallery + Typeform-style survey)
// ---------------------------------------------------------------------------

function OpenHousePage({ archiveSlug }) {
  const [config, setConfig] = useState(null);
  const [archiveNotFound, setArchiveNotFound] = useState(false);
  const [step, setStep] = useState(-1); // -1 = hero, 0..N-1 = questions, N = email, N+1 = unlocked
  const [answers, setAnswers] = useState({});
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState(() => {
    if (typeof window === 'undefined') return '';
    try { return window.sessionStorage.getItem('hddn_openhouse_name') || ''; } catch { return ''; }
  });
  const [nameError, setNameError] = useState('');
  const [subscribe, setSubscribe] = useState(true);
  const [emailError, setEmailError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [unlocked, setUnlocked] = useState(() => {
    if (typeof window === 'undefined') return false;
    try { return window.sessionStorage.getItem('hddn_openhouse_unlocked') === '1'; } catch { return false; }
  });

  useEffect(() => {
    setArchiveNotFound(false);
    if (archiveSlug) {
      fetch(`${API_BASE}/api/openhouse/archive/${encodeURIComponent(archiveSlug)}`)
        .then(async (r) => {
          if (r.status === 404) { setArchiveNotFound(true); return null; }
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json();
        })
        .then((data) => { if (data && data.content) setConfig(data.content); })
        .catch(() => setArchiveNotFound(true));
    } else {
      fetch(`${API_BASE}/api/openhouse/config`)
        .then((r) => r.json())
        .then((data) => setConfig(data))
        .catch(() => setConfig({}));
    }
  }, [archiveSlug]);

  // Archive view: show the unlocked gallery + content directly (no survey gating).
  // The unlock state below is overridden by the archive-aware logic further down.

  if (archiveNotFound) {
    return (
      <div style={{ ...baseStyles.app, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{ textAlign: 'center', maxWidth: 480 }}>
          <h1 style={{ fontFamily: fonts, fontSize: 22, color: palette.brown, margin: '0 0 12px' }}>Event not found</h1>
          <p style={{ fontSize: 14, color: palette.textMuted, marginBottom: 18 }}>We couldn't find an archived open house at that link.</p>
          <a href="/openhouse" style={{ fontFamily: fonts, fontSize: 12, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', padding: '10px 20px', background: palette.brown, color: palette.cream, textDecoration: 'none', display: 'inline-block' }}>
            Latest Open House &rarr;
          </a>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div style={{ ...baseStyles.app, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: palette.textMuted, fontFamily: fonts }}>Loading...</div>
      </div>
    );
  }

  // For archived events, treat as unlocked from the start — no survey, no
  // expiration redirect. Just show the gallery + share buttons.
  const isArchive = !!archiveSlug;

  // Event is expired when expiresAt is set and in the past. We compare at end-of-day
  // so the configured day still shows the active event until midnight.
  const isExpired = isArchive ? false : (() => {
    if (!config.expiresAt) return false;
    try {
      const end = new Date(config.expiresAt);
      if (Number.isNaN(end.getTime())) return false;
      // Treat the date as end-of-day local time
      end.setHours(23, 59, 59, 999);
      return Date.now() > end.getTime();
    } catch { return false; }
  })();

  const questions = Array.isArray(config.questions) ? config.questions : [];
  const totalSteps = questions.length + 1; // questions + email
  const onQuestion = step >= 0 && step < questions.length;
  const onEmail = step === questions.length;
  const onUnlocked = isArchive || unlocked || step > questions.length;

  const setAnswer = (qid, value) => setAnswers((prev) => ({ ...prev, [qid]: value }));

  const isAnswered = (q) => {
    if (!q.required) return true;
    const v = answers[q.id];
    if (q.type === 'multi') return Array.isArray(v) && v.length > 0;
    if (q.type === 'long' || q.type === 'short') return typeof v === 'string' && v.trim().length > 0;
    return v !== undefined && v !== null && v !== '';
  };

  const next = () => {
    if (onQuestion) {
      const q = questions[step];
      if (!isAnswered(q)) return;
    }
    setStep((s) => s + 1);
  };
  const back = () => setStep((s) => Math.max(-1, s - 1));

  const handleSubmit = async () => {
    setNameError('');
    setEmailError('');
    setSubmitError('');
    const trimmedName = firstName.trim();
    const trimmed = email.trim();
    if (!trimmedName) {
      setNameError('First name is required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/openhouse/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed, name: trimmedName, answers, subscribe }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Submission failed');
      }
      try {
        window.sessionStorage.setItem('hddn_openhouse_unlocked', '1');
        window.sessionStorage.setItem('hddn_openhouse_name', trimmedName);
      } catch (_e) { /* no sessionStorage */ }
      setUnlocked(true);
      setStep(questions.length + 1);
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const progressPct = onQuestion
    ? Math.round(((step + 1) / totalSteps) * 100)
    : onEmail
      ? 100
      : 0;

  const headerBar = (
    <header style={{ background: palette.brown, padding: '16px 0', borderBottom: `3px solid ${palette.orange}`, textAlign: 'center' }}>
      <a href="/" style={{ textDecoration: 'none', display: 'inline-block' }} title="Back to HDDN LAB booking">
        <span style={{
          fontFamily: `'Trade Gothic Next LT Pro Bold Compressed', 'Trade Gothic Next LT Pro', 'Trade Gothic Next', 'Arial Narrow', sans-serif`,
          fontWeight: 700, fontSize: 22, color: palette.cream, letterSpacing: 4, textTransform: 'uppercase',
        }}>HDDN LAB</span>
        <span style={{ color: palette.orange, fontSize: 13, letterSpacing: 2, marginLeft: 10 }}>OPEN HOUSE</span>
      </a>
    </header>
  );

  const card = (children) => (
    <div style={{
      background: palette.white,
      border: `1px solid ${palette.border}`,
      maxWidth: 560,
      width: '100%',
      padding: '40px 32px',
      boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    }}>{children}</div>
  );

  // ============ Expired: collect newsletter signups for the next event ============
  if (isExpired && !onUnlocked) {
    return <OpenHouseExpiredView config={config} headerBar={headerBar} />;
  }

  // ============ Unlocked: gallery + Instagram CTA ============
  if (onUnlocked) {
    return (
      <div style={baseStyles.app}>
        {headerBar}
        <main className="hddn-container" style={{ ...baseStyles.container, paddingTop: 32, paddingBottom: 60 }}>
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <h1 style={{ fontFamily: fonts, fontSize: 26, fontWeight: 700, color: palette.brown, margin: 0, letterSpacing: 1 }}>
              Thanks{firstName ? `, ${firstName.split(' ')[0]}` : ''}!
            </h1>
            <p style={{ fontSize: 14, color: palette.textMuted, marginTop: 12, maxWidth: 540, margin: '12px auto 0' }}>
              Here are the photos and videos from the open house. While you're here — follow us so you don't miss the next one.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginTop: 20 }}>
              <a
                href={config.instagramUrl || 'https://instagram.com/hddn.lab'}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  padding: '12px 24px',
                  background: palette.orange,
                  color: palette.white,
                  fontFamily: fonts, fontSize: 13, fontWeight: 700, letterSpacing: 2,
                  textTransform: 'uppercase', textDecoration: 'none',
                }}
              >
                Instagram
              </a>
              <a
                href={config.tiktokUrl || 'https://tiktok.com/@hddn.lab'}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  padding: '12px 24px',
                  background: palette.brown,
                  color: palette.cream,
                  fontFamily: fonts, fontSize: 13, fontWeight: 700, letterSpacing: 2,
                  textTransform: 'uppercase', textDecoration: 'none',
                }}
              >
                TikTok
              </a>
            </div>
            <div style={{
              maxWidth: 560, margin: '24px auto 0',
              padding: '14px 18px',
              border: `1px solid ${palette.border}`,
              background: palette.beige,
              fontSize: 13, color: palette.text, lineHeight: 1.5,
            }}>
              Posting your own pics or videos from the night? Invite us as a <strong>collaborator</strong> on your Instagram post, or tag <strong>@hddn.lab</strong> on TikTok — we'd love to repost.
            </div>
          </div>
          {config.galleryUrl ? (
            <div style={{ textAlign: 'center', padding: '32px 24px', background: palette.beige, border: `1px solid ${palette.border}` }}>
              <div style={{ fontFamily: fonts, fontSize: 12, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: palette.textMuted, marginBottom: 14 }}>
                Open House Gallery
              </div>
              <p style={{ fontSize: 14, color: palette.text, marginTop: 0, marginBottom: 20, maxWidth: 520, marginLeft: 'auto', marginRight: 'auto' }}>
                All the photos and videos from the night are in this shared folder. Click below to open it.
              </p>
              <a
                href={config.galleryUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  padding: '14px 32px',
                  background: palette.brown,
                  color: palette.cream,
                  fontFamily: fonts, fontSize: 14, fontWeight: 700, letterSpacing: 2,
                  textTransform: 'uppercase', textDecoration: 'none',
                }}
              >
                View photos &amp; videos &rarr;
              </a>
            </div>
          ) : (
            <PhotoGallery event={config.eventTag || 'open-house-2026-04'} title="Open House" />
          )}

          {/* Book the space CTA */}
          <div style={{
            marginTop: 48,
            padding: '32px 28px',
            background: palette.brown,
            color: palette.cream,
            textAlign: 'center',
          }}>
            <div style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: palette.orange, marginBottom: 10 }}>
              Now booking
            </div>
            <h2 style={{ fontFamily: fonts, fontSize: 22, fontWeight: 700, color: palette.cream, margin: 0, letterSpacing: 1 }}>
              Want the space for yourself?
            </h2>
            <p style={{ fontSize: 14, color: palette.cream, opacity: 0.85, lineHeight: 1.6, marginTop: 14, maxWidth: 540, margin: '14px auto 0' }}>
              The studio is open for bookings — photo and video shoots, podcast recording, creative sessions, and more from $100/hr. Got something different in mind? <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: palette.orange, textDecoration: 'underline' }}>Reach out</a> and let's talk.
            </p>
            <a
              href="/"
              style={{
                display: 'inline-block',
                marginTop: 20,
                padding: '14px 32px',
                background: palette.orange,
                color: palette.white,
                fontFamily: fonts, fontSize: 14, fontWeight: 700, letterSpacing: 2,
                textTransform: 'uppercase', textDecoration: 'none',
              }}
            >
              Book the space &rarr;
            </a>
          </div>

          <div style={{ textAlign: 'center', marginTop: 30 }}>
            <a href="/" style={{ fontFamily: fonts, fontSize: 12, color: palette.textMuted, letterSpacing: 2, textTransform: 'uppercase' }}>
              ← Back to HDDN LAB
            </a>
          </div>
        </main>
      </div>
    );
  }

  // ============ Hero (step === -1) ============
  if (step === -1) {
    return (
      <div style={baseStyles.app}>
        {headerBar}
        <main className="hddn-container" style={{ ...baseStyles.container, paddingTop: 60, paddingBottom: 60, display: 'flex', justifyContent: 'center' }}>
          {card(
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: palette.orange, marginBottom: 12 }}>
                Open House
              </div>
              <h1 style={{ fontFamily: fonts, fontSize: 26, fontWeight: 700, color: palette.brown, margin: 0, letterSpacing: 1 }}>
                {config.headline || 'Thanks for coming to the HDDN Lab Open House'}
              </h1>
              <p style={{ fontSize: 14, color: palette.text, lineHeight: 1.6, marginTop: 18, marginBottom: 28 }}>
                {config.intro || "We snapped photos and shot videos of everyone — fill out a quick survey and we'll send the link."}
              </p>
              <button
                onClick={next}
                style={{
                  fontFamily: fonts, fontSize: 14, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase',
                  padding: '14px 32px', background: palette.brown, color: palette.cream,
                  border: 'none', cursor: 'pointer',
                }}
              >
                Get the photos &amp; videos &rarr;
              </button>
              <div style={{ fontSize: 11, color: palette.textMuted, marginTop: 16, letterSpacing: 1 }}>
                Takes about 60 seconds
              </div>
            </div>
          )}
        </main>
      </div>
    );
  }

  // ============ Survey question (step 0..N-1) and email (step N) ============
  return (
    <div style={baseStyles.app}>
      {headerBar}
      {/* Progress bar */}
      <div style={{ height: 4, background: palette.border, position: 'relative' }}>
        <div style={{ height: '100%', width: `${progressPct}%`, background: palette.orange, transition: 'width 0.3s' }} />
      </div>
      <main className="hddn-container" style={{ ...baseStyles.container, paddingTop: 40, paddingBottom: 60, display: 'flex', justifyContent: 'center' }}>
        {card(
          <div>
            <div style={{ fontFamily: fonts, fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: palette.textMuted, marginBottom: 16 }}>
              {onEmail ? `Step ${totalSteps} of ${totalSteps}` : `Step ${step + 1} of ${totalSteps}`}
            </div>

            {onQuestion && (() => {
              const q = questions[step];
              return (
                <div>
                  <h2 style={{ fontFamily: fonts, fontSize: 20, fontWeight: 700, color: palette.brown, margin: 0, lineHeight: 1.3 }}>
                    {q.label}
                    {q.required && <span style={{ color: palette.orange, marginLeft: 4 }}>*</span>}
                  </h2>
                  <div style={{ marginTop: 24 }}>
                    {q.type === 'single' && Array.isArray(q.options) && q.options.map((opt) => (
                      <label key={opt} style={{
                        display: 'block', padding: '14px 18px', marginBottom: 10,
                        border: `2px solid ${answers[q.id] === opt ? palette.brown : palette.border}`,
                        background: answers[q.id] === opt ? palette.beige : palette.white,
                        cursor: 'pointer', fontSize: 14, color: palette.text,
                      }}>
                        <input
                          type="radio"
                          name={q.id}
                          checked={answers[q.id] === opt}
                          onChange={() => setAnswer(q.id, opt)}
                          style={{ marginRight: 12 }}
                        />
                        {opt}
                      </label>
                    ))}
                    {q.type === 'multi' && Array.isArray(q.options) && q.options.map((opt) => {
                      const selected = Array.isArray(answers[q.id]) && answers[q.id].includes(opt);
                      return (
                        <label key={opt} style={{
                          display: 'block', padding: '14px 18px', marginBottom: 10,
                          border: `2px solid ${selected ? palette.brown : palette.border}`,
                          background: selected ? palette.beige : palette.white,
                          cursor: 'pointer', fontSize: 14, color: palette.text,
                        }}>
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={(e) => {
                              const cur = Array.isArray(answers[q.id]) ? answers[q.id] : [];
                              setAnswer(q.id, e.target.checked ? [...cur, opt] : cur.filter((x) => x !== opt));
                            }}
                            style={{ marginRight: 12 }}
                          />
                          {opt}
                        </label>
                      );
                    })}
                    {q.type === 'short' && (
                      <input
                        type="text"
                        value={answers[q.id] || ''}
                        onChange={(e) => setAnswer(q.id, e.target.value)}
                        autoFocus
                        style={{
                          width: '100%', padding: '14px 18px', fontSize: 15, fontFamily: fonts,
                          border: `2px solid ${palette.border}`, background: palette.white,
                          color: palette.text, outline: 'none', boxSizing: 'border-box',
                        }}
                      />
                    )}
                    {q.type === 'long' && (
                      <textarea
                        value={answers[q.id] || ''}
                        onChange={(e) => setAnswer(q.id, e.target.value)}
                        rows={4}
                        autoFocus
                        style={{
                          width: '100%', padding: '14px 18px', fontSize: 15, fontFamily: fonts,
                          border: `2px solid ${palette.border}`, background: palette.white,
                          color: palette.text, outline: 'none', resize: 'vertical', boxSizing: 'border-box',
                        }}
                      />
                    )}
                  </div>
                </div>
              );
            })()}

            {onEmail && (
              <div>
                <h2 style={{ fontFamily: fonts, fontSize: 20, fontWeight: 700, color: palette.brown, margin: 0, lineHeight: 1.3 }}>
                  Last step — your name &amp; email
                  <span style={{ color: palette.orange, marginLeft: 4 }}>*</span>
                </h2>
                <p style={{ fontSize: 13, color: palette.textMuted, marginTop: 8 }}>
                  We'll unlock the photos and videos as soon as you submit.
                </p>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => { setFirstName(e.target.value); setNameError(''); }}
                  placeholder="First name"
                  autoFocus
                  autoComplete="given-name"
                  style={{
                    width: '100%', padding: '14px 18px', fontSize: 15, fontFamily: fonts,
                    border: `2px solid ${nameError ? palette.red : palette.border}`,
                    background: palette.white, color: palette.text, outline: 'none', boxSizing: 'border-box',
                    marginTop: 20,
                  }}
                />
                {nameError && <div style={{ color: palette.red, fontSize: 12, marginTop: 6 }}>{nameError}</div>}
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
                  placeholder="you@example.com"
                  autoComplete="email"
                  onKeyDown={(e) => { if (e.key === 'Enter' && !submitting) handleSubmit(); }}
                  style={{
                    width: '100%', padding: '14px 18px', fontSize: 15, fontFamily: fonts,
                    border: `2px solid ${emailError ? palette.red : palette.border}`,
                    background: palette.white, color: palette.text, outline: 'none', boxSizing: 'border-box',
                    marginTop: 12,
                  }}
                />
                <label style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 16,
                  padding: '12px 14px', border: `1px solid ${palette.border}`, background: palette.beige,
                  cursor: 'pointer', fontSize: 13, color: palette.text, lineHeight: 1.4,
                }}>
                  <input
                    type="checkbox"
                    checked={subscribe}
                    onChange={(e) => setSubscribe(e.target.checked)}
                    style={{ marginTop: 3 }}
                  />
                  <span>
                    Subscribe to the HDDN Lab newsletter — be the first to hear about upcoming events &amp; workshops. Unsubscribe anytime.
                  </span>
                </label>
                {emailError && <div style={{ color: palette.red, fontSize: 12, marginTop: 6 }}>{emailError}</div>}
                {submitError && <div style={{ color: palette.red, fontSize: 12, marginTop: 6 }}>{submitError}</div>}
              </div>
            )}

            {/* Nav buttons */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 32 }}>
              <button
                onClick={back}
                style={{
                  fontFamily: fonts, fontSize: 12, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase',
                  padding: '10px 18px', background: 'transparent', color: palette.textMuted,
                  border: `1px solid ${palette.border}`, cursor: 'pointer',
                }}
              >
                &larr; Back
              </button>
              {onEmail ? (
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  style={{
                    fontFamily: fonts, fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase',
                    padding: '12px 26px', background: palette.brown, color: palette.cream,
                    border: 'none', cursor: submitting ? 'wait' : 'pointer', opacity: submitting ? 0.6 : 1,
                  }}
                >
                  {submitting ? 'Submitting...' : 'Unlock the gallery'}
                </button>
              ) : (
                <button
                  onClick={next}
                  disabled={onQuestion && !isAnswered(questions[step])}
                  style={{
                    fontFamily: fonts, fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase',
                    padding: '12px 26px', background: palette.brown, color: palette.cream,
                    border: 'none',
                    cursor: onQuestion && !isAnswered(questions[step]) ? 'not-allowed' : 'pointer',
                    opacity: onQuestion && !isAnswered(questions[step]) ? 0.4 : 1,
                  }}
                >
                  Next &rarr;
                </button>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function OpenHouseExpiredView({ config, headerBar }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async () => {
    setError('');
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    if (!trimmedName) { setError('First name is required'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) { setError('Please enter a valid email address'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/openhouse/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmedEmail, name: trimmedName, answers: {}, subscribe: true, waitlist: true }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Submission failed');
      }
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={baseStyles.app}>
      {headerBar}
      <main style={{ ...baseStyles.container, paddingTop: 60, paddingBottom: 60, display: 'flex', justifyContent: 'center' }}>
        <div style={{
          background: palette.white, border: `1px solid ${palette.border}`,
          maxWidth: 560, width: '100%', padding: '40px 32px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: palette.orange, marginBottom: 12 }}>
              Open House
            </div>
            <h1 style={{ fontFamily: fonts, fontSize: 24, fontWeight: 700, color: palette.brown, margin: 0, letterSpacing: 1 }}>
              {config.expiredHeadline || 'Open House — Stay in the Loop'}
            </h1>
            <p style={{ fontSize: 14, color: palette.text, lineHeight: 1.6, marginTop: 16, marginBottom: 0 }}>
              {config.expiredDescription || "Open House is a chance to come experience HDDN Lab in person — meet other creators, network, and get a feel for the space. We host one every couple of months. Drop your email and we'll send you the details for the next one."}
            </p>
          </div>

          {done ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>&#10003;</div>
              <h2 style={{ fontFamily: fonts, fontSize: 16, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, margin: '0 0 6px' }}>
                You&apos;re on the list
              </h2>
              <p style={{ fontSize: 13, color: palette.textMuted }}>
                We&apos;ll send the details for the next Open House to your inbox.
              </p>
            </div>
          ) : (
            <div>
              <input
                type="text"
                value={name}
                onChange={(e) => { setName(e.target.value); setError(''); }}
                placeholder="First name"
                autoComplete="given-name"
                style={{
                  width: '100%', padding: '14px 18px', fontSize: 15, fontFamily: fonts,
                  border: `2px solid ${palette.border}`, background: palette.white, color: palette.text,
                  outline: 'none', boxSizing: 'border-box',
                }}
              />
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                placeholder="you@example.com"
                autoComplete="email"
                onKeyDown={(e) => { if (e.key === 'Enter' && !submitting) handleSubmit(); }}
                style={{
                  width: '100%', padding: '14px 18px', fontSize: 15, fontFamily: fonts,
                  border: `2px solid ${palette.border}`, background: palette.white, color: palette.text,
                  outline: 'none', boxSizing: 'border-box', marginTop: 10,
                }}
              />
              {error && <div style={{ color: palette.red, fontSize: 12, marginTop: 8 }}>{error}</div>}
              <button
                onClick={handleSubmit}
                disabled={submitting}
                style={{
                  width: '100%', marginTop: 16,
                  fontFamily: fonts, fontSize: 14, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase',
                  padding: '14px 28px', background: palette.brown, color: palette.cream,
                  border: 'none', cursor: submitting ? 'wait' : 'pointer', opacity: submitting ? 0.6 : 1,
                }}
              >
                {submitting ? 'Submitting…' : 'Notify me about the next one'}
              </button>
            </div>
          )}

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginTop: 28, paddingTop: 20, borderTop: `1px solid ${palette.border}` }}>
            <a href={config.instagramUrl || 'https://instagram.com/hddn.lab'} target="_blank" rel="noopener noreferrer"
              style={{ display: 'inline-block', padding: '10px 20px', background: palette.orange, color: palette.white, fontFamily: fonts, fontSize: 12, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', textDecoration: 'none' }}>
              Instagram
            </a>
            <a href={config.tiktokUrl || 'https://tiktok.com/@hddn.lab'} target="_blank" rel="noopener noreferrer"
              style={{ display: 'inline-block', padding: '10px 20px', background: palette.brown, color: palette.cream, fontFamily: fonts, fontSize: 12, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', textDecoration: 'none' }}>
              TikTok
            </a>
          </div>
          <div style={{ marginTop: 22, paddingTop: 18, borderTop: `1px solid ${palette.border}`, textAlign: 'center' }}>
            <p style={{ fontSize: 13, color: palette.textMuted, margin: '0 0 12px' }}>
              Looking to book the studio? Open now from $100/hr — photo / video / podcast / creative sessions, and we're open to other kinds of bookings too. <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: palette.orange }}>Email us</a> if you have a different idea.
            </p>
            <a
              href="/"
              style={{
                display: 'inline-block',
                padding: '10px 22px',
                background: palette.brown,
                color: palette.cream,
                fontFamily: fonts, fontSize: 12, fontWeight: 700, letterSpacing: 2,
                textTransform: 'uppercase', textDecoration: 'none',
              }}
            >
              Book the space &rarr;
            </a>
          </div>
        </div>
      </main>
    </div>
  );
}

function NewsletterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async () => {
    setError('');
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError('Please enter a valid email address');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmedEmail, name: trimmedName }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Submission failed');
      }
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const headerBar = <SimpleMasthead label="Newsletter" />;

  return (
    <div style={{ ...baseStyles.app, fontFamily: sans }}>
      {headerBar}
      <main style={{ ...baseStyles.container, paddingTop: 60, paddingBottom: 60, display: 'flex', justifyContent: 'center' }}>
        <div style={{
          background: palette.white, border: `1px solid ${palette.border}`,
          maxWidth: 520, width: '100%', padding: '40px 32px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: palette.orange, marginBottom: 12 }}>
              Subscribe
            </div>
            <h1 style={{ fontFamily: sans, fontSize: 30, fontWeight: 800, color: site.ink, margin: 0, letterSpacing: '-0.02em' }}>
              Stay in the Loop
            </h1>
            <p style={{ fontSize: 14, color: palette.text, lineHeight: 1.6, marginTop: 16, marginBottom: 0 }}>
              Open house invites, new bookings windows, behind-the-scenes from sessions in the studio. No spam — just the good stuff. Unsubscribe anytime.
            </p>
          </div>

          {done ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: 38, color: palette.sage, marginBottom: 10 }}>&#10003;</div>
              <h2 style={{ fontFamily: fonts, fontSize: 16, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, margin: '0 0 6px' }}>
                You&apos;re in
              </h2>
              <p style={{ fontSize: 13, color: palette.textMuted }}>
                Check your inbox — a welcome note may already be waiting.
              </p>
              <a href="/" style={{ display: 'inline-block', marginTop: 18, fontFamily: fonts, fontSize: 12, color: palette.orange, letterSpacing: 1, textTransform: 'uppercase' }}>
                ← Back to HDDN LAB
              </a>
            </div>
          ) : (
            <div>
              <input
                type="text"
                value={name}
                onChange={(e) => { setName(e.target.value); setError(''); }}
                placeholder="First name (optional)"
                autoComplete="given-name"
                style={{
                  width: '100%', padding: '14px 18px', fontSize: 15, fontFamily: sans,
                  border: `2px solid ${palette.border}`, background: palette.white, color: palette.text,
                  outline: 'none', boxSizing: 'border-box',
                }}
              />
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                placeholder="you@example.com"
                autoComplete="email"
                onKeyDown={(e) => { if (e.key === 'Enter' && !submitting) handleSubmit(); }}
                style={{
                  width: '100%', padding: '14px 18px', fontSize: 15, fontFamily: sans,
                  border: `2px solid ${palette.border}`, background: palette.white, color: palette.text,
                  outline: 'none', boxSizing: 'border-box', marginTop: 10,
                }}
              />
              {error && <div style={{ color: palette.red, fontSize: 12, marginTop: 8 }}>{error}</div>}
              <button
                onClick={handleSubmit}
                disabled={submitting}
                style={{
                  width: '100%', marginTop: 16,
                  fontFamily: fonts, fontSize: 14, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase',
                  padding: '14px 28px', background: palette.brown, color: palette.cream,
                  border: 'none', cursor: submitting ? 'wait' : 'pointer', opacity: submitting ? 0.6 : 1,
                }}
              >
                {submitting ? 'Subscribing…' : 'Subscribe'}
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function PhotographerQuotePage() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    description: '',
    referenceLinks: '',
    budget: '',
    timeline: '',
  });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const MAX_FILES = 8;
  const MAX_TOTAL_BYTES = 25 * 1024 * 1024 * MAX_FILES;
  const ACCEPTED = 'image/*,application/pdf,.heic,.heif';

  const updateField = (k, v) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    setError('');
  };

  const onPickFiles = (incoming) => {
    const list = Array.from(incoming || []);
    if (list.length === 0) return;
    const combined = [...files, ...list].slice(0, MAX_FILES);
    const totalSize = combined.reduce((sum, f) => sum + (f.size || 0), 0);
    if (totalSize > MAX_TOTAL_BYTES) {
      setError(`Attachments are too large. Max ~${Math.round(MAX_TOTAL_BYTES / (1024 * 1024))} MB total.`);
      return;
    }
    if (files.length + list.length > MAX_FILES) {
      setError(`Maximum ${MAX_FILES} attachments. Extra files ignored.`);
    } else {
      setError('');
    }
    setFiles(combined);
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setError('');
    const email = form.email.trim();
    const phone = form.phone.trim();
    const description = form.description.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!phone) {
      setError('Please include a phone number so we can reach you.');
      return;
    }
    if (description.length < 10) {
      setError('Tell us a little more about what you’re looking for.');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('name', form.name.trim());
      fd.append('email', email);
      fd.append('phone', phone);
      fd.append('projectType', form.projectType);
      fd.append('description', description);
      fd.append('referenceLinks', form.referenceLinks.trim());
      fd.append('budget', form.budget);
      fd.append('timeline', form.timeline);
      files.forEach((f) => fd.append('attachments', f));

      const res = await fetch(`${API_BASE}/api/photographer-quote`, {
        method: 'POST',
        body: fd,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Submission failed');
      }
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const headerBar = (
    <header style={{ background: palette.brown, padding: '16px 0', borderBottom: `3px solid ${palette.orange}`, textAlign: 'center' }}>
      <a href="/" style={{ textDecoration: 'none', display: 'inline-block' }}>
        <span style={{
          fontFamily: `'Trade Gothic Next LT Pro Bold Compressed', 'Trade Gothic Next LT Pro', 'Trade Gothic Next', 'Arial Narrow', sans-serif`,
          fontWeight: 700, fontSize: 22, color: palette.cream, letterSpacing: 4, textTransform: 'uppercase',
        }}>HDDN LAB</span>
        <span style={{ color: palette.orange, fontSize: 13, letterSpacing: 2, marginLeft: 10 }}>PHOTOGRAPHY</span>
      </a>
    </header>
  );

  const labelStyle = {
    fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase',
    color: palette.brown, display: 'block', marginBottom: 6,
  };
  const inputStyle = {
    width: '100%', padding: '12px 14px', fontSize: 14, fontFamily: fonts,
    border: `1px solid ${palette.border}`, background: palette.white, color: palette.text,
    outline: 'none', boxSizing: 'border-box',
  };

  const formatBytes = (b) => {
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div style={baseStyles.app}>
      <style>{`
        * { box-sizing: border-box; }
        body { margin: 0; }
        .quote-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
        @media (max-width: 640px) {
          .quote-grid-2 { grid-template-columns: 1fr !important; }
        }
      `}</style>
      {headerBar}
      <main style={{ ...baseStyles.container, paddingTop: 40, paddingBottom: 60, display: 'flex', justifyContent: 'center' }}>
        <div style={{
          background: palette.white, border: `1px solid ${palette.border}`,
          maxWidth: 720, width: '100%', padding: '36px 32px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        }}>
          <div style={{ marginBottom: 22 }}>
            <div style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: palette.orange, marginBottom: 10 }}>
              Need a Photographer?
            </div>
            <h1 style={{ fontFamily: fonts, fontSize: 26, fontWeight: 700, color: palette.brown, margin: 0, letterSpacing: 1 }}>
              Request a Quote
            </h1>
            <p style={{ fontSize: 14, color: palette.text, lineHeight: 1.6, marginTop: 14, marginBottom: 0 }}>
              Tell us what you’re looking for and we’ll match you with one of our in-house photographers. Share references, briefs, or moodboards — anything that helps us nail the vibe. We’ll get back to you with a quote and next steps within 1–2 business days.
            </p>
          </div>

          {done ? (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <div style={{ fontSize: 38, color: palette.sage, marginBottom: 10 }}>&#10003;</div>
              <h2 style={{ fontFamily: fonts, fontSize: 16, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, margin: '0 0 6px' }}>
                Request received
              </h2>
              <p style={{ fontSize: 13, color: palette.textMuted, lineHeight: 1.6, maxWidth: 420, margin: '0 auto' }}>
                Thanks — we&apos;ll review what you sent and reply with a quote and a few questions soon. Check your inbox for a confirmation.
              </p>
              <a href="/" style={{ display: 'inline-block', marginTop: 20, fontFamily: fonts, fontSize: 12, color: palette.orange, letterSpacing: 1, textTransform: 'uppercase' }}>
                ← Back to HDDN LAB
              </a>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="quote-grid-2" style={{ marginBottom: 14 }}>
                <div>
                  <label style={labelStyle}>Your name</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    placeholder="(optional)"
                    autoComplete="name"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Phone *</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => updateField('phone', e.target.value)}
                    placeholder="(555) 555-1234"
                    autoComplete="tel"
                    required
                    style={inputStyle}
                  />
                </div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Email *</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  required
                  style={inputStyle}
                />
              </div>

              <div className="quote-grid-2" style={{ marginBottom: 14 }}>
                <div>
                  <label style={labelStyle}>Project type</label>
                  <select
                    value={form.projectType}
                    onChange={(e) => updateField('projectType', e.target.value)}
                    style={{ ...inputStyle, appearance: 'auto' }}
                  >
                    <option value="">Select…</option>
                    <option value="portrait">Portrait</option>
                    <option value="headshots">Headshots</option>
                    <option value="product">Product / e-commerce</option>
                    <option value="brand">Brand / lifestyle</option>
                    <option value="editorial">Editorial / lookbook</option>
                    <option value="event">Event coverage</option>
                    <option value="music">Music / artist promo</option>
                    <option value="content">Content for social</option>
                    <option value="other">Something else</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Timeline</label>
                  <select
                    value={form.timeline}
                    onChange={(e) => updateField('timeline', e.target.value)}
                    style={{ ...inputStyle, appearance: 'auto' }}
                  >
                    <option value="">Select…</option>
                    <option value="asap">ASAP / this week</option>
                    <option value="2-4-weeks">In the next 2–4 weeks</option>
                    <option value="1-2-months">1–2 months out</option>
                    <option value="flexible">Flexible</option>
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Budget (optional)</label>
                <select
                  value={form.budget}
                  onChange={(e) => updateField('budget', e.target.value)}
                  style={{ ...inputStyle, appearance: 'auto' }}
                >
                  <option value="">Prefer not to say</option>
                  <option value="under-500">Under $500</option>
                  <option value="500-1000">$500 – $1,000</option>
                  <option value="1000-2500">$1,000 – $2,500</option>
                  <option value="2500-5000">$2,500 – $5,000</option>
                  <option value="5000-plus">$5,000+</option>
                  <option value="not-sure">Not sure yet</option>
                </select>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>What are you looking for? *</label>
                <textarea
                  value={form.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  rows={6}
                  placeholder="The shoot concept, deliverables, location, # of looks/people, anything we should know…"
                  required
                  style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }}
                />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Reference links</label>
                <textarea
                  value={form.referenceLinks}
                  onChange={(e) => updateField('referenceLinks', e.target.value)}
                  rows={3}
                  placeholder="Pinterest boards, IG posts, drive folders, brand sites — paste any URLs that show the vibe (one per line)."
                  style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }}
                />
              </div>

              <div style={{ marginBottom: 18 }}>
                <label style={labelStyle}>Attach references (images / PDFs)</label>
                <div style={{
                  border: `1.5px dashed ${palette.border}`, padding: 16, textAlign: 'center',
                  background: palette.beige,
                }}>
                  <input
                    id="quote-files"
                    type="file"
                    multiple
                    accept={ACCEPTED}
                    onChange={(e) => { onPickFiles(e.target.files); e.target.value = ''; }}
                    style={{ display: 'none' }}
                  />
                  <label
                    htmlFor="quote-files"
                    style={{
                      display: 'inline-block', cursor: 'pointer',
                      fontFamily: fonts, fontSize: 12, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase',
                      padding: '10px 20px', background: palette.brown, color: palette.cream,
                    }}
                  >
                    Choose files
                  </label>
                  <div style={{ fontSize: 11, color: palette.textMuted, marginTop: 8 }}>
                    Images or PDFs, up to {MAX_FILES} files · 25 MB each
                  </div>
                </div>
                {files.length > 0 && (
                  <ul style={{ listStyle: 'none', padding: 0, margin: '12px 0 0', display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {files.map((f, i) => (
                      <li key={i} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '8px 12px', border: `1px solid ${palette.border}`, background: palette.white,
                        fontFamily: fonts, fontSize: 12,
                      }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 10, color: palette.text }}>
                          {f.name} <span style={{ color: palette.textMuted }}>· {formatBytes(f.size)}</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => removeFile(i)}
                          style={{ background: 'none', border: 'none', color: palette.red, cursor: 'pointer', fontSize: 16, lineHeight: 1 }}
                          aria-label={`Remove ${f.name}`}
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {error && (
                <div style={{ color: palette.red, fontSize: 13, marginBottom: 12 }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                style={{
                  width: '100%',
                  fontFamily: fonts, fontSize: 14, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase',
                  padding: '14px 28px', background: palette.brown, color: palette.cream,
                  border: 'none', cursor: submitting ? 'wait' : 'pointer', opacity: submitting ? 0.6 : 1,
                }}
              >
                {submitting ? 'Sending…' : 'Send Request'}
              </button>
              <p style={{ fontSize: 11, color: palette.textMuted, marginTop: 12, marginBottom: 0, textAlign: 'center' }}>
                We&apos;ll only use your contact info to follow up about this project.
              </p>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}

// Renders an admin-protected image by fetching it with the auth header and
// turning the response into an object URL (an <img src> can't send headers).
function AuthedImg({ path, alt, style, onClick }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let revoked = false;
    let objUrl = null;
    const token = localStorage.getItem('hddn_token');
    fetch(`${API_BASE}${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => (r.ok ? r.blob() : Promise.reject()))
      .then((blob) => { if (!revoked) { objUrl = URL.createObjectURL(blob); setSrc(objUrl); } })
      .catch(() => setFailed(true));
    return () => { revoked = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [path]);
  if (failed) return <div style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: palette.textMuted, background: palette.beige }}>🐾</div>;
  if (!src) return <div style={{ ...style, background: palette.beige }} />;
  return <img src={src} alt={alt} onClick={onClick} style={style} />;
}

// ---------------------------------------------------------------------------
// Paws & Portraits admin tab — full signup list, photos, status, CSV export.
// ---------------------------------------------------------------------------
// Admin: photo orders — who paid, what they picked, and their delivery links.
function PawsPhotoOrdersPanel({ palette, fonts }) {
  const [orders, setOrders] = useState([]);
  const [meta, setMeta] = useState({ prices: { gallery: 5000 } });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [gdrafts, setGdrafts] = useState({}); // orderId -> { pet_name, story, drive_folder_url }
  const [announceAudience, setAnnounceAudience] = useState('booked'); // booked | all
  const [announceOne, setAnnounceOne] = useState('');     // one-off send to someone missed off the list
  const [announceOneName, setAnnounceOneName] = useState('');
  const [importing, setImporting] = useState(false);
  const [importLog, setImportLog] = useState([]);
  const [matchResult, setMatchResult] = useState(null);
  const [matchConfidence, setMatchConfidence] = useState('strong');
  const [listCounts, setListCounts] = useState(null);
  const [listAudience, setListAudience] = useState('both');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const authHeader = () => {
    const t = localStorage.getItem('hddn_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const fetchOrders = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/paws-photo-orders`, { headers: authHeader() });
      if (res.ok) {
        const d = await res.json();
        setOrders(d.orders || []);
        setMeta({ prices: d.prices });
      }
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };
  useEffect(() => { fetchOrders(); }, []);

  // Audience sizes, so the export dropdown can show what each one contains.
  useEffect(() => {
    fetch(`${API_BASE}/api/paws-email-list/counts`, { headers: authHeader() })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setListCounts(d))
      .catch(() => {});
  }, []);

  const downloadEmailList = async () => {
    setBusy(true); setNote('');
    try {
      const r = await fetch(`${API_BASE}/api/paws-email-list.csv?audience=${listAudience}`, { headers: authHeader() });
      if (!r.ok) throw new Error('Could not build the list');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `paws-emails-${listAudience}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setNote('✓ Downloaded.');
    } catch (err) { setNote(`✗ ${err.message}`); } finally { setBusy(false); }
  };


  const toggleExpand = (id) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
  };

  const createFromAttendees = async () => {
    if (!confirm('Create a photo order for everyone who booked a session (skips anyone who already has one)?')) return;
    setBusy(true); setNote('');
    try {
      const res = await fetch(`${API_BASE}/api/paws-photo-orders`, {
        method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify({ fromQuestionnaire: true }),
      });
      const d = await res.json().catch(() => ({}));
      setNote(res.ok ? `✓ Created ${d.created} new order${d.created === 1 ? '' : 's'}.` : `✗ ${d.error || 'Failed'}`);
      if (res.ok) fetchOrders();
    } catch (err) { setNote(`✗ ${err.message}`); } finally { setBusy(false); }
  };


  // --- Bulk import from the Drive manifest --------------------------------
  // Runs in slices so a 28-pet import can't outlive a single request, and so
  // the operator sees progress rather than a spinner.
  const runImport = async ({ dryRun = false, publish = false } = {}) => {
    if (!dryRun && !confirm(publish
      ? 'Import all galleries from Drive AND publish them live?'
      : 'Import all galleries from Drive? They stay unpublished until you publish.')) return;

    setImporting(true);
    setImportLog([]);
    setNote('');
    let offset = 0, done = 0, failed = 0, total = null;
    try {
      for (;;) {
        const res = await fetch(`${API_BASE}/api/paws-gallery-import`, {
          method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ dryRun, publish, offset }),
        });
        const d = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(d.error || 'Import failed');

        total = d.total;
        done += d.results.filter((r) => r.ok).length;
        failed += d.results.filter((r) => !r.ok).length;
        setImportLog((prev) => [...prev, ...d.results]);
        setNote(`${dryRun ? 'Checking' : 'Importing'} ${Math.min(offset + d.results.length, total)} of ${total}…`);

        if (d.nextOffset === null) break;
        offset = d.nextOffset;
      }
      setNote(dryRun
        ? `Dry run: ${done} of ${total} look fine${failed ? `, ${failed} with problems` : ''}. Nothing changed.`
        : `✓ ${done} of ${total} imported${failed ? `, ${failed} failed` : ''}${publish ? ' and published' : ' — not published yet'}.`);
      if (!dryRun) fetchOrders();
    } catch (err) {
      setNote(`✗ ${err.message}`);
    } finally { setImporting(false); }
  };

  // --- Match galleries back to the people who filled in the signup form ----
  const matchOwners = async (apply) => {
    if (apply && !confirm('Attach these signup emails to their galleries?\n\nOnly galleries still on a placeholder address are touched.')) return;
    setImporting(true);
    setNote('');
    try {
      const res = await fetch(`${API_BASE}/api/paws-gallery-match-owners`, {
        method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ apply, confidence: matchConfidence }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Match failed');
      setMatchResult(d);
      const s = d.summary;
      setNote(apply
        ? `✓ Attached ${d.matched.filter((m) => m.applied).length} owner${d.matched.length === 1 ? '' : 's'}.`
        : `${s.matched} matched, ${s.ambiguous} need a look, ${s.unmatched} with no signup. Nothing changed.`);
      if (apply) fetchOrders();
    } catch (err) { setNote(`✗ ${err.message}`); } finally { setImporting(false); }
  };

  // --- Gallery announcement (the one-shot "your photos are up" blast) ------
  const announce = async (mode) => {
    if (mode === 'one') {
      const email = announceOne.trim();
      if (!email) return;
      if (!confirm(`Email the gallery link to ${email}?\n\nThis cannot be unsent.`)) return;
      setBusy(true); setNote('');
      try {
        const res = await fetch(`${API_BASE}/api/paws-send-gallery-announcement`, {
          method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, name: announceOneName.trim() || undefined }),
        });
        const d = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(d.error || 'Failed');
        setNote(`✓ Sent to ${d.to}.${d.alreadySentAt ? ` (Heads up — they were already emailed on ${new Date(d.alreadySentAt).toLocaleDateString()}.)` : ''}`);
        setAnnounceOne(''); setAnnounceOneName('');
      } catch (err) { setNote(`✗ ${err.message}`); } finally { setBusy(false); }
      return;
    }

    const body = mode === 'test' ? { test: true }
      : mode === 'dryRun' ? { dryRun: true, audience: announceAudience }
      : { audience: announceAudience, resend: mode === 'resend' };

    if (mode === 'send' || mode === 'resend') {
      const who = announceAudience === 'all' ? 'everyone who signed up, waitlisted, or booked' : 'everyone who booked a session';
      const extra = mode === 'resend' ? '\n\nThis INCLUDES people who already got it.' : '';
      if (!confirm(`Email the gallery link to ${who}?${extra}\n\nThis cannot be unsent.`)) return;
    }
    setBusy(true); setNote('');
    try {
      const res = await fetch(`${API_BASE}/api/paws-send-gallery-announcement`, {
        method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Failed');
      if (d.test) setNote(d.ok ? `✓ Test sent to ${d.to}.` : `✗ ${d.error || 'Test failed'}`);
      else if (d.dryRun) setNote(`${d.wouldSend} ${d.wouldSend === 1 ? 'person' : 'people'} would get it (${d.audience}). Nothing sent.`);
      else setNote(`✓ Sent to ${d.sent} of ${d.recipients}${d.failed ? ` — ${d.failed} failed` : ''}.`);
    } catch (err) { setNote(`✗ ${err.message}`); } finally { setBusy(false); }
  };

  // --- Print pickup notice ---------------------------------------------------
  const sendPickup = async (mode) => {
    if (mode === 'send' && !confirm('Email the print pickup notice to everyone who got the gallery announcement?\n\nThis cannot be unsent.')) return;
    setBusy(true); setNote('');
    try {
      const res = await fetch(`${API_BASE}/api/paws-send-print-pickup`, {
        method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(mode === 'test' ? { test: true } : mode === 'dryRun' ? { dryRun: true } : {}),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Failed');
      if (d.test) setNote(d.ok ? `✓ Test sent to ${d.to}.` : `✗ ${d.error || 'Test failed'}`);
      else if (d.dryRun) setNote(`${d.wouldSend} ${d.wouldSend === 1 ? 'person' : 'people'} would get it. Nothing sent.`);
      else setNote(`✓ Sent to ${d.sent} of ${d.recipients}${d.failed ? ` — ${d.failed} failed` : ''}.`);
    } catch (err) { setNote(`✗ ${err.message}`); } finally { setBusy(false); }
  };

  // --- Halloween Pet Ball invite --------------------------------------------
  const sendPetBall = async (mode) => {
    if (mode === 'send' && !confirm('Email the Halloween Pet Ball invite to every Paws & Portraits signup?\n\nThis cannot be unsent.')) return;
    setBusy(true); setNote('');
    try {
      const res = await fetch(`${API_BASE}/api/paws-send-pet-ball-invite`, {
        method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(mode === 'test' ? { test: true } : mode === 'dryRun' ? { dryRun: true } : {}),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Failed');
      if (d.test) setNote(d.ok ? `✓ Test sent to ${d.to}.` : `✗ ${d.error || 'Test failed'}`);
      else if (d.dryRun) setNote(`${d.wouldSend} ${d.wouldSend === 1 ? 'person' : 'people'} would get it. Nothing sent.`);
      else setNote(`✓ Sent to ${d.sent} of ${d.recipients}${d.failed ? ` — ${d.failed} failed` : ''}.`);
    } catch (err) { setNote(`✗ ${err.message}`); } finally { setBusy(false); }
  };

  // --- Public gallery (blog-style page per pet) ---------------------------
  const saveGallery = async (id, overrides = {}) => {
    const d = gdrafts[id] || {};
    setBusy(true); setNote('');
    try {
      const res = await fetch(`${API_BASE}/api/paws-photo-orders/${id}/gallery`, {
        method: 'PUT', headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          petName: d.pet_name,
          story: d.story,
          driveFolderUrl: d.drive_folder_url,
          ownerEmail: d.owner_email,
          ...overrides,
        }),
      });
      const r = await res.json().catch(() => ({}));
      setNote(res.ok ? `✓ Saved — live at /paws-gallery/${r.slug}` : `✗ ${r.error || 'Could not save.'}`);
      if (res.ok) { setGdrafts((p) => ({ ...p, [id]: {} })); fetchOrders(); }
    } catch (err) { setNote(`✗ ${err.message}`); } finally { setBusy(false); }
  };

  const uploadHero = async (id, file) => {
    if (!file) return;
    setBusy(true); setNote('Uploading the free photo…');
    try {
      const fd = new FormData();
      fd.append('photo', file);
      const res = await fetch(`${API_BASE}/api/paws-photo-orders/${id}/hero`, { method: 'POST', headers: authHeader(), body: fd });
      const d = await res.json().catch(() => ({}));
      setNote(res.ok ? '✓ Free photo uploaded.' : `✗ ${d.error || 'Upload failed.'}`);
      if (res.ok) fetchOrders();
    } catch (err) { setNote(`✗ ${err.message}`); } finally { setBusy(false); }
  };

  const sendGalleryDelivery = async (o) => {
    if (!confirm(`Email ${o.name} their Google Drive link?`)) return;
    setBusy(true); setNote('');
    try {
      const res = await fetch(`${API_BASE}/api/paws-photo-orders/${o.id}/send-gallery-delivery`, {
        method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' }, body: '{}',
      });
      const d = await res.json().catch(() => ({}));
      setNote(res.ok ? `✓ Drive link sent to ${o.email}.` : `✗ ${d.error || 'Send failed'}`);
      if (res.ok) fetchOrders();
    } catch (err) { setNote(`✗ ${err.message}`); } finally { setBusy(false); }
  };

  // Ask Stripe what happened with this order's checkout — recovers the buyer's
  // address and finishes delivery without anyone retyping anything.
  const checkStripe = async (o) => {
    setBusy(true); setNote('');
    try {
      const res = await fetch(`${API_BASE}/api/paws-photo-orders/${o.id}/check-stripe`, {
        method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' }, body: '{}',
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Check failed');
      setNote(`${d.paid ? '✓' : '—'} ${d.message}`);
      fetchOrders();
    } catch (err) { setNote(`✗ ${err.message}`); } finally { setBusy(false); }
  };

  const markPaid = async (o) => {
    if (!confirm(`Mark ${o.name} as paid outside Stripe? They'll be recorded as buying every photo.`)) return;
    setBusy(true); setNote('');
    try {
      const res = await fetch(`${API_BASE}/api/paws-photo-orders/${o.id}`, {
        method: 'PUT', headers: { ...authHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify({ paid: true, tier: 'all' }),
      });
      setNote(res.ok ? `✓ ${o.name} marked paid. Now hit “Send Drive link”.` : '✗ Could not update.');
      if (res.ok) fetchOrders();
    } catch (err) { setNote(`✗ ${err.message}`); } finally { setBusy(false); }
  };


  const deleteOrder = async (o) => {
    if (!confirm(`Delete ${o.name}'s photo order and all their proofs? This cannot be undone.`)) return;
    await fetch(`${API_BASE}/api/paws-photo-orders/${o.id}`, { method: 'DELETE', headers: authHeader() });
    fetchOrders();
  };

  const timeAgo = (ts) => {
    const mins = Math.floor((Date.now() - new Date(ts).getTime()) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return days < 30 ? `${days}d ago` : new Date(ts).toLocaleDateString();
  };
  const th = { padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: palette.textMuted, whiteSpace: 'nowrap' };
  const td = { padding: '10px', fontFamily: fonts, fontSize: 12, color: palette.text, verticalAlign: 'top', borderBottom: `1px solid ${palette.border}` };
  const btn = (bg, fg, extra = {}) => ({
    fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
    padding: '5px 10px', background: bg, color: fg, border: 'none', cursor: busy ? 'wait' : 'pointer', ...extra,
  });
  const input = { fontFamily: fonts, fontSize: 12, padding: '7px 9px', border: `1px solid ${palette.border}`, background: palette.white, color: palette.text, width: '100%', boxSizing: 'border-box' };
  // The bulk tools live in collapsed sections so the day-to-day view is just
  // the stats and the orders table.
  const toolBox = { border: `1px solid ${palette.border}`, background: palette.white, padding: '0 12px 12px', marginBottom: 10 };
  const toolSummary = {
    fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
    color: palette.brown, cursor: 'pointer', padding: '12px 0', outline: 'none', listStyle: 'none',
  };

  if (loading) return <div style={{ fontFamily: fonts, padding: 20, color: palette.textMuted }}>Loading photo orders…</div>;

  // A sale is a sale however it was taken — `paid` covers Stripe, `manual_paid`
  // covers the cash and Zelle ones marked by hand. Counting only the first
  // understated both the total and the number still waiting on delivery.
  const soldOrders = orders.filter((o) => o.paid || o.manual_paid);
  const salesCents = soldOrders.reduce((n, o) => n + (o.amount_cents || 0), 0);
  // Tips are banked separately from the sale price — amount_cents has the tip
  // split back out at checkout — and a tip can arrive on its own without
  // anything being bought. So they're summed across every order, and shown
  // apart rather than folded in, because they say something different.
  const tipsCents = orders.reduce((n, o) => n + (o.tip_cents || 0), 0);
  const readyToSend = soldOrders.filter((o) => !o.delivered_at).length;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12, padding: '12px 14px', background: withAlpha(palette.orange, 0.08), border: `1px solid ${palette.orange}`, borderRadius: 6 }}>
        <span style={{ fontFamily: fonts, fontSize: 12, fontWeight: 700, color: palette.brown, marginRight: 2 }}>📸 Photo orders</span>
        {[[`${orders.length} galleries`, palette.beige, palette.brown],
          [`${soldOrders.length} sold`, palette.sage, palette.white],
          [`${money(salesCents)} sales`, palette.brown, palette.cream],
          ...(tipsCents ? [[`${money(tipsCents)} tips`, palette.orangeLight, palette.brown]] : []),
          [`${readyToSend} not delivered`, readyToSend ? palette.orange : palette.beige, readyToSend ? palette.white : palette.textMuted]].map(([label, bg, fg]) => (
          <span key={label} style={{ fontFamily: fonts, fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 12, background: bg, color: fg }}>{label}</span>
        ))}
        <button onClick={createFromAttendees} disabled={busy} style={btn(palette.orange, palette.white, { padding: '6px 12px' })}>Create orders for attendees</button>
        {note && <span style={{ fontFamily: fonts, fontSize: 12, fontWeight: 700, color: note.startsWith('✗') ? palette.red : palette.sage }}>{note}</span>}
      </div>

      {/* Bulk import from the Drive manifest. */}
      <details style={toolBox}>
        <summary style={toolSummary}>▸ 🐾 Import galleries from Drive</summary>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button onClick={() => runImport({ dryRun: true })} disabled={importing || busy}
            style={btn(palette.white, palette.brown, { border: `1px solid ${palette.border}` })}>Check first</button>
          <button onClick={() => runImport({})} disabled={importing || busy}
            style={btn(palette.brown, palette.cream)}>Import (unpublished)</button>
          <button onClick={() => runImport({ publish: true })} disabled={importing || busy}
            style={btn(palette.orange, palette.white)}>Import &amp; publish</button>
          {importing && <span style={{ fontFamily: fonts, fontSize: 11, color: palette.textMuted }}>working…</span>}
        </div>
        <div style={{ fontFamily: fonts, fontSize: 11, color: palette.textMuted, marginTop: 8, lineHeight: 1.6 }}>
          Pulls each pet’s free photo from Google Drive and fills in their gallery page. Safe to run again —
          it updates the same pets rather than duplicating them.
        </div>
        {!!importLog.length && (
          <div style={{ marginTop: 10, maxHeight: 220, overflowY: 'auto', border: `1px solid ${palette.border}`, background: palette.cream, padding: 8 }}>
            {importLog.map((r, i) => (
              <div key={i} style={{ fontFamily: fonts, fontSize: 11, color: r.ok ? palette.text : palette.red, lineHeight: 1.7 }}>
                {r.ok ? '✓' : '✗'} {r.petName}
                {r.slug ? ` → /paws-gallery/${r.slug}` : ''}
                {r.action ? ` (${r.action})` : ''}
                {r.error ? ` — ${r.error}` : ''}
              </div>
            ))}
          </div>
        )}
      </details>

      {/* Email list export. */}
      <details style={toolBox}>
        <summary style={toolSummary}>▸ 📇 Email list export</summary>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <select value={listAudience} onChange={(e) => setListAudience(e.target.value)}
            style={{ fontFamily: fonts, fontSize: 12, padding: '5px 8px', border: `1px solid ${palette.border}`, background: palette.white, color: palette.text }}>
            <option value="both">Signed up AND did the questionnaire{listCounts ? ` (${listCounts.both})` : ''}</option>
            <option value="either">Either form{listCounts ? ` (${listCounts.either})` : ''}</option>
            <option value="signups">Signup form only{listCounts ? ` (${listCounts.signups})` : ''}</option>
            <option value="questionnaire">Questionnaire only{listCounts ? ` (${listCounts.questionnaire})` : ''}</option>
          </select>
          <button onClick={downloadEmailList} disabled={busy} style={btn(palette.brown, palette.cream)}>Download CSV</button>
        </div>
        <div style={{ fontFamily: fonts, fontSize: 11, color: palette.textMuted, marginTop: 8, lineHeight: 1.6 }}>
          Name, email, phone, pet, which forms they filled in, and their block and slot. One row per person —
          anyone in both forms appears once.
        </div>
      </details>

      {/* Attach real owners from the signup form. */}
      <details style={toolBox}>
        <summary style={toolSummary}>▸ 🔗 Match owners from signups</summary>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <select value={matchConfidence} onChange={(e) => setMatchConfidence(e.target.value)}
            style={{ fontFamily: fonts, fontSize: 12, padding: '5px 8px', border: `1px solid ${palette.border}`, background: palette.white, color: palette.text }}>
            <option value="strong">Pet name + owner name must match</option>
            <option value="likely">Pet name alone is enough</option>
          </select>
          <button onClick={() => matchOwners(false)} disabled={importing || busy}
            style={btn(palette.white, palette.brown, { border: `1px solid ${palette.border}` })}>Preview matches</button>
          <button onClick={() => matchOwners(true)} disabled={importing || busy}
            style={btn(palette.brown, palette.cream)}>Attach emails</button>
        </div>
        <div style={{ fontFamily: fonts, fontSize: 11, color: palette.textMuted, marginTop: 8, lineHeight: 1.6 }}>
          Looks up each gallery's pet in the signup form and puts the real owner and email on it. Galleries that already
          have a real address are left alone. Preview first — a wrong address sends someone else's photos to a stranger.
        </div>
        {matchResult && (
          <div style={{ marginTop: 10, maxHeight: 260, overflowY: 'auto', border: `1px solid ${palette.border}`, background: palette.cream, padding: 8 }}>
            {matchResult.matched.map((m, i) => (
              <div key={`m${i}`} style={{ fontFamily: fonts, fontSize: 11, color: palette.text, lineHeight: 1.7 }}>
                ✓ {m.petName} → {m.owner} &lt;{m.email}&gt; <span style={{ color: palette.textMuted }}>({m.confidence}{m.applied ? ', saved' : ''})</span>
              </div>
            ))}
            {matchResult.ambiguous.map((m, i) => (
              <div key={`a${i}`} style={{ fontFamily: fonts, fontSize: 11, color: palette.orange, lineHeight: 1.7 }}>
                ? {m.petName} — {m.reason || `${(m.candidates || []).length} possible: ${(m.candidates || []).join(', ')}`}
              </div>
            ))}
            {matchResult.unmatched.map((m, i) => (
              <div key={`u${i}`} style={{ fontFamily: fonts, fontSize: 11, color: palette.textMuted, lineHeight: 1.7 }}>
                – {m.petName} — {m.reason}
              </div>
            ))}
            {!!(matchResult.orphanForms || []).length && (
              <div style={{ marginTop: 8, borderTop: `1px solid ${palette.border}`, paddingTop: 8 }}>
                <div style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, color: palette.brown }}>
                  Signed up but has no gallery — likely the unidentified pets:
                </div>
                {matchResult.orphanForms.map((o, i) => (
                  <div key={`o${i}`} style={{ fontFamily: fonts, fontSize: 11, color: palette.text, lineHeight: 1.7 }}>
                    {o.petName} — {o.name} &lt;{o.email}&gt;
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </details>

      {/* Gallery announcement — one shot to the whole shoot list. */}
      <details style={toolBox}>
        <summary style={toolSummary}>▸ 📣 Gallery announcement blast</summary>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <select value={announceAudience} onChange={(e) => setAnnounceAudience(e.target.value)}
            style={{ fontFamily: fonts, fontSize: 12, padding: '5px 8px', border: `1px solid ${palette.border}`, background: palette.white, color: palette.text }}>
            <option value="booked">Everyone who booked</option>
            <option value="all">Everyone (signups + waitlist + booked)</option>
          </select>
          <button onClick={() => announce('test')} disabled={busy} style={btn(palette.white, palette.brown, { border: `1px solid ${palette.border}` })}>Send me a test</button>
          <button onClick={() => announce('dryRun')} disabled={busy} style={btn(palette.white, palette.brown, { border: `1px solid ${palette.border}` })}>Count recipients</button>
          <button onClick={() => announce('send')} disabled={busy} style={btn(palette.orange, palette.white)}>Send announcement</button>
          <button onClick={() => announce('resend')} disabled={busy} style={btn(palette.white, palette.red, { border: `1px solid ${palette.red}` })}>Resend to all</button>
        </div>
        <div style={{ fontFamily: fonts, fontSize: 11, color: palette.textMuted, marginTop: 8, lineHeight: 1.6 }}>
          Points everyone at /paws-gallery to find their own pet — one free download, {money(meta.prices.gallery)} for the full set.
          Anyone who already received it is skipped, so this is safe to run again after a partial send. “Resend to all” ignores that.
        </div>

        {/* Someone who was left off the blast won't be in any of the tables the
            audience options read — that absence is why they were missed — so
            this takes the address directly. */}
        <div style={{ borderTop: `1px solid ${palette.border}`, marginTop: 10, paddingTop: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: palette.brown }}>
              Missed someone?
            </span>
            <input
              type="email"
              value={announceOne}
              onChange={(e) => setAnnounceOne(e.target.value)}
              placeholder="their@email.com"
              onKeyDown={(e) => { if (e.key === 'Enter') announce('one'); }}
              style={{ fontFamily: fonts, fontSize: 12, padding: '5px 8px', border: `1px solid ${palette.border}`, background: palette.white, color: palette.text, width: 200 }}
            />
            <input
              value={announceOneName}
              onChange={(e) => setAnnounceOneName(e.target.value)}
              placeholder="First name (optional)"
              onKeyDown={(e) => { if (e.key === 'Enter') announce('one'); }}
              style={{ fontFamily: fonts, fontSize: 12, padding: '5px 8px', border: `1px solid ${palette.border}`, background: palette.white, color: palette.text, width: 150 }}
            />
            <button onClick={() => announce('one')} disabled={busy || !announceOne.trim()} style={btn(palette.sage, palette.white)}>
              Send to this person
            </button>
          </div>
          <div style={{ fontFamily: fonts, fontSize: 11, color: palette.textMuted, marginTop: 6, lineHeight: 1.6 }}>
            Same email, but it opens with an apology for them being left off. Recorded like any other send, so they won’t get it twice.
          </div>
        </div>
      </details>

      {/* Print pickup — free prints are back from the Vanda show. */}
      <details style={toolBox}>
        <summary style={toolSummary}>▸ 🖼️ Print pickup notice</summary>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button onClick={() => sendPickup('test')} disabled={busy} style={btn(palette.white, palette.brown, { border: `1px solid ${palette.border}` })}>Send me a test</button>
          <button onClick={() => sendPickup('dryRun')} disabled={busy} style={btn(palette.white, palette.brown, { border: `1px solid ${palette.border}` })}>Count recipients</button>
          <button onClick={() => sendPickup('send')} disabled={busy} style={btn(palette.orange, palette.white)}>Send pickup notice</button>
        </div>
        <div style={{ fontFamily: fonts, fontSize: 11, color: palette.textMuted, marginTop: 8, lineHeight: 1.6 }}>
          Emails everyone who got the gallery announcement that their print is ready at 72 Centre Ave, Sun 10/11, 12–3pm. Replies go to hi@hddnlab.com.
          Anyone already sent it is skipped.
        </div>
      </details>

      {/* Halloween Pet Ball — invite the Paws owners to the dog fashion show. */}
      <details style={toolBox}>
        <summary style={toolSummary}>▸ 🎃 Halloween Pet Ball invite</summary>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button onClick={() => sendPetBall('test')} disabled={busy} style={btn(palette.white, palette.brown, { border: `1px solid ${palette.border}` })}>Send me a test</button>
          <button onClick={() => sendPetBall('dryRun')} disabled={busy} style={btn(palette.white, palette.brown, { border: `1px solid ${palette.border}` })}>Count recipients</button>
          <button onClick={() => sendPetBall('send')} disabled={busy} style={btn(palette.orange, palette.white)}>Send Pet Ball invite</button>
        </div>
        <div style={{ fontFamily: fonts, fontSize: 11, color: palette.textMuted, marginTop: 8, lineHeight: 1.6 }}>
          Emails every Paws & Portraits signup, waitlist entry and booking about the free dog fashion show at Clinton Park, Sat 10/24 (entry at happslocal.com/petball).
          Replies go to hi@hddnlab.com; hello@happslocal.com gets one FYI copy per run. Anyone already sent it is skipped.
        </div>
      </details>

      <p style={{ fontFamily: fonts, fontSize: 11, color: palette.textMuted, margin: '0 0 12px', lineHeight: 1.6 }}>
        Everyone gets one photo free and can buy their whole session for {money(meta.prices.gallery)} from their gallery page.
        Import the galleries above, then send the announcement. If someone pays and the Drive link doesn’t reach them,
        open their order and hit “Send Drive link”.
      </p>

      {orders.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: palette.textMuted, fontFamily: fonts }}>
          No photo orders yet — click “Create orders for attendees” to make one for everyone who booked.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', border: `1px solid ${palette.border}`, background: palette.white }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
            <thead>
              <tr style={{ background: palette.beige, borderBottom: `2px solid ${palette.border}` }}>
                {['Client', 'Gallery', 'Downloads', 'Paid', 'Delivery', 'Link', ''].map((h) => <th key={h} style={th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => {
                const isOpen = expanded === o.id;
                // Cash and Zelle sales are marked by hand and never set `paid`,
                // so every row-level "has this sold?" question asks for both.
                const sold = o.paid || o.manual_paid;
                return (
                  <React.Fragment key={o.id}>
                    <tr style={{ background: sold ? withAlpha(palette.sage, 0.08) : undefined }}>
                      <td style={td}>
                        <div style={{ fontWeight: 700, color: palette.brown }}>{o.name}</div>
                        <a href={`mailto:${o.email}`} style={{ color: palette.orange, textDecoration: 'none', fontSize: 11 }}>{o.email}</a>
                      </td>
                      <td style={td}>
                        {o.slug
                          ? <a href={`/paws-gallery/${o.slug}`} target="_blank" rel="noreferrer" style={{ color: palette.orange, textDecoration: 'none' }}>
                              {o.pet_name || <em style={{ color: palette.textMuted }}>no name</em>}
                            </a>
                          : <span style={{ color: palette.textMuted }}>—</span>}
                        {o.slug && !o.published && <div style={{ fontSize: 10, color: palette.textMuted }}>not published</div>}
                      </td>
                      {/* Someone taking the free photo is how an unclaimed pet
                          gets found, so the count and its recency sit in the
                          table rather than behind an expand. */}
                      <td style={td}>
                        {o.free_downloads
                          ? <>
                              <span style={{ fontWeight: 700, color: palette.sage }}>{o.free_downloads}</span>
                              {o.last_download_at && (
                                <div style={{ fontSize: 10, color: palette.textMuted }}>{timeAgo(o.last_download_at)}</div>
                              )}
                              {(o.download_emails || []).map((em) => (
                                <div key={em}>
                                  <a href={`mailto:${em}`} style={{ fontSize: 10, color: palette.orange, textDecoration: 'none' }}>{em}</a>
                                </div>
                              ))}
                            </>
                          : <span style={{ color: palette.textMuted }}>—</span>}
                      </td>
                      <td style={td}>
                        {sold
                          ? <span style={{ color: palette.sage, fontWeight: 700 }}>✓ {money(o.amount_cents)}{o.manual_paid ? ' (manual)' : ''}</span>
                          : <button onClick={() => markPaid(o)} disabled={busy} style={btn(palette.white, palette.brown, { border: `1px solid ${palette.border}` })}>Mark paid</button>}
                        {o.stripe_session_id && (!sold || !o.delivered_at) && (
                          <div style={{ marginTop: 4 }}>
                            <button onClick={() => checkStripe(o)} disabled={busy} style={btn(palette.white, palette.orange, { border: `1px solid ${palette.orange}` })}>Check Stripe</button>
                          </div>
                        )}
                      </td>
                      <td style={td}>
                        {o.delivered_at ? <span style={{ color: palette.sage, fontWeight: 700 }}>✓ sent</span>
                          : sold ? <span style={{ color: palette.red, fontWeight: 700 }}>not sent</span>
                          : <span style={{ color: palette.textMuted }}>—</span>}
                      </td>
                      <td style={td}>
                        <button onClick={() => { navigator.clipboard?.writeText(o.link); setNote(`✓ Copied ${o.name}'s link.`); }} style={btn(palette.white, palette.brown, { border: `1px solid ${palette.border}` })}>Copy</button>
                        <div style={{ fontSize: 10, color: palette.textMuted, marginTop: 3 }}>{o.link_sent_at ? `sent ${new Date(o.link_sent_at).toLocaleDateString()}` : 'not sent'}</div>
                      </td>
                      <td style={td}>
                        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', whiteSpace: 'nowrap' }}>
                          <button onClick={() => toggleExpand(o.id)} style={btn(palette.brown, palette.cream)}>{isOpen ? 'Close' : 'Manage'}</button>

                          <button onClick={() => deleteOrder(o)} style={btn('none', palette.red, { border: `1px solid ${palette.border}` })}>Del</button>
                        </div>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={7} style={{ ...td, background: palette.beige }}>
                          {/* Delivery links */}
                          {/* Public gallery — the blog-style page for this pet */}
                          {(() => {
                            const g = gdrafts[o.id] || {};
                            const val = (k) => g[k] ?? (o[k] || '');
                            const set = (k, v) => setGdrafts((p) => ({ ...p, [o.id]: { ...g, [k]: v } }));
                            const canPublish = !!(val('pet_name') || o.pet_name) && !!o.hero_filename;
                            return (
                              <div style={{ border: `2px solid ${o.published ? palette.sage : palette.border}`, background: palette.white, padding: 14, marginBottom: 16 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                                  <span style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: palette.brown }}>
                                    🐾 Public gallery page
                                  </span>
                                  <span style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, color: o.published ? palette.sage : palette.textMuted }}>
                                    {o.published ? 'LIVE' : 'Not published'}
                                  </span>
                                  {o.slug && (
                                    <a href={`/paws-gallery/${o.slug}`} target="_blank" rel="noreferrer"
                                      style={{ fontFamily: fonts, fontSize: 11, color: palette.orange }}>/paws-gallery/{o.slug} ↗</a>
                                  )}
                                  {typeof o.free_downloads === 'number' && o.free_downloads > 0 && (
                                    <span style={{ fontFamily: fonts, fontSize: 11, color: palette.textMuted }}>
                                      {o.free_downloads} free download{o.free_downloads === 1 ? '' : 's'}
                                    </span>
                                  )}
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 12, marginBottom: 12 }}>
                                  <div>
                                    <label style={{ fontFamily: fonts, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: palette.brown, display: 'block', marginBottom: 4 }}>Pet name</label>
                                    <input style={input} placeholder="e.g. Mochi" value={val('pet_name')} onChange={(e) => set('pet_name', e.target.value)} />
                                  </div>
                                  <div>
                                    <label style={{ fontFamily: fonts, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: /@unknown\./i.test(o.email) ? palette.red : palette.brown, display: 'block', marginBottom: 4 }}>
                                      Owner email{/@unknown\./i.test(o.email) ? ' — placeholder, emails can’t send' : ''}
                                    </label>
                                    <input style={input} placeholder="their@realemail.com"
                                      value={g.owner_email ?? (/@unknown\./i.test(o.email) ? '' : o.email)}
                                      onChange={(e) => set('owner_email', e.target.value)} />
                                  </div>
                                  <div>
                                    <label style={{ fontFamily: fonts, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: palette.brown, display: 'block', marginBottom: 4 }}>Google Drive folder — all photos ($50)</label>
                                    <input style={input} placeholder="https://drive.google.com/drive/folders/…" value={val('drive_folder_url')} onChange={(e) => set('drive_folder_url', e.target.value)} />
                                  </div>
                                </div>
                                <div style={{ marginBottom: 12 }}>
                                  <label style={{ fontFamily: fonts, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: palette.brown, display: 'block', marginBottom: 4 }}>Short caption (optional)</label>
                                  <input style={input} placeholder="A line about their session" value={val('story')} onChange={(e) => set('story', e.target.value)} />
                                </div>

                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                  <label style={{ ...btn(palette.white, palette.brown, { border: `1px solid ${palette.border}`, display: 'inline-block' }), cursor: 'pointer' }}>
                                    {o.hero_filename ? 'Replace free photo' : 'Upload free photo'}
                                    <input type="file" accept="image/*" style={{ display: 'none' }}
                                      onChange={(e) => { uploadHero(o.id, e.target.files[0]); e.target.value = ''; }} />
                                  </label>
                                  {o.hero_filename && (
                                    <span style={{ fontFamily: fonts, fontSize: 11, color: palette.sage }}>✓ {o.hero_filename}</span>
                                  )}
                                  <button onClick={() => saveGallery(o.id)} disabled={busy} style={btn(palette.brown, palette.cream, { padding: '7px 14px' })}>Save</button>
                                  <button onClick={() => saveGallery(o.id, { published: !o.published })} disabled={busy || (!o.published && !canPublish)}
                                    style={btn(o.published ? palette.textMuted : palette.sage, palette.white, { padding: '7px 14px', opacity: (!o.published && !canPublish) ? 0.5 : 1 })}>
                                    {o.published ? 'Unpublish' : 'Publish'}
                                  </button>
                                  <button onClick={() => sendGalleryDelivery(o)} disabled={busy || !o.drive_folder_url}
                                    style={btn(palette.orange, palette.white, { padding: '7px 14px', opacity: !o.drive_folder_url ? 0.5 : 1 })}>
                                    Send Drive link
                                  </button>
                                </div>
                                {!o.published && !canPublish && (
                                  <div style={{ fontFamily: fonts, fontSize: 11, color: palette.textMuted, marginTop: 8 }}>
                                    Add a pet name and upload the free photo before publishing.
                                  </div>
                                )}
                              </div>
                            );
                          })()}

                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Custom Jobs — quote a one-off shoot, block the calendar, invoice the customer
//
// Anything the admin types here becomes three things at once: a confirmed
// booking holding the studio, a Stripe invoice at the negotiated price, and an
// email to the customer with the details and a link to pay. Times run the full
// 24 hours on purpose — these jobs are allowed outside normal hours.
// ---------------------------------------------------------------------------

const CUSTOM_JOB_ROOMS = [
  { id: 'upstairs-1', label: 'Full Studio' },
  { id: 'upstairs-2', label: 'Podcast Recording' },
];

// Every half hour of the day, so a 6am call time or a 10pm wrap is one click.
const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2);
  const m = i % 2 ? '30' : '00';
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return { value: `${String(h).padStart(2, '0')}:${m}`, label: `${h12}:${m} ${ampm}` };
});

function blankCustomJob() {
  return {
    name: '', email: '', date: '', startTime: '10:00', hours: 2,
    room: 'upstairs-1', service: '', includes: '', notes: '',
    priceDollars: '', photographer: '', stripeEmails: false,
  };
}

function CustomJobsTab({ palette, fonts }) {
  const [jobs, setJobs] = useState([]);
  const [photographers, setPhotographers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(blankCustomJob());
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null); // { ok, message } | { error }
  const [conflicts, setConflicts] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [newPhotographer, setNewPhotographer] = useState('');
  const [showCancelled, setShowCancelled] = useState(false);

  const load = async () => {
    try {
      const [list, ph] = await Promise.all([
        api('/api/admin/custom-jobs'),
        api('/api/admin/photographers'),
      ]);
      setJobs(Array.isArray(list) ? list : []);
      setPhotographers(ph.photographers || []);
    } catch (err) {
      setFeedback({ error: err.error || 'Could not load custom jobs' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);


  const dateLabel = (d) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(d || ''));
    if (!m) return String(d || '');
    return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]))
      .toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
  };

  const timeLabel = (start, hours) => {
    const [h, mm] = String(start || '00:00').split(':').map(Number);
    const fmt = (t) => {
      const hh = Math.floor(t / 60) % 24, m2 = t % 60;
      const ampm = hh >= 12 ? 'PM' : 'AM';
      return `${hh % 12 === 0 ? 12 : hh % 12}:${String(m2).padStart(2, '0')} ${ampm}`;
    };
    const s = h * 60 + (mm || 0);
    return `${fmt(s)} – ${fmt(s + (hours || 0) * 60)}`;
  };

  const set = (k) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: val }));
  };

  const submit = async (force) => {
    setSaving(true);
    setFeedback(null);
    if (!force) setConflicts([]);
    try {
      const body = { ...form, hours: Number(form.hours), force: !!force, resend: true };
      const path = editingId ? `/api/admin/custom-jobs/${editingId}` : '/api/admin/custom-jobs';
      const out = await api(path, { method: editingId ? 'PATCH' : 'POST', body: JSON.stringify(body) });
      const price = Number(form.priceDollars) || 0;
      setFeedback({
        ok: true,
        message: out.emailed === false
          ? `Saved ${out.job.booking_ref}, but the email did not send${out.emailError ? ` (${out.emailError})` : ''}. Use "Re-send email" below.`
          : price > 0
            ? `${out.job.booking_ref} booked. Invoice for ${money(Math.round(price * 100))} emailed to ${form.email} (hddnlab CC'd).`
            : `${out.job.booking_ref} booked. Confirmation emailed to ${form.email} (hddnlab CC'd). No charge.`,
      });
      setForm(blankCustomJob());
      setEditingId(null);
      setConflicts([]);
      load();
    } catch (err) {
      if (err.status === 409 && err.conflicts) {
        setConflicts(err.conflicts);
        setFeedback({ error: err.error });
      } else {
        setFeedback({ error: err.error || 'Something went wrong saving the job' });
      }
    } finally {
      setSaving(false);
    }
  };

  const act = async (job, path, opts, confirmMsg) => {
    if (confirmMsg && !confirm(confirmMsg)) return;
    setBusyId(job.id);
    setFeedback(null);
    try {
      const out = await api(`/api/admin/custom-jobs/${job.id}/${path}`, { method: 'POST', ...opts });
      if (path === 'check-payment') {
        setFeedback(out.paid
          ? { ok: true, message: out.alreadyRecorded ? `${job.booking_ref} was already recorded as paid.` : `${job.booking_ref} is paid (${money(out.amountCents)}). Confirmation sent.` }
          : { error: `${job.booking_ref} is not paid yet — Stripe says "${out.stripeStatus}".` });
      } else if (path === 'resend') {
        setFeedback({ ok: true, message: `${out.sent === 'confirmation' ? 'Confirmation' : 'Invoice'} re-sent to ${out.to} (CC ${out.cc}).` });
      } else if (path === 'mark-paid') {
        setFeedback({ ok: true, message: `${job.booking_ref} marked paid. Confirmation sent and the Stripe invoice was voided.` });
      } else if (path === 'cancel') {
        setFeedback({ ok: true, message: `${job.booking_ref} cancelled. The calendar is free and the payment link is dead.` });
      }
      load();
    } catch (err) {
      setFeedback({ error: err.error || 'That action failed' });
    } finally {
      setBusyId(null);
    }
  };

  const startEdit = (job) => {
    setEditingId(job.id);
    setConflicts([]);
    setFeedback(null);
    setForm({
      name: job.user_name || '', email: job.user_email || '', date: job.date || '',
      startTime: String(job.start_time || '10:00').slice(0, 5), hours: job.hours || 2,
      room: job.room || 'upstairs-1', service: job.service || '',
      includes: job.includes || '', notes: job.notes || '',
      priceDollars: job.quoted_cents ? String(job.quoted_cents / 100) : '',
      photographer: job.photographer || '', stripeEmails: false,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const savePhotographers = async (list) => {
    try {
      const out = await api('/api/admin/photographers', { method: 'PUT', body: JSON.stringify({ photographers: list }) });
      setPhotographers(out.photographers);
    } catch (err) {
      setFeedback({ error: err.error || 'Could not save the photographer list' });
    }
  };

  const label = { display: 'block', fontFamily: fonts, fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: palette.textMuted, marginBottom: 4 };
  const input = { fontFamily: fonts, fontSize: 13, padding: '9px 10px', border: `1px solid ${palette.border}`, background: palette.white, color: palette.text, width: '100%', boxSizing: 'border-box' };
  const btn = (bg, fg) => ({ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '8px 14px', background: bg, color: fg, border: 'none', cursor: 'pointer' });
  const field = (w) => ({ flex: `1 1 ${w}px`, minWidth: Math.min(w, 150) });

  const visible = jobs.filter((j) => showCancelled || j.status !== 'cancelled');
  const crossesMidnight = parseInt(String(form.startTime).split(':')[0], 10) + Number(form.hours) > 24;

  return (
    <div>
      <style>{`
        @media (max-width: 700px) {
          .cj-row { flex-direction: column; }
          .cj-row > div { flex: 1 1 auto !important; width: 100%; }
          .cj-card-actions { width: 100%; }
          /* Stacked, the right-aligned status column reads as stray indented
             text — pull it back to the left edge with everything else. */
          .cj-status { text-align: left !important; }
        }
      `}</style>

      <h3 style={{ fontFamily: fonts, fontSize: 14, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, marginTop: 0, marginBottom: 6 }}>
        {editingId ? 'Edit custom job' : 'New custom job'}
      </h3>
      <p style={{ fontFamily: fonts, fontSize: 12, color: palette.textMuted, margin: '0 0 18px', lineHeight: 1.6 }}>
        Sets your own price, holds the studio for those hours (any time of day — these aren't limited to normal
        hours), and emails the customer their booking details plus a Stripe invoice. {CONTACT_EMAIL} is CC'd
        on every email.
      </p>

      <div style={{ background: palette.white, border: `1px solid ${palette.border}`, padding: 20, marginBottom: 26 }}>
        <div className="cj-row" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
          <div style={field(220)}>
            <label style={label}>Customer name</label>
            <input style={input} value={form.name} onChange={set('name')} placeholder="Jordan Reyes" />
          </div>
          <div style={field(260)}>
            <label style={label}>Customer email</label>
            <input style={input} type="email" value={form.email} onChange={set('email')} placeholder="jordan@example.com" />
          </div>
        </div>

        <div className="cj-row" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
          <div style={field(160)}>
            <label style={label}>Date</label>
            <input style={input} type="date" value={form.date} onChange={set('date')} />
          </div>
          <div style={field(140)}>
            <label style={label}>Start time</label>
            <select style={input} value={form.startTime} onChange={set('startTime')}>
              {TIME_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div style={field(110)}>
            <label style={label}>Hours</label>
            <select style={input} value={form.hours} onChange={set('hours')}>
              {Array.from({ length: 14 }, (_, i) => i + 1).map((h) => <option key={h} value={h}>{h} hr{h > 1 ? 's' : ''}</option>)}
            </select>
          </div>
          <div style={field(170)}>
            <label style={label}>Space</label>
            <select style={input} value={form.room} onChange={set('room')}>
              {CUSTOM_JOB_ROOMS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
          </div>
          <div style={field(140)}>
            <label style={label}>Price (USD)</label>
            <input style={input} type="number" min="0" step="0.01" value={form.priceDollars} onChange={set('priceDollars')} placeholder="750" />
          </div>
        </div>
        {crossesMidnight ? (
          <p style={{ fontFamily: fonts, fontSize: 11, color: palette.orange, margin: '-6px 0 14px', lineHeight: 1.6 }}>
            {timeLabel(form.startTime, Number(form.hours))} runs past midnight. The calendar blocks by day, so this
            has to be booked as two jobs — one ending at midnight, one starting at it.
          </p>
        ) : (
          <p style={{ fontFamily: fonts, fontSize: 11, color: palette.textMuted, margin: '-6px 0 14px' }}>
            {form.date ? `Blocks ${dateLabel(form.date)}, ${timeLabel(form.startTime, Number(form.hours))}. ` : ''}
            Leave the price at $0 for a comped shoot — no invoice, the confirmation goes out immediately.
          </p>
        )}

        <div className="cj-row" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
          <div style={field(260)}>
            <label style={label}>Job name (shows on the invoice)</label>
            <input style={input} value={form.service} onChange={set('service')} placeholder="Brand headshots" />
          </div>
          <div style={field(200)}>
            <label style={label}>Photographer</label>
            <select style={input} value={form.photographer} onChange={set('photographer')}>
              <option value="">— Not assigned —</option>
              {photographers.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={label}>What's included (customer sees this on the invoice and in their email)</label>
          <textarea
            style={{ ...input, minHeight: 70, resize: 'vertical' }}
            value={form.includes}
            onChange={set('includes')}
            placeholder="2 hours in the full studio, 3 looks, 15 retouched images delivered within 5 business days."
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={label}>Internal notes (team only — never emailed)</label>
          <textarea
            style={{ ...input, minHeight: 50, resize: 'vertical' }}
            value={form.notes}
            onChange={set('notes')}
            placeholder="Client is bringing their own stylist. Needs the seamless up by 9."
          />
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: fonts, fontSize: 12, color: palette.text, marginBottom: 16, cursor: 'pointer' }}>
          <input type="checkbox" checked={form.stripeEmails} onChange={set('stripeEmails')} />
          Also have Stripe email its own copy of the invoice
        </label>

        {conflicts.length > 0 && (
          <div style={{ background: '#fff4e6', border: `1px solid ${palette.orange}`, padding: '12px 14px', marginBottom: 16 }}>
            <div style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: palette.orange, marginBottom: 6 }}>
              Already on the calendar
            </div>
            {conflicts.map((c, i) => (
              <div key={i} style={{ fontFamily: fonts, fontSize: 12, color: palette.text, lineHeight: 1.7 }}>· {c}</div>
            ))}
            <button onClick={() => submit(true)} disabled={saving} style={{ ...btn(palette.orange, palette.white), marginTop: 10 }}>
              Book it anyway
            </button>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button onClick={() => submit(false)} disabled={saving} style={btn(palette.brown, palette.cream)}>
            {saving ? 'Working…' : editingId ? 'Save & re-send invoice' : 'Book & send invoice'}
          </button>
          {editingId && (
            <button onClick={() => { setEditingId(null); setForm(blankCustomJob()); setConflicts([]); }} style={btn(palette.beige, palette.brown)}>
              Cancel edit
            </button>
          )}
        </div>

        {feedback && (
          <div style={{
            marginTop: 14, padding: '10px 12px', fontFamily: fonts, fontSize: 12, lineHeight: 1.6,
            background: feedback.error ? '#fdecea' : '#eef7ed',
            border: `1px solid ${feedback.error ? palette.red : palette.sage}`,
            color: feedback.error ? palette.red : palette.text,
          }}>
            {feedback.error || feedback.message}
          </div>
        )}
      </div>

      {/* Photographer roster */}
      <div style={{ background: palette.beige, border: `1px solid ${palette.border}`, padding: '14px 18px', marginBottom: 26 }}>
        <div style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: palette.brown, marginBottom: 10 }}>
          Photographers
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
          {photographers.map((p) => (
            <span key={p} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: palette.white, border: `1px solid ${palette.border}`, padding: '5px 10px', fontFamily: fonts, fontSize: 12 }}>
              {p}
              <button
                onClick={() => { if (confirm(`Remove ${p} from the photographer list? Jobs already assigned to them keep the name.`)) savePhotographers(photographers.filter((x) => x !== p)); }}
                style={{ background: 'none', border: 'none', color: palette.textMuted, cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}
                title={`Remove ${p}`}
              >×</button>
            </span>
          ))}
          {photographers.length === 0 && <span style={{ fontFamily: fonts, fontSize: 12, color: palette.textMuted }}>No photographers yet.</span>}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input
            style={{ ...input, width: 200 }}
            value={newPhotographer}
            onChange={(e) => setNewPhotographer(e.target.value)}
            placeholder="Add a photographer"
            onKeyDown={(e) => { if (e.key === 'Enter' && newPhotographer.trim()) { savePhotographers([...photographers, newPhotographer.trim()]); setNewPhotographer(''); } }}
          />
          <button
            onClick={() => { if (newPhotographer.trim()) { savePhotographers([...photographers, newPhotographer.trim()]); setNewPhotographer(''); } }}
            style={btn(palette.sage, palette.white)}
          >Add</button>
        </div>
      </div>

      {/* Job list */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
        <h3 style={{ fontFamily: fonts, fontSize: 14, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, margin: 0 }}>
          Custom jobs ({visible.length})
        </h3>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: fonts, fontSize: 12, color: palette.textMuted, cursor: 'pointer' }}>
          <input type="checkbox" checked={showCancelled} onChange={(e) => setShowCancelled(e.target.checked)} />
          Show cancelled
        </label>
      </div>

      {loading ? (
        <p style={{ fontFamily: fonts, fontSize: 13, color: palette.textMuted }}>Loading…</p>
      ) : visible.length === 0 ? (
        <p style={{ fontFamily: fonts, fontSize: 13, color: palette.textMuted }}>No custom jobs yet.</p>
      ) : visible.map((job) => {
        const paid = job.payment_status === 'paid';
        const comped = !job.quoted_cents;
        const cancelled = job.status === 'cancelled';
        const statusColor = cancelled ? palette.textMuted : paid ? palette.sage : comped ? palette.brown : palette.orange;
        const statusText = cancelled ? 'Cancelled' : paid ? `Paid ${money(job.amount_paid)}` : comped ? 'No charge' : `Awaiting ${money(job.quoted_cents)}`;
        const busy = busyId === job.id;
        return (
          <div key={job.id} style={{
            background: palette.white, border: `1px solid ${palette.border}`,
            borderLeft: `3px solid ${statusColor}`, padding: '14px 16px', marginBottom: 10,
            opacity: cancelled ? 0.6 : 1,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
              <div style={{ minWidth: 220, flex: 1 }}>
                <div style={{ fontFamily: fonts, fontSize: 14, fontWeight: 700, color: palette.text }}>
                  {job.user_name} <span style={{ fontWeight: 400, color: palette.textMuted }}>· {job.service}</span>
                </div>
                <div style={{ fontFamily: fonts, fontSize: 12, color: palette.textMuted, lineHeight: 1.8, marginTop: 4 }}>
                  {dateLabel(job.date)} · {timeLabel(String(job.start_time).slice(0, 5), job.hours)} ({job.hours}h)<br />
                  {CUSTOM_JOB_ROOMS.find((r) => r.id === job.room)?.label || job.room}
                  {job.photographer ? ` · 📸 ${job.photographer}` : ' · no photographer assigned'}<br />
                  {job.user_email}
                </div>
              </div>
              <div className="cj-status" style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: fonts, fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: statusColor }}>
                  {statusText}
                </div>
                <div style={{ fontFamily: fonts, fontSize: 11, color: palette.textMuted, marginTop: 2 }}>{job.booking_ref}</div>
                {job.invoice_sent_at && (
                  <div style={{ fontFamily: fonts, fontSize: 11, color: palette.textMuted }}>
                    emailed {new Date(job.invoice_sent_at).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>

            {job.includes && (
              <div style={{ fontFamily: fonts, fontSize: 12, color: palette.text, background: palette.beige, padding: '8px 10px', marginBottom: 8, whiteSpace: 'pre-wrap' }}>
                {job.includes}
              </div>
            )}
            {job.notes && (
              <div style={{ fontFamily: fonts, fontSize: 12, color: palette.textMuted, marginBottom: 8, whiteSpace: 'pre-wrap' }}>
                🗒️ {job.notes}
              </div>
            )}

            <div className="cj-card-actions" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {job.payment_url && !paid && !cancelled && (
                <>
                  <a href={job.payment_url} target="_blank" rel="noopener noreferrer" style={{ ...btn(palette.beige, palette.brown), textDecoration: 'none', display: 'inline-block' }}>
                    Open invoice
                  </a>
                  <button onClick={() => { navigator.clipboard?.writeText(job.payment_url); setFeedback({ ok: true, message: `Payment link for ${job.booking_ref} copied.` }); }} style={btn(palette.beige, palette.brown)}>
                    Copy link
                  </button>
                </>
              )}
              {!cancelled && (
                <button disabled={busy} onClick={() => act(job, 'resend')} style={btn(palette.brown, palette.cream)}>
                  {busy ? '…' : paid || comped ? 'Re-send confirmation' : 'Re-send invoice'}
                </button>
              )}
              {!paid && !comped && !cancelled && job.stripe_invoice_id && (
                <button disabled={busy} onClick={() => act(job, 'check-payment')} style={btn(palette.sage, palette.white)}>
                  Check Stripe
                </button>
              )}
              {!paid && !comped && !cancelled && (
                <button
                  disabled={busy}
                  onClick={() => act(job, 'mark-paid', {}, `Mark ${job.booking_ref} paid outside Stripe (cash, Zelle, etc.)? This sends the confirmation and voids the Stripe invoice.`)}
                  style={btn(palette.beige, palette.brown)}
                >
                  Mark paid manually
                </button>
              )}
              {!cancelled && !paid && (
                <button disabled={busy} onClick={() => startEdit(job)} style={btn(palette.beige, palette.brown)}>
                  Edit
                </button>
              )}
              {!cancelled && (
                <button
                  disabled={busy}
                  onClick={() => act(job, 'cancel', {}, `Cancel ${job.booking_ref}? This frees the calendar and kills the payment link.${paid ? ' They have already paid — refund them in Stripe separately.' : ''}`)}
                  style={btn(palette.red, palette.white)}
                >
                  Cancel job
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PawsPortraitsTab({ palette, fonts }) {
  const [signups, setSignups] = useState([]);
  const [meta, setMeta] = useState({ total: 25, taken: 0, remaining: 25 });
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null); // { path, label }
  const [emailTest, setEmailTest] = useState(null); // { ok, provider, from, to, error }
  const [testing, setTesting] = useState(false);
  const [waitlist, setWaitlist] = useState([]);
  const [questionnaire, setQuestionnaire] = useState([]);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null); // { invited, sent, failed } | { error }
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterGroup, setFilterGroup] = useState('all'); // all | invited | notInvited | booked | awaiting
  const [filterText, setFilterText] = useState('');
  const [filterSource, setFilterSource] = useState('all'); // all | signup | waitlist
  const [bookedRound, setBookedRound] = useState(2); // which session the Booked tab shows
  const [subTab, setSubTab] = useState('signups'); // signups (Interested) | questionnaire (Booked) | photos
  const [msgVariant, setMsgVariant] = useState('standard'); // standard | lastCall

  const authHeader = () => {
    const token = localStorage.getItem('hddn_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchQuestionnaire = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/paws-questionnaire`, { headers: authHeader() });
      if (res.ok) setQuestionnaire(await res.json());
    } catch (err) { console.error(err); }
  };

  const toggleInvited = async (id, kind, value) => {
    // Optimistic update so the checkbox feels instant.
    if (kind === 'signup') setSignups((prev) => prev.map((s) => (s.id === id ? { ...s, invited: value } : s)));
    else setWaitlist((prev) => prev.map((w) => (w.id === id ? { ...w, invited: value } : w)));
    const path = kind === 'signup' ? 'paws-signups' : 'paws-waitlist';
    try {
      await fetch(`${API_BASE}/api/${path}/${id}`, {
        method: 'PUT', headers: { ...authHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify({ invited: value }),
      });
    } catch (err) { console.error(err); }
  };

  // Bulk set the invite flag across the current filtered view. Rows come from
  // two tables, so signups go through the bulk endpoint and waitlist entries
  // are updated one by one (there are only ever a handful).
  const bulkInvite = async (rows, value) => {
    if (!rows.length) return;
    const verb = value ? 'Select' : 'Deselect';
    if (!confirm(`${verb} all ${rows.length} shown ${rows.length === 1 ? 'person' : 'people'}?`)) return;
    const signupIds = rows.filter((r) => r._kind === 'signup').map((r) => r.id);
    const waitIds = rows.filter((r) => r._kind === 'waitlist').map((r) => r.id);
    setSignups((prev) => prev.map((s) => (signupIds.includes(s.id) ? { ...s, invited: value } : s)));
    setWaitlist((prev) => prev.map((w) => (waitIds.includes(w.id) ? { ...w, invited: value } : w)));
    try {
      if (signupIds.length) {
        await fetch(`${API_BASE}/api/paws-signups/bulk-invite`, {
          method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify({ ids: signupIds, invited: value }),
        });
      }
      for (const id of waitIds) {
        await fetch(`${API_BASE}/api/paws-waitlist/${id}`, {
          method: 'PUT', headers: { ...authHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify({ invited: value }),
        });
      }
    } catch (err) { console.error(err); }
    fetchSignups(); fetchWaitlist();
  };

  const sendInvites = async () => {
    const count = signups.filter((s) => s.invited).length + waitlist.filter((w) => w.invited).length;
    if (!confirm(`Send the questionnaire link to ${count} selected ${count === 1 ? 'person' : 'people'}? (${CONTACT_EMAIL} is CC'd on each so you can confirm they went out.)`)) return;
    setSending(true); setSendResult(null);
    try {
      const res = await fetch(`${API_BASE}/api/paws-send-invites`, { method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify({ variant: msgVariant }) });
      const data = await res.json().catch(() => ({}));
      setSendResult(res.ok ? data : { error: data.error || `Request failed (${res.status})` });
      if (res.ok) { fetchSignups(); fetchWaitlist(); }
    } catch (err) {
      setSendResult({ error: err.message });
    } finally { setSending(false); }
  };

  // Re-send the link only to invited people who haven't booked a time yet.
  const resendUnresponded = async () => {
    const booked = new Set(questionnaire.map((q) => (q.email || '').toLowerCase()));
    const n = [...signups, ...waitlist].filter((x) => x.invited && !booked.has((x.email || '').toLowerCase())).length;
    if (n === 0) { setSendResult({ error: 'Everyone invited has already booked 🎉' }); return; }
    if (!confirm(`Re-send the questionnaire link to the ${n} invited ${n === 1 ? 'person' : 'people'} who haven't booked yet?`)) return;
    setSending(true); setSendResult(null);
    try {
      const res = await fetch(`${API_BASE}/api/paws-send-invites`, { method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify({ onlyUnresponded: true, variant: msgVariant }) });
      const data = await res.json().catch(() => ({}));
      setSendResult(res.ok ? data : { error: data.error || `Request failed (${res.status})` });
      if (res.ok) { fetchSignups(); fetchWaitlist(); }
    } catch (err) {
      setSendResult({ error: err.message });
    } finally { setSending(false); }
  };

  const sendTestInvite = async () => {
    setSending(true); setSendResult(null);
    try {
      const res = await fetch(`${API_BASE}/api/paws-send-invites`, { method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify({ test: true, variant: msgVariant }) });
      const data = await res.json().catch(() => ({}));
      setSendResult(res.ok ? { ...data, test: true } : { error: data.error || `Request failed (${res.status})` });
    } catch (err) {
      setSendResult({ error: err.message });
    } finally { setSending(false); }
  };

  const downloadQuestionnaireCsv = () => {
    const esc = (v) => { const s = v == null ? '' : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const headers = ['Submitted', 'Session', 'Attended', 'Name', 'Email', 'Phone', 'Best contact', 'Block', 'Slot', 'People', 'Children', 'Pets', 'Pet details', 'Behavioral notes', 'Leash notes', 'Props', 'Poses', 'Extra notes'];
    const rows = questionnaire.map((r) => [
      new Date(r.created_at).toLocaleString(),
      (r.session_round || 1) === 2 ? 'Aug 9' : 'Jul 26',
      r.attended === true ? 'Showed' : r.attended === false ? 'No-show' : 'Not marked',
      esc(r.name), esc(r.email), esc(r.phone), esc(r.contact_method), esc(r.block), esc(r.slot), esc(r.num_people),
      esc(r.children_info), esc(r.num_pets), esc(r.pet_details), esc(r.behavioral_notes),
      esc(r.leash_notes), esc(r.props), esc(r.poses), esc(r.extra_notes),
    ].join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `paws-questionnaire-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  // Shoot-day attendance: null = not marked, true = showed, false = no-show.
  const [attFilter, setAttFilter] = useState('all'); // all | showed | noshow | unmarked
  const setAttended = async (id, attended) => {
    setQuestionnaire((prev) => prev.map((q) => (q.id === id ? { ...q, attended } : q)));
    try {
      await fetch(`${API_BASE}/api/paws-questionnaire/${id}`, {
        method: 'PUT', headers: { ...authHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify({ attended }),
      });
    } catch (err) { console.error(err); fetchQuestionnaire(); }
  };

  const [rosterLoading, setRosterLoading] = useState(false);
  const downloadRosterPdf = async (roundNum = 1) => {
    setRosterLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/paws-roster.pdf?round=${roundNum}`, { headers: authHeader() });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `paws-roster-round${roundNum}-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch (err) {
      alert(`Could not download the roster PDF: ${err.message}`);
    } finally { setRosterLoading(false); }
  };

  const fetchWaitlist = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/paws-waitlist`, { headers: authHeader() });
      if (res.ok) setWaitlist(await res.json());
    } catch (err) { console.error(err); }
  };

  const updateWaitStatus = async (id, status) => {
    await fetch(`${API_BASE}/api/paws-waitlist/${id}`, {
      method: 'PUT', headers: { ...authHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify({ status }),
    });
    fetchWaitlist();
  };

  const deleteWaitlist = async (id) => {
    if (!confirm('Remove this person from the waitlist?')) return;
    await fetch(`${API_BASE}/api/paws-waitlist/${id}`, { method: 'DELETE', headers: authHeader() });
    fetchWaitlist();
  };

  // Move a waitlist entry into the main signup list (e.g. to fill a cancellation).
  const promoteWaitlist = async (id, name) => {
    if (!confirm(`Move ${name || 'this person'} to the main list?`)) return;
    const res = await fetch(`${API_BASE}/api/paws-waitlist/${id}/promote`, { method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' }, body: '{}' });
    if (res.ok) { fetchSignups(); fetchWaitlist(); }
    else { const d = await res.json().catch(() => ({})); alert(d.error || 'Could not move to the main list.'); }
  };

  const sendTestEmail = async () => {
    setTesting(true); setEmailTest(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/test-email`, { method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' }, body: '{}' });
      const data = await res.json().catch(() => ({}));
      setEmailTest(res.ok ? data : { ok: false, error: data.error || `Request failed (${res.status})` });
    } catch (err) {
      setEmailTest({ ok: false, error: err.message });
    } finally { setTesting(false); }
  };

  const fetchSignups = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/paws-signups`, { headers: authHeader() });
      if (res.ok) {
        const data = await res.json();
        setSignups(data.signups || []);
        setMeta({ total: data.total, taken: data.taken, remaining: data.remaining });
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchSignups(); fetchWaitlist(); fetchQuestionnaire(); }, []);

  const updateStatus = async (id, status) => {
    await fetch(`${API_BASE}/api/paws-signups/${id}`, {
      method: 'PUT',
      headers: { ...authHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    fetchSignups();
  };

  const deleteSignup = async (id) => {
    if (!confirm('Delete this signup? This cannot be undone.')) return;
    await fetch(`${API_BASE}/api/paws-signups/${id}`, { method: 'DELETE', headers: authHeader() });
    fetchSignups();
  };

  // Exports the whole Interested list — signups and waitlist together.
  const downloadCsv = () => {
    const headers = ['Submitted', 'Source', 'Name', 'Email', 'Phone', 'Pet Name', 'Pet Type', 'Story', 'Preferred Time', 'Media Release', 'Status', 'Booked', 'Pet Photo', 'Reference Photo'];
    const escape = (val) => {
      const s = val == null ? '' : String(val);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const all = [
      ...signups.map((s) => ({ ...s, _kind: 'signup' })),
      ...waitlist.map((w) => ({ ...w, _kind: 'waitlist' })),
    ];
    const rows = all.map((r) => {
      const q = bookedByEmail[(r.email || '').toLowerCase()];
      const wait = r._kind === 'waitlist';
      return [
        new Date(r.created_at).toLocaleString(),
        wait ? 'Waitlist' : 'Signed up',
        escape(r.name), escape(r.email), escape(r.phone),
        escape(wait ? '' : r.pet_name), escape(wait ? '' : r.pet_type), escape(wait ? '' : r.story),
        escape(wait ? '' : (r.preferred_time || '')),
        wait ? '' : (r.media_release ? 'Yes' : 'No'), r.status,
        escape(q ? `${q.block || ''} ${q.slot || ''}`.trim() : ''),
        !wait && r.has_pet_photo ? 'Yes' : 'No', !wait && r.has_reference_photo ? 'Yes' : 'No',
      ].join(',');
    });
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `paws-interested-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const statusColors = { new: '#C8621A', contacted: '#1A4C6E', scheduled: '#7a8a5e', done: '#6b7c5e', cancelled: '#999' };
  const th = { padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: palette.textMuted, whiteSpace: 'nowrap' };
  const td = { padding: '10px', fontFamily: fonts, fontSize: 12, color: palette.text, verticalAlign: 'top', borderBottom: `1px solid ${palette.border}` };

  if (loading) return <div style={{ fontFamily: fonts, padding: 20, color: palette.textMuted }}>Loading signups…</div>;

  const remaining = meta.remaining;
  const lowSlots = remaining < 10;

  // Link each person's booked time back to their signup/waitlist row by email.
  const bookedByEmail = {};
  questionnaire.forEach((q) => { if (q.email) bookedByEmail[q.email.toLowerCase()] = q; });
  // …and their original signup, so we can show the pet photo they uploaded.
  const signupByEmail = {};
  signups.forEach((s) => { if (s.email) signupByEmail[s.email.toLowerCase()] = s; });
  const bookedCell = (email) => {
    const q = bookedByEmail[(email || '').toLowerCase()];
    if (!q) return <span style={{ color: palette.textMuted }}>—</span>;
    return (
      <>
        <div style={{ fontWeight: 700, color: palette.orange }}>{q.slot}</div>
        {q.block && <div style={{ color: palette.textMuted, fontSize: 11 }}>{q.block}</div>}
      </>
    );
  };
  // Small "Sent ✓ date" badge under the invite checkbox once the invite mailed.
  const sentBadge = (row) => {
    if (!row.invite_sent_at) return null;
    const booked = bookedByEmail[(row.email || '').toLowerCase()];
    return (
      <div style={{ fontFamily: fonts, fontSize: 9, fontWeight: 700, marginTop: 3, color: booked ? palette.sage : palette.orange, whiteSpace: 'nowrap' }}
        title={`Invite sent ${new Date(row.invite_sent_at).toLocaleString()}${booked ? ' · booked' : ' · awaiting reply'}`}>
        Sent ✓ {new Date(row.invite_sent_at).toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' })}
      </div>
    );
  };

  // "Interested" = the original signups and the waitlist, shown as one list.
  // _kind keeps track of which table a row came from (different statuses/actions).
  const interested = [
    ...signups.map((s) => ({ ...s, _kind: 'signup' })),
    ...waitlist.map((w) => ({ ...w, _kind: 'waitlist' })),
  ];
  const SIGNUP_STATUSES = ['new', 'contacted', 'scheduled', 'done', 'cancelled'];
  const WAITLIST_STATUSES = ['waiting', 'contacted', 'offered', 'converted', 'declined'];

  // Filtered view (status dropdown + group + source + text search).
  // Cancelled/declined people are hidden by default; pick that status to see them.
  const q = filterText.trim().toLowerCase();
  const hiddenCount = interested.filter((r) => r.status === 'cancelled' || r.status === 'declined').length;
  const filteredSignups = interested.filter((r) => {
    if (filterStatus === 'all') { if (r.status === 'cancelled' || r.status === 'declined') return false; }
    else if (r.status !== filterStatus) return false;
    if (filterSource !== 'all' && r._kind !== filterSource) return false;
    const isBooked = !!bookedByEmail[(r.email || '').toLowerCase()];
    if (filterGroup === 'invited' && !r.invited) return false;
    if (filterGroup === 'notInvited' && r.invited) return false;
    if (filterGroup === 'booked' && !isBooked) return false;
    if (filterGroup === 'notBooked' && isBooked) return false;
    if (filterGroup === 'awaiting' && (!r.invited || isBooked)) return false;
    if (q) {
      const hay = `${r.name || ''} ${r.email || ''} ${r.phone || ''} ${r.pet_name || ''} ${r.pet_type || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
  const filtersActive = filterStatus !== 'all' || filterGroup !== 'all' || filterSource !== 'all' || q !== '';

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
        <span style={{
          fontFamily: fonts, fontSize: 12, fontWeight: 700, padding: '5px 12px', borderRadius: 12,
          background: lowSlots ? palette.orange : palette.beige, color: lowSlots ? palette.white : palette.brown,
        }}>
          {meta.taken}/{meta.total} booked · {remaining} left
        </span>
        {subTab === 'signups' && (
        <button onClick={downloadCsv} disabled={interested.length === 0} style={{
          fontFamily: fonts, fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
          padding: '8px 16px', background: palette.brown, color: palette.cream, border: 'none',
          cursor: interested.length === 0 ? 'not-allowed' : 'pointer', opacity: interested.length === 0 ? 0.5 : 1,
        }}>Export CSV</button>
        )}
      </div>

      {/* One pipeline, three steps: people sign up → invited people book a
          slot → after the shoot, galleries go out and orders come in. */}
      <div style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${palette.border}`, marginBottom: 16, flexWrap: 'wrap' }}>
        {[
          ['signups', `1 · Signups (${interested.length})`],
          ['questionnaire', `2 · Booked (${questionnaire.length})`],
          ['photos', '3 · Galleries & Orders'],
        ].map(([key, label]) => (
          <button key={key} onClick={() => setSubTab(key)} style={{
            fontFamily: fonts, fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
            padding: '8px 16px', background: 'none', border: 'none', cursor: 'pointer',
            color: subTab === key ? palette.brown : palette.textMuted,
            borderBottom: subTab === key ? `3px solid ${palette.orange}` : '3px solid transparent', marginBottom: -1,
          }}>{label}</button>
        ))}
      </div>

      {subTab === 'signups' && (<>
      {/* Email diagnostics — confirm signup notifications can actually send */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16, padding: '10px 14px', background: palette.beige, border: `1px solid ${palette.border}`, borderRadius: 6 }}>
        <span style={{ fontFamily: fonts, fontSize: 12, color: palette.textMuted }}>Not getting signup emails?</span>
        <button onClick={sendTestEmail} disabled={testing} style={{
          fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
          padding: '6px 12px', background: palette.white, color: palette.brown, border: `1px solid ${palette.border}`,
          cursor: testing ? 'wait' : 'pointer',
        }}>{testing ? 'Sending…' : 'Send test email'}</button>
        {emailTest && (
          emailTest.ok ? (
            <span style={{ fontFamily: fonts, fontSize: 12, color: palette.sage, fontWeight: 700 }}>
              ✓ Sent via {emailTest.provider} to {emailTest.to} — check your inbox (and spam).
            </span>
          ) : (
            <span style={{ fontFamily: fonts, fontSize: 12, color: palette.red }}>
              ✗ Failed{emailTest.provider ? ` (${emailTest.provider})` : ''}: {emailTest.error || 'unknown error'}
              {emailTest.from ? ` · from: ${emailTest.from}` : ''}
            </span>
          )
        )}
      </div>

      {/* Invite + send questionnaire — tick the people to invite below, then send them the
          questionnaire + time-slot link in one click. */}
      {(() => {
        const invitedRows = [...signups, ...waitlist].filter((x) => x.invited);
        const invitedCount = invitedRows.length;
        const bookedSet = new Set(questionnaire.map((q) => (q.email || '').toLowerCase()));
        const sentCount = invitedRows.filter((x) => x.invite_sent_at).length;
        const bookedCount = invitedRows.filter((x) => bookedSet.has((x.email || '').toLowerCase())).length;
        const awaitingCount = invitedCount - bookedCount;
        const pill = (label, value, tone) => (
          <span style={{ fontFamily: fonts, fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 12, background: tone.bg, color: tone.fg }}>
            {value} {label}
          </span>
        );
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16, padding: '12px 14px', background: withAlpha(palette.orange, 0.08), border: `1px solid ${palette.orange}`, borderRadius: 6 }}>
            {/* Status breakdown: invited → sent → booked → awaiting */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontFamily: fonts, fontSize: 12, color: palette.brown, fontWeight: 700, marginRight: 2 }}>🐾 Batch</span>
              {pill('selected', invitedCount, { bg: palette.beige, fg: palette.brown })}
              {pill('sent', sentCount, { bg: withAlpha(palette.sage, 0.25), fg: palette.brown })}
              {pill('booked', bookedCount, { bg: palette.sage, fg: palette.white })}
              {pill('awaiting reply', awaitingCount, { bg: awaitingCount > 0 ? palette.orange : palette.beige, fg: awaitingCount > 0 ? palette.white : palette.textMuted })}
            </div>
            <span style={{ fontFamily: fonts, fontSize: 11, color: palette.textMuted }}>
              Tick “Select” on the people below (or use the filter + “Select all”), then send them the questionnaire &amp; time-slot link. Selecting doesn’t email anyone — only “Send questionnaire” does. {CONTACT_EMAIL} is CC’d on each so you can confirm they went out. Rows show “Sent ✓” once mailed and their booked time once they reply.
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <label style={{ fontFamily: fonts, fontSize: 11, color: palette.textMuted }}>Message:</label>
              <select value={msgVariant} onChange={(e) => setMsgVariant(e.target.value)} style={{
                fontFamily: fonts, fontSize: 11, fontWeight: 700, padding: '6px 8px', border: `1px solid ${palette.border}`, background: palette.white, color: palette.text,
              }}>
                <option value="standard">Standard invite</option>
                <option value="lastCall">A few spots open (waitlist)</option>
              </select>
              <button onClick={sendTestInvite} disabled={sending} style={{
                fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
                padding: '6px 12px', background: palette.white, color: palette.brown, border: `1px solid ${palette.border}`,
                cursor: sending ? 'wait' : 'pointer',
              }}>Send test to me</button>
              <button onClick={sendInvites} disabled={sending || invitedCount === 0} style={{
                fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
                padding: '6px 14px', background: palette.orange, color: palette.white, border: 'none',
                cursor: sending || invitedCount === 0 ? 'not-allowed' : 'pointer', opacity: sending || invitedCount === 0 ? 0.5 : 1,
              }}>{sending ? 'Sending…' : `Send questionnaire to ${invitedCount} selected`}</button>
              <button onClick={resendUnresponded} disabled={sending || awaitingCount === 0} title="Email only the selected people who haven't booked yet" style={{
                fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
                padding: '6px 14px', background: palette.white, color: palette.orange, border: `1px solid ${palette.orange}`,
                cursor: sending || awaitingCount === 0 ? 'not-allowed' : 'pointer', opacity: sending || awaitingCount === 0 ? 0.5 : 1,
              }}>{`Re-send to ${awaitingCount} awaiting`}</button>
              {sendResult && (
                sendResult.error ? (
                  <span style={{ fontFamily: fonts, fontSize: 12, color: palette.red }}>✗ {sendResult.error}</span>
                ) : sendResult.test ? (
                  <span style={{ fontFamily: fonts, fontSize: 12, color: sendResult.ok ? palette.sage : palette.red, fontWeight: 700 }}>
                    {sendResult.ok ? `✓ Test invite sent to ${sendResult.to} — check your inbox (and spam).` : `✗ Test failed: ${sendResult.error || 'email not configured'}`}
                  </span>
                ) : (
                  <span style={{ fontFamily: fonts, fontSize: 12, color: palette.sage, fontWeight: 700 }}>
                    ✓ Sent to {sendResult.sent} of {sendResult.invited}{sendResult.onlyUnresponded ? ' awaiting reply' : ''}{sendResult.failed ? ` · ${sendResult.failed} failed` : ''}{sendResult.cc ? ` · CC’d ${sendResult.cc}` : ''}
                  </span>
                )
              )}
            </div>
          </div>
        );
      })()}

      {interested.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: palette.textMuted, fontFamily: fonts }}>Nobody on the list yet.</div>
      ) : (
        <>
        {/* Filter the interested list */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          <input type="text" value={filterText} onChange={(e) => setFilterText(e.target.value)} placeholder="Search name, email, phone, pet…" style={{
            fontFamily: fonts, fontSize: 12, padding: '7px 10px', border: `1px solid ${palette.border}`, background: palette.white, color: palette.text, minWidth: 220, flex: '1 1 220px', maxWidth: 320,
          }} />
          <select value={filterSource} onChange={(e) => setFilterSource(e.target.value)} style={{ fontFamily: fonts, fontSize: 12, fontWeight: 700, padding: '7px 8px', border: `1px solid ${palette.border}`, background: palette.white, color: palette.text }}>
            <option value="all">All sources</option>
            <option value="signup">Signed up</option>
            <option value="waitlist">Waitlist</option>
          </select>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={{ fontFamily: fonts, fontSize: 12, fontWeight: 700, padding: '7px 8px', border: `1px solid ${palette.border}`, background: palette.white, color: palette.text }}>
            <option value="all">All statuses</option>
            {[...new Set([...SIGNUP_STATUSES, ...WAITLIST_STATUSES])].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filterGroup} onChange={(e) => setFilterGroup(e.target.value)} style={{ fontFamily: fonts, fontSize: 12, fontWeight: 700, padding: '7px 8px', border: `1px solid ${palette.border}`, background: palette.white, color: palette.text }}>
            <option value="all">Everyone</option>
            <option value="invited">Selected</option>
            <option value="notInvited">Not selected</option>
            <option value="booked">Booked a time</option>
            <option value="notBooked">Not booked yet</option>
            <option value="awaiting">Selected · awaiting reply</option>
          </select>
          <span style={{ fontFamily: fonts, fontSize: 11, color: palette.textMuted }}>
            {`${filteredSignups.length} shown`}
            {filterStatus === 'all' && hiddenCount > 0 && (
              <> · <span style={{ color: palette.textMuted }}>{hiddenCount} cancelled/declined hidden</span></>
            )}
          </span>
          {filtersActive && (
            <button onClick={() => { setFilterText(''); setFilterStatus('all'); setFilterGroup('all'); setFilterSource('all'); }} style={{
              fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '6px 10px',
              background: palette.white, color: palette.brown, border: `1px solid ${palette.border}`, cursor: 'pointer',
            }}>Clear</button>
          )}
          {/* Bulk invite/un-invite everyone in the current filtered view. */}
          <span style={{ width: 1, height: 20, background: palette.border, margin: '0 2px' }} />
          <button onClick={() => bulkInvite(filteredSignups, true)} disabled={filteredSignups.length === 0} title="Select everyone currently shown" style={{
            fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '6px 10px',
            background: palette.orange, color: palette.white, border: 'none', cursor: filteredSignups.length === 0 ? 'not-allowed' : 'pointer', opacity: filteredSignups.length === 0 ? 0.5 : 1,
          }}>Select all {filteredSignups.length}</button>
          <button onClick={() => bulkInvite(filteredSignups, false)} disabled={filteredSignups.length === 0} title="Deselect everyone currently shown" style={{
            fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '6px 10px',
            background: palette.white, color: palette.brown, border: `1px solid ${palette.border}`, cursor: filteredSignups.length === 0 ? 'not-allowed' : 'pointer', opacity: filteredSignups.length === 0 ? 0.5 : 1,
          }}>Deselect all</button>
        </div>
        <div style={{ overflowX: 'auto', border: `1px solid ${palette.border}`, background: palette.white }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
            <thead>
              <tr style={{ background: palette.beige, borderBottom: `2px solid ${palette.border}` }}>
                {['Select', 'Pet', 'Person', 'Contact', 'Booked', 'Story', 'Pref.', 'Release', 'Status', 'Added', ''].map((h) => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredSignups.length === 0 && (
                <tr><td colSpan={11} style={{ ...td, textAlign: 'center', color: palette.textMuted, padding: 24 }}>No matches — try clearing the filters.</td></tr>
              )}
              {filteredSignups.map((r) => {
                const isWait = r._kind === 'waitlist';
                const dash = <span style={{ color: palette.textMuted }}>—</span>;
                return (
                <tr key={`${r._kind}-${r.id}`} style={r.invited ? { background: withAlpha(palette.orange, 0.06) } : undefined}>
                  <td style={{ ...td, textAlign: 'center' }}>
                    <input type="checkbox" checked={!!r.invited} onChange={(e) => toggleInvited(r.id, r._kind, e.target.checked)}
                      title="Select for the questionnaire email" style={{ width: 18, height: 18, cursor: 'pointer', accentColor: palette.orange }} />
                    {sentBadge(r)}
                  </td>
                  <td style={td}>
                    {isWait ? dash : (
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        {r.has_pet_photo && (
                          <AuthedImg
                            path={`/api/paws-signups/${r.id}/photo/pet`}
                            alt={r.pet_name}
                            onClick={() => setLightbox({ path: `/api/paws-signups/${r.id}/photo/pet`, label: `${r.pet_name} (${r.pet_type})` })}
                            style={{ width: 46, height: 46, objectFit: 'cover', borderRadius: 6, cursor: 'pointer', border: `1px solid ${palette.border}` }}
                          />
                        )}
                        <div>
                          <div style={{ fontWeight: 700, color: palette.brown }}>{r.pet_name}</div>
                          <div style={{ color: palette.textMuted, fontSize: 11 }}>{r.pet_type}</div>
                          {r.has_reference_photo && (
                            <button onClick={() => setLightbox({ path: `/api/paws-signups/${r.id}/photo/reference`, label: `${r.pet_name} — reference` })}
                              style={{ fontFamily: fonts, fontSize: 10, color: palette.orange, background: 'none', border: 'none', padding: 0, cursor: 'pointer', marginTop: 2 }}>
                              📐 reference
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </td>
                  <td style={td}>
                    <span style={{ fontWeight: 600, color: palette.brown }}>{r.name}</span>
                    {isWait && (
                      <div style={{ fontFamily: fonts, fontSize: 9, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: palette.textMuted, marginTop: 2 }}>
                        from waitlist
                      </div>
                    )}
                  </td>
                  <td style={td}>
                    <a href={`mailto:${r.email}`} style={{ color: palette.orange, textDecoration: 'none' }}>{r.email}</a>
                    <div style={{ color: palette.textMuted }}><a href={`tel:${r.phone}`} style={{ color: palette.textMuted, textDecoration: 'none' }}>{r.phone}</a></div>
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{bookedCell(r.email)}</td>
                  <td style={{ ...td, maxWidth: 240, whiteSpace: 'normal' }}>{isWait ? dash : (r.story || dash)}</td>
                  <td style={td}>{isWait ? dash : (r.preferred_time || <span style={{ color: palette.textMuted }}>Any</span>)}</td>
                  <td style={td}>{isWait ? dash : (r.media_release
                    ? <span style={{ color: palette.sage, fontWeight: 700 }}>Yes</span>
                    : <span style={{ color: palette.red, fontWeight: 700 }}>No</span>)}</td>
                  <td style={td}>
                    <select value={r.status} onChange={(e) => (isWait ? updateWaitStatus(r.id, e.target.value) : updateStatus(r.id, e.target.value))} style={{
                      fontFamily: fonts, fontSize: 11, fontWeight: 700, padding: '4px 6px',
                      color: statusColors[r.status] || palette.text, border: `1px solid ${palette.border}`, background: palette.white,
                    }}>
                      {(isWait ? WAITLIST_STATUSES : SIGNUP_STATUSES).map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ ...td, color: palette.textMuted, whiteSpace: 'nowrap' }}>{new Date(r.created_at).toLocaleDateString()}</td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: 5, whiteSpace: 'nowrap' }}>
                      {isWait && (
                        <button onClick={() => promoteWaitlist(r.id, r.name)} title="Convert this waitlist entry into a full signup" style={{
                          fontFamily: fonts, fontSize: 11, fontWeight: 700, color: palette.white, background: palette.sage,
                          border: 'none', padding: '4px 8px', cursor: 'pointer',
                        }}>Convert</button>
                      )}
                      <button onClick={() => (isWait ? deleteWaitlist(r.id) : deleteSignup(r.id))} style={{
                        fontFamily: fonts, fontSize: 11, color: palette.red, background: 'none',
                        border: `1px solid ${palette.border}`, padding: '4px 8px', cursor: 'pointer',
                      }}>Del</button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}
      </>)}

      {/* Photo orders — pay for 5 or all, pick favorites, deliver links */}
      {subTab === 'photos' && <PawsPhotoOrdersPanel palette={palette} fonts={fonts} />}

      {/* Questionnaire responses — its own sub-tab: what guests filled in, the
          slot they booked, and who actually showed up */}
      {subTab === 'questionnaire' && (() => {
        const roundRows = questionnaire.filter((r) => (r.session_round || 1) === bookedRound);
        const roundLabel = bookedRound === 2 ? 'Aug 9' : 'Jul 26';
        const roundPicker = (
          <div style={{ display: 'flex', gap: 4 }}>
            {[[2, 'Aug 9'], [1, 'Jul 26']].map(([n, lbl]) => (
              <button key={n} onClick={() => setBookedRound(n)} style={{
                fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '6px 12px',
                background: bookedRound === n ? palette.brown : palette.white, color: bookedRound === n ? palette.cream : palette.brown,
                border: `1px solid ${bookedRound === n ? palette.brown : palette.border}`, cursor: 'pointer',
              }}>{lbl} ({questionnaire.filter((r) => (r.session_round || 1) === n).length})</button>
            ))}
          </div>
        );
        return roundRows.length === 0 ? (
        <div>
          <div style={{ marginBottom: 12 }}>{roundPicker}</div>
          <div style={{ padding: 40, textAlign: 'center', color: palette.textMuted, fontFamily: fonts }}>
            Nobody has booked the {roundLabel} session yet.
          </div>
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <h2 style={{ fontFamily: fonts, fontSize: 16, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, margin: 0 }}>
                Booked — {roundLabel} ({roundRows.length})
              </h2>
              {roundPicker}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={() => downloadRosterPdf(bookedRound)} disabled={rosterLoading} title="Full roster: questionnaire + signup info + photos, in time order" style={{
                fontFamily: fonts, fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
                padding: '8px 16px', background: palette.orange, color: palette.white, border: 'none', cursor: rosterLoading ? 'wait' : 'pointer',
              }}>{rosterLoading ? 'Building…' : 'Download roster (PDF)'}</button>
              <button onClick={downloadQuestionnaireCsv} style={{
                fontFamily: fonts, fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
                padding: '8px 16px', background: palette.brown, color: palette.cream, border: 'none', cursor: 'pointer',
              }}>Export CSV</button>
            </div>
          </div>

          {/* Attendance — mark who showed, then filter to the no-shows */}
          {(() => {
            const showed = roundRows.filter((r) => r.attended === true).length;
            const noshow = roundRows.filter((r) => r.attended === false).length;
            const unmarked = roundRows.length - showed - noshow;
            const pill = (label, n, bg, fg) => (
              <span style={{ fontFamily: fonts, fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 12, background: bg, color: fg }}>{n} {label}</span>
            );
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                <span style={{ fontFamily: fonts, fontSize: 11, color: palette.textMuted }}>Attendance:</span>
                {pill('showed', showed, palette.sage, palette.white)}
                {pill('no-show', noshow, noshow ? palette.red : palette.beige, noshow ? palette.white : palette.textMuted)}
                {pill('not marked', unmarked, unmarked ? palette.orange : palette.beige, unmarked ? palette.white : palette.textMuted)}
                <select value={attFilter} onChange={(e) => setAttFilter(e.target.value)} style={{
                  fontFamily: fonts, fontSize: 12, fontWeight: 700, padding: '6px 8px', border: `1px solid ${palette.border}`, background: palette.white, color: palette.text,
                }}>
                  <option value="all">Show everyone</option>
                  <option value="showed">Only showed</option>
                  <option value="noshow">Only no-shows</option>
                  <option value="unmarked">Only not marked</option>
                </select>
                {unmarked > 0 && (
                  <span style={{ fontFamily: fonts, fontSize: 11, color: palette.textMuted }}>
                    Tick “Showed” or “No-show” on each row below.
                  </span>
                )}
              </div>
            );
          })()}

          <div style={{ overflowX: 'auto', border: `1px solid ${palette.border}`, background: palette.white }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
              <thead>
                <tr style={{ background: palette.beige, borderBottom: `2px solid ${palette.border}` }}>
                  {['Slot', 'Guest', 'Attended', 'Contact', 'Party', 'Pets', 'Notes', 'Submitted'].map((h) => <th key={h} style={th}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {[...roundRows]
                  .filter((r) => attFilter === 'all'
                    || (attFilter === 'showed' && r.attended === true)
                    || (attFilter === 'noshow' && r.attended === false)
                    || (attFilter === 'unmarked' && r.attended !== true && r.attended !== false))
                  .sort((a, b) => String(a.slot || '').localeCompare(String(b.slot || ''))).map((r) => (
                  <tr key={r.id} style={r.attended === false ? { background: withAlpha(palette.red, 0.06) } : undefined}>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 700, color: palette.orange }}>{r.slot || '—'}</div>
                      {r.block && <div style={{ color: palette.textMuted, fontSize: 11 }}>{r.block}</div>}
                    </td>
                    <td style={td}>
                      {(() => {
                        const s = signupByEmail[(r.email || '').toLowerCase()];
                        // Round 2 guests upload their photo with the questionnaire;
                        // round 1 guests had theirs on the original signup.
                        const photoPath = r.has_pet_photo
                          ? `/api/paws-questionnaire/${r.id}/photo`
                          : (s && s.has_pet_photo ? `/api/paws-signups/${s.id}/photo/pet` : null);
                        const petLabel = s && s.pet_name ? `${s.pet_name}${s.pet_type ? ` · ${s.pet_type}` : ''}` : (r.pet_details || '');
                        return (
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            {photoPath && (
                              <AuthedImg
                                path={photoPath}
                                alt={petLabel || r.name}
                                onClick={() => setLightbox({ path: photoPath, label: `${petLabel || 'Pet'} — ${r.name}` })}
                                style={{ width: 42, height: 42, objectFit: 'cover', borderRadius: 6, cursor: 'pointer', border: `1px solid ${palette.border}`, flexShrink: 0 }}
                              />
                            )}
                            <div>
                              <div style={{ fontWeight: 600, color: palette.brown }}>{r.name}</div>
                              {petLabel && (
                                <div style={{ color: palette.textMuted, fontSize: 11, maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{petLabel}</div>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {[['Showed', true, palette.sage], ['No-show', false, palette.red]].map(([label, val, color]) => {
                        const on = r.attended === val;
                        return (
                          <button key={label} onClick={() => setAttended(r.id, on ? null : val)}
                            title={on ? 'Click to clear' : `Mark as ${label.toLowerCase()}`}
                            style={{
                              fontFamily: fonts, fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
                              padding: '4px 7px', marginRight: 4, cursor: 'pointer',
                              background: on ? color : palette.white, color: on ? palette.white : palette.textMuted,
                              border: `1px solid ${on ? color : palette.border}`,
                            }}>{label}</button>
                        );
                      })}
                    </td>
                    <td style={td}>
                      <a href={`mailto:${r.email}`} style={{ color: palette.orange, textDecoration: 'none' }}>{r.email}</a>
                      <div style={{ color: palette.textMuted }}><a href={`tel:${r.phone}`} style={{ color: palette.textMuted, textDecoration: 'none' }}>{r.phone}</a></div>
                      {r.contact_method && <div style={{ color: palette.sage, fontSize: 10, fontWeight: 700 }}>prefers {r.contact_method}</div>}
                    </td>
                    <td style={{ ...td, whiteSpace: 'normal', maxWidth: 160 }}>
                      <div>{r.num_people || '—'} {r.num_people ? 'people' : ''}</div>
                      {r.children_info && <div style={{ color: palette.textMuted, fontSize: 11 }}>Kids: {r.children_info}</div>}
                    </td>
                    <td style={{ ...td, whiteSpace: 'normal', maxWidth: 200 }}>
                      <div>{r.num_pets || '—'} {r.num_pets ? 'pets' : ''}</div>
                      {r.pet_details && <div style={{ color: palette.textMuted, fontSize: 11 }}>{r.pet_details}</div>}
                      {r.behavioral_notes && <div style={{ color: palette.red, fontSize: 11 }}>⚠ {r.behavioral_notes}</div>}
                      {r.leash_notes && <div style={{ color: palette.textMuted, fontSize: 11 }}>Leash: {r.leash_notes}</div>}
                    </td>
                    <td style={{ ...td, whiteSpace: 'normal', maxWidth: 240 }}>
                      {r.props && <div><span style={{ color: palette.textMuted }}>Props:</span> {r.props}</div>}
                      {r.poses && <div><span style={{ color: palette.textMuted }}>Poses:</span> {r.poses}</div>}
                      {r.extra_notes && <div><span style={{ color: palette.textMuted }}>Notes:</span> {r.extra_notes}</div>}
                      {!r.props && !r.poses && !r.extra_notes && <span style={{ color: palette.textMuted }}>—</span>}
                    </td>
                    <td style={{ ...td, color: palette.textMuted, whiteSpace: 'nowrap' }}>{new Date(r.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        );
      })()}

      {lightbox && (
        <div onClick={() => setLightbox(null)} style={{
          position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.8)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24,
        }}>
          <AuthedImg path={lightbox.path} alt={lightbox.label} style={{ maxWidth: '90vw', maxHeight: '80vh', objectFit: 'contain', borderRadius: 6 }} />
          <div style={{ color: '#fff', fontFamily: fonts, fontSize: 13, marginTop: 14 }}>{lightbox.label}</div>
          <div style={{ color: 'rgba(255,255,255,0.6)', fontFamily: fonts, fontSize: 11, marginTop: 6 }}>click anywhere to close</div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Paws & Portraits — public signup page for the free pet-portrait promo.
// Reachable at /paws (point the flyer QR code here: hddnlab.com/paws).
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Gallery — public portfolio page showing studio work in a masonry grid with a
// keyboard-navigable lightbox. Pulls from the shared photos system.
// ---------------------------------------------------------------------------
function GalleryPage() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null); // index into photos

  useEffect(() => {
    // The gallery shows our WORK — a separate set from the space-showcase photos
    // used during booking. Curate it by tagging photos with the "work" event.
    fetch(`${API_BASE}/api/photos?event=work`)
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => setPhotos(Array.isArray(list) ? list : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (lightbox === null) return;
    const h = (e) => {
      if (e.key === 'Escape') setLightbox(null);
      if (e.key === 'ArrowLeft') setLightbox((i) => (i > 0 ? i - 1 : photos.length - 1));
      if (e.key === 'ArrowRight') setLightbox((i) => (i < photos.length - 1 ? i + 1 : 0));
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [lightbox, photos.length]);

  const active = lightbox !== null ? photos[lightbox] : null;

  return (
    <div style={baseStyles.app}>
      <style>{`
        * { box-sizing: border-box; } body { margin: 0; }
        .gallery-masonry { column-count: 1; column-gap: 16px; }
        @media (min-width: 620px) { .gallery-masonry { column-count: 2; } }
        @media (min-width: 1000px) { .gallery-masonry { column-count: 3; } }
        .gal-card { break-inside: avoid; margin: 0 0 16px; position: relative; border-radius: 10px; overflow: hidden; cursor: pointer; display: block; }
        /* Landscape and portrait both fit; floors/caps keep tiles balanced. */
        .gal-card img, .gal-card video { width: 100%; height: auto; min-height: 160px; max-height: 640px; object-fit: cover; display: block; transition: transform 0.4s ease; }
        .gal-card:hover img, .gal-card:hover video { transform: scale(1.05); }
        .gal-cap { position: absolute; inset: 0; display: flex; align-items: flex-end; padding: 16px;
          background: linear-gradient(to top, rgba(0,0,0,0.6), rgba(0,0,0,0) 55%); opacity: 0; transition: opacity 0.2s; }
        .gal-card:hover .gal-cap { opacity: 1; }
      `}</style>

      <header style={{ background: palette.brown, padding: '16px 0', borderBottom: `3px solid ${palette.orange}` }}>
        <div className="hddn-container" style={{ ...baseStyles.container, maxWidth: 1280, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <a href="/" style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '6px 14px', border: `1px solid ${palette.cream}40`, color: palette.cream, textDecoration: 'none' }}>
            Back to Studio
          </a>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <span style={{ fontFamily: fonts, fontWeight: 700, fontSize: 18, color: palette.cream, letterSpacing: 4, textTransform: 'uppercase' }}>HDDN LAB</span>
            <span style={{ color: palette.orange, fontSize: 12, letterSpacing: 2 }}>GALLERY</span>
          </div>
          <div style={{ width: 100 }} />
        </div>
      </header>

      <main className="hddn-container" style={{ ...baseStyles.container, maxWidth: 1280, paddingTop: 48, paddingBottom: 72 }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <span style={{
            display: 'inline-block', fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 3,
            textTransform: 'uppercase', color: palette.orange, border: `1px solid ${withAlpha(palette.orange, 0.4)}`,
            background: withAlpha(palette.orange, 0.08), borderRadius: 999, padding: '4px 14px',
          }}>Our Work</span>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500, fontSize: 'clamp(2rem, 1.4rem + 2.4vw, 2.75rem)', color: palette.brown, margin: '14px 0 10px', lineHeight: 1.15 }}>
            The Gallery
          </h1>
          <p style={{ fontFamily: fonts, fontSize: 14, color: palette.textMuted, maxWidth: 520, margin: '0 auto' }}>
            A look at the work created inside HDDN LAB — shoots, sessions, and stories from our studio.
          </p>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, fontFamily: fonts, color: palette.textMuted, letterSpacing: 2 }}>LOADING...</div>
        ) : photos.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, fontFamily: fonts, fontSize: 14, color: palette.textMuted }}>
            No photos yet — check back soon.
          </div>
        ) : (
          <div className="gallery-masonry">
            {photos.map((photo, i) => (
              <div key={photo.id} className="gal-card" onClick={() => setLightbox(i)}>
                {isVideoMime(photo.mime_type)
                  ? <video src={photo.url} muted loop playsInline />
                  : <img src={photo.url} alt={photo.caption || 'Studio work'} loading="lazy" />}
                {photo.caption && (
                  <div className="gal-cap">
                    <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 500, color: '#fff', textShadow: '0 1px 8px rgba(0,0,0,0.5)' }}>{photo.caption}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      {active && (
        <div onClick={() => setLightbox(null)} style={{
          position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.9)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, flexDirection: 'column',
        }}>
          {isVideoMime(active.mime_type)
            ? <video src={active.url} controls autoPlay style={{ maxWidth: '92vw', maxHeight: '82vh' }} />
            : <img src={active.url} alt={active.caption || ''} style={{ maxWidth: '92vw', maxHeight: '82vh', objectFit: 'contain' }} />}
          {active.caption && <div style={{ color: '#fff', fontFamily: fonts, fontSize: 13, marginTop: 14 }}>{active.caption}</div>}
          <div style={{ color: 'rgba(255,255,255,0.55)', fontFamily: fonts, fontSize: 11, marginTop: 8 }}>← → to browse · click anywhere to close</div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Campaign splash landing — a two-panel gateway shown at the root URL while the
// Paws & Portraits campaign was running. Left → main studio site, right → /paws.
// The campaign is over, so this is off and the root URL shows the booking page
// again. Flip back to true to reuse the gateway for a future campaign.
// ---------------------------------------------------------------------------
const SPLASH_LANDING_ACTIVE = false;

function SplashLanding({ onEnterStudio, onPaws }) {
  const NAVY = '#294156';
  const RUST = '#a8490f';
  const TEAL = '#5aa8a2';
  const [bgUrl, setBgUrl] = useState(null);
  const [remaining, setRemaining] = useState(null);
  const [full, setFull] = useState(false);

  useEffect(() => {
    // Studio space photo for the left panel background (first non-work photo).
    fetch(`${API_BASE}/api/photos`)
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => {
        const pic = Array.isArray(list) ? list.find((p) => p.url && !p.event && !isVideoMime(p.mime_type)) : null;
        if (pic) setBgUrl(pic.url);
      })
      .catch(() => {});
    // Live slot count for the right panel.
    fetch(`${API_BASE}/api/paws-signups/availability`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) { setRemaining(d.remaining); setFull(d.full); } })
      .catch(() => {});
  }, []);

  const cond = `'Trade Gothic Next LT Pro Bold Compressed', 'Trade Gothic Next LT Pro', 'Arial Narrow', sans-serif`;
  const spotsText = full ? 'SESSIONS COMPLETE' : `${remaining == null ? 25 : remaining} SPOTS AVAILABLE`;

  const panelBase = {
    flex: 1, position: 'relative', display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', padding: '48px 32px', textAlign: 'center',
    minHeight: '50vh', cursor: 'pointer', overflow: 'hidden',
  };
  const pill = (bg, color) => ({
    fontFamily: fonts, fontSize: 14, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase',
    padding: '14px 34px', background: bg, color, border: 'none', borderRadius: 40, cursor: 'pointer',
    boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
  });

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', minHeight: '100vh', width: '100%', background: NAVY }}>
      <style>{`
        * { box-sizing: border-box; } body { margin: 0; }
        .splash-panel { transition: filter .2s ease; }
        .splash-panel:hover { filter: brightness(1.06); }
        @media (max-width: 760px) { .splash-half { flex: 1 1 100% !important; } }
      `}</style>

      {/* LEFT — HDDN LAB / main site */}
      <div className="splash-panel splash-half" style={{ ...panelBase, flex: '1 1 50%' }} onClick={onEnterStudio}>
        <div style={{ position: 'absolute', inset: 0, background: bgUrl ? `#3d2b1f url("${bgUrl}") center/cover no-repeat` : 'linear-gradient(135deg,#7a3a10,#a8490f)' }} />
        <div style={{ position: 'absolute', inset: 0, background: bgUrl ? `linear-gradient(${RUST}dd, ${RUST}e6)` : 'transparent' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <h1 style={{ fontFamily: cond, fontSize: 'clamp(48px, 7vw, 88px)', fontWeight: 700, letterSpacing: 4, color: '#faf8f3', margin: 0, lineHeight: 1 }}>
            HDDN LAB
          </h1>
          <p style={{ fontFamily: fonts, fontSize: 'clamp(13px,1.6vw,16px)', letterSpacing: 2, textTransform: 'uppercase', color: '#faf8f3', margin: '22px 0 30px', lineHeight: 1.6 }}>
            Learn more about<br />our creative studio
          </p>
          <button style={pill('#faf8f3', '#3d2b1f')} onClick={(e) => { e.stopPropagation(); onEnterStudio(); }}>
            Learn More
          </button>
        </div>
      </div>

      {/* RIGHT — Paws & Portraits */}
      <div className="splash-panel splash-half" style={{ ...panelBase, flex: '1 1 50%', background: NAVY }} onClick={onPaws}>
        <div style={{ position: 'relative', zIndex: 1 }}>
          {/* Recreated Paws & Portraits logo mark */}
          <div style={{ display: 'inline-block', background: '#f3ede1', borderRadius: 12, padding: '18px 30px', marginBottom: 30 }}>
            <div style={{ fontFamily: fonts, fontSize: 10, fontWeight: 700, letterSpacing: 3, color: NAVY, marginBottom: 4 }}>NEW ROCHELLE, NY</div>
            <div style={{ fontFamily: cond, fontSize: 'clamp(30px,4vw,44px)', fontWeight: 700, letterSpacing: 1, color: NAVY, lineHeight: 0.95 }}>
              PAWS <span style={{ color: TEAL }}>🐾</span><br />PORTRAITS
            </div>
          </div>
          <p style={{ fontFamily: fonts, fontSize: 'clamp(13px,1.6vw,17px)', fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: '#ffffff', margin: '0 0 28px', lineHeight: 1.7 }}>
            Free portrait sessions<br />
            July 26 · {spotsText}<br />
            Gallery reveal this September<br />
            at Vanda Gallery, New Rochelle
          </p>
          <button style={pill(TEAL, '#ffffff')} onClick={(e) => { e.stopPropagation(); onPaws(); }}>
            {full ? 'Get Updates' : 'Free Session'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Paws & Portraits questionnaire + time-slot scheduling (invited attendees).
// Reachable at /paws-details — the link emailed to confirmed attendees.
// ---------------------------------------------------------------------------
function PawsQuestionnairePage({ round = 1 }) {
  const [f, setF] = useState({
    name: '', email: '', phone: '', contactMethod: '', numPeople: '', childrenInfo: '', numPets: '',
    petDetails: '', behavioralNotes: '', leashNotes: '', props: '', poses: '', extraNotes: '',
  });
  const [slot, setSlot] = useState('');
  const [sched, setSched] = useState(null); // { date, capacity, blocks, requirePetPhoto }
  const [petPhoto, setPetPhoto] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [confirm, setConfirm] = useState(null); // POST response (has calendar links)

  const [prefilled, setPrefilled] = useState(false); // we recognized them from their link
  const needsPhoto = !!sched?.requirePetPhoto;
  const MAX_PHOTO_BYTES = 12 * 1024 * 1024;

  const loadSlots = () => {
    fetch(`${API_BASE}/api/paws-slots?round=${round}`).then((r) => (r.ok ? r.json() : null)).then((d) => d && setSched(d)).catch(() => {});
  };
  useEffect(() => {
    loadSlots();
    // Round 1 links carry a token we can pre-fill from; round 2 is a plain link.
    if (round !== 1) return;
    const t = new URLSearchParams(window.location.search).get('t');
    if (!t) return;
    fetch(`${API_BASE}/api/paws-invitee/${encodeURIComponent(t)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d) return;
        setF((p) => ({ ...p, name: d.name || p.name, email: d.email || p.email, phone: d.phone || p.phone }));
        if (d.name || d.email) setPrefilled(true);
      })
      .catch(() => {});
  }, [round]);

  const set = (k, v) => { setF((p) => ({ ...p, [k]: v })); setError(''); };

  const pickPhoto = (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type) && !/\.(heic|heif)$/i.test(file.name)) {
      setError('Please choose an image file (JPEG, PNG, WEBP, or HEIC).'); return;
    }
    if (file.size > MAX_PHOTO_BYTES) { setError('That image is too large — please keep it under 12 MB.'); return; }
    setError('');
    setPetPhoto(file);
  };

  const submit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setError('');
    if (!f.name.trim()) { setError('Please tell us your name.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) { setError('Please enter a valid email address.'); return; }
    if (!f.phone.trim()) { setError('Please add a phone number.'); return; }
    if (needsPhoto && !petPhoto) { setError('Please add a photo of your pet.'); return; }
    if (!slot) { setError('Please pick an arrival time.'); return; }
    setSubmitting(true);
    try {
      const payload = { ...f, name: f.name.trim(), email: f.email.trim(), phone: f.phone.trim(), slot, round: String(round) };
      let res;
      if (petPhoto) {
        const fd = new FormData();
        Object.entries(payload).forEach(([k, v]) => fd.append(k, v ?? ''));
        fd.append('petPhoto', petPhoto);
        res = await fetch(`${API_BASE}/api/paws-questionnaire`, { method: 'POST', body: fd });
      } else {
        res = await fetch(`${API_BASE}/api/paws-questionnaire`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
        });
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { if (data.slotFull || data.blockFull || data.full) loadSlots(); throw new Error(data.error || 'Submission failed'); }
      setConfirm(data);
      setDone(true);
    } catch (err) { setError(err.message); } finally { setSubmitting(false); }
  };

  const labelStyle = { fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: palette.brown, display: 'block', marginBottom: 6 };
  const inputStyle = { width: '100%', padding: '11px 13px', fontSize: 14, fontFamily: fonts, border: `1px solid ${palette.border}`, background: palette.white, color: palette.text, outline: 'none', boxSizing: 'border-box' };
  const field = (label, key, opts = {}) => (
    <div style={{ marginBottom: 14 }}>
      <label style={labelStyle}>{label}</label>
      {opts.textarea
        ? <textarea value={f[key]} onChange={(e) => set(key, e.target.value)} rows={opts.rows || 2} placeholder={opts.ph || ''} style={{ ...inputStyle, resize: 'vertical' }} />
        : <input type={opts.type || 'text'} value={f[key]} onChange={(e) => set(key, e.target.value)} placeholder={opts.ph || ''} style={inputStyle} />}
      {opts.hint && <div style={{ fontFamily: fonts, fontSize: 11, color: palette.textMuted, marginTop: 4 }}>{opts.hint}</div>}
    </div>
  );

  // The shoots have happened — this link may still be in old emails, so it
  // lands somewhere useful instead of on a booking form for a past date.
  if (sched?.closed) {
    return (
      <div style={{ ...baseStyles.app, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: 20 }}>
        <div style={{ maxWidth: 480, textAlign: 'center', background: palette.white, border: `1px solid ${palette.border}`, padding: '40px 28px' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>🐾</div>
          <h1 style={{ fontFamily: fonts, fontSize: 18, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, margin: '0 0 10px' }}>
            Sessions have wrapped
          </h1>
          <p style={{ fontFamily: fonts, fontSize: 13, color: palette.textMuted, lineHeight: 1.7, margin: '0 0 22px' }}>
            Paws &amp; Portraits took place July 26 &amp; August 9. If you came, your photos are
            waiting — one free download, and every photo from your session for $50.
          </p>
          <a href="/paws-gallery" style={{ display: 'inline-block', fontFamily: fonts, fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', padding: '14px 26px', background: palette.brown, color: palette.cream, textDecoration: 'none' }}>
            Browse the gallery →
          </a>
        </div>
      </div>
    );
  }

  return (
    <div style={baseStyles.app}>
      <style>{`*{box-sizing:border-box}body{margin:0}
        .pq-slots{display:grid;grid-template-columns:repeat(auto-fill,minmax(96px,1fr));gap:8px}`}</style>
      <header style={{ background: palette.brown, padding: '16px 0', borderBottom: `3px solid ${palette.orange}`, textAlign: 'center' }}>
        <a href="/" style={{ textDecoration: 'none' }}>
          <span style={{ fontFamily: `'Trade Gothic Next LT Pro Bold Compressed','Arial Narrow',sans-serif`, fontWeight: 700, fontSize: 22, color: palette.cream, letterSpacing: 4, textTransform: 'uppercase' }}>HDDN LAB</span>
          <span style={{ color: palette.orange, fontSize: 13, letterSpacing: 2, marginLeft: 10 }}>STUDIO</span>
        </a>
      </header>
      <main style={{ ...baseStyles.container, maxWidth: 640, paddingTop: 40, paddingBottom: 64, display: 'flex', justifyContent: 'center' }}>
        <div style={{ background: palette.white, border: `1px solid ${palette.border}`, width: '100%', padding: '32px 30px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          {done ? (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <div style={{ fontSize: 38, color: palette.sage, marginBottom: 10 }}>&#10003;</div>
              <h2 style={{ fontFamily: fonts, fontSize: 16, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, margin: '0 0 8px' }}>You&apos;re scheduled!</h2>
              <p style={{ fontSize: 13.5, color: palette.text, lineHeight: 1.7, maxWidth: 440, margin: '0 auto' }}>
                Thanks {f.name.trim().split(' ')[0]}! Please arrive around <strong>{slot}</strong> on <strong>{sched?.date || 'your session date'}</strong> — a little early is great. Sessions run about {sched?.sessionMinutes || 15} minutes, and there&apos;s an outdoor area with refreshments while you wait. We emailed you a confirmation.
              </p>
              {confirm?.calendar && (
                <div style={{ marginTop: 18 }}>
                  <div style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: palette.textMuted, marginBottom: 8 }}>Add to your calendar</div>
                  <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <a href={confirm.calendar.google} target="_blank" rel="noreferrer" style={{ fontFamily: fonts, fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '9px 16px', background: palette.brown, color: palette.cream, textDecoration: 'none' }}>Google Calendar</a>
                    <a href={confirm.calendar.ics} style={{ fontFamily: fonts, fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '9px 16px', background: palette.white, color: palette.brown, border: `1px solid ${palette.border}`, textDecoration: 'none' }}>Apple / Outlook</a>
                  </div>
                </div>
              )}
              <a href="/" style={{ display: 'inline-block', marginTop: 20, fontFamily: fonts, fontSize: 12, color: palette.orange, letterSpacing: 1, textTransform: 'uppercase' }}>← Back to HDDN LAB</a>
            </div>
          ) : (
            <form onSubmit={submit}>
              <div style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: palette.orange, marginBottom: 8 }}>{sched?.date || 'Session'} · HDDN LAB</div>
              <h1 style={{ fontFamily: fonts, fontSize: 24, fontWeight: 700, color: palette.brown, margin: '0 0 10px', letterSpacing: 1 }}>Paws &amp; Portraits — session details</h1>
              <p style={{ fontSize: 13.5, color: palette.text, lineHeight: 1.7, margin: '0 0 8px' }}>
                {round === 2
                  ? <>You&apos;ve been invited to a Paws &amp; Portraits session on <strong>{sched?.date || 'August 9'}</strong>! Fill this out so we&apos;re ready for you and your pet, and pick your arrival time below.</>
                  : <>You&apos;re confirmed! Fill this out so we&apos;re ready for you and your pet, and pick your arrival time below.</>}
                {' '}Sessions run about <strong>{sched?.sessionMinutes || 15} minutes</strong> — please <strong>arrive a little early</strong>. There&apos;s an outdoor area with refreshments while you wait.
              </p>
              <p style={{ fontSize: 12, color: palette.textMuted, margin: '0 0 20px' }}>72 Centre Avenue, New Rochelle, NY.</p>
              {round === 2 && sched && (
                <p style={{ fontFamily: fonts, fontSize: 12, color: palette.orange, fontWeight: 700, margin: '-10px 0 20px' }}>
                  {sched.full
                    ? 'All spots for this session are now taken.'
                    : `Only ${sched.totalRemaining} of ${sched.totalCapacity} spots left — this link is just for you, please don’t share it.`}
                </p>
              )}

              {sched?.details && sched.details.length > 0 && (
                <>
                  <SectionHead>A few things to know</SectionHead>
                  <div style={{ background: withAlpha(palette.orange, 0.05), border: `1px solid ${palette.border}`, borderRadius: 6, padding: '14px 16px', marginBottom: 8 }}>
                    {sched.details.map((s) => (
                      <div key={s.title} style={{ marginBottom: 12 }}>
                        <div style={{ fontFamily: fonts, fontSize: 12.5, fontWeight: 700, color: palette.brown, marginBottom: 3 }}>{s.title}</div>
                        {s.body && <p style={{ fontFamily: fonts, fontSize: 12.5, color: palette.text, lineHeight: 1.6, margin: 0 }}>{s.body}</p>}
                        {s.bullets && (
                          <ul style={{ margin: '3px 0 0', paddingLeft: 18 }}>
                            {s.bullets.map((b, i) => <li key={i} style={{ fontFamily: fonts, fontSize: 12.5, color: palette.text, lineHeight: 1.6 }}>{b}</li>)}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}

              <SectionHead>Your contact</SectionHead>
              {prefilled && (
                <p style={{ fontFamily: fonts, fontSize: 12, color: palette.sage, margin: '-4px 0 12px', fontWeight: 700 }}>
                  ✓ We filled in your details from your signup — just double-check them below.
                </p>
              )}
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Your name *</label>
                <input type="text" value={f.name} onChange={(e) => set('name', e.target.value)} required style={inputStyle} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle}>Email *</label>
                  <input type="email" value={f.email} onChange={(e) => set('email', e.target.value)}
                    placeholder={round === 2 ? 'you@example.com' : 'the email you signed up with'} required style={inputStyle} />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle}>Phone *</label>
                  <input type="tel" value={f.phone} onChange={(e) => set('phone', e.target.value)} required style={inputStyle} />
                </div>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Best way to reach you *</label>
                <select value={f.contactMethod} onChange={(e) => set('contactMethod', e.target.value)} required style={{ ...inputStyle, appearance: 'auto' }}>
                  <option value="">Select…</option>
                  <option value="Text">Text message</option>
                  <option value="Call">Phone call</option>
                  <option value="Email">Email</option>
                  <option value="WhatsApp">WhatsApp</option>
                </select>
              </div>

              <SectionHead>People &amp; pets</SectionHead>
              {field('How many people will be in the photo?', 'numPeople')}
              {field('Names & ages of any children in the photo?', 'childrenInfo')}
              {field('How many pets are you bringing?', 'numPets')}
              {field('Pet name(s), breed(s), and age(s)?', 'petDetails', { textarea: true })}
              {field('Any behavioral notes we should know?', 'behavioralNotes', { textarea: true, ph: 'Nervous around strangers, reactive to other animals, needs treats to cooperate, etc.' })}
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Leash — off during the shoot, or stays on?</label>
                <select value={f.leashNotes} onChange={(e) => set('leashNotes', e.target.value)} style={{ ...inputStyle, appearance: 'auto' }}>
                  <option value="">Select…</option>
                  <option value="Comfortable off-leash">Comfortable with the leash off</option>
                  <option value="Needs to stay on">Needs to stay on-leash</option>
                  <option value="Not sure">Not sure yet</option>
                </select>
              </div>
              {needsPhoto && (
                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle}>A photo of your pet *</label>
                  <label style={{
                    display: 'block', border: `2px dashed ${petPhoto ? palette.sage : palette.border}`, background: petPhoto ? withAlpha(palette.sage, 0.06) : palette.beige,
                    padding: '16px 14px', textAlign: 'center', cursor: 'pointer',
                  }}>
                    <input type="file" accept="image/*,.heic,.heif" style={{ display: 'none' }}
                      onChange={(e) => { pickPhoto(e.target.files && e.target.files[0]); e.target.value = ''; }} />
                    <span style={{ fontFamily: fonts, fontSize: 12.5, color: petPhoto ? palette.sage : palette.textMuted, fontWeight: petPhoto ? 700 : 400 }}>
                      {petPhoto ? `✓ ${petPhoto.name}` : '📷 Tap to upload a recent photo of your pet'}
                    </span>
                  </label>
                  <div style={{ fontFamily: fonts, fontSize: 11, color: palette.textMuted, marginTop: 4 }}>
                    Helps our photographer plan the lighting for their fur &amp; features. Up to 12 MB.
                  </div>
                </div>
              )}

              <SectionHead>Pick your arrival time</SectionHead>
              {sched?.full && (
                <p style={{ fontFamily: fonts, fontSize: 12.5, color: palette.red, fontWeight: 700, margin: '0 0 12px' }}>
                  All sessions are now booked. If you still need a spot, email {CONTACT_EMAIL}.
                </p>
              )}
              <p style={{ fontFamily: fonts, fontSize: 12, color: palette.textMuted, margin: '0 0 12px' }}>
                {sched?.capacity === 1
                  ? 'Choose an open time — one family per slot. Please arrive a few minutes early.'
                  : 'Choose an open time. Two families per time keeps things moving — please arrive a few minutes early.'}
              </p>
              {!sched ? (
                <div style={{ fontFamily: fonts, fontSize: 13, color: palette.textMuted, marginBottom: 14 }}>Loading times…</div>
              ) : sched.blocks.map((b) => (
                <div key={b.name} style={{ marginBottom: 16 }}>
                  <div style={{ fontFamily: fonts, fontSize: 12, fontWeight: 700, color: palette.brown, marginBottom: 8 }}>
                    {b.name} <span style={{ color: palette.textMuted, fontWeight: 400 }}>· {b.window}</span>
                    {typeof b.remaining === 'number' && !sched.singleBlock && (
                      b.remaining <= 0
                        ? <span style={{ color: palette.orange, fontWeight: 700, marginLeft: 6 }}>· Block full</span>
                        : b.remaining <= 4 && <span style={{ color: palette.textMuted, fontWeight: 400, marginLeft: 6 }}>· {b.remaining} spots left in this block</span>
                    )}
                  </div>
                  <div className="pq-slots">
                    {b.slots.map((s) => {
                      const full = s.remaining <= 0;
                      const selected = slot === s.time;
                      return (
                        <button type="button" key={s.time} disabled={full} onClick={() => { setSlot(s.time); setError(''); }}
                          style={{
                            fontFamily: fonts, fontSize: 12, fontWeight: 700, padding: '9px 4px', cursor: full ? 'not-allowed' : 'pointer',
                            border: `1px solid ${selected ? palette.brown : palette.border}`,
                            background: selected ? palette.brown : full ? palette.beige : palette.white,
                            color: selected ? palette.cream : full ? palette.textMuted : palette.text,
                            opacity: full ? 0.6 : 1, textAlign: 'center', lineHeight: 1.3,
                          }}>
                          {s.time}
                          <div style={{ fontSize: 9, fontWeight: 400, marginTop: 2 }}>
                            {full ? 'Full' : sched.capacity === 1 ? 'Open' : `${s.remaining} left`}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

              <SectionHead>Style &amp; extras</SectionHead>
              {field('Any props, outfits, or accessories you’re bringing?', 'props', { textarea: true })}
              {field('Any specific poses or shots you’re hoping for?', 'poses', { textarea: true })}
              {field('Anything else we should know to make the session go smoothly?', 'extraNotes', { textarea: true })}
              <p style={{ fontFamily: fonts, fontSize: 11.5, color: palette.textMuted, margin: '-4px 0 18px' }}>Have inspiration shots? Email them to <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: palette.orange }}>{CONTACT_EMAIL}</a>.</p>

              {error && <div style={{ color: palette.red, fontSize: 13, marginBottom: 12 }}>{error}</div>}
              <button type="submit" disabled={submitting} style={{ width: '100%', fontFamily: fonts, fontSize: 14, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', padding: '14px 28px', background: palette.brown, color: palette.cream, border: 'none', cursor: submitting ? 'wait' : 'pointer', opacity: submitting ? 0.6 : 1 }}>
                {submitting ? 'Saving…' : 'Confirm My Time'}
              </button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}

function SectionHead({ children }) {
  return <h3 style={{ fontFamily: fonts, fontSize: 12, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.brown, margin: '22px 0 12px', paddingBottom: 6, borderBottom: `2px solid ${palette.border}` }}>{children}</h3>;
}

// ---------------------------------------------------------------------------
// Paws & Portraits photo delivery — private per-client page at /photos/:token.
// Pay first, then (for the 5-photo tier) pick favorites from watermarked
// proofs, then get the download link.
// ---------------------------------------------------------------------------
function PawsPhotoOrderPage({ token }) {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [picks, setPicks] = useState([]);
  const [justPaid, setJustPaid] = useState(false);

  const load = () => {
    fetch(`${API_BASE}/api/paws-photos/${encodeURIComponent(token)}`)
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.error || 'We couldn’t load your photos.');
        return d;
      })
      .then((d) => {
        // The old private page sold five photos for $50 or the set for $100.
        // Anyone still holding that link goes to their gallery instead of a
        // page quoting prices we no longer offer.
        if (d.gallerySlug) {
          window.location.replace(`/paws-gallery/${d.gallerySlug}`);
          return;
        }
        setData(d);
        setPicks(d.proofs.filter((p) => p.selected).map((p) => p.id));
      })
      .catch((e) => setLoadError(e.message));
  };

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get('paid')) setJustPaid(true);
    load();
    // Stripe's webhook can land a moment after the redirect — re-check briefly.
    if (q.get('paid')) {
      const timers = [1500, 4000, 8000].map((ms) => setTimeout(load, ms));
      return () => timers.forEach(clearTimeout);
    }
  }, []);


  const checkout = async (tier) => {
    setBusy(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/api/paws-photos/${encodeURIComponent(token)}/checkout`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tier }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Could not start checkout.');
      window.location.href = d.url;
    } catch (e) { setError(e.message); setBusy(false); }
  };

  const togglePick = (id) => {
    setError('');
    setPicks((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= (data?.selectCount || 5)) return prev; // cap reached
      return [...prev, id];
    });
  };

  const submitPicks = async () => {
    setBusy(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/api/paws-photos/${encodeURIComponent(token)}/select`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids: picks }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Could not save your picks.');
      load();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const shell = (children) => (
    <div style={baseStyles.app}>
      <style>{`*{box-sizing:border-box}body{margin:0}
        .pp-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(150px,1fr));gap:10px}
        .pp-img{width:100%;aspect-ratio:1;object-fit:cover;display:block;-webkit-user-drag:none;user-select:none;pointer-events:none}`}</style>
      <header style={{ background: palette.brown, padding: '16px 0', borderBottom: `3px solid ${palette.orange}`, textAlign: 'center' }}>
        <a href="/" style={{ textDecoration: 'none' }}>
          <span style={{ fontFamily: `'Trade Gothic Next LT Pro Bold Compressed','Arial Narrow',sans-serif`, fontWeight: 700, fontSize: 22, color: palette.cream, letterSpacing: 4, textTransform: 'uppercase' }}>HDDN LAB</span>
          <span style={{ color: palette.orange, fontSize: 13, letterSpacing: 2, marginLeft: 10 }}>STUDIO</span>
        </a>
      </header>
      <main style={{ ...baseStyles.container, maxWidth: 720, paddingTop: 40, paddingBottom: 64 }}>
        <div style={{ background: palette.white, border: `1px solid ${palette.border}`, padding: '30px 28px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          {children}
        </div>
      </main>
    </div>
  );

  if (loadError) return shell(<p style={{ fontFamily: fonts, fontSize: 14, color: palette.red, margin: 0 }}>{loadError}</p>);
  if (!data) return shell(<p style={{ fontFamily: fonts, fontSize: 14, color: palette.textMuted, margin: 0 }}>Loading your photos…</p>);

  const first = (data.name || '').split(' ')[0];
  const need = data.selectCount;
  const heading = { fontFamily: fonts, fontSize: 22, fontWeight: 700, color: palette.brown, margin: '0 0 10px', letterSpacing: 0.5 };
  const body = { fontFamily: fonts, fontSize: 13.5, color: palette.text, lineHeight: 1.7 };
  const linkBtn = { display: 'inline-block', fontFamily: fonts, fontSize: 14, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', padding: '14px 26px', background: palette.orange, color: palette.white, textDecoration: 'none', marginTop: 14 };

  // --- Paid: full set -> show the download link ---
  if (data.paid && data.tier === 'all') {
    return shell(
      <>
        <div style={{ fontSize: 34, color: palette.sage, marginBottom: 8 }}>&#10003;</div>
        <h1 style={heading}>Thanks{first ? `, ${first}` : ''} — you’re all set!</h1>
        <p style={body}>You bought <strong>every photo</strong> from your Paws &amp; Portraits session.</p>
        {data.deliveryUrl ? (
          <>
            <p style={body}>Your full gallery is ready to download:</p>
            <a href={data.deliveryUrl} target="_blank" rel="noreferrer" style={linkBtn}>Download my photos</a>
          </>
        ) : (
          <p style={{ ...body, color: palette.orange, fontWeight: 700 }}>
            We’re putting your gallery together right now — we’ll email your download link shortly (usually within a day).
          </p>
        )}
        <p style={{ ...body, fontSize: 12, color: palette.textMuted, marginTop: 20 }}>Questions? Email {CONTACT_EMAIL}.</p>
      </>
    );
  }

  // --- Paid: 5-photo tier ---
  if (data.paid && data.tier === 'five') {
    if (data.selectionSubmitted) {
      return shell(
        <>
          <div style={{ fontSize: 34, color: palette.sage, marginBottom: 8 }}>&#10003;</div>
          <h1 style={heading}>Your picks are in!</h1>
          {data.deliveryUrl ? (
            <>
              <p style={body}>Your {need} edited photos are ready:</p>
              <a href={data.deliveryUrl} target="_blank" rel="noreferrer" style={linkBtn}>Download my photos</a>
            </>
          ) : (
            <p style={{ ...body, color: palette.orange, fontWeight: 700 }}>
              We’re editing your {need} photos now — we’ll email your download link shortly (usually within a day or two).
            </p>
          )}
          <p style={{ ...body, fontSize: 12, color: palette.textMuted, marginTop: 20 }}>
            Changed your mind on a pick? Email {CONTACT_EMAIL} and we’ll sort it out.
          </p>
        </>
      );
    }
    if (!data.proofs.length) {
      return shell(
        <>
          <h1 style={heading}>Thanks{first ? `, ${first}` : ''}!</h1>
          <p style={{ ...body, color: palette.orange, fontWeight: 700 }}>
            Your payment went through. We’re uploading your proofs now — check back shortly and you’ll be able to pick your {need} favorites here.
          </p>
        </>
      );
    }
    return shell(
      <>
        <h1 style={heading}>Pick your {need} photos</h1>
        <p style={body}>
          Tap the {need} you’d like edited and delivered in full resolution. These previews are watermarked and low-resolution — your final files are clean, high-resolution, and lightly edited.
        </p>
        <div style={{ fontFamily: fonts, fontSize: 13, fontWeight: 700, color: picks.length === need ? palette.sage : palette.orange, margin: '14px 0 10px' }}>
          {picks.length} of {need} selected
        </div>
        <div className="pp-grid">
          {data.proofs.map((p, i) => {
            const on = picks.includes(p.id);
            return (
              <div key={p.id} onClick={() => togglePick(p.id)} style={{
                position: 'relative', cursor: 'pointer', border: `3px solid ${on ? palette.orange : palette.border}`, background: palette.beige,
              }}>
                <img className="pp-img" src={`${API_BASE}/api/paws-proofs/${p.id}/preview?token=${encodeURIComponent(token)}`} alt={`Proof ${i + 1}`} />
                <div style={{
                  position: 'absolute', top: 6, left: 6, width: 24, height: 24, borderRadius: '50%',
                  background: on ? palette.orange : 'rgba(255,255,255,0.85)', color: on ? palette.white : palette.brown,
                  fontFamily: fonts, fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>{on ? '✓' : i + 1}</div>
              </div>
            );
          })}
        </div>
        {error && <p style={{ fontFamily: fonts, fontSize: 13, color: palette.red, marginTop: 14 }}>{error}</p>}
        <button onClick={submitPicks} disabled={busy || picks.length !== need} style={{
          width: '100%', marginTop: 18, fontFamily: fonts, fontSize: 14, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase',
          padding: '14px 26px', background: picks.length === need ? palette.brown : palette.beige,
          color: picks.length === need ? palette.cream : palette.textMuted, border: 'none',
          cursor: busy || picks.length !== need ? 'not-allowed' : 'pointer',
        }}>{busy ? 'Saving…' : picks.length === need ? `Send these ${need}` : `Choose ${need - picks.length} more`}</button>
      </>
    );
  }

  // --- Not paid yet: choose a package ---
  const card = (tier, price, title, sub) => (
    <div style={{ border: `1px solid ${palette.border}`, padding: '20px 18px', background: palette.beige, display: 'flex', flexDirection: 'column' }}>
      <div style={{ fontFamily: fonts, fontSize: 30, fontWeight: 700, color: palette.brown }}>{money(price)}</div>
      <div style={{ fontFamily: fonts, fontSize: 13.5, fontWeight: 700, color: palette.brown, marginTop: 6 }}>{title}</div>
      <div style={{ fontFamily: fonts, fontSize: 12, color: palette.textMuted, marginTop: 4, lineHeight: 1.6, flex: 1 }}>{sub}</div>
      <button onClick={() => checkout(tier)} disabled={busy} style={{
        marginTop: 16, fontFamily: fonts, fontSize: 12.5, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase',
        padding: '12px 18px', background: tier === 'all' ? palette.orange : palette.brown, color: tier === 'all' ? palette.white : palette.cream,
        border: 'none', cursor: busy ? 'wait' : 'pointer',
      }}>{busy ? 'One moment…' : 'Choose this'}</button>
    </div>
  );

  return shell(
    <>
      <div style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: palette.orange, marginBottom: 8 }}>
        {data.sessionDate} · Paws &amp; Portraits
      </div>
      <h1 style={heading}>Your photos are ready{first ? `, ${first}` : ''}!</h1>
      <p style={body}>Thanks so much for coming out. Choose how you’d like your photos below — you’ll get your download link as soon as your payment goes through.</p>
      {justPaid && (
        <p style={{ fontFamily: fonts, fontSize: 13, color: palette.orange, fontWeight: 700, marginTop: 12 }}>
          Confirming your payment… this page will update in a few seconds. (If it doesn’t, refresh — or email {CONTACT_EMAIL}.)
        </p>
      )}
      {error && <p style={{ fontFamily: fonts, fontSize: 13, color: palette.red, marginTop: 12 }}>{error}</p>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 14, marginTop: 20 }}>
        {card('five', data.prices.five, `${need} photos of your choice`, `Pick your ${need} favorites from your gallery. Lightly edited, full resolution.`)}
        {card('all', data.prices.all, 'Every photo', 'The complete set from your session, lightly edited and full resolution.')}
      </div>
      <p style={{ ...body, fontSize: 12, color: palette.textMuted, marginTop: 18 }}>
        Payments are handled securely by Stripe. This link is just for you — please don’t share it. Questions? Email {CONTACT_EMAIL}.
      </p>
    </>
  );
}

// ---------------------------------------------------------------------------
// Privacy Policy — public /privacy page
// ---------------------------------------------------------------------------
function PrivacyPolicyPage() {
  const h2 = { fontFamily: fonts, fontSize: 15, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: palette.brown, margin: '28px 0 10px' };
  const p = { fontFamily: sans, fontSize: 14.5, lineHeight: 1.75, color: palette.text, margin: '0 0 12px' };
  const li = { fontFamily: sans, fontSize: 14.5, lineHeight: 1.7, color: palette.text, marginBottom: 6 };
  const ul = { margin: '0 0 12px', paddingLeft: 20 };

  return (
    <div style={{ ...baseStyles.app, fontFamily: sans }}>
      <style>{` *{box-sizing:border-box} body{margin:0}`}</style>
      <SimpleMasthead label="Privacy" />
      <main style={{ ...baseStyles.container, maxWidth: 760, paddingTop: 44, paddingBottom: 72 }}>
        <h1 style={{ fontFamily: sans, fontSize: 34, fontWeight: 800, letterSpacing: '-0.02em', color: site.ink, margin: '0 0 6px' }}>Privacy Policy</h1>
        <p style={{ fontFamily: fonts, fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: palette.textMuted, margin: '0 0 8px' }}>Last updated: October 2026</p>
        <p style={p}>
          This Privacy Policy explains how <strong>HDDN LAB</strong> (&quot;HDDN LAB,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;), located at 72 Centre Avenue, New Rochelle, NY 10801, collects, uses, and shares information when you visit our website, book our studio, or participate in our programs such as Paws &amp; Portraits. By using our site or services, you agree to this policy.
        </p>

        <h2 style={h2}>Information We Collect</h2>
        <p style={p}>We collect information you provide directly to us, including when you:</p>
        <ul style={ul}>
          <li style={li}><strong>Book the studio</strong> — your name, email, phone number, session details, and (when you pay online) payment information processed by our payment provider.</li>
          <li style={li}><strong>Sign up for programs like Paws &amp; Portraits</strong> — your name, email, phone, details about your pet, a short story, uploaded photos, scheduling preferences, and your media-release choice.</li>
          <li style={li}><strong>Join our waitlist or newsletter</strong> — your name and contact details.</li>
          <li style={li}><strong>Contact us or request a quote</strong> — your name, email, phone, project details, and any files or references you send.</li>
          <li style={li}><strong>Attend an event or fill out a survey</strong> — the information you choose to share.</li>
        </ul>
        <p style={p}>
          We also automatically collect limited technical information (such as basic device and usage data) and use browser storage/cookies as described below. Our studio premises are under <strong>24/7 video surveillance</strong> for security and dispute-resolution purposes; that footage is used only for those purposes and is not shared publicly without legal cause.
        </p>

        <h2 style={h2}>How We Use Your Information</h2>
        <ul style={ul}>
          <li style={li}>To provide and manage bookings, sessions, and the services you request</li>
          <li style={li}>To process payments and send confirmations, reminders, and receipts</li>
          <li style={li}>To respond to your questions and communicate about your booking or project</li>
          <li style={li}>To send updates and marketing (such as our newsletter) where you&apos;ve opted in — you can unsubscribe anytime</li>
          <li style={li}>To operate, secure, and improve our website and services</li>
          <li style={li}>To comply with legal obligations and enforce our studio policies</li>
        </ul>

        <h2 style={h2}>Photos, Portraits &amp; Media Releases</h2>
        <p style={p}>
          When you take part in a portrait session or program, we create photos and video. You retain rights to content created for you. We only share or publicly feature images of you or content you create <strong>with your consent</strong> — for example, the media-release toggle on the Paws &amp; Portraits form. You can withdraw consent for future use by contacting us.
        </p>

        <h2 style={h2}>How We Share Information</h2>
        <p style={p}>We do <strong>not</strong> sell your personal information. We share it only with service providers who help us operate, and only as needed:</p>
        <ul style={ul}>
          <li style={li}><strong>Payment processing</strong> (e.g., Stripe) to handle transactions</li>
          <li style={li}><strong>Email delivery</strong> (e.g., Brevo) to send confirmations and notifications</li>
          <li style={li}><strong>Newsletter management</strong> (e.g., Beehiiv) if you subscribe</li>
          <li style={li}><strong>Booking platforms</strong> you may reserve through (e.g., Peerspace)</li>
          <li style={li}><strong>Hosting and infrastructure</strong> providers that run our website</li>
          <li style={li}><strong>Advertising and measurement</strong> (Meta) — see Cookies below</li>
        </ul>
        <p style={p}>We may also disclose information if required by law or to protect our rights, safety, or property.</p>

        <h2 style={h2}>Cookies &amp; Browser Storage</h2>
        <p style={p}>
          We use minimal cookies and local/session storage — primarily to keep you signed in and to remember basic site preferences. We also use the Meta Pixel, which records visits and completed bookings on this site so we can measure our ads on Facebook and Instagram and show them to people likely to be interested. Meta receives that information and may use it to deliver ads. You can manage this in your <a href="https://accountscenter.facebook.com/ad_preferences" target="_blank" rel="noopener noreferrer" style={{ color: palette.orange }}>Meta ad settings</a>, and clear or block cookies and browser storage in your browser settings, though some features may not work as intended.
        </p>

        <h2 style={h2}>Data Retention</h2>
        <p style={p}>
          We keep personal information for as long as needed to provide our services, comply with legal, tax, and accounting requirements, and resolve disputes. When it&apos;s no longer needed, we delete or anonymize it.
        </p>

        <h2 style={h2}>Your Rights &amp; Choices</h2>
        <p style={p}>You may request to access, correct, or delete your personal information, and you can unsubscribe from marketing emails at any time using the link in the email or by contacting us. To make a request, email <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: palette.orange }}>{CONTACT_EMAIL}</a>.</p>

        <h2 style={h2}>Children&apos;s Privacy</h2>
        <p style={p}>
          Our services are directed to adults. We do not knowingly collect personal information from children under 13. Minors on our premises must be accompanied by a guardian per our studio policies. If you believe a child has provided us information, contact us and we will delete it.
        </p>

        <h2 style={h2}>Data Security</h2>
        <p style={p}>
          We take reasonable measures to protect your information. However, no method of transmission or storage is completely secure, and we cannot guarantee absolute security.
        </p>

        <h2 style={h2}>Changes to This Policy</h2>
        <p style={p}>
          We may update this Privacy Policy from time to time. Material changes will be reflected by updating the &quot;Last updated&quot; date above.
        </p>

        <h2 style={h2}>Contact Us</h2>
        <p style={p}>
          Questions about this policy or your information? Reach us at <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: palette.orange }}>{CONTACT_EMAIL}</a> or HDDN LAB, 72 Centre Avenue, New Rochelle, NY 10801.
        </p>

        <a href="/" style={{ display: 'inline-block', marginTop: 24, fontFamily: fonts, fontSize: 12, color: palette.orange, letterSpacing: 1, textTransform: 'uppercase' }}>← Back to HDDN LAB</a>
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main App
// ---------------------------------------------------------------------------

export default function HddnLabBooking() {
  // Auth state
  const [user, setUser] = useState(null);
  // Calendar state
  const [currentRoom, setCurrentRoom] = useState('upstairs-1');
  const chooseRoom = (roomId) => {
    setCurrentRoom(roomId);
    setSelectedDate(null);
  };
  const now = new Date();
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());
  const [booked, setBooked] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [slots, setSlots] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);

  // UI state
  const [showModal, setShowModal] = useState(false);
  const [successBooking, setSuccessBooking] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [userBookings, setUserBookings] = useState([]);
  const [showAdmin, setShowAdmin] = useState(false);

  // Payment / Stripe state
  // Read from the URL at first render, not in an effect: the marketing site
  // owns "/" now, so the decision below about which app to show has to be made
  // before the first paint.
  const [paymentReturn, setPaymentReturn] = useState(() => {
    if (typeof window === 'undefined') return null;
    const q = new URLSearchParams(window.location.search);
    const status = q.get('payment');
    const ref = q.get('ref');
    return status && ref ? { status, ref } : null;
  });
  const [stripeEnabled, setStripeEnabled] = useState(false);

  // Info section tab (for deep-linking from booking modal)
  const [infoTab, setInfoTab] = useState(null);

  // Dynamic packages config (pricing, includes, add-ons)
  const [packagesConfig, setPackagesConfig] = useState(null);

  const refreshPackagesConfig = useCallback(() => {
    fetch(`${API_BASE}/api/packages-config`)
      .then((r) => r.json())
      .then((data) => { if (data) setPackagesConfig(data); })
      .catch(() => {});
  }, []);

  // Password reset token from URL
  const [resetToken, setResetToken] = useState(() => {
    if (typeof window === 'undefined') return null;
    return new URLSearchParams(window.location.search).get('reset_token');
  });

  // Check URL params for Stripe payment return, reset token + fetch Stripe config
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');
    const ref = params.get('ref');
    const rt = params.get('reset_token');
    if (payment && ref) {
      setPaymentReturn({ status: payment, ref });
      if (payment === 'success') {
        let total;
        try { total = sessionStorage.getItem(`hddn_booking_total_${ref}`); } catch { /* storage blocked */ }
        trackBooking(ref, total);
      }
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (rt) {
      setResetToken(rt);
      window.history.replaceState({}, '', window.location.pathname);
    }
    // Check if Stripe is enabled
    fetch(`${API_BASE}/api/stripe/config`)
      .then((r) => r.json())
      .then((data) => { if (data.enabled) setStripeEnabled(true); })
      .catch(() => {});
    // Fetch dynamic packages config
    refreshPackagesConfig();
  }, []);

  // Check existing admin auth on mount
  useEffect(() => {
    const token = localStorage.getItem('hddn_token');
    if (!token) return;
    api('/api/auth/me')
      .then((u) => {
        setUser(u);
        if (u && u.role === 'admin' && shouldOpenDashboard()) setShowAdmin(true);
      })
      // Only a rejected token signs you out. A 429 or a network blip used to
      // wipe the token too, logging the team out whenever they hit the limit.
      .catch((err) => { if (err?.status === 401 || err?.status === 403) localStorage.removeItem('hddn_token'); });
  }, []);

  // Fetch availability when room or month changes
  const fetchAvailability = useCallback(() => {
    const m = String(viewMonth + 1).padStart(2, '0');
    fetch(`${API_BASE}/api/availability/${viewYear}/${m}/${currentRoom}`)
      .then((r) => r.json())
      .then((data) => {
        setBooked(data.booked || []);
        setBlocked(data.blocked || []);
        setSlots(data.slots || {});
      })
      .catch(() => {});
  }, [viewYear, viewMonth, currentRoom]);

  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);

  // Navigation
  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else {
      setViewMonth(viewMonth - 1);
    }
    setSelectedDate(null);
  };

  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else {
      setViewMonth(viewMonth + 1);
    }
    setSelectedDate(null);
  };

  // Handlers
  const handleAuth = (u) => {
    setUser(u);
    if (u && u.role === 'admin' && shouldOpenDashboard()) setShowAdmin(true);
  };

  // The dashboard and its sign-in both live on the admin hostname. Sending
  // someone there to sign in, rather than signing them in here and then moving
  // them, matters: the token is in localStorage, which is per-origin, so a
  // session started on the customer site would not travel with them.
  const goToAdmin = () => {
    if (adminHostActive() && !onAdminHost()) { window.location.href = adminUrl('/admin'); return; }
    setShowAdmin(true);
  };
  const goToAdminLogin = () => {
    if (adminHostActive() && !onAdminHost()) { window.location.href = adminUrl('/admin'); return; }
    setShowAdminLogin(true);
  };
  // Leaving the dashboard for the customer view means leaving the hostname too,
  // since nothing customer-facing is served under the admin one.
  const leaveAdmin = () => {
    if (onAdminHost()) { window.location.href = siteUrl('/'); return; }
    setShowAdmin(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('hddn_token');
    setUser(null);
    setSelectedDate(null);
  };

  const handleDateSelect = (ds) => {
    setSelectedDate(ds);
    setShowModal(true);
  };

  const handleBookingSubmit = async (data) => {
    const endpoint = user ? '/api/book' : '/api/book-public';
    const booking = await api(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    // If Stripe is enabled, redirect to checkout
    if (booking.checkoutUrl) {
      // Remember the total so the Meta booking event on Stripe's return
      // (which only carries the ref) can report the value.
      try { sessionStorage.setItem(`hddn_booking_total_${booking.booking_ref}`, String(data.totalPrice || '')); } catch { /* storage blocked */ }
      window.location.href = booking.checkoutUrl;
      return;
    }
    setShowModal(false);
    setSelectedDate(null);
    setSuccessBooking(booking);
    trackBooking(booking.booking_ref, data.totalPrice);
    fetchAvailability();
  };

  const handleShowHistory = async () => {
    try {
      const list = await api('/api/bookings');
      setUserBookings(list);
      setShowHistory(true);
    } catch {
      setUserBookings([]);
      setShowHistory(true);
    }
  };

  // Login modal state
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [showCustomerLogin, setShowCustomerLogin] = useState(false);
  const [showManageBooking, setShowManageBooking] = useState(false);

  // Blog state
  const [showBlog, setShowBlog] = useState(false);
  const [blogSlug, setBlogSlug] = useState(null);

  // Public calendar state
  const [calendarToken, setCalendarToken] = useState(null);

  // Open house state
  const [showOpenHouse, setShowOpenHouse] = useState(false);
  const [openHouseArchiveSlug, setOpenHouseArchiveSlug] = useState(null);
  const [showNewsletter, setShowNewsletter] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(typeof window !== 'undefined' && (window.location.pathname === '/privacy' || window.location.pathname === '/privacy/'));
  const [showPawsDetails, setShowPawsDetails] = useState(typeof window !== 'undefined' && (window.location.pathname === '/paws-details' || window.location.pathname === '/paws-details/'));
  // Round 2 (Aug 9) books through the same questionnaire at its own URL.
  const [showPawsAug9, setShowPawsAug9] = useState(typeof window !== 'undefined' && (window.location.pathname === '/paws-aug9' || window.location.pathname === '/paws-aug9/'));
  const [photoToken, setPhotoToken] = useState(typeof window !== 'undefined' && window.location.pathname.startsWith('/photos/')
    ? window.location.pathname.replace('/photos/', '').replace(/\/$/, '') : null);
  // Public contract signing page: /sign/<token>
  const [signToken, setSignToken] = useState(typeof window !== 'undefined' && window.location.pathname.startsWith('/sign/')
    ? window.location.pathname.replace('/sign/', '').replace(/\/$/, '') : null);
  // Contractor onboarding page: /onboard/<token>
  const [onboardToken, setOnboardToken] = useState(typeof window !== 'undefined' && window.location.pathname.startsWith('/onboard/')
    ? window.location.pathname.replace('/onboard/', '').replace(/\/$/, '') : null);
  // Public Paws & Portraits gallery: /paws-gallery is the index, and
  // /paws-gallery/<slug> is one pet's page.
  const [pawsGalleryRoute, setPawsGalleryRoute] = useState(
    typeof window !== 'undefined' ? parsePawsGalleryPath(window.location.pathname) : null);
  // Public event galleries: /events/<event> and /events/<event>/<slug>.
  const [eventGalleryRoute, setEventGalleryRoute] = useState(
    typeof window !== 'undefined' ? parseEventGalleryPath(window.location.pathname) : null);
  // Marketing site (src/site) route. The pages live in their own module; this
  // holds the path they render from, kept in step by handleRoute below.
  const [sitePath, setSitePath] = useState(
    typeof window !== 'undefined' ? window.location.pathname : '/');
  const [showPhotographerQuote, setShowPhotographerQuote] = useState(false);
  const [showGallery, setShowGallery] = useState(false);

  // Campaign splash gateway shown at the root URL. Initialized synchronously to
  // avoid a flash of the main site before the effect runs.
  const [showSplash, setShowSplash] = useState(() => {
    if (!SPLASH_LANDING_ACTIVE || typeof window === 'undefined') return false;
    const p = window.location.pathname;
    const atRoot = p === '/' || p === '';
    let skipped = false;
    try { skipped = !!sessionStorage.getItem('hddn_skipSplash'); } catch { /* ignore */ }
    return atRoot && !skipped;
  });

  // /admin URL routing — set when path is /admin, resolved by an effect that
  // also watches `user` so an already-signed-in admin lands directly on the
  // dashboard while everyone else gets the login modal.
  const [adminRouteRequested, setAdminRouteRequested] = useState(false);
  useEffect(() => {
    if (!adminRouteRequested) return;
    if (user && user.role === 'admin') {
      setShowAdmin(true);
      setAdminRouteRequested(false);
    } else if (!localStorage.getItem('hddn_token')) {
      setShowAdminLogin(true);
      setAdminRouteRequested(false);
    }
    // If a token exists but user hasn't loaded yet, wait — the auth fetch will
    // populate `user` shortly and this effect will re-run.
  }, [adminRouteRequested, user]);

  // Check URL for /blog, /calendar, /openhouse routes on mount + handle browser back/forward
  useEffect(() => {
    const handleRoute = () => {
      const path = window.location.pathname;
      // /gallery was the portfolio URL and is indexed; it is now /work.
      // The server answers a fresh hit with a 301; this covers in-app links.
      const redirect = isRedirect(path);
      if (redirect) { navigate(redirect); return; }
      setSitePath(path);
      // Root URL shows the campaign splash unless the visitor already entered.
      const atRoot = path === '/' || path === '';
      let skipped = false;
      try { skipped = !!sessionStorage.getItem('hddn_skipSplash'); } catch { /* ignore */ }
      setShowSplash(SPLASH_LANDING_ACTIVE && atRoot && !skipped);
      setShowGallery(false); // default off; the /gallery branch below turns it on
      setShowPrivacy(path === '/privacy' || path === '/privacy/');
      setShowPawsDetails(path === '/paws-details' || path === '/paws-details/');
      setShowPawsAug9(path === '/paws-aug9' || path === '/paws-aug9/');
      setPhotoToken(path.startsWith('/photos/') ? path.replace('/photos/', '').replace(/\/$/, '') : null);
      setSignToken(path.startsWith('/sign/') ? path.replace('/sign/', '').replace(/\/$/, '') : null);
      setOnboardToken(path.startsWith('/onboard/') ? path.replace('/onboard/', '').replace(/\/$/, '') : null);
      setPawsGalleryRoute(parsePawsGalleryPath(path));
      setEventGalleryRoute(parseEventGalleryPath(path));
      if (path === '/blog') {
        setShowBlog(true);
        setBlogSlug(null);
        setCalendarToken(null);
        setShowOpenHouse(false);
      } else if (path.startsWith('/blog/')) {
        setShowBlog(true);
        setBlogSlug(path.replace('/blog/', ''));
        setCalendarToken(null);
        setShowOpenHouse(false);
      } else if (path.startsWith('/calendar/')) {
        setCalendarToken(path.replace('/calendar/', ''));
        setShowBlog(false);
        setBlogSlug(null);
        setShowOpenHouse(false);
      } else if (path === '/openhouse') {
        setShowOpenHouse(true);
        setOpenHouseArchiveSlug(null);
        setShowBlog(false);
        setBlogSlug(null);
        setCalendarToken(null);
        setShowNewsletter(false);
      } else if (path.startsWith('/openhouse/')) {
        setShowOpenHouse(true);
        setOpenHouseArchiveSlug(decodeURIComponent(path.replace('/openhouse/', '')));
        setShowBlog(false);
        setBlogSlug(null);
        setCalendarToken(null);
        setShowNewsletter(false);
      } else if (path === '/newsletter' || path === '/subscribe') {
        setShowNewsletter(true);
        setShowOpenHouse(false);
        setOpenHouseArchiveSlug(null);
        setShowBlog(false);
        setBlogSlug(null);
        setCalendarToken(null);
        setShowPhotographerQuote(false);
      } else if (path === '/photographer' || path === '/photographer/' || path === '/photo-quote') {
        setShowPhotographerQuote(true);
        setShowNewsletter(false);
        setShowOpenHouse(false);
        setOpenHouseArchiveSlug(null);
        setShowBlog(false);
        setBlogSlug(null);
        setCalendarToken(null);
      } else if (path === '/paws' || path === '/paws/' || path === '/paws-and-portraits') {
        // The informational page is gone with the campaign it advertised. These
        // links were really for the gallery, which is where the closed-signups
        // message already sends people.
        window.history.replaceState({}, '', '/paws-gallery');
        setPawsGalleryRoute({ slug: null });
        setShowGallery(false);
        setShowPhotographerQuote(false);
        setShowNewsletter(false);
        setShowOpenHouse(false);
        setOpenHouseArchiveSlug(null);
        setShowBlog(false);
        setBlogSlug(null);
        setCalendarToken(null);
      } else if (path === '/gallery' || path === '/gallery/') {
        setShowGallery(true);
        setShowPhotographerQuote(false);
        setShowNewsletter(false);
        setShowOpenHouse(false);
        setOpenHouseArchiveSlug(null);
        setShowBlog(false);
        setBlogSlug(null);
        setCalendarToken(null);
      } else if (path === '/admin' || path === '/admin/') {
        setShowBlog(false);
        setBlogSlug(null);
        setCalendarToken(null);
        setShowOpenHouse(false);
        setShowNewsletter(false);
        setShowPhotographerQuote(false);
        setAdminRouteRequested(true);
      } else {
        setShowBlog(false);
        setBlogSlug(null);
        setCalendarToken(null);
        setShowOpenHouse(false);
        setOpenHouseArchiveSlug(null);
        setShowNewsletter(false);
        setShowPhotographerQuote(false);
      }
    };
    handleRoute();
    window.addEventListener('popstate', handleRoute);
    return () => window.removeEventListener('popstate', handleRoute);
  }, []);

  // Campaign splash gateway (root URL only)
  if (showSplash) {
    return (
      <SplashLanding
        onEnterStudio={() => {
          try { sessionStorage.setItem('hddn_skipSplash', '1'); } catch { /* ignore */ }
          setShowSplash(false);
        }}
        onPaws={() => {
          try { sessionStorage.setItem('hddn_skipSplash', '1'); } catch { /* ignore */ }
          setShowSplash(false);
          navigate('/paws-gallery');
        }}
      />
    );
  }

  // Paws & Portraits private photo-order page
  if (photoToken) {
    return <PawsPhotoOrderPage token={photoToken} />;
  }

  // Public contract signing page
  if (signToken) {
    return <ContractSignPage token={signToken} />;
  }

  // Contractor onboarding page (profile + agreement signing)
  if (onboardToken) {
    return <ContractorOnboardPage token={onboardToken} />;
  }

  if (pawsGalleryRoute) {
    return <PawsGallery slug={pawsGalleryRoute.slug} />;
  }

  if (eventGalleryRoute) {
    return <EventGallery event={eventGalleryRoute.event} slug={eventGalleryRoute.slug} />;
  }

  // Public calendar view
  if (calendarToken) {
    return <PublicCalendar token={calendarToken} />;
  }

  // Marketing site. Matches only '/', '/content', '/studio', '/work', '/about'
  // and '/contact', so every other branch below is reached exactly as before.
  // Sits after the token routes above so a signing or gallery link still wins.
  // Two flows land on "/" carrying a query param and render below this point:
  // a password reset link (?reset_token=) and Stripe's return (?payment=&ref=).
  // The marketing site owns "/", so without this it answers both — a reset link
  // opens the home page, and a customer who just paid gets marketing copy
  // instead of their confirmation. Hand those two back to the booking app.
  const siteRoute = (resetToken || paymentReturn) ? null : matchSiteRoute(sitePath);
  if (siteRoute) {
    return <SiteShell route={siteRoute} path={siteRoute.path} />;
  }

  // Open house landing page
  if (showOpenHouse) {
    return <OpenHousePage archiveSlug={openHouseArchiveSlug} />;
  }

  // Newsletter subscribe page
  if (showNewsletter) {
    return <NewsletterPage />;
  }

  // Privacy policy page
  if (showPrivacy) {
    return <PrivacyPolicyPage />;
  }

  // Paws & Portraits questionnaire + scheduling page (invited attendees)
  if (showPawsDetails) {
    return <PawsQuestionnairePage round={1} />;
  }

  if (showPawsAug9) {
    return <PawsQuestionnairePage round={2} />;
  }

  // Photographer quote request page
  if (showPhotographerQuote) {
    return <PhotographerQuotePage />;
  }

  // Paws & Portraits public signup page

  // Gallery / portfolio page
  if (showGallery) {
    return <GalleryPage />;
  }

  // Admin dashboard
  if (showAdmin && user && user.role === 'admin') {
    return <AdminDashboard user={user} onBack={() => { if (onAdminHost()) { window.location.href = siteUrl('/'); return; } setShowAdmin(false); fetchAvailability(); }} onPackagesConfigSaved={refreshPackagesConfig} />;
  }

  // The admin hostname serves nothing customer-facing. Without this, anyone who
  // is not a signed-in admin would get the booking calendar showing through
  // behind the login modal — the customer view leaking onto the very host that
  // exists to keep it out.
  if (onAdminHost()) {
    const signedInNotAdmin = !!user && user.role !== 'admin';
    return (
      <div style={{ minHeight: '100vh', background: site.paper, display: 'flex', flexDirection: 'column' }}>
        <SimpleMasthead label="Admin" />
        <div style={{ flex: 1, display: 'grid', placeItems: 'center', padding: '72px 24px' }}>
          <div style={{ maxWidth: 440, textAlign: 'center' }}>
            <p style={{ fontFamily: fonts, fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: site.accentInk, margin: 0 }}>
              Staff only
            </p>
            <h1 style={{ fontFamily: sans, fontSize: 34, fontWeight: 800, letterSpacing: '-0.035em', lineHeight: 1.05, color: site.ink, margin: '14px 0 0' }}>
              HDDN LAB dashboard
            </h1>
            <p style={{ fontFamily: sans, fontSize: 15, color: site.muted, lineHeight: 1.7, margin: '16px 0 28px' }}>
              {signedInNotAdmin
                ? 'This account does not have dashboard access. Sign in with an admin account, or ask someone on the team to add you under Team Access.'
                : 'Bookings, clients, contracts and the site all live in here. Sign in to continue.'}
            </p>
            <button
              onClick={signedInNotAdmin ? handleLogout : () => setShowAdminLogin(true)}
              style={{
                fontFamily: fonts, fontSize: 12, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase',
                background: site.ink, color: site.onGround, border: 'none', padding: '15px 34px', cursor: 'pointer',
              }}
            >
              {signedInNotAdmin ? 'Sign out' : 'Sign in'}
            </button>
            <p style={{ marginTop: 26 }}>
              <a href={siteUrl('/')} style={{ fontFamily: fonts, fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: site.muted, textDecoration: 'none' }}>
                Back to the site &rarr;
              </a>
            </p>
          </div>
        </div>
        {showAdminLogin && !user && (
          <AdminLoginModal
            onAuth={(u) => { setUser(u); setShowAdminLogin(false); if (u && u.role === 'admin') setShowAdmin(true); }}
            onClose={() => setShowAdminLogin(false)}
          />
        )}
      </div>
    );
  }

  // Public blog views
  if (showBlog) {
    if (blogSlug) {
      return (
        <BlogPostPage
          slug={blogSlug}
          onBack={() => { setShowBlog(false); setBlogSlug(null); window.history.pushState({}, '', '/'); }}
          onBackToList={() => { setBlogSlug(null); window.history.pushState({}, '', '/blog'); }}
        />
      );
    }
    return (
      <BlogListingPage
        onViewPost={(slug) => { setBlogSlug(slug); window.history.pushState({}, '', `/blog/${slug}`); }}
        onBack={() => { setShowBlog(false); setBlogSlug(null); window.history.pushState({}, '', '/'); }}
      />
    );
  }

  // Main booking view
  return (
    <div style={{ ...baseStyles.app, fontFamily: sans }}>
      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        * { box-sizing: border-box; }
        body { margin: 0; }
        .gallery-scroll::-webkit-scrollbar { display: none; }
        @media (max-width: 640px) {
          .hddn-container { padding-left: 14px !important; padding-right: 14px !important; }
          .hddn-modal-pad { padding: 24px 18px !important; }
          .hddn-stack-mobile { grid-template-columns: 1fr !important; gap: 16px !important; }
          .hddn-pricing-banner { gap: 14px !important; }
          .hddn-pricing-banner > [data-divider="true"] { display: none !important; }
          .hddn-touch-target { padding: 10px 14px !important; font-size: 12px !important; }
          .hddn-carousel-arrow { display: none !important; }
          .hddn-nav-desktop { display: none !important; }
          .hddn-nav-mobile { display: flex !important; }
          .hddn-nav-drawer { display: block !important; }
        }
      `}</style>

      <Header user={user} onLogout={handleLogout} onProfile={handleShowHistory} onAdmin={goToAdmin} onAdminLogin={goToAdminLogin} onCustomerView={leaveAdmin} onLogin={() => setShowCustomerLogin(true)} onManageBooking={() => setShowManageBooking(true)} onBlog={() => { setShowBlog(true); window.history.pushState({}, '', '/blog'); }} onSubscribe={() => { setShowNewsletter(true); window.history.pushState({}, '', '/newsletter'); }} onPhotographer={() => { setShowPhotographerQuote(true); window.history.pushState({}, '', '/photographer'); }} onAbout={() => { setInfoTab('about'); setTimeout(() => { const el = document.getElementById('info-section'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }, 60); }} onGallery={() => { setShowGallery(true); window.history.pushState({}, '', '/gallery'); }} />

      <main className="hddn-container" style={{ ...baseStyles.container, paddingTop: 32, paddingBottom: 60 }}>
        {/* Payment return banner */}
        {paymentReturn && (
          <div style={{
            background: paymentReturn.status === 'success' ? palette.sage : palette.orangeLight,
            color: palette.white,
            padding: '16px 24px',
            marginBottom: 20,
            textAlign: 'center',
            position: 'relative',
          }}>
            <div style={{ fontFamily: fonts, fontSize: 14, fontWeight: 700, letterSpacing: 1 }}>
              {paymentReturn.status === 'success'
                ? `Payment confirmed! Your booking ${paymentReturn.ref} is reserved.`
                : `Payment was cancelled. Your booking ${paymentReturn.ref} has not been confirmed.`}
            </div>
            <div style={{ fontSize: 12, marginTop: 4, opacity: 0.9 }}>
              {paymentReturn.status === 'success'
                ? 'A confirmation email will be sent shortly. Your card is saved on file for any incidentals.'
                : 'You can try booking again by selecting a date below.'}
            </div>
            <button
              onClick={() => setPaymentReturn(null)}
              style={{
                position: 'absolute', top: 8, right: 12,
                background: 'none', border: 'none', color: palette.white,
                fontSize: 18, cursor: 'pointer', opacity: 0.8,
              }}
            >
              &times;
            </button>
          </div>
        )}

        {/* Space picker — what are you booking today? */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: palette.orange, marginBottom: 8, textAlign: 'center' }}>
            Step 1
          </div>
          <h2 style={{ fontFamily: sans, fontSize: 30, fontWeight: 800, color: site.ink, margin: 0, marginBottom: 18, letterSpacing: '-0.02em', textAlign: 'center' }}>
            What are you booking today?
          </h2>
          <div className="hddn-stack-mobile" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
            {[
              { id: 'upstairs-1', label: 'Full Studio', desc: 'Photo & video shoots, creative sessions', price: 'from $100/hr' },
              { id: 'upstairs-2', label: 'Podcast Recording', desc: 'Professional podcast recording', price: 'from $150/hr' },
            ].map((opt) => {
              const active = currentRoom === opt.id;
              return (
                <button
                  key={opt.id}
                  onClick={() => chooseRoom(opt.id)}
                  style={{
                    textAlign: 'left',
                    padding: '20px 22px',
                    background: active ? palette.brown : palette.white,
                    color: active ? palette.cream : palette.brown,
                    border: `2px solid ${active ? palette.brown : palette.border}`,
                    cursor: 'pointer',
                    fontFamily: sans,
                    transition: 'all 0.15s',
                    position: 'relative',
                  }}
                >
                  {active && (
                    <div style={{ position: 'absolute', top: 10, right: 12, fontFamily: fonts, fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.orange }}>
                      Selected
                    </div>
                  )}
                  <div style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: active ? palette.cream : palette.textMuted, marginBottom: 6 }}>
                    {opt.id === 'upstairs-1' ? 'Studio' : 'Podcast'}
                  </div>
                  <div style={{ fontSize: 21, fontWeight: 700, marginBottom: 6, letterSpacing: '-0.01em' }}>{opt.label}</div>
                  <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 8 }}>{opt.desc}</div>
                  <div style={{ fontFamily: fonts, fontSize: 13, fontWeight: 700, color: active ? palette.orange : palette.brown }}>{opt.price}</div>
                </button>
              );
            })}
          </div>
          <p style={{ fontFamily: sans, fontSize: 12, color: palette.textMuted, textAlign: 'center', marginTop: 14, marginBottom: 0, lineHeight: 1.5 }}>
            Got something else in mind — an event, workshop, meeting, shoot we haven't listed? <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: palette.orange }}>Reach out</a> and we'll work it out.
          </p>
        </div>

        {/* Pricing banner — contextual based on selected room */}
        <div style={{
          background: palette.beige,
          border: `1px solid ${palette.border}`,
          padding: '20px 24px',
          marginBottom: 28,
        }}>
          <div className="hddn-pricing-banner" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 24, textAlign: 'center' }}>
            {currentRoom === 'upstairs-1' ? (
              <>
                <div>
                  <div style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.textMuted, marginBottom: 4 }}>Studio Rental</div>
                  <div style={{ fontFamily: fonts, fontSize: 24, fontWeight: 700, color: palette.brown }}>
                    $100<span style={{ fontSize: 14, fontWeight: 400 }}>/hr</span>
                  </div>
                  <div style={{ fontSize: 12, color: palette.textMuted }}>3 hr minimum</div>
                </div>
              </>
            ) : (
              Object.entries(packagesConfig?.podcastPackages || PODCAST_PACKAGES)
                .filter(([, pkg]) => pkg && pkg.enabled !== false)
                .map(([pkgId, pkg], idx) => {
                  const cheapest = (pkg.durations || []).reduce(
                    (min, d) => (!min || d.price < min.price ? d : min),
                    null
                  );
                  if (!cheapest) return null;
                  const unit = cheapest.hours === 1 ? '/hr' : `/${cheapest.hours}hr`;
                  return (
                    <React.Fragment key={pkgId}>
                      {idx > 0 && <div data-divider="true" style={{ width: 1, background: palette.border, alignSelf: 'stretch' }} />}
                      <div>
                        <div style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: palette.textMuted, marginBottom: 4 }}>{pkg.name}</div>
                        <div style={{ fontFamily: fonts, fontSize: 24, fontWeight: 700, color: palette.brown }}>
                          ${cheapest.price}<span style={{ fontSize: 14, fontWeight: 400 }}>{unit}</span>
                        </div>
                        <div style={{ fontSize: 12, color: palette.textMuted }}>from {cheapest.hours} hr</div>
                      </div>
                    </React.Fragment>
                  );
                })
            )}
          </div>
          <div style={{ fontSize: 12, color: palette.textMuted, letterSpacing: 1, textAlign: 'center', marginTop: 12 }}>
            Select a date below to book
          </div>
        </div>

        {/* Photo Gallery */}
        <PhotoGallery room={currentRoom} />

        {/* Month nav */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 20,
          }}
        >
          <button onClick={prevMonth} style={navBtnStyle}>&#8592;</button>
          <span
            style={{
              fontFamily: fonts,
              fontSize: 16,
              fontWeight: 700,
              letterSpacing: 2,
              textTransform: 'uppercase',
              color: palette.brown,
            }}
          >
            {MONTH_NAMES[viewMonth]} {viewYear}
          </span>
          <button onClick={nextMonth} style={navBtnStyle}>&#8594;</button>
        </div>

        {/* Calendar */}
        <Calendar
          year={viewYear}
          month={viewMonth}
          booked={booked}
          blocked={blocked}
          slots={slots}
          selected={selectedDate}
          onSelect={handleDateSelect}
        />

        <Legend />

        {/* Info Section (FAQs, Policies, Studio Rules, Contact) */}
        <InfoSection initialTab={infoTab} packagesConfig={packagesConfig} />
      </main>

      {/* Footer — the marketing site's, so the page ends the way its pages do.
          It is also where Paws, the journal and the newsletter now live: they
          came out of the desktop nav to make room for the site links, and a
          footer is where the marketing site keeps its secondary navigation. */}
      <footer style={{ background: site.ground, color: site.onGround, padding: '56px 0 30px', marginTop: 40 }}>
        <div className="hddn-container" style={{ ...baseStyles.container }}>
          <div className="hddn-stack-mobile" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 28 }}>
            <div>
              <p style={{ fontFamily: sans, fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', margin: 0 }}>Just Create</p>
              <address style={{ fontStyle: 'normal', marginTop: 16, color: site.groundMuted, fontSize: 13, lineHeight: 1.7 }}>
                72 Centre Avenue<br />New Rochelle, NY 10801<br />
                <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: site.accent, textDecoration: 'none' }}>{CONTACT_EMAIL}</a>
              </address>
            </div>
            {[
              { head: 'Hire us', links: [
                { label: 'Content creation', go: () => navigate('/content') },
                { label: 'Book a photographer', go: () => navigate('/photographer') },
                { label: 'The work', go: () => navigate('/work') },
              ] },
              { head: 'Book the room', links: [
                { label: 'The studio & rates', go: () => navigate('/studio') },
                { label: 'Availability', go: () => navigate('/book') },
                { label: 'Manage booking', go: () => setShowManageBooking(true) },
              ] },
              { head: 'More', links: [
                { label: 'Paws & Portraits', go: () => navigate('/paws-gallery') },
                { label: 'Journal', go: () => { setShowBlog(true); window.history.pushState({}, '', '/blog'); } },
                { label: 'Subscribe', go: () => { setShowNewsletter(true); window.history.pushState({}, '', '/newsletter'); } },
              ] },
            ].map((col) => (
              <div key={col.head}>
                <p style={{ fontFamily: fonts, fontSize: 11, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: site.accent, margin: 0 }}>{col.head}</p>
                <ul style={{ listStyle: 'none', margin: '14px 0 0', padding: 0, display: 'grid', gap: 9 }}>
                  {col.links.map((l) => (
                    <li key={l.label}>
                      <button
                        onClick={l.go}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: sans, fontSize: 13, color: site.onGround, textAlign: 'left' }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = site.accent; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = site.onGround; }}
                      >
                        {l.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div style={{ borderTop: `1px solid ${site.groundLine}`, marginTop: 34, paddingTop: 20, display: 'flex', flexWrap: 'wrap', gap: 14, justifyContent: 'space-between', fontFamily: fonts, fontSize: 11, letterSpacing: '0.08em', color: site.groundMuted }}>
            <span>© {new Date().getFullYear()} HDDN LAB · 8 AM – 8 PM, seven days</span>
            <a
              href="/privacy"
              onClick={(e) => { e.preventDefault(); setShowPrivacy(true); window.history.pushState({}, '', '/privacy'); window.scrollTo(0, 0); }}
              style={{ color: site.groundMuted, textDecoration: 'none' }}
            >
              Privacy
            </a>
          </div>
        </div>
      </footer>

      {/* Modals */}
      {showModal && selectedDate && (
        <BookingModal
          date={selectedDate}
          room={currentRoom}
          user={user}
          slots={slots[selectedDate] || []}
          onClose={() => { setShowModal(false); setSelectedDate(null); }}
          onSubmit={handleBookingSubmit}
          stripeEnabled={stripeEnabled}
          packagesConfig={packagesConfig}
        />
      )}

      {successBooking && (
        <SuccessModal
          booking={successBooking}
          onClose={() => setSuccessBooking(null)}
        />
      )}

      {showHistory && (
        <BookingHistory
          bookings={userBookings}
          onClose={() => setShowHistory(false)}
          onRefresh={handleShowHistory}
        />
      )}

      {/* Customer Login Modal */}
      {showCustomerLogin && !user && (
        <CustomerLoginModal
          onAuth={(u) => { setUser(u); setShowCustomerLogin(false); }}
          onClose={() => setShowCustomerLogin(false)}
        />
      )}

      {/* Manage Booking Modal (public cancellation) */}
      {showManageBooking && (
        <ManageBookingModal onClose={() => setShowManageBooking(false)} />
      )}

      {/* Admin Login Modal */}
      {showAdminLogin && !user && (
        <AdminLoginModal
          onAuth={(u) => { setUser(u); setShowAdminLogin(false); if (u && u.role === 'admin') setShowAdmin(true); }}
          onClose={() => setShowAdminLogin(false)}
        />
      )}

      {/* Password Reset Modal */}
      {resetToken && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(60,43,31,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 20,
          }}
        >
          <ResetPasswordModal
            token={resetToken}
            onClose={() => setResetToken(null)}
          />
        </div>
      )}

    </div>
  );
}

const navBtnStyle = {
  background: 'none',
  border: `1px solid ${palette.border}`,
  fontFamily: fonts,
  fontSize: 18,
  padding: '6px 14px',
  cursor: 'pointer',
  color: palette.brown,
};
